/////////////////////////////////////////////////////////
// Configurations related to listing.                  //
// Main configuration here is the extended data config //
/////////////////////////////////////////////////////////

// NOTE: if you want to change the structure of the data,
// you should also check src/util/configHelpers.js
// some validation is added there.

/**
 * Configuration options for listing fields (custom extended data fields):
 * - key:                           Unique key for the extended data field.
 * - scope (optional):              Scope of the extended data can be either 'public' or 'private'.
 *                                  Default value: 'public'.
 *                                  Note: listing doesn't support 'protected' scope atm.
 * - includeForListingTypes:        An array of listing types, for which the extended
 *   (optional)                     data is relevant and should be added.
 * - schemaType (optional):         Schema for this extended data field.
 *                                  This is relevant when rendering components and querying listings.
 *                                  Possible values: 'enum', 'multi-enum', 'text', 'long', 'boolean'.
 * - enumOptions (optional):        Options shown for 'enum' and 'multi-enum' extended data.
 *                                  These are used to render options for inputs and filters on
 *                                  EditListingPage, ListingPage, and SearchPage.
 * - filterConfig:                  Filter configuration for listings query.
 *    - indexForSearch (optional):    If set as true, it is assumed that the extended data key has
 *                                    search index in place. I.e. the key can be used to filter
 *                                    listing queries (then scope needs to be 'public').
 *                                    Note: Flex CLI can be used to set search index for the key:
 *                                    https://www.sharetribe.com/docs/references/extended-data/#search-schema
 *                                    Read more about filtering listings with public data keys from API Reference:
 *                                    https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
 *                                    Default value: false,
 *   - filterType:                    Sometimes a single schemaType can be rendered with different filter components.
 *                                    For 'enum' schema, filterType can be 'SelectSingleFilter' or 'SelectMultipleFilter'
 *   - label:                         Label for the filter, if the field can be used as query filter
 *   - searchMode (optional):         Search mode for indexed data with multi-enum schema.
 *                                    Possible values: 'has_all' or 'has_any'.
 *   - group:                         SearchPageWithMap has grouped filters. Possible values: 'primary' or 'secondary'.
 * - showConfig:                    Configuration for rendering listing. (How the field should be shown.)
 *   - label:                         Label for the saved data.
 *   - isDetail                       Can be used to hide detail row (of type enum, boolean, or long) from listing page.
 *                                    Default value: true,
 * - saveConfig:                    Configuration for adding and modifying extended data fields.
 *   - label:                         Label for the input field.
 *   - placeholderMessage (optional): Default message for user input.
 *   - isRequired (optional):         Is the field required for providers to fill
 *   - requiredMessage (optional):    Message for those fields, which are mandatory.
 */
export const listingFields = [
  {
    key: 'early_historical_winds_renaissance_baroque_flute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'philippe_allaindupre', label: 'Philippe Allain-Dupré' },
      { option: 'g_astor_co', label: 'G. Astor & Co.' },
      { option: 'aulos_toyama_musical_instrument_co', label: 'Aulos (Toyama Musical Instrument Co.)' },
      { option: 'fridtjof_aurin', label: 'Fridtjof Aurin' },
      { option: 'roberto_bando', label: 'Roberto Bando' },
      { option: 'jeanfrancois_beaudin', label: 'Jean-François Beaudin' },
      { option: 'stefan_beck_musikhandwerk', label: 'Stefan Beck (Musikhandwerk)' },
      { option: 'vincent_bernolin', label: 'Vincent Bernolin' },
      { option: 'boaz_berney', label: 'Boaz Berney' },
      { option: 'andreas_besteck', label: 'Andreas Besteck' },
      { option: 'beukers', label: 'Beukers' },
      { option: 'phil_bleazey', label: 'Phil Bleazey' },
      { option: 'jeanluc_boudreau', label: 'Jean-Luc Boudreau' },
      { option: 'bressan_pierre_jaillard', label: 'Bressan (Pierre Jaillard)' },
      { option: 'david_r_brooks', label: 'David R. Brooks' },
      { option: 'pierre_gabriel_buffardin', label: 'Pierre Gabriel Buffardin' },
      { option: 'roderick_cameron', label: 'Roderick Cameron' },
      { option: 'eugene_crijnen', label: 'Eugene Crijnen' },
      { option: 'jan_de_winne', label: 'Jan De Winne' },
      { option: 'daniel_deitch', label: 'Daniel Deitch' },
      { option: 'denner_family', label: 'Denner family' },
      { option: 'jacob_denner', label: 'Jacob Denner' },
      { option: 'fabio_di_natale', label: 'Fabio Di Natale' },
      { option: 'martin_doyle', label: 'Martin Doyle' },
      { option: 'johann_heinrich_eichentopf', label: 'Johann Heinrich Eichentopf' },
      { option: 'pierre_etchegoyen', label: 'Pierre Etchegoyen' },
      { option: 'thomas_fehr', label: 'Thomas Fehr' },
      { option: 'florio', label: 'Florio' },
      { option: 'folkers_powell', label: 'Folkers & Powell' },
      { option: 'john_gallagher', label: 'John Gallagher' },
      { option: 'andreas_glatt', label: 'Andreas Glatt' },
      { option: 'benjamin_jonas_glauser', label: 'Benjamin Jonas Glauser' },
      { option: 'august_grenser', label: 'August Grenser' },
      { option: 'heinrich_grenser', label: 'Heinrich Grenser' },
      { option: 'griessling_schlott', label: 'Griessling & Schlott' },
      { option: 'gilbert_gros_babillane', label: 'Gilbert Gros (Babillane)' },
      { option: 'gt_instruments_grzegorz_tomaszewicz', label: 'GT Instruments (Grzegorz Tomaszewicz)' },
      { option: 'marcelo_gurovich', label: 'Marcelo Gurovich' },
      { option: 'richard_haka', label: 'Richard Haka' },
      { option: 'johann_heitz', label: 'Johann Heitz' },
      { option: 'hotteterre_co_keigo_takesa', label: 'Hotteterre & Co. (Keigo Takesa)' },
      { option: 'hotteterre_family', label: 'Hotteterre family' },
      { option: 'jacques_hotteterre', label: 'Jacques Hotteterre' },
      { option: 'martin_hotteterre', label: 'Martin Hotteterre' },
      { option: 'guido_klemisch', label: 'Guido Klemisch' },
      { option: 'gerhard_kowalewsky', label: 'Gerhard Kowalewsky' },
      { option: 'gilles_lot', label: 'Gilles Lot' },
      { option: 'martin_lot', label: 'Martin Lot' },
      { option: 'thomas_lot', label: 'Thomas Lot' },
      { option: 'ariel_louis', label: 'Ariel Louis' },
      { option: 'kiyoto_mashima', label: 'Kiyoto Mashima' },
      { option: 'terry_mcgee', label: 'Terry McGee' },
      { option: 'jeanjacques_melzer', label: 'Jean-Jacques Melzer' },
      { option: 'sam_murray', label: 'Sam Murray' },
      { option: 'pierre_naust', label: 'Pierre Naust' },
      { option: 'peter_noy', label: 'Peter Noy' },
      { option: 'johann_wilhelm_oberlender', label: 'Johann Wilhelm Oberlender' },
      { option: 'carlo_palanca', label: 'Carlo Palanca' },
      { option: 'johann_poerschmann', label: 'Johann Poerschmann' },
      { option: 'alain_polak', label: 'Alain Polak' },
      { option: 'simon_polak', label: 'Simon Polak' },
      { option: 'johann_joachim_quantz', label: 'Johann Joachim Quantz' },
      { option: 'claude_rafi', label: 'Claude Rafi' },
      { option: 'michau_rafi', label: 'Michau Rafi' },
      { option: 'nicolas_marcel_raingo', label: 'Nicolas Marcel Raingo' },
      { option: 'chris_raven', label: 'Chris Raven' },
      { option: 'luca_ripanti', label: 'Luca Ripanti' },
      { option: 'jeanjacques_rippert', label: 'Jean-Jacques Rippert' },
      { option: 'godfridus_adrianus_rottenburgh', label: 'Godfridus Adrianus Rottenburgh' },
      { option: 'joannes_hyacinthus_rottenburgh', label: 'Joannes Hyacinthus Rottenburgh' },
      { option: 'scherer', label: 'Scherer' },
      { option: 'john_just_schuchart', label: 'John Just Schuchart' },
      { option: 'thomas_stanesby_jr', label: 'Thomas Stanesby Jr.' },
      { option: 'thomas_stanesby_sr', label: 'Thomas Stanesby Sr.' },
      { option: 'giovanni_tardino', label: 'Giovanni Tardino' },
      { option: 'tortochot', label: 'Tortochot' },
      { option: 'rudolf_tutz', label: 'Rudolf Tutz' },
      { option: 'thijs_van_baarsel', label: 'Thijs van Baarsel' },
      { option: 'remko_van_der_vegt', label: 'Remko van der Vegt' },
      { option: 'van_heerde', label: 'Van Heerde' },
      { option: 'friedrich_von_huene', label: 'Friedrich von Huene' },
      { option: 'alain_stephanie_weemaels', label: 'Alain & Stéphanie Weemaels' },
      { option: 'martin_wenner', label: 'Martin Wenner' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'early_historical_winds_renaissance_baroque_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: false,
    },
  },
  {
    key: 'early_historical_winds_renaissance_baroque_flute_era',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'baroque', label: 'Baroque' },
      { option: 'renaissance', label: 'Renaissance' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'early_historical_winds_renaissance_baroque_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Era',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Era',
      isDetail: true,
    },
    saveConfig: {
      label: 'Era',
      isRequired: false,
    },
  },
  {
    key: 'early_historical_winds_renaissance_baroque_flute_model',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'rottenburgh', label: 'Rottenburgh' },
      { option: 'grenser', label: 'Grenser' },
      { option: 'hotteterre', label: 'Hotteterre' },
      { option: 'denner', label: 'Denner' },
      { option: 'stanesby', label: 'Stanesby' },
      { option: 'quantz', label: 'Quantz' },
      { option: 'naust', label: 'Naust' },
      { option: 'rafi', label: 'Rafi' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'early_historical_winds_renaissance_baroque_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Model After',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Model After',
      isDetail: true,
    },
    saveConfig: {
      label: 'Model After',
      isRequired: false,
    },
  },
  {
    key: 'early_historical_winds_renaissance_baroque_flute_pitch',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'a440', label: 'A=440' },
      { option: 'a430', label: 'A=430' },
      { option: 'a415', label: 'A=415' },
      { option: 'a392', label: 'A=392' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'early_historical_winds_renaissance_baroque_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Pitch Standard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Pitch Standard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Pitch Standard',
      isRequired: false,
    },
  },
  {
    key: 'early_historical_winds_renaissance_baroque_flute_material',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'grenadilla', label: 'Grenadilla' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'maple', label: 'Maple' },
      { option: 'plum', label: 'Plum' },
      { option: 'plastic_resin', label: 'Plastic / Resin' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'early_historical_winds_renaissance_baroque_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_flutes_bansuri_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'anubodh', label: 'Anubodh' },
      { option: 'ramesh_bakale', label: 'Ramesh Bakale' },
      { option: 'anand_dhotre_dhotre_flutes', label: 'Anand Dhotre (Dhotre Flutes)' },
      { option: 'geoffrey_ellis_ellis_flutes', label: 'Geoffrey Ellis (Ellis Flutes)' },
      { option: 'ethnic_musical', label: 'Ethnic Musical' },
      { option: 'pratap_gujar', label: 'Pratap Gujar' },
      { option: 'digamber_kamble_digamber_flutes', label: 'Digamber Kamble (Digamber Flutes)' },
      { option: 'mukund_lekurwale_mukund_flutes', label: 'Mukund Lekurwale (Mukund Flutes)' },
      { option: 'linus_lundquist_bansuri_shop', label: 'Linus Lundquist (Bansuri Shop)' },
      { option: 'ravi_shankar_mishra_bansuri_world', label: 'Ravi Shankar Mishra (Bansuri World)' },
      { option: 'one_world_flutes_kirti_l_shah', label: 'One World Flutes (Kirti L. Shah)' },
      { option: 'hiren_pandit', label: 'Hiren Pandit' },
      { option: 'kanti_patel_kanti_flutes', label: 'Kanti Patel (Kanti Flutes)' },
      { option: 'nilesh_pote_kanha_flutes', label: 'Nilesh Pote (Kanha Flutes)' },
      { option: 'radhe_flutes_tej_kumar_sharma', label: 'Radhe Flutes (Tej Kumar Sharma)' },
      { option: 'satyabrata_sahu_satyabrata_flutes', label: 'Satyabrata Sahu (Satyabrata Flutes)' },
      { option: 'sarfuddin_sarfuddin_flutes', label: 'Sarfuddin (Sarfuddin Flutes)' },
      { option: 'dinesh_sharma', label: 'Dinesh Sharma' },
      { option: 'deepak_soni_dp_flutes', label: 'Deepak Soni (DP Flutes)' },
      { option: 'juan_tepui_tepui_bansuri', label: 'Juan Tepui (Tepui Bansuri)' },
      { option: 'subhash_thakur_punam_flutes', label: 'Subhash Thakur (Punam Flutes)' },
      { option: 'harsh_wardhan_international_bansuri_academy', label: 'Harsh Wardhan (International Bansuri Academy)' },
      { option: 'jeff_whittier', label: 'Jeff Whittier' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_flutes_bansuri',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_flutes_bansuri_size',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'small_piccolo_treble', label: 'Small (Piccolo / Treble)' },
      { option: 'medium_middle_octave', label: 'Medium (Middle Octave)' },
      { option: 'bass', label: 'Bass' },
      { option: 'double_bass_shankh', label: 'Double Bass (Shankh)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_flutes_bansuri',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size / Register',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size / Register',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size / Register',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_flutes_bansuri_holes',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: '8_venu_carnatic', label: '8 (Venu / Carnatic)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_flutes_bansuri',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Holes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Holes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Holes',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_flutes_bansuri_material',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'wood', label: 'Wood' },
      { option: 'acrylic', label: 'Acrylic' },
      { option: 'pvc_fiberglass', label: 'PVC / Fiberglass' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'metal', label: 'Metal' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_flutes_bansuri',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_flutes_bansuri_pitch',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'a440', label: 'A=440' },
      { option: 'a432', label: 'A=432' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_flutes_bansuri',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Pitch Standard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Pitch Standard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Pitch Standard',
      isRequired: false,
    },
  },
  {
    key: 'flute_other_flutes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'anubodh', label: 'Anubodh' },
      { option: 'geoffrey_ellis', label: 'Geoffrey Ellis' },
      { option: 'erik_the_flutemaker_erik_sampson', label: 'Erik the Flutemaker (Erik Sampson)' },
      { option: 'gentle_winds_flutes', label: 'Gentle Winds Flutes' },
      { option: 'patrick_olwell', label: 'Patrick Olwell' },
      { option: 'one_world_flutes', label: 'One World Flutes' },
      { option: 'serenity_bamboo_flutes', label: 'Serenity Bamboo Flutes' },
      { option: 'sopro_flutes_rui_gomes', label: 'Sopro Flutes (Rui Gomes)' },
      { option: 'peter_spring', label: 'Peter Spring' },
      { option: 'woodsong_rob_yard', label: 'Woodsong (Rob Yard)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_other_flutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'early_historical_winds_renaissance_baroque_flute_joints',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '1piece', label: '1-Piece' },
      { option: '2piece', label: '2-Piece' },
      { option: '3piece', label: '3-Piece' },
      { option: '4piece', label: '4-Piece' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'early_historical_winds_renaissance_baroque_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Joints',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Joints',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Joints',
      isRequired: false,
    },
  },
  {
    key: 'flute_concert_boehm_flute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'christopher_a_flatell', label: 'Christopher Abell' },
      { option: 'aihara', label: 'Aihara' },
      { option: 'akiyama', label: 'Akiyama' },
      { option: 'michael_j_allen', label: 'Michael J. Allen' },
      { option: 'edward_ed_almeida', label: 'Edward "Ed" Almeida' },
      { option: 'altus', label: 'Altus' },
      { option: 'juan_arista', label: 'Juan Arista' },
      { option: 'manuel_arista', label: 'Manuel Arista' },
      { option: 'miguel_arista', label: 'Miguel Arista' },
      { option: 'armstrong_connselmer', label: 'Armstrong (Conn-Selmer)' },
      { option: 'artley_connselmer', label: 'Artley (Conn-Selmer)' },
      { option: 'avanti_connselmer', label: 'Avanti (Conn-Selmer)' },
      { option: 'azumi_by_altus', label: 'Azumi (by Altus)' },
      { option: 'brian_bertrem', label: 'Brian Bertrem' },
      { option: 'bonneville', label: 'Bonneville' },
      { option: 'ro_flatert_bigio', label: 'Robert Bigio' },
      { option: 'brannen_brothers', label: 'Brannen Brothers' },
      { option: 'anton_braun', label: 'Anton Braun' },
      { option: 'bulgheroni', label: 'Bulgheroni' },
      { option: 'burkartphelan', label: 'Burkart-Phelan' },
      { option: 'david_chu', label: 'David Chu' },
      { option: 'connselmer', label: 'Conn-Selmer' },
      { option: 'al_flatert_cooper', label: 'Albert Cooper' },
      { option: 'di_zhao', label: 'Di Zhao' },
      { option: 'sanford_sandy_drelinger', label: 'Sanford "Sandy" Drelinger' },
      { option: 'eloy', label: 'Eloy' },
      { option: 'emanuel_flutes', label: 'Emanuel Flutes' },
      { option: 'emerson', label: 'Emerson' },
      { option: 'alexander_eppler', label: 'Alexander Eppler' },
      { option: 'salvatore_faulisi', label: 'Salvatore Faulisi' },
      { option: 'thomas_fehr', label: 'Thomas Fehr' },
      { option: 'flute_la_flat', label: 'Flute Lab' },
      { option: 'fluteworx', label: 'Fluteworx' },
      { option: 'fmc', label: 'FMC' },
      { option: 'gemeinhardt', label: 'Gemeinhardt' },
      { option: 'geoghegan', label: 'Geoghegan' },
      { option: 'giorgio_giorgessi', label: 'Giorgio Giorgessi' },
      { option: 'mara_goosman', label: 'Mara Goosman' },
      { option: 'martin_gordon', label: 'Martin Gordon' },
      { option: 'harry_gosse', label: 'Harry Gosse' },
      { option: 'tom_green', label: 'Tom Green' },
      { option: 'guo_musical_instruments', label: 'Guo Musical Instruments' },
      { option: 'bernhard_hammig', label: 'Bernhard Hammig' },
      { option: 'philipp_hammig', label: 'Philipp Hammig' },
      { option: 'william_s_haynes', label: 'William S. Haynes' },
      { option: 'inder_flatinen', label: 'Inderbinen' },
      { option: 'iwao', label: 'Iwao' },
      { option: 'j_michael', label: 'J. Michael' },
      { option: 'jupiter', label: 'Jupiter' },
      { option: 'keefe', label: 'Keefe' },
      { option: 'eva_kingma', label: 'Eva Kingma' },
      { option: 'george_koregelos', label: 'George Koregelos' },
      { option: 'kotato', label: 'Kotato' },
      { option: 'christoph_kramer', label: 'Christoph Kramer' },
      { option: 'tom_lacy', label: 'Tom Lacy' },
      { option: 'jr_lafin', label: 'J.R. Lafin' },
      { option: 'jonathon_landell', label: 'Jonathon Landell' },
      { option: 'john_lehner', label: 'John Lehner' },
      { option: 'levit', label: 'Levit' },
      { option: 'gary_lewis', label: 'Gary Lewis' },
      { option: 'leonard_lopatin', label: 'Leonard Lopatin' },
      { option: 'louis_lot_historic', label: 'Louis Lot (historic)' },
      { option: 'john_lunn', label: 'John Lunn' },
      { option: 'mancke', label: 'Mancke' },
      { option: 'mascolo', label: 'Mascolo' },
      { option: 'mateki', label: 'Mateki' },
      { option: 'matit', label: 'Matit' },
      { option: 'mccanless', label: 'McCanless' },
      { option: 'mcchord', label: 'McChord' },
      { option: 'chris_mckenna', label: 'Chris McKenna' },
      { option: 'mehnert', label: 'Mehnert' },
      { option: 'miyazawa', label: 'Miyazawa' },
      { option: 'jack_moore', label: 'Jack Moore' },
      { option: 'muramatsu', label: 'Muramatsu' },
      { option: 'murali', label: 'Murali' },
      { option: 'nagahara', label: 'Nagahara' },
      { option: 'natsuki', label: 'Natsuki' },
      { option: 'nomata', label: 'Nomata' },
      { option: 'north_flatridge', label: 'Northbridge' },
      { option: 'juan_novo', label: 'Juan Novo' },
      { option: 'opperman', label: 'Opperman' },
      { option: 'andrew_oxley', label: 'Andrew Oxley' },
      { option: 'parmenon_buffet_crampon', label: 'Parmenon (Buffet Crampon)' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'pettry', label: 'Pettry' },
      { option: 'verne_q_powell_buffet_crampon', label: 'Verne Q. Powell (Buffet Crampon)' },
      { option: 'resona_by_burkart', label: 'Resona (by Burkart)' },
      { option: 'howell_ro_flaterts', label: 'Howell Roberts' },
      { option: 'rudall_carte_historic', label: 'Rudall Carte (historic)' },
      { option: 'gene_sagerman', label: 'Gene Sagerman' },
      { option: 'sakurai', label: 'Sakurai' },
      { option: 'sankyo', label: 'Sankyo' },
      { option: 'dana_sheridan', label: 'Dana Sheridan' },
      { option: 'solexa', label: 'Solexa' },
      { option: 'sonare_by_powell', label: 'Sonaré (by Powell)' },
      { option: 'song_flute', label: 'Song Flute' },
      { option: 'strau_flatinger', label: 'Straubinger' },
      { option: 'takumi', label: 'Takumi' },
      { option: 'tomasi', label: 'Tomasi' },
      { option: 'trevor_james', label: 'Trevor James' },
      { option: 'alfred_verhoef', label: 'Alfred Verhoef' },
      { option: 'viento', label: 'Viento' },
      { option: 'john_we_flatb', label: 'John Webb' },
      { option: 'martin_wenner', label: 'Martin Wenner' },
      { option: 'stephen_wessel', label: 'Stephen Wessel' },
      { option: 'david_c_williams', label: 'David C. Williams' },
      { option: 'david_wim_flaterly', label: 'David Wimberly' },
      { option: 'wisemann', label: 'Wisemann' },
      { option: 'peter_worrell', label: 'Peter Worrell' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'dean_yang', label: 'Dean Yang' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_concert_boehm_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'flute_concert_boehm_flute_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'piccolo', label: 'Piccolo' },
      { option: 'piccolo_headjoint', label: 'Piccolo Headjoint' },
      { option: 'concert_flute_c_flute', label: 'Concert Flute (C Flute)' },
      { option: 'concert_flute_headjoint', label: 'Concert Flute Headjoint' },
      { option: 'soprano_flute_e_flat', label: 'Soprano Flute (Eb)' },
      { option: 'tre_flatle_flute_g', label: 'Treble Flute (G)' },
      { option: 'flute_damour_b_flata', label: 'Flûte d\'Amour (Bb/A)' },
      { option: 'alto_flute_g', label: 'Alto Flute (G)' },
      { option: 'alto_flute_headjoint', label: 'Alto Flute Headjoint' },
      { option: 'bass_flute', label: 'Bass Flute' },
      { option: 'bass_flute_headjoint', label: 'Bass Flute Headjoint' },
      { option: 'contraalto_flute', label: 'Contra-Alto Flute' },
      { option: 'contra_flatass_flute', label: 'Contrabass Flute' },
      { option: 'su_flatcontra_flatass_flute', label: 'Subcontrabass Flute' },
      { option: 'dou_flatle_contra_flatass_flute', label: 'Double Contrabass Flute' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_concert_boehm_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: true,
    },
  },
  {
    key: 'flute_concert_boehm_flute_body',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'silverplated_nickel_silver', label: 'Silver-Plated Nickel Silver' },
      { option: 'sterling_silver_925', label: 'Sterling Silver (.925)' },
      { option: 'britannia_silver_958', label: 'Britannia Silver (.958)' },
      { option: 'solid_silver', label: 'Solid Silver' },
      { option: '9k_gold', label: '9K Gold' },
      { option: '14k_gold', label: '14K Gold' },
      { option: '18k_gold', label: '18K Gold' },
      { option: '24k_gold', label: '24K Gold' },
      { option: 'platinum', label: 'Platinum' },
      { option: 'aurumite_goldsilver_alloy', label: 'Aurumite (Gold/Silver Alloy)' },
      { option: 'grenadilla_wood', label: 'Grenadilla Wood' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'cocuswood', label: 'Cocuswood' },
      { option: 'other_wood', label: 'Other Wood' },
      { option: 'composite_grenaditte', label: 'Composite / Grenaditte' },
      { option: 'plastic_resin', label: 'Plastic / Resin' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_concert_boehm_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Material',
      isRequired: false,
    },
  },
  {
    key: 'flute_concert_boehm_flute_head',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'silverplated_nickel_silver', label: 'Silver-Plated Nickel Silver' },
      { option: 'sterling_silver_925', label: 'Sterling Silver (.925)' },
      { option: 'britannia_silver_958', label: 'Britannia Silver (.958)' },
      { option: 'solid_silver', label: 'Solid Silver' },
      { option: '9k_gold', label: '9K Gold' },
      { option: '14k_gold', label: '14K Gold' },
      { option: '18k_gold', label: '18K Gold' },
      { option: '24k_gold', label: '24K Gold' },
      { option: 'platinum', label: 'Platinum' },
      { option: 'aurumite_gold_silver_alloy', label: 'Aurumite (Gold / Silver Alloy)' },
      { option: 'grenadilla_wood', label: 'Grenadilla Wood' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'other_wood', label: 'Other Wood' },
      { option: 'composite_grenaditte', label: 'Composite / Grenaditte' },
      { option: 'gold_lip_plate_silver_tu_flate', label: 'Gold Lip Plate / Silver Tube' },
      { option: 'gold_riser_silver_tu_flate', label: 'Gold Riser / Silver Tube' },
      { option: 'platinum_riser_silver_tu_flate', label: 'Platinum Riser / Silver Tube' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_concert_boehm_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Headjoint Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Headjoint Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Headjoint Material',
      isRequired: false,
    },
  },
  {
    key: 'flute_concert_boehm_flute_features',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'open_hole_french_model', label: 'Open Hole (French Model)' },
      { option: 'b_foot', label: 'B Foot' },
      { option: 'offset_g', label: 'Offset G' },
      { option: 'split_e_mechanism', label: 'Split E Mechanism' },
      { option: 'c_sharp_trill_key', label: 'C# Trill Key' },
      { option: 'gizmo_key_high_c_facilitator', label: 'Gizmo Key (High C Facilitator)' },
      { option: 'd_sharp_roller', label: 'D# Roller' },
      { option: 'c_sharp_roller', label: 'C# Roller' },
      { option: 'curved_headjoint', label: 'Curved Headjoint' },
      { option: 'pointed_key_arms', label: 'Pointed Key Arms' },
      { option: 'pinless_mechanism', label: 'Pinless Mechanism' },
      { option: 'soldered_tone_holes', label: 'Soldered Tone Holes' },
      { option: 'drawn_tone_holes', label: 'Drawn Tone Holes' },
      { option: 'brogger_system_miyazawa', label: 'Brögger System (Miyazawa)' },
      { option: 'kingma_system_quartertone', label: 'Kingma System (Quarter-Tone)' },
      { option: 'open_g_sharp_french_system', label: 'Open G# (French System)' },
      { option: 'converti_flatle_bc_foot', label: 'Convertible B/C Foot' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_concert_boehm_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Features',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Features',
      isDetail: true,
    },
    saveConfig: {
      label: 'Features',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_dizi_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ammoon', label: 'Ammoon' },
      { option: 'ya_nan_ying_ying_bao', label: 'Ya Nan & Ying Ying Bao' },
      { option: 'carrotmusic', label: 'Carrotmusic' },
      { option: 'dantang', label: 'DanTang' },
      { option: 'sheng_hua_dong', label: 'Sheng Hua Dong' },
      { option: 'xue_hua_dong_lin_yin', label: 'Xue Hua Dong (Lin Yin)' },
      { option: 'eason_music', label: 'Eason Music' },
      { option: 'eight_tones', label: 'Eight Tones' },
      { option: 'jian_ming_ge', label: 'Jian Ming Ge' },
      { option: 'wei_dong_huang', label: 'Wei Dong Huang' },
      { option: 'kmise', label: 'Kmise' },
      { option: 'lin_yin_xue_hua_dong', label: 'Lin Yin (Xue Hua Dong)' },
      { option: 'nicoshine', label: 'Nicoshine' },
      { option: 'ns_musical', label: 'NS Musical' },
      { option: 'orientalmusicsanctuary', label: 'OrientalMusicSanctuary' },
      { option: 'pures_music', label: 'Pures Music' },
      { option: 'red_music_shop', label: 'Red Music Shop' },
      { option: 'sound_of_china', label: 'Sound of China' },
      { option: 'sound_of_dragon', label: 'Sound of Dragon' },
      { option: 'sound_of_mountain', label: 'Sound of Mountain' },
      { option: 'bing_xie', label: 'Bing Xie' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_dizi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'east_asian_flutes_dizi_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'xiaodi_soprano', label: 'Xiaodi (Soprano)' },
      { option: 'bangdi_alto', label: 'Bangdi (Alto)' },
      { option: 'qudi_tenor', label: 'Qudi (Tenor)' },
      { option: 'dadi_bass', label: 'Dadi (Bass)' },
      { option: 'deidi_contrabass', label: 'Deidi (Contrabass)' },
      { option: 'xindi_chromatic_11_hole', label: 'Xindi (Chromatic, 11-Hole)' },
      { option: 'koudi_pocket', label: 'Koudi (Pocket)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_dizi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'flute_simple_system_irish_flute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'thomas_aebi', label: 'Thomas Aebi' },
      { option: 'davy_angus', label: 'Davy Angus' },
      { option: 'edward_baack', label: 'Edward Baack' },
      { option: 'francois_baubet', label: 'François Baubet' },
      { option: 'boaz_berney', label: 'Boaz Berney' },
      { option: 'robert_bigio', label: 'Robert Bigio' },
      { option: 'phil_bleazey', label: 'Phil Bleazey' },
      { option: 'boosey_co', label: 'Boosey & Co.' },
      { option: 'boosey_hawkes', label: 'Boosey & Hawkes' },
      { option: 'casey_burns', label: 'Casey Burns' },
      { option: 'butler', label: 'Butler' },
      { option: 'brian_byrne', label: 'Brian Byrne' },
      { option: 'roderick_cameron', label: 'Roderick Cameron' },
      { option: 'carbony_celtic_winds', label: 'Carbony Celtic Winds' },
      { option: 'cochran', label: 'Cochran' },
      { option: 'cooperman', label: 'Cooperman' },
      { option: 'michael_copeland', label: 'Michael Copeland' },
      { option: 'copley', label: 'Copley' },
      { option: 'copley_boegli', label: 'Copley & Boegli' },
      { option: 'jon_cornia', label: 'Jon Cornia' },
      { option: 'eamonn_cotter', label: 'Eamonn Cotter' },
      { option: 'hugh_cottier', label: 'Hugh Cottier' },
      { option: 'd_almaine', label: 'D\'Almaine' },
      { option: 'arie_de_keyzer', label: 'Arie de Keyzer' },
      { option: 'thierry_denni', label: 'Thierry Denni' },
      { option: 'vincenzo_di_mauro', label: 'Vincenzo Di Mauro' },
      { option: 'tony_dixon', label: 'Tony Dixon' },
      { option: 'martin_doyle', label: 'Martin Doyle' },
      { option: 'b_s_dulcett', label: 'B.S. Dulcett' },
      { option: 'geoffrey_ellis', label: 'Geoffrey Ellis' },
      { option: 'ferris', label: 'Ferris' },
      { option: 'firth_hall_pond', label: 'Firth, Hall & Pond' },
      { option: 'rob_forbes', label: 'Rob Forbes' },
      { option: 'g_litz_co', label: 'G. Litz & Co.' },
      { option: 'steffen_gabriel', label: 'Steffen Gabriel' },
      { option: 'barna_gabos', label: 'Barna Gabos' },
      { option: 'john_gallagher', label: 'John Gallagher' },
      { option: 'galeon', label: 'Galeón' },
      { option: 'michael_grinter', label: 'Michael Grinter' },
      { option: 'william_hall', label: 'William Hall' },
      { option: 'jay_ham', label: 'Jay Ham' },
      { option: 'colin_hammy_hamilton', label: 'Colin "Hammy" Hamilton' },
      { option: 'randal_hauck', label: 'Randal Hauck' },
      { option: 'hawkes_son', label: 'Hawkes & Son' },
      { option: 'w_h_hayden', label: 'W.H. Hayden' },
      { option: 'skip_healy', label: 'Skip Healy' },
      { option: 'holmes_mcnaughton', label: 'Holmes McNaughton' },
      { option: 'mark_hoza', label: 'Mark Hoza' },
      { option: 'hudson', label: 'Hudson' },
      { option: 'jerome_thibouville_lamy', label: 'Jérôme Thibouville-Lamy' },
      { option: 'pol_jezequel', label: 'Pol Jézéquel' },
      { option: 'andrew_kauffmann', label: 'Andrew Kauffmann' },
      { option: 'julius_zimmermann', label: 'Julius Zimmermann' },
      { option: 'eugene_lambe', label: 'Eugene Lambe' },
      { option: 'gilles_lehart', label: 'Gilles Léhart' },
      { option: 'geert_lejeune', label: 'Geert Lejeune' },
      { option: 'solen_lesouef', label: 'Solen Lesouef' },
      { option: 'm_e', label: 'M&E' },
      { option: 'c_mahillon_co', label: 'C. Mahillon & Co' },
      { option: 'martin_freres', label: 'Martin Frères' },
      { option: 'terry_mcgee', label: 'Terry McGee' },
      { option: 'mcneela_paraic_mcneela', label: 'McNeela (Paraic McNeela)' },
      { option: 'metzler', label: 'Metzler' },
      { option: 'metzler_wylde', label: 'Metzler & Wylde' },
      { option: 'william_milhouse', label: 'William Milhouse' },
      { option: 'tony_millyard', label: 'Tony Millyard' },
      { option: 'morneaux', label: 'Morneaux' },
      { option: 'stephane_morvan', label: 'Stéphane Morvan' },
      { option: 'sam_murray', label: 'Sam Murray' },
      { option: 'nach_meyer', label: 'Nach Meyer' },
      { option: 'chris_norman', label: 'Chris Norman' },
      { option: 'peter_noy', label: 'Peter Noy' },
      { option: 'olwell', label: 'Olwell' },
      { option: 'ormiston', label: 'Ormiston' },
      { option: 'henry_potter', label: 'Henry Potter' },
      { option: 'renouard_cie_marque', label: 'Renouard & Cie Marque' },
      { option: 'reviol', label: 'Reviol' },
      { option: 'riley', label: 'Riley' },
      { option: 'rudall_rose', label: 'Rudall & Rose' },
      { option: 'rudall_carte_co', label: 'Rudall Carte & Co.' },
      { option: 'rudall_rose_carte_co', label: 'Rudall, Rose, Carte & Co' },
      { option: 'carl_schubert', label: 'Carl Schubert' },
      { option: 'seery', label: 'Seery' },
      { option: 'abel_siccama', label: 'Abel Siccama' },
      { option: 'silverwood', label: 'Silverwood' },
      { option: 'garry_somers', label: 'Garry Somers' },
      { option: 'walt_sweet_wd_sweet_flutes', label: 'Walt Sweet (W.D. Sweet Flutes)' },
      { option: 'sweetheart', label: 'Sweetheart' },
      { option: 'thibouville_buffet', label: 'Thibouville Buffet' },
      { option: 'damian_thompson', label: 'Damian Thompson' },
      { option: 'thornton_tommy_martin', label: 'Thornton (Tommy Martin)' },
      { option: 'doug_tipple', label: 'Doug Tipple' },
      { option: 'jonathan_walpole', label: 'Jonathan Walpole' },
      { option: 'paddy_ward', label: 'Paddy Ward' },
      { option: 'glenn_watson', label: 'Glenn Watson' },
      { option: 'chris_wilkes', label: 'Chris Wilkes' },
      { option: 'william_hall_son', label: 'William Hall & Son' },
      { option: 'dave_williams', label: 'Dave Williams' },
      { option: 'windward', label: 'Windward' },
      { option: 'wood_ivy', label: 'Wood & Ivy' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_simple_system_irish_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'flute_native_american_flute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4him_flutes_harold_chambers', label: '4HIM Flutes (Harold Chambers)' },
      { option: '4_winds_custom_flutes_brad_young', label: '4 Winds Custom Flutes (Brad Young)' },
      { option: 'amon_olorin_flutes_ken_light', label: 'Amon Olorin Flutes (Ken Light)' },
      { option: 'ancient_territories_flutes_pat_haran', label: 'Ancient Territories Flutes (Pat Haran)' },
      { option: 'richard_t_anderson_flutes', label: 'Richard T. Anderson Flutes' },
      { option: 'anita_badrena_flutes', label: 'Anita Badrena Flutes' },
      { option: 'bedford_avenue_flutes_mark_bickett', label: 'Bedford Avenue Flutes (Mark Bickett)' },
      { option: 'beech_creek_studio_rick_heller', label: 'Beech Creek Studio (Rick Heller)' },
      { option: 'blackfeather_flutes_gary_hanson', label: 'Blackfeather Flutes (Gary Hanson)' },
      { option: 'blue_bear_flutes', label: 'Blue Bear Flutes' },
      { option: 'branch_singer_flutes_aj_taranto', label: 'Branch Singer Flutes (AJ Taranto)' },
      { option: 'buffalo_moon_flutes_keith_glowka', label: 'Buffalo Moon Flutes (Keith Glowka)' },
      { option: 'catahoula_flutes_rand_rowe', label: 'Catahoula Flutes (Rand Rowe)' },
      { option: 'cedar_mountain_flutes', label: 'Cedar Mountain Flutes' },
      { option: 'cora_flutes_jake_cohara', label: 'Cora Flutes (Jake Cohara)' },
      { option: 'coyote_oldman_michael_graham_allen', label: 'Coyote Oldman (Michael Graham Allen)' },
      { option: 'crazy_bear_flutes_bob_carlson', label: 'Crazy Bear Flutes (Bob Carlson)' },
      { option: 'custom_flute_shop_jeff_calavan', label: 'Custom Flute Shop (Jeff Calavan)' },
      { option: 'dreamwind_flutes_lee_entrekin', label: 'Dreamwind Flutes (Lee Entrekin)' },
      { option: 'dryad_flutes_john_sherman', label: 'Dryad Flutes (John Sherman)' },
      { option: 'eagle_wind_flutes_jonathan_maracle', label: 'Eagle Wind Flutes (Jonathan Maracle)' },
      { option: 'eaglewing_flutes_ray_watchman', label: 'Eaglewing Flutes (Ray Watchman)' },
      { option: 'earthsong_flutes_of_australia', label: 'Earthsong Flutes of Australia' },
      { option: 'ellis_flutes_geoffrey_ellis', label: 'Ellis Flutes (Geoffrey Ellis)' },
      { option: 'farpoint_flutes_lee_nix', label: 'Farpoint Flutes (Lee Nix)' },
      { option: 'feather_ridge_flutes_randy_shelly_stenzel', label: 'Feather Ridge Flutes (Randy & Shelly Stenzel)' },
      { option: 'fire_flutes_craig_noss', label: 'Fire Flutes (Craig Noss)' },
      { option: 'flanagan_s_flutes_charles_flanagan', label: 'Flanagan\'s Flutes (Charles Flanagan)' },
      { option: 'flauta_nativa_ashar_ralph_ashar', label: 'Flauta Nativa Ashar (Ralph Ashar)' },
      { option: 'flautas_de_amor_daniel_gutierrez', label: 'Flautas De Amor (Daniel Gutierrez)' },
      { option: 'flute_craft_nathaniel_tomczyk', label: 'Flute Craft (Nathaniel Tomczyk)' },
      { option: 'flutes_by_nash_nash_tavewa', label: 'Flutes by Nash (Nash Tavewa)' },
      { option: 'flutewalker_flutes_werner_john', label: 'Flutewalker Flutes (Werner John)' },
      { option: 'four_winds_flute_company', label: 'Four Winds Flute Company' },
      { option: 'ghost_owl_flutes_dave_myers', label: 'Ghost Owl Flutes (Dave Myers)' },
      { option: 'jim_gilliland_flutes', label: 'Jim Gilliland Flutes' },
      { option: 'green_grass_flutes_geri_littlejohn', label: 'Green Grass Flutes (Geri LittleJohn)' },
      { option: 'butch_hall_flutes', label: 'Butch Hall Flutes' },
      { option: 'dg_hatch_flutes_dennis_dg_hatch', label: 'dg Hatch Flutes (Dennis "dg" Hatch)' },
      { option: 'heart_radiance', label: 'Heart Radiance' },
      { option: 'heart_song_flutes_rommy_verlaan', label: 'Heart Song Flutes (Rommy Verlaan)' },
      { option: 'heartsong_flutes_jp_gomez', label: 'Heartsong Flutes (J.P. Gomez)' },
      { option: 'hawk_henries', label: 'Hawk Henries' },
      { option: 'high_spirits_flutes_odell_borg', label: 'High Spirits Flutes (Odell Borg)' },
      { option: 'jim_s_flutes_jim_verrelli', label: 'Jim\'s Flutes (Jim Verrelli)' },
      { option: 'jon_norris_music_arts', label: 'Jon Norris Music & Arts' },
      { option: 'gary_kuhl_flutes', label: 'Gary Kuhl Flutes' },
      { option: 'kunkuman_flutes_jhon_canon', label: 'KunKuMan Flutes (Jhon Cañón)' },
      { option: 'laughing_crow_flutes_richard_maynard', label: 'Laughing Crow Flutes (Richard Maynard)' },
      { option: 'littleleaf_native_american_flutes_charles_littleleaf', label: 'Littleleaf Native American Flutes (Charles Littleleaf)' },
      { option: 'lone_crow_flutes_leonard_mcgann', label: 'Lone Crow Flutes (Leonard McGann)' },
      { option: 'magic_wind_flutes_chuck_pavey', label: 'Magic Wind Flutes (Chuck Pavey)' },
      { option: 'magic_woodworks', label: 'Magic Woodworks' },
      { option: 'makwa_flutes_steve_rensink', label: 'Makwa Flutes (Steve Rensink)' },
      { option: 'alex_maldonado_flutes', label: 'Alex Maldonado Flutes' },
      { option: 'meadowlark_flutes_john_kulias', label: 'Meadowlark Flutes (John Kulias)' },
      { option: 'robert_mirabal', label: 'Robert Mirabal' },
      { option: 'mosom_flutes_ned_bear', label: 'Mosom Flutes (Ned Bear)' },
      { option: 'mountain_spirits_flutes_danny_bigay_kay_littlejohn', label: 'Mountain Spirits Flutes (Danny Bigay & Kay LittleJohn)' },
      { option: 'mystic_flutes_jim_bixel', label: 'Mystic Flutes (Jim Bixel)' },
      { option: 'native_sunrise_flutes_ron_stutz', label: 'Native Sunrise Flutes (Ron Stutz)' },
      { option: 'natural_winds_flutes_bob_nelson', label: 'Natural Winds Flutes (Bob Nelson)' },
      { option: 'doc_tate_nevaquaya', label: 'Doc Tate Nevaquaya' },
      { option: 'nighteagle_flute_company_david_nighteagle', label: 'Nighteagle Flute Company (David Nighteagle)' },
      { option: 'northern_arapaho_flutes_herb_welsh', label: 'Northern Arapaho Flutes (Herb Welsh)' },
      { option: 'nothos_natural_music_ken_talaga', label: 'Nothos Natural Music (Ken Talaga)' },
      { option: 'otter_lake_flutes_jeff_burris', label: 'Otter Lake Flutes (Jeff Burris)' },
      { option: 'peaceful_heart_creations_joey_carroll', label: 'Peaceful Heart Creations (Joey Carroll)' },
      { option: 'peaceful_sounds_in_alaska_jason_leyva', label: 'Peaceful Sounds in Alaska (Jason Leyva)' },
      { option: 'prana_flutes_raffaello_hendrickx', label: 'Prana Flutes (Raffaello Hendrickx)' },
      { option: 'querencia_woodwinds_rich_halliburton', label: 'Querencia Woodwinds (Rich Halliburton)' },
      { option: 'quiet_bear_flutes_dwight_lind', label: 'Quiet Bear Flutes (Dwight Lind)' },
      { option: 'rambling_flutes_joe_silvia', label: 'Rambling Flutes (Joe Silvia)' },
      { option: 'raum_music_eric_raum', label: 'Raum Music (Eric Raum)' },
      { option: 'raven_wing_flutes_frank_harter', label: 'Raven Wing Flutes (Frank Harter)' },
      { option: 'redbird_flutes_roger_bennett', label: 'Redbird Flutes (Roger Bennett)' },
      { option: 'sevenwinds_flutes_michael_fuger', label: 'Sevenwinds Flutes (Michael Fuger)' },
      { option: 'shades_of_rez_flutes_tim_blueflint', label: 'Shades of Rez Flutes (Tim Blueflint)' },
      { option: 'singing_tree_flutes_miguel_medina', label: 'Singing Tree Flutes (Miguel Medina)' },
      { option: 'southern_cross_flutes_todd_chaplin', label: 'Southern Cross Flutes (Todd Chaplin)' },
      { option: 'spirit_of_the_woods_flutes_ed_hrebec', label: 'Spirit of the Woods Flutes (Ed Hrebec)' },
      { option: 'spirit_winds_flutes_tony_de_flat_richards', label: 'Spirit Winds Flutes (Tony & Deb Richards)' },
      { option: 'spirit_world_flutes_lee_westover', label: 'Spirit World Flutes (Lee Westover)' },
      { option: 'stellar_flutes_tom_matt_stewart', label: 'Stellar Flutes (Tom & Matt Stewart)' },
      { option: 'temple_wind_flutes', label: 'Temple Wind Flutes' },
      { option: 'jonah_thompson_flutes', label: 'Jonah Thompson Flutes' },
      { option: 'mike_turner_flutes', label: 'Mike Turner Flutes' },
      { option: 'ugly_boy_flutes_bob_child', label: 'Ugly Boy Flutes (Bob Child)' },
      { option: 'uguna_flutes_geoff_norman', label: 'Uguna Flutes (Geoff Norman)' },
      { option: 'waking_spirit_flutes_vance_pennington', label: 'Waking Spirit Flutes (Vance Pennington)' },
      { option: 'whirlwind_studios_mac_cherry_lopez', label: 'Whirlwind Studios (Mac & Cherry Lopez)' },
      { option: 'white_crow_flutes_barry_d_higgins', label: 'White Crow Flutes (Barry D. Higgins)' },
      { option: 'wind_s_song_flutes_scott_loomis', label: 'Wind\'s Song Flutes (Scott Loomis)' },
      { option: 'woodland_voices_colyn_kitty_petersen', label: 'Woodland Voices (Colyn & Kitty Petersen)' },
      { option: 'woodsounds_flutes_brent_haines', label: 'Woodsounds Flutes (Brent Haines)' },
      { option: 'yazzie_flutes_marvin_yazzie', label: 'Yazzie Flutes (Marvin Yazzie)' },
      { option: 'yucca_flutes_joseph_yeatman', label: 'Yucca Flutes (Joseph Yeatman)' },
      { option: 'yxayotl_clay_flutes_xavier_quijas_yxayotl', label: 'Yxayotl Clay Flutes (Xavier Quijas Yxayotl)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_native_american_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'flute_native_american_flute_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single_flute', label: 'Single Flute' },
      { option: 'double_drone_flute', label: 'Double / Drone Flute' },
      { option: 'harmony_double_melody_flute', label: 'Harmony / Double Melody Flute' },
      { option: 'triple_flute', label: 'Triple Flute' },
      { option: 'walking_pocket_flute', label: 'Walking / Pocket Flute' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_native_american_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_flutes_ney_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ahmed_abdel_ghani', label: 'Ahmed Abdel Ghani' },
      { option: 'adana_ney', label: 'Adana Ney' },
      { option: 'alsaid_bayomy', label: 'Alsaid Bayomy' },
      { option: 'master_huseyin', label: 'Master Hüseyin' },
      { option: 'saber_kawala', label: 'Saber Kawala' },
      { option: 'native_flute_ashar_ralph_ashar', label: 'Native Flute Ashar (Ralph Ashar)' },
      { option: 'niri_sadeh', label: 'Niri Sadeh' },
      { option: 'rifat_varol_neyneva', label: 'Rifat Varol (NeyNeva)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_flutes_ney',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'middle_eastern_north_african_flutes_ney_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'turkish_ney', label: 'Turkish Ney' },
      { option: 'arabic_egyptian_ney_nay', label: 'Arabic / Egyptian Ney (Nay)' },
      { option: 'persian_ney', label: 'Persian Ney' },
      { option: 'kawala', label: 'Kawala' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_flutes_ney',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'flute_pan_flute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'kevin_budd', label: 'Kevin Budd' },
      { option: 'dajoeri', label: 'Dajoeri' },
      { option: 'erik_the_flutemaker_erik_sampson', label: 'Erik the Flutemaker (Erik Sampson)' },
      { option: 'gibonus', label: 'Gibonus' },
      { option: 'hora', label: 'Hora' },
      { option: 'inka_tumi', label: 'Inka Tumi' },
      { option: 'kjarkas', label: 'Kjarkas' },
      { option: 'lupaca_nestor_lupaca', label: 'Lupaca (Nestor Lupaca)' },
      { option: 'radu_nechifor', label: 'Radu Nechifor' },
      { option: 'occorso_santi_occorso', label: 'Occorso (Santi Occorso)' },
      { option: 'panex', label: 'Panex' },
      { option: 'plaschke', label: 'Plaschke' },
      { option: 'preda_ion_preda', label: 'Preda (Ion Preda)' },
      { option: 'pures_music', label: 'Pures Music' },
      { option: 'ulitza_ulrich_herkenhoff', label: 'Ulitza (Ulrich Herkenhoff)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_pan_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'flute_pan_flute_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'romanian_nai', label: 'Romanian Nai' },
      { option: 'zampona_siku_double_row', label: 'Zampoña / Siku (Double Row)' },
      { option: 'antara_single_row', label: 'Antara (Single Row)' },
      { option: 'rondador', label: 'Rondador' },
      { option: 'chromatic_pan_flute', label: 'Chromatic Pan Flute' },
      { option: 'bass_toyo', label: 'Bass / Toyo' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_pan_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'andean_flutes_quena_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'juan_acha', label: 'Juan Acha' },
      { option: 'paco_acha', label: 'Paco Acha' },
      { option: 'ivan_alandres', label: 'Iván Alandres' },
      { option: 'ayriway_fernando_aiziczon', label: 'Ayriway (Fernando Aiziczon)' },
      { option: 'jhonatan_castillo_quenas_castillo', label: 'Jhonatan Castillo (Quenas Castillo)' },
      { option: 'geoffrey_ellis', label: 'Geoffrey Ellis' },
      { option: 'erik_the_flutemaker', label: 'Erik the Flutemaker' },
      { option: 'norman_gordillo', label: 'Norman Gordillo' },
      { option: 'harmonyflute', label: 'HarmonyFlute' },
      { option: 'tyrone_head', label: 'Tyrone Head' },
      { option: 'hikaru_iwakawa_cachi', label: 'Hikaru Iwakawa (CACHI)' },
      { option: 'kaypacha', label: 'Kaypacha' },
      { option: 'lupaca', label: 'Lupaca' },
      { option: 'native_flute_ashar_ralph_ashar', label: 'Native Flute Ashar (Ralph Ashar)' },
      { option: 'arnoldo_pintos', label: 'Arnoldo Pintos' },
      { option: 'dionisio_quilla', label: 'Dionisio Quilla' },
      { option: 'fredy_salazar_quispe', label: 'Fredy Salazar Quispe' },
      { option: 'angel_sampedro_del_rio_un_mundo_de_bambu', label: 'Ángel Sampedro del Río (Un Mundo de Bambú)' },
      { option: 'benito_tito', label: 'Benito Tito' },
      { option: 'mauricio_vicencio', label: 'Mauricio Vicencio' },
      { option: 'wonderflute', label: 'Wonderflute' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'andean_flutes_quena',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'andean_flutes_quena_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'quena_standard', label: 'Quena (Standard)' },
      { option: 'quenacho', label: 'Quenacho' },
      { option: 'quenilla', label: 'Quenilla' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'andean_flutes_quena',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_shakuhachi_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'abedabun_flutes', label: 'Abedabun Flutes' },
      { option: 'david_brown', label: 'David Brown' },
      { option: 'chiku_shin_shawn_schroeder', label: 'Chiku-Shin (Shawn Schroeder)' },
      { option: 'tom_deaver_bei_shu', label: 'Tom Deaver (Bei Shu)' },
      { option: 'geoffrey_ellis', label: 'Geoffrey Ellis' },
      { option: 'hosetsu_shakuhachi_jem_klein', label: 'Hosetsu Shakuhachi (Jem Klein)' },
      { option: 'josen_jon_kypros', label: 'Josen (Jon Kypros)' },
      { option: 'kitahara_seikado', label: 'Kitahara Seikado' },
      { option: 'thorsten_knaub', label: 'Thorsten Knaub' },
      { option: 'mejiro', label: 'Mejiro' },
      { option: 'ryuho_miura', label: 'Ryuho Miura' },
      { option: 'mujitsu_ken_lacosse', label: 'Mujitsu (Ken LaCosse)' },
      { option: 'john_kaizan_neptune', label: 'John Kaizan Neptune' },
      { option: 'alcvin_ryuzen_ramos', label: 'Alcvin Ryūzen Ramos' },
      { option: 'senshu_yukihiko_mitsuka', label: 'Senshu (Yukihiko Mitsuka)' },
      { option: 'shakuhachi_yuu_nihon_conductor', label: 'Shakuhachi Yuu (Nihon Conductor)' },
      { option: 'tai_hei_shakuhachi_monty_levenson', label: 'Tai Hei Shakuhachi (Monty Levenson)' },
      { option: 'windelf_flutes_noel_dowling', label: 'Windelf Flutes (Noel Dowling)' },
      { option: 'perry_yung_yung_flutes', label: 'Perry Yung (Yung Flutes)' },
      { option: 'ricardo_zapata', label: 'Ricardo Zapata' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_shakuhachi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'east_asian_flutes_xiao_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bao_family_orientalmusicsanctuary', label: 'Bao Family (OrientalMusicSanctuary)' },
      { option: 'carrotmusic', label: 'Carrotmusic' },
      { option: 'shenghua_dong', label: 'Shenghua Dong' },
      { option: 'xuehua_dong', label: 'Xuehua Dong' },
      { option: 'geoffrey_ellis', label: 'Geoffrey Ellis' },
      { option: 'jianming_ge', label: 'Jianming Ge' },
      { option: 'daqiang_guo', label: 'Daqiang Guo' },
      { option: 'chaoqing_huang', label: 'Chaoqing Huang' },
      { option: 'weidong_huang', label: 'Weidong Huang' },
      { option: 'lejiang', label: 'LeJiang' },
      { option: 'lingyin', label: 'Lingyin' },
      { option: 'vance_pennington_waking_spirit_flutes', label: 'Vance Pennington (Waking Spirit Flutes)' },
      { option: 'bing_xie', label: 'Bing Xie' },
      { option: 'ycxyq', label: 'YCXYQ' },
      { option: 'ypxd_houhuai_shu', label: 'YPXD (Houhuai Shu)' },
      { option: 'yuejiang', label: 'YueJiang' },
      { option: 'wenbing_zhan', label: 'Wenbing Zhan' },
      { option: 'linsheng_zhu', label: 'Linsheng Zhu' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_xiao',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'east_asian_flutes_xiao_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bei_xiao_northern_standard', label: 'Bei Xiao (Northern / Standard)' },
      { option: 'nan_xiao_southern', label: 'Nan Xiao (Southern)' },
      { option: 'qin_xiao', label: 'Qin Xiao' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_xiao',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_tin_whistle_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'chris_abell', label: 'Chris Abell' },
      { option: 'acorn', label: 'Acorn' },
      { option: 'alba', label: 'Alba' },
      { option: 'anak', label: 'Anak' },
      { option: 'chris_backhouse', label: 'Chris Backhouse' },
      { option: 'becker', label: 'Becker' },
      { option: 'david_boisvert', label: 'David Boisvert' },
      { option: 'michael_burke', label: 'Michael Burke' },
      { option: 'busman', label: 'Busman' },
      { option: 'camac', label: 'Camac' },
      { option: 'carbony_celtic_winds', label: 'Carbony Celtic Winds' },
      { option: 'clare', label: 'Clare' },
      { option: 'clarke', label: 'Clarke' },
      { option: 'clover', label: 'Clover' },
      { option: 'copeland', label: 'Copeland' },
      { option: 'ian_corrigan_deerness', label: 'Ian Corrigan (Deerness)' },
      { option: 'dante', label: 'Dante' },
      { option: 'tommy_dion', label: 'Tommy Dion' },
      { option: 'tony_dixon', label: 'Tony Dixon' },
      { option: 'james_dominic', label: 'James Dominic' },
      { option: 'elfsong', label: 'Elfsong' },
      { option: 'ellis', label: 'Ellis' },
      { option: 'erik_the_flutemaker', label: 'Erik the Flutemaker' },
      { option: 'ethnic_winds', label: 'Ethnic Winds' },
      { option: 'feadog', label: 'Feadóg' },
      { option: 'fidgin_fain', label: 'Fidgin Fain' },
      { option: 'jerry_freeman', label: 'Jerry Freeman' },
      { option: 'furman', label: 'Furman' },
      { option: 'galeon', label: 'Galeón' },
      { option: 'generation', label: 'Generation' },
      { option: 'colin_goldie', label: 'Colin Goldie' },
      { option: 'goldfinch', label: 'Goldfinch' },
      { option: 'michael_grinter', label: 'Michael Grinter' },
      { option: 'colin_hammy_hamilton', label: 'Colin "Hammy" Hamilton' },
      { option: 'seth_hamon', label: 'Seth Hamon' },
      { option: 'harper', label: 'Harper' },
      { option: 'heikes', label: 'Heikes' },
      { option: 'hermit_hill', label: 'Hermit Hill' },
      { option: 'gal_hiltch', label: 'Gal Hiltch' },
      { option: 'holifield', label: 'Holifield' },
      { option: 'mack_hoover', label: 'Mack Hoover' },
      { option: 'howard', label: 'Howard' },
      { option: 'gary_humphrey', label: 'Gary Humphrey' },
      { option: 'impempe', label: 'Impempe' },
      { option: 'ivolga', label: 'iVolga' },
      { option: 'karavaev', label: 'Karavaev' },
      { option: 'kerry', label: 'Kerry' },
      { option: 'kildare', label: 'Kildare' },
      { option: 'killarney', label: 'Killarney' },
      { option: 'ian_lambe', label: 'Ian Lambe' },
      { option: 'lindstruments', label: 'Lindstruments' },
      { option: 'lir', label: 'Lír' },
      { option: 'lochlan', label: 'Lochlan' },
      { option: 'marc_lofgren', label: 'Marc Löfgren' },
      { option: 'paddy_martin', label: 'Paddy Martin' },
      { option: 'mazur', label: 'Mazur' },
      { option: 'mchaffie', label: 'McHaffie' },
      { option: 'roy_mcmanus', label: 'Roy McManus' },
      { option: 'mcneela_paraic_mcneela', label: 'McNeela (Paraic McNeela)' },
      { option: 'merlin', label: 'Merlin' },
      { option: 'nick_metcalf', label: 'Nick Metcalf' },
      { option: 'guillaume_migault', label: 'Guillaume Migault' },
      { option: 'gene_milligan', label: 'Gene Milligan' },
      { option: 'mk', label: 'MK' },
      { option: 'morneaux', label: 'Morneaux' },
      { option: 'cillian_o_briain', label: 'Cillian O\'Briain' },
      { option: 'david_o_brien', label: 'David O\'Brien' },
      { option: 'o_dwyer', label: 'O\'Dwyer' },
      { option: 'oliver_ditson', label: 'Oliver Ditson' },
      { option: 'o_riordan', label: 'O\'Riordan' },
      { option: 'ormiston', label: 'Ormiston' },
      { option: 'bernard_overton', label: 'Bernard Overton' },
      { option: 'oz', label: 'Oz' },
      { option: 'pa_music', label: 'PA Music' },
      { option: 'carey_parks', label: 'Carey Parks' },
      { option: 'david_parkhurst', label: 'David Parkhurst' },
      { option: 'timothy_potter', label: 'Timothy Potter' },
      { option: 'reviol', label: 'Reviol' },
      { option: 'reyburn', label: 'Reyburn' },
      { option: 'andreas_rogge', label: 'Andreas Rogge' },
      { option: 'fred_rose', label: 'Fred Rose' },
      { option: 'flo_ryan', label: 'Flo Ryan' },
      { option: 'glenn_schultz', label: 'Glenn Schultz' },
      { option: 'des_seery', label: 'Des Seery' },
      { option: 'eric_segret', label: 'Eric Segret' },
      { option: 'serpent_music', label: 'Serpent Music' },
      { option: 'setanta', label: 'Setanta' },
      { option: 'dave_shaw', label: 'Dave Shaw' },
      { option: 'shearwater', label: 'Shearwater' },
      { option: 'silkstone', label: 'Silkstone' },
      { option: 'willy_simmons', label: 'Willy Simmons' },
      { option: 'sindt', label: 'Sindt' },
      { option: 'benedict_stewart', label: 'Benedict Stewart' },
      { option: 'susato', label: 'Susato' },
      { option: 'jon_swayne', label: 'Jon Swayne' },
      { option: 'walt_sweet', label: 'Walt Sweet' },
      { option: 'sweetheart', label: 'Sweetheart' },
      { option: 'syn', label: 'Syn' },
      { option: 'szbe', label: 'SZBE' },
      { option: 'noah_talmadge', label: 'Noah Talmadge' },
      { option: 'thornton_tommy_martin', label: 'Thornton (Tommy Martin)' },
      { option: 'tilbury', label: 'Tilbury' },
      { option: 'tully', label: 'Tully' },
      { option: 'twz', label: 'TWZ' },
      { option: 'tyrone_head', label: 'Tyrone Head' },
      { option: 'verdant', label: 'Verdant' },
      { option: 'chris_wall', label: 'Chris Wall' },
      { option: 'waltons', label: 'Waltons' },
      { option: 'weston', label: 'Weston' },
      { option: 'whistlesmith', label: 'WhistleSmith' },
      { option: 'chuck_whitney', label: 'Chuck Whitney' },
      { option: 'whorfin_woods', label: 'Whorfin Woods' },
      { option: 'wild', label: 'Wild' },
      { option: 'woodi_smartwoodi', label: 'Woodi (SmartWoodi)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_tin_whistle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'east_asian_flutes_shakuhachi_bore',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'jinashi', label: 'Jinashi' },
      { option: 'jimori', label: 'Jimori' },
      { option: 'jiari', label: 'Jiari' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_shakuhachi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bore Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bore Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bore Type',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_dizi_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sandalwood', label: 'Sandalwood' },
      { option: 'other_wood', label: 'Other Wood' },
      { option: 'jade', label: 'Jade' },
      { option: 'pvc_resin_composite', label: 'PVC / Resin / Composite' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_dizi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_recorder_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aafa_flat_coolsma', label: 'Aafab (Coolsma)' },
      { option: 'adrian_brown', label: 'Adrian Brown' },
      { option: 'adriana_breukink', label: 'Adriana Breukink' },
      { option: 'aulos_toyama_musical_instrument_co', label: 'Aulos (Toyama Musical Instrument Co.)' },
      { option: 'phil_bleazey', label: 'Phil Bleazey' },
      { option: 'stephan_blezinger', label: 'Stephan Blezinger' },
      { option: 'philippe_bolton', label: 'Philippe Bolton' },
      { option: 'jean_luc_boudreau', label: 'Jean-Luc Boudreau' },
      { option: 'tim_cranmore', label: 'Tim Cranmore' },
      { option: 'luca_de_paolis', label: 'Luca de Paolis' },
      { option: 'bodil_diesen', label: 'Bodil Diesen' },
      { option: 'dolmetsch', label: 'Dolmetsch' },
      { option: 'ralf_ehlert', label: 'Ralf Ehlert' },
      { option: 'fehr', label: 'Fehr' },
      { option: 'michael_grinter', label: 'Michael Grinter' },
      { option: 'shigeharu_seiji_hirao', label: 'Shigeharu & Seiji Hirao' },
      { option: 'hopf', label: 'Hopf' },
      { option: 'huber', label: 'Huber' },
      { option: 'guido_hulsens', label: 'Guido Hulsens' },
      { option: 'eugene_iliaronov', label: 'Eugene Iliaronov' },
      { option: 'guido_klemisch', label: 'Guido Klemisch' },
      { option: 'kobliczek_christoph_hammann', label: 'Kobliczek (Christoph Hammann)' },
      { option: 'doris_kulossa', label: 'Doris Kulossa' },
      { option: 'kunath_paetzold', label: 'Kunath (Paetzold)' },
      { option: 'kung', label: 'Küng' },
      { option: 'francesco_li_virghi', label: 'Francesco Li Virghi' },
      { option: 'margret_lobner', label: 'Margret Löbner' },
      { option: 'ernst_meyer', label: 'Ernst Meyer' },
      { option: 'sebastian_meyer', label: 'Sebastian Meyer' },
      { option: 'moeck', label: 'Moeck' },
      { option: 'mollenhauer', label: 'Mollenhauer' },
      { option: 'fred_morgan_morgan_workshop', label: 'Fred Morgan (Morgan Workshop)' },
      { option: 'monika_musch', label: 'Monika Musch' },
      { option: 'ralf_netsch', label: 'Ralf Netsch' },
      { option: 'tom_prescott_prescott_workshop', label: 'Tom Prescott (Prescott Workshop)' },
      { option: 'yoav_ran', label: 'Yoav Ran' },
      { option: 'roessler', label: 'Roessler' },
      { option: 'joachim_rohmer', label: 'Joachim Rohmer' },
      { option: 'fumitaka_saito', label: 'Fumitaka Saito' },
      { option: 'jacqueline_sorel', label: 'Jacqueline Sorel' },
      { option: 'takeyama_hiroyuki_takeyama', label: 'Takeyama (Hiroyuki Takeyama)' },
      { option: 'von_huene', label: 'Von Huene' },
      { option: 'martin_wenner', label: 'Martin Wenner' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'zen_on', label: 'Zen-On' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_recorder',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'global_range',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'sopranino_very_high_f5g7', label: 'Sopranino / Very High (F5-G7)' },
      { option: 'soprano_high_c5e5', label: 'Soprano / High (C5-E5)' },
      { option: 'alto_middle_f4b4', label: 'Alto / Middle (F4-B4)' },
      { option: 'tenor_low_c4e4', label: 'Tenor / Low (C4-E4)' },
      { option: 'bass_f3b3', label: 'Bass (F3-B3)' },
      { option: 'contrabass_e3_and_below', label: 'Contrabass (E3 and Below)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_recorder',
        'flute_other_flutes',
        'middle_eastern_north_african_flutes_ney',
        'flute_native_american_flute',
        'flute_simple_system_irish_flute',
        'wind_reed_tin_whistle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Range',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Range',
      isDetail: true,
    },
    saveConfig: {
      label: 'Range',
      isRequired: false,
    },
  },
  {
    key: 'global_tunable',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'yes', label: 'Yes' },
      { option: 'no', label: 'No' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_dizi',
        'flute_simple_system_irish_flute',
        'wind_reed_tin_whistle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Tunable',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tunable',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tunable',
      isRequired: false,
    },
  },
  {
    key: 'flute_other_flutes_scale',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ionian_major', label: 'Ionian (Major)' },
      { option: 'dorian', label: 'Dorian' },
      { option: 'phrygian', label: 'Phrygian' },
      { option: 'lydian', label: 'Lydian' },
      { option: 'mixolydian', label: 'Mixolydian' },
      { option: 'aeolian_natural_minor', label: 'Aeolian (Natural Minor)' },
      { option: 'locrian', label: 'Locrian' },
      { option: 'major_pentatonic', label: 'Major Pentatonic' },
      { option: 'minor_pentatonic', label: 'Minor Pentatonic' },
      { option: 'harmonic_minor', label: 'Harmonic Minor' },
      { option: 'melodic_minor', label: 'Melodic Minor' },
      { option: 'blues', label: 'Blues' },
      { option: 'chromatic', label: 'Chromatic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_other_flutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale',
      isRequired: false,
    },
  },
  {
    key: 'flute_other_flutes_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'wood', label: 'Wood' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'pvc_plastic_resin', label: 'PVC / Plastic / Resin' },
      { option: 'bone', label: 'Bone' },
      { option: 'metal', label: 'Metal' },
      { option: 'ceramic_clay', label: 'Ceramic / Clay' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_other_flutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'flute_simple_system_irish_flute_keys',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'keyless', label: 'Keyless' },
      { option: '1', label: '1' },
      { option: '2', label: '2' },
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: '9', label: '9+' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_simple_system_irish_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Keys',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Keys',
      isDetail: true,
    },
    saveConfig: {
      label: 'Keys',
      isRequired: false,
    },
  },
  {
    key: 'flute_simple_system_irish_flute_material',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'blackwood_grenadilla', label: 'Blackwood / Grenadilla' },
      { option: 'bocote', label: 'Bocote' },
      { option: 'bone', label: 'Bone' },
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'cocus', label: 'Cocus' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'imitation_ivory', label: 'Imitation Ivory' },
      { option: 'ivory', label: 'Ivory' },
      { option: 'kingwood', label: 'Kingwood' },
      { option: 'maple', label: 'Maple' },
      { option: 'mopane', label: 'Mopane' },
      { option: 'olivewood', label: 'Olivewood' },
      { option: 'pink_ivory', label: 'Pink Ivory' },
      { option: 'pvc', label: 'PVC' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'tulipwood', label: 'Tulipwood' },
      { option: 'polymer_delrin', label: 'Polymer / Delrin' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_simple_system_irish_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'flute_native_american_flute_holes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_native_american_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Holes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Holes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Holes',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_flutes_ney_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'reed_arundo_donax', label: 'Reed (Arundo donax)' },
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'pvc_plastic', label: 'PVC / Plastic' },
      { option: 'metal', label: 'Metal' },
      { option: 'wood', label: 'Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_flutes_ney',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'flute_pan_flute_pipes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '8_12', label: '8-12' },
      { option: '13_15', label: '13-15' },
      { option: '16_20', label: '16-20' },
      { option: '21_25', label: '21-25' },
      { option: '26_30', label: '26-30' },
      { option: '31', label: '31+' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_pan_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Pipes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Pipes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Pipes',
      isRequired: false,
    },
  },
  {
    key: 'flute_pan_flute_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'reed_cane', label: 'Reed / Cane' },
      { option: 'wood', label: 'Wood' },
      { option: 'pvc_plastic_resin', label: 'PVC / Plastic / Resin' },
      { option: 'ceramic_clay', label: 'Ceramic / Clay' },
      { option: 'metal', label: 'Metal' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_pan_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'andean_flutes_quena_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'acrylic', label: 'Acrylic' },
      { option: 'african_blackwood', label: 'African Blackwood' },
      { option: 'ash', label: 'Ash' },
      { option: 'bone', label: 'Bone' },
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'brass', label: 'Brass' },
      { option: 'bronze', label: 'Bronze' },
      { option: 'cane', label: 'Cane' },
      { option: 'ceramic', label: 'Ceramic' },
      { option: 'chonta', label: 'Chonta' },
      { option: 'clay', label: 'Clay' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'cocus', label: 'Cocus' },
      { option: 'copper', label: 'Copper' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'granadillo', label: 'Granadillo' },
      { option: 'grenadilla', label: 'Grenadilla' },
      { option: 'jacaranda', label: 'Jacaranda' },
      { option: 'maple', label: 'Maple' },
      { option: 'mopane', label: 'Mopane' },
      { option: 'nickel', label: 'Nickel' },
      { option: 'palo_santo', label: 'Palo Santo' },
      { option: 'plastic', label: 'Plastic' },
      { option: 'pvc', label: 'PVC' },
      { option: 'resin', label: 'Resin' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'stone', label: 'Stone' },
      { option: 'ziricote', label: 'Ziricote' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'andean_flutes_quena',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'andean_flutes_quena_notch',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'u', label: 'U' },
      { option: 'v', label: 'V' },
      { option: 'square', label: 'Square' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'andean_flutes_quena',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Notch Shape',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Notch Shape',
      isDetail: true,
    },
    saveConfig: {
      label: 'Notch Shape',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_shakuhachi_end',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'root_end', label: 'Root End' },
      { option: 'straight_end', label: 'Straight End' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_shakuhachi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'End Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'End Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'End Style',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_shakuhachi_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bamboo_madake', label: 'Bamboo (Madake)' },
      { option: 'wood', label: 'Wood' },
      { option: 'plastic_resin', label: 'Plastic / Resin' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_shakuhachi',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_xiao_holes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6_hole_traditional', label: '6-Hole (Traditional)' },
      { option: '8_hole_modern_standard', label: '8-Hole (Modern Standard)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_xiao',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Finger Holes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Finger Holes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Finger Holes',
      isRequired: false,
    },
  },
  {
    key: 'east_asian_flutes_xiao_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'wood', label: 'Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'east_asian_flutes_xiao',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_tin_whistle_material',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'metal', label: 'Metal' },
      { option: 'delrin_polymer', label: 'Delrin / Polymer' },
      { option: 'wood', label: 'Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_tin_whistle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_recorder_pitch',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'a440442_modern', label: 'A=440/442 (Modern)' },
      { option: 'a415_baroque', label: 'A=415 (Baroque)' },
      { option: 'a466_renaissance', label: 'A=466 (Renaissance)' },
      { option: 'a392_french_baroque', label: 'A=392 (French Baroque)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_recorder',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Pitch Standard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Pitch Standard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Pitch Standard',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_recorder_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'grenadilla_african_blackwood', label: 'Grenadilla / African Blackwood' },
      { option: 'maple', label: 'Maple' },
      { option: 'olivewood', label: 'Olivewood' },
      { option: 'pearwood', label: 'Pearwood' },
      { option: 'plumwood', label: 'Plumwood' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'plastic_resin', label: 'Plastic / Resin' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_recorder',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_recorder_model',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bressan', label: 'Bressan' },
      { option: 'denner', label: 'Denner' },
      { option: 'ganassi', label: 'Ganassi' },
      { option: 'haka', label: 'Haka' },
      { option: 'hotteterre', label: 'Hotteterre' },
      { option: 'kynseker', label: 'Kynseker' },
      { option: 'oberlender', label: 'Oberlender' },
      { option: 'praetorius', label: 'Praetorius' },
      { option: 'rippert', label: 'Rippert' },
      { option: 'rottenburgh', label: 'Rottenburgh' },
      { option: 'schell', label: 'Schell' },
      { option: 'stanesby_sr', label: 'Stanesby Sr.' },
      { option: 'stanesby_jr', label: 'Stanesby Jr.' },
      { option: 'steenbergen', label: 'Steenbergen' },
      { option: 'original_modern_design', label: 'Original / Modern Design' },
      { option: 'na', label: 'N/A' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_recorder',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Modeled After',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Modeled After',
      isDetail: true,
    },
    saveConfig: {
      label: 'Modeled After',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_recorder_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'baroque', label: 'Baroque' },
      { option: 'german', label: 'German' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'wind_reed_recorder',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Fingering System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Fingering System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Fingering System',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_uilleann_pipes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'thomas_aebi', label: 'Thomas Aebi' },
      { option: 'alexander_anistratov', label: 'Alexander Anistratov' },
      { option: 'carlos_aragon_juan_manuel_parrado', label: 'Carlos Aragón & Juan Manuel Parrado' },
      { option: 'josh_bailey', label: 'Josh Bailey' },
      { option: 'tim_benson', label: 'Tim Benson' },
      { option: 'brian_bigley', label: 'Brian Bigley' },
      { option: 'xavier_boderiou', label: 'Xavier Boderiou' },
      { option: 'john_butler', label: 'John Butler' },
      { option: 'bruce_childress', label: 'Bruce Childress' },
      { option: 'coyne', label: 'Coyne' },
      { option: 'martin_crossin', label: 'Martin Crossin' },
      { option: 'eoin_dillon', label: 'Eóin Dillon' },
      { option: 'mark_donohoe', label: 'Mark Donohoe' },
      { option: 'michael_dooley', label: 'Michael Dooley' },
      { option: 'ken_doyle', label: 'Ken Doyle' },
      { option: 'mickey_dunne', label: 'Mickey Dunne' },
      { option: 'donnacha_dwyer', label: 'Donnacha Dwyer' },
      { option: 'egan', label: 'Egan' },
      { option: 'andy_faden', label: 'Andy Faden' },
      { option: 'seth_gallagher', label: 'Seth Gallagher' },
      { option: 'benjamin_jonas_glauser', label: 'Benjamin Jonas Glauser' },
      { option: 'derrick_gleeson', label: 'Derrick Gleeson' },
      { option: 'daniel_gloaguen', label: 'Daniel Gloaguen' },
      { option: 'david_goldsworthy', label: 'David Goldsworthy' },
      { option: 'bill_haneman', label: 'Bill Haneman' },
      { option: 'evertjan_t_hart', label: 'Evertjan \'t Hart' },
      { option: 'hennelly', label: 'Hennelly' },
      { option: 'aodan_jeffries', label: 'Aodán Jeffries' },
      { option: 'donncha_keegan', label: 'Donncha Keegan' },
      { option: 'kenna', label: 'Kenna' },
      { option: 'benedict_koehler', label: 'Benedict Koehler' },
      { option: 'hubert_kwisthout', label: 'Hubert Kwisthout' },
      { option: 'eugene_lambe', label: 'Eugene Lambe' },
      { option: 'david_lim', label: 'David Lim' },
      { option: 'tommy_martin', label: 'Tommy Martin' },
      { option: 'stephen_mccordick', label: 'Stephen McCordick' },
      { option: 'dirk_mewes', label: 'Dirk Mewes' },
      { option: 'hendrik_morgenbrodt', label: 'Hendrik Morgenbrodt' },
      { option: 'fred_morrison', label: 'Fred Morrison' },
      { option: 'makoto_nakatsui', label: 'Makoto Nakatsui' },
      { option: 'micha_nawara', label: 'Michał Nawara' },
      { option: 'aaron_o_hagan', label: 'Aaron O\'Hagan' },
      { option: 'eoin_o_riabhaigh', label: 'Eoin Ó Riabhaigh' },
      { option: 'mallory_pogam', label: 'Mallory Pogam' },
      { option: 'david_quinn', label: 'David Quinn' },
      { option: 'brendan_ring', label: 'Brendan Ring' },
      { option: 'andreas_rogge', label: 'Andreas Rogge' },
      { option: 'leo_rowsome', label: 'Leo Rowsome' },
      { option: 'ray_sloan', label: 'Ray Sloan' },
      { option: 'neil_speers', label: 'Neil Speers' },
      { option: 'taylor_brothers', label: 'Taylor Brothers' },
      { option: 'william_thomas', label: 'William Thomas' },
      { option: 'leo_vauclin', label: 'Léo Vauclin' },
      { option: 'joe_walsh', label: 'Joe Walsh' },
      { option: 'jim_wenham', label: 'Jim Wenham' },
      { option: 'philip_westwell', label: 'Philip Westwell' },
      { option: 'dave_williams', label: 'Dave Williams' },
      { option: 'geoff_wooff', label: 'Geoff Wooff' },
      { option: 'will_woodson', label: 'Will Woodson' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_uilleann_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_uilleann_pipes_config',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'practice_set', label: 'Practice Set' },
      { option: 'half_set', label: 'Half Set' },
      { option: 'three_quarter_set', label: 'Three-Quarter Set' },
      { option: 'full_set', label: 'Full Set' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_uilleann_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Set Configuration',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Set Configuration',
      isDetail: true,
    },
    saveConfig: {
      label: 'Set Configuration',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_uilleann_pipes_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'concert_d', label: 'Concert D' },
      { option: 'c_sharp', label: 'C#' },
      { option: 'c', label: 'C' },
      { option: 'b', label: 'B' },
      { option: 'b_flat', label: 'Bb' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_uilleann_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_uilleann_pipes_keys',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'keyless', label: 'Keyless' },
      { option: '1', label: '1' },
      { option: '2', label: '2' },
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5+' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_uilleann_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chanter Keys',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chanter Keys',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chanter Keys',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_uilleann_pipes_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'african_blackwood', label: 'African Blackwood' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'other_hardwood', label: 'Other Hardwood' },
      { option: 'synthetic_polymer', label: 'Synthetic / Polymer' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_uilleann_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Primary Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Primary Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Primary Material',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_highland_pipes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'atherton', label: 'Atherton' },
      { option: 'campbell', label: 'Campbell' },
      { option: 'ce_kron', label: 'C.E. Kron' },
      { option: 'colin_kyo', label: 'Colin Kyo' },
      { option: 'crisler', label: 'Crisler' },
      { option: 'cushing', label: 'Cushing' },
      { option: 'david_naill', label: 'David Naill' },
      { option: 'dunbar', label: 'Dunbar' },
      { option: 'dunfion', label: 'Dunfion' },
      { option: 'fletcher', label: 'Fletcher' },
      { option: 'gellaitry', label: 'Gellaitry' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'glen', label: 'Glen' },
      { option: 'grainger_campbell', label: 'Grainger & Campbell' },
      { option: 'inveran', label: 'Inveran' },
      { option: 'kintail', label: 'Kintail' },
      { option: 'donald_macdougall', label: 'Donald MacDougall' },
      { option: 'maclellan', label: 'MacLellan' },
      { option: 'macleod', label: 'MacLeod' },
      { option: 'macmurchie', label: 'MacMurchie' },
      { option: 'macpherson', label: 'MacPherson' },
      { option: 'macrae', label: 'MacRae' },
      { option: 'mccallum', label: 'McCallum' },
      { option: 'hamish_moore', label: 'Hamish Moore' },
      { option: 'murray', label: 'Murray' },
      { option: 'peter_henderson', label: 'Peter Henderson' },
      { option: 'rg_hardie', label: 'R.G. Hardie' },
      { option: 'rg_lawrie', label: 'R.G. Lawrie' },
      { option: 'robertson', label: 'Robertson' },
      { option: 'shepherd', label: 'Shepherd' },
      { option: 'sinclair', label: 'Sinclair' },
      { option: 'souter', label: 'Souter' },
      { option: 'st_kilda', label: 'St. Kilda' },
      { option: 'henry_starcke', label: 'Henry Starcke' },
      { option: 'strathmore', label: 'Strathmore' },
      { option: 'wallace', label: 'Wallace' },
      { option: 'warmac', label: 'Warmac' },
      { option: 'warnock', label: 'Warnock' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_highland_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_highland_pipes_config',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'full_set', label: 'Full Set' },
      { option: 'pipes_only_no_bagbellows', label: 'Pipes Only (no bag/bellows)' },
      { option: 'drones_only', label: 'Drones Only' },
      { option: 'chanter_only', label: 'Chanter Only' },
      { option: 'practice_chanter', label: 'Practice Chanter' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_highland_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Configuration',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Configuration',
      isDetail: true,
    },
    saveConfig: {
      label: 'Configuration',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_highland_pipes_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'african_blackwood', label: 'African Blackwood' },
      { option: 'cocuswood', label: 'Cocuswood' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'mopane', label: 'Mopane' },
      { option: 'other_hardwood', label: 'Other Hardwood' },
      { option: 'polypenco_delrin', label: 'Polypenco / Delrin' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_highland_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Primary Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Primary Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Primary Material',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_highland_pipes_fittings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'nickel_silver', label: 'Nickel Silver' },
      { option: 'sterling_silver', label: 'Sterling Silver' },
      { option: 'alloy', label: 'Alloy' },
      { option: 'imitation_ivory', label: 'Imitation Ivory' },
      { option: 'ivory', label: 'Ivory' },
      { option: 'antler_horn', label: 'Antler / Horn' },
      { option: 'engraved_silver', label: 'Engraved Silver' },
      { option: 'plain', label: 'Plain' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_highland_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Fittings Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Fittings Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Fittings Material',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_highland_pipes_pitch',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'standard_approx_470_480_hz', label: 'Standard (approx 470-480 Hz)' },
      { option: 'b_flat_466_hz', label: 'Bb (466 Hz)' },
      { option: 'concert_440_hz', label: 'Concert (440 Hz)' },
      { option: 'flat', label: 'Flat' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_highland_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Pitch Standard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Pitch Standard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Pitch Standard',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_scottish_smallpipes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'nate_banton', label: 'Nate Banton' },
      { option: 'matthias_branschke', label: 'Matthias Branschke' },
      { option: 'ross_calderwood_lochalsh_pipes', label: 'Ross Calderwood (Lochalsh Pipes)' },
      { option: 'richard_anita_evans', label: 'Richard & Anita Evans' },
      { option: 'julian_goodacre', label: 'Julian Goodacre' },
      { option: 'seth_hamon', label: 'Seth Hamon' },
      { option: 'heriot_allan', label: 'Heriot & Allan' },
      { option: 'kilberry_bagpipes', label: 'Kilberry Bagpipes' },
      { option: 'malin_lewis', label: 'Malin Lewis' },
      { option: 'lindstruments_donald_lindsay', label: 'Lindstruments (Donald Lindsay)' },
      { option: 'michael_macharg', label: 'Michael MacHarg' },
      { option: 'maclellan_roddy_maclellan', label: 'MacLellan (Roddy MacLellan)' },
      { option: 'macmurchie_david_macmurchie', label: 'MacMurchie (David MacMurchie)' },
      { option: 'mccallum_bagpipes_fred_morrison', label: 'McCallum Bagpipes (Fred Morrison)' },
      { option: 'gordon_mooney', label: 'Gordon Mooney' },
      { option: 'fin_moore', label: 'Fin Moore' },
      { option: 'hamish_moore', label: 'Hamish Moore' },
      { option: 'the_quietpiper_robert_felsburg', label: 'The QuietPiper (Robert Felsburg)' },
      { option: 'colin_ross', label: 'Colin Ross' },
      { option: 'ray_sloan', label: 'Ray Sloan' },
      { option: 'toru_sonoda', label: 'Toru Sonoda' },
      { option: 'tidy_cottage_smallpipes_ej_jones', label: 'Tidy Cottage Smallpipes (EJ Jones)' },
      { option: 'john_walsh', label: 'John Walsh' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_scottish_smallpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_scottish_smallpipes_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bellows', label: 'Bellows' },
      { option: 'mouth', label: 'Mouth' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_scottish_smallpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Blowing System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Blowing System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Blowing System',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_scottish_smallpipes_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a', label: 'A' },
      { option: 'd', label: 'D' },
      { option: 'c', label: 'C' },
      { option: 'b_flat', label: 'Bb' },
      { option: 'g', label: 'G' },
      { option: 'ad_combination', label: 'A/D Combination' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_scottish_smallpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_scottish_smallpipes_keys',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'keyless', label: 'Keyless' },
      { option: 'c_natural_thumb_hole', label: 'C Natural Thumb Hole' },
      { option: '1_2_keys', label: '1-2 Keys' },
      { option: '3_keys', label: '3+ Keys' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_scottish_smallpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chanter Keys',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chanter Keys',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chanter Keys',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_scottish_smallpipes_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '2', label: '2' },
      { option: '3', label: '3' },
      { option: '4_ad_combination', label: '4 (A/D Combination)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_scottish_smallpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Drones',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Drones',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Drones',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_border_pipes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'nate_banton', label: 'Nate Banton' },
      { option: 'john_burke', label: 'John Burke' },
      { option: 'ross_calderwood_lochalsh_pipes', label: 'Ross Calderwood (Lochalsh Pipes)' },
      { option: 'ian_corrigan_deerness_pipes', label: 'Ian Corrigan (Deerness Pipes)' },
      { option: 'richard_anita_evans', label: 'Richard & Anita Evans' },
      { option: 'garvie_bagpipes_nigel_richard', label: 'Garvie Bagpipes (Nigel Richard)' },
      { option: 'julian_goodacre', label: 'Julian Goodacre' },
      { option: 'robbie_greensitt', label: 'Robbie Greensitt' },
      { option: 'joe_hagan_j_r_glen', label: 'Joe Hagan (J & R Glen)' },
      { option: 'ej_jones_tidy_cottage_smallpipes', label: 'EJ Jones (Tidy Cottage Smallpipes)' },
      { option: 'kilberry_bagpipes', label: 'Kilberry Bagpipes' },
      { option: 'ian_kinnear', label: 'Ian Kinnear' },
      { option: 'michael_macharg', label: 'Michael MacHarg' },
      { option: 'macmurchie', label: 'MacMurchie' },
      { option: 'mccallum', label: 'McCallum' },
      { option: 'gordon_mooney', label: 'Gordon Mooney' },
      { option: 'fin_moore', label: 'Fin Moore' },
      { option: 'hamish_moore', label: 'Hamish Moore' },
      { option: 'fred_morrison_fred_morrison_pipes', label: 'Fred Morrison (Fred Morrison Pipes)' },
      { option: 'colin_ross', label: 'Colin Ross' },
      { option: 'ray_sloan', label: 'Ray Sloan' },
      { option: 'jon_swayne', label: 'Jon Swayne' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_border_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_border_pipes_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bellows', label: 'Bellows' },
      { option: 'mouth', label: 'Mouth' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_border_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Blowing System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Blowing System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Blowing System',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_border_pipes_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a', label: 'A' },
      { option: 'b_flat', label: 'Bb' },
      { option: 'g', label: 'G' },
      { option: 'd', label: 'D' },
      { option: 'c', label: 'C' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_border_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_border_pipes_keys',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'keyless', label: 'Keyless' },
      { option: 'high_b_key', label: 'High B Key' },
      { option: '2_keys', label: '2+ Keys' },
      { option: 'chromatic_keyed', label: 'Chromatic Keyed' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_border_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chanter Keys',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chanter Keys',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chanter Keys',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_border_pipes_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3_drones_bass_2_tenors', label: '3 Drones (Bass + 2 Tenors)' },
      { option: '3_drones_bass_baritone_tenor', label: '3 Drones (Bass + Baritone + Tenor)' },
      { option: '2_drones', label: '2 Drones' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_border_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Drone Configuration',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Drone Configuration',
      isDetail: true,
    },
    saveConfig: {
      label: 'Drone Configuration',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_northumbrian_pipes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'iain_allan', label: 'Iain Allan' },
      { option: 'b_j_say_barry_julia_say', label: 'B & J Say (Barry & Julia Say)' },
      { option: 'alex_barrass', label: 'Alex Barrass' },
      { option: 'kim_barlow', label: 'Kim Barlow' },
      { option: 'david_burleigh', label: 'David Burleigh' },
      { option: 'tom_clough_historical', label: 'Tom Clough (Historical)' },
      { option: 'anita_evans', label: 'Anita Evans' },
      { option: 'richard_evans', label: 'Richard Evans' },
      { option: 'heriot_and_allan_robbie_greensitt_ann_sessoms', label: 'Heriot and Allan (Robbie Greensitt & Ann Sessoms)' },
      { option: 'andy_lawrenson', label: 'Andy Lawrenson' },
      { option: 'andy_may', label: 'Andy May' },
      { option: 'dave_mcquade', label: 'Dave McQuade' },
      { option: 'mike_nelson', label: 'Mike Nelson' },
      { option: 'james_reid', label: 'James Reid' },
      { option: 'robert_reid', label: 'Robert Reid' },
      { option: 'colin_ross', label: 'Colin Ross' },
      { option: 'rob_say', label: 'Rob Say' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_northumbrian_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_northumbrian_pipes_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'f_traditional_concert', label: 'F (Traditional / Concert)' },
      { option: 'f_sharp_f', label: 'F+ (Sharp F)' },
      { option: 'f_sharp_historical_g', label: 'F# (Historical G)' },
      { option: 'g', label: 'G' },
      { option: 'd', label: 'D' },
      { option: 'c', label: 'C' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_northumbrian_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chanter Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chanter Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chanter Key',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_northumbrian_pipes_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5+' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_northumbrian_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Drones',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Drones',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Drones',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_northumbrian_pipes_keys',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'keyless_simple', label: 'Keyless (Simple)' },
      { option: '7_keys_standard', label: '7 Keys (Standard)' },
      { option: '9_keys', label: '9 Keys' },
      { option: '14_keys_chromatic', label: '14 Keys (Chromatic)' },
      { option: '17_keys_full_chromatic', label: '17 Keys (Full Chromatic)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_northumbrian_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chanter Keys',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chanter Keys',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chanter Keys',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_northumbrian_pipes_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'african_blackwood', label: 'African Blackwood' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'cocuswood', label: 'Cocuswood' },
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'fruitwood', label: 'Fruitwood' },
      { option: 'syntheticpolymer', label: 'Synthetic/Polymer' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_northumbrian_pipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Primary Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Primary Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Primary Material',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_gaita_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'afonso_castro', label: 'Afonso Castro' },
      { option: 'anton_corral', label: 'Antón Corral' },
      { option: 'xose_v_ferreiros', label: 'Xosé V. Ferreirós' },
      { option: 'alberto_jambrina_leal', label: 'Alberto Jambrina Leal' },
      { option: 'mourino', label: 'Mouriño' },
      { option: 'x_miguel_mosquera_medin', label: 'X. Miguel Mosquera Medín' },
      { option: 'seivane', label: 'Seivane' },
      { option: 'anton_varela', label: 'Antón Varela' },
      { option: 'oli_xiraldez', label: 'Oli Xiráldez' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_gaita',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_gaita_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'open_fingering_galician_style', label: 'Open Fingering (Galician style)' },
      { option: 'closed_fingering_asturian_style', label: 'Closed Fingering (Asturian style)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_gaita',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Fingering System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Fingering System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Fingering System',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_gaita_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gaita_gallega_galician', label: 'Gaita Gallega (Galician)' },
      { option: 'gaita_asturiana_asturian', label: 'Gaita Asturiana (Asturian)' },
      { option: 'gaita_de_banda_gaita_marcial', label: 'Gaita de Banda / Gaita Marcial' },
      { option: 'gaita_sanabresa_zamora', label: 'Gaita Sanabresa (Zamora)' },
      { option: 'gaita_transmontana_portuguese', label: 'Gaita Transmontana (Portuguese)' },
      { option: 'other_regional', label: 'Other Regional' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_gaita',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type / Tradition',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type / Tradition',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type / Tradition',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_gaita_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'c_do', label: 'C (Do)' },
      { option: 'c_brillante_sharp_c', label: 'C Brillante (Sharp C)' },
      { option: 'b_flat_si_bemol', label: 'Bb (Si Bemol)' },
      { option: 'd', label: 'D' },
      { option: 'b', label: 'B' },
      { option: 'a', label: 'A' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_gaita',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chanter Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chanter Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chanter Key',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_gaita_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '1_drone_bass_ronco_only', label: '1 Drone (Bass / Ronco only)' },
      { option: '2_drones_bass_tenor', label: '2 Drones (Bass + Tenor)' },
      { option: '3_drones_bass_tenor_ronquillo', label: '3 Drones (Bass + Tenor + Ronquillo)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_gaita',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Drones',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Drones',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Drones',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_zampogna_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'amedeo_cannazzaro', label: 'Amedeo Cannazzaro' },
      { option: 'carmine_carbone', label: 'Carmine Carbone' },
      { option: 'marco_cignini', label: 'Marco Cignini' },
      { option: 'carmine_dante', label: 'Carmine Dante' },
      { option: 'sergio_di_giorgio', label: 'Sergio di Giorgio' },
      { option: 'antonio_forastiero', label: 'Antonio Forastiero' },
      { option: 'forastiero_brothers', label: 'Forastiero Brothers' },
      { option: 'vincenzo_forastiero', label: 'Vincenzo Forastiero' },
      { option: 'lerose', label: 'LeRose' },
      { option: 'vito_leo', label: 'Vito Leo' },
      { option: 'giuseppe_russo', label: 'Giuseppe Russo' },
      { option: 'pino_salamone', label: 'Pino Salamone' },
      { option: 'scapoli_makers', label: 'Scapoli Makers' },
      { option: 'st_cecilia_mazziotti', label: 'St. Cecilia (Mazziotti)' },
      { option: 'marco_tomassi', label: 'Marco Tomassi' },
      { option: 'carmine_trimarco', label: 'Carmine Trimarco' },
      { option: 'vito_trimarco', label: 'Vito Trimarco' },
      { option: 'quirino_valvano', label: 'Quirino Valvano' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_zampogna',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_zampogna_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'zampogna_a_chiave_keyed', label: 'Zampogna a Chiave (Keyed)' },
      { option: 'zampogna_a_paro_equal_chanters', label: 'Zampogna a Paro (Equal Chanters)' },
      { option: 'zampogna_zoppa_lame_keyless', label: 'Zampogna Zoppa (Lame / Keyless)' },
      { option: 'surdulina_short_single_reed', label: 'Surdulina (Short, Single Reed)' },
      { option: 'ciaramedda_ciaramella_sicilian', label: 'Ciaramedda / Ciaramella (Sicilian)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_zampogna',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type / Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type / Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type / Style',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_zampogna_region',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'lucania_campania_basilicata', label: 'Lucania (Campania / Basilicata)' },
      { option: 'scapoli_molise', label: 'Scapoli / Molise' },
      { option: 'abruzzo', label: 'Abruzzo' },
      { option: 'latium_lazio', label: 'Latium (Lazio)' },
      { option: 'calabria', label: 'Calabria' },
      { option: 'sicily', label: 'Sicily' },
      { option: 'marches', label: 'Marches' },
      { option: 'pollino', label: 'Pollino' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_zampogna',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Regional Tradition',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Regional Tradition',
      isDetail: true,
    },
    saveConfig: {
      label: 'Regional Tradition',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_zampogna_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'due_palmi_2_palms', label: 'Due Palmi (2 Palms)' },
      { option: 'tre_palmi_3_palms', label: 'Tre Palmi (3 Palms)' },
      { option: 'quattro_palmi_4_palms', label: 'Quattro Palmi (4 Palms)' },
      { option: 'larger', label: 'Larger' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_zampogna',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size (Palmi)',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size (Palmi)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size (Palmi)',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_zampogna_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '0', label: '0' },
      { option: '1_drone_bass', label: '1 Drone (Bass)' },
      { option: '2_drones_bass_alto_tenor', label: '2 Drones (Bass + Alto / Tenor)' },
      { option: '3_drones', label: '3 Drones' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_zampogna',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Drones',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Drones',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Drones',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_musette_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'joseph_bechonnet', label: 'Joseph Béchonnet' },
      { option: 'bernard_blanc', label: 'Bernard Blanc' },
      { option: 'thierry_boisvert', label: 'Thierry Boisvert' },
      { option: 'jean_dechaud', label: 'Jean Déchaud' },
      { option: 'remy_dubois', label: 'Rémy Dubois' },
      { option: 'olle_geris', label: 'Olle Geris' },
      { option: 'claude_girard', label: 'Claude Girard' },
      { option: 'sean_jones', label: 'Sean Jones' },
      { option: 'eric_montbel', label: 'Eric Montbel' },
      { option: 'gilles_musik', label: 'Gilles Musik' },
      { option: 'luc_pilartz', label: 'Luc Pilartz' },
      { option: 'jean_sautivet', label: 'Jean Sautivet' },
      { option: 'jean_pierre_van_hees', label: 'Jean-Pierre Van Hees' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_musette',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_musette_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cornemuse_du_centre', label: 'Cornemuse du Centre' },
      { option: 'musette_bechonnet', label: 'Musette Béchonnet' },
      { option: 'cabrette_auvergne', label: 'Cabrette (Auvergne)' },
      { option: 'chabrette_limousin', label: 'Chabrette (Limousin)' },
      { option: 'biniou_koz_breton', label: 'Biniou Koz (Breton)' },
      { option: 'biniou_bras_grand_biniou_breton', label: 'Biniou Bras / Grand Biniou (Breton)' },
      { option: 'veuze_loire_atlantique_vendee', label: 'Veuze (Loire-Atlantique / Vendée)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_musette',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_musette_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'mouth', label: 'Mouth' },
      { option: 'bellows', label: 'Bellows' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_musette',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Blowing System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Blowing System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Blowing System',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_musette_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'g_20_pouces_most_common', label: 'G / 20 Pouces (Most Common)' },
      { option: 'd_14_pouces', label: 'D / 14 Pouces' },
      { option: 'c_16_pouces', label: 'C / 16 Pouces' },
      { option: 'b_flat', label: 'Bb' },
      { option: 'a', label: 'A' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_musette',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_musette_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '0_cabrette_solo_style', label: '0 (Cabrette Solo Style)' },
      { option: '1_drone', label: '1 Drone' },
      { option: '2_drones', label: '2 Drones' },
      { option: '3_drones', label: '3 Drones' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_musette',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Drones',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Drones',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Drones',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_other_bagpipes_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'boha_french_gascony', label: 'Boha (French - Gascony)' },
      { option: 'bodega_french_languedoc', label: 'Bodega (French - Languedoc)' },
      { option: 'swedish_sackpipa', label: 'Swedish Säckpipa' },
      { option: 'tulum_turkish', label: 'Tulum (Turkish)' },
      { option: 'musette_de_cour_baroque', label: 'Musette de Cour (Baroque)' },
      { option: 'hummelchen_german', label: 'Hümmelchen (German)' },
      { option: 'schaferpfeife_german_shepherd_s_pipe', label: 'Schäferpfeife (German Shepherd\'s Pipe)' },
      { option: 'dudelsack_german', label: 'Dudelsack (German)' },
      { option: 'gaida_gajda_balkan', label: 'Gaida / Gajda (Balkan)' },
      { option: 'cimpoi_romanian', label: 'Cimpoi (Romanian)' },
      { option: 'duda_hungarian', label: 'Duda (Hungarian)' },
      { option: 'koziol_polish', label: 'Koziol (Polish)' },
      { option: 'bock_czechgerman', label: 'Bock (Czech/German)' },
      { option: 'sackpipa_scandinavian', label: 'Sackpipa (Scandinavian)' },
      { option: 'medieval_reproduction', label: 'Medieval Reproduction' },
      { option: 'renaissance_reproduction', label: 'Renaissance Reproduction' },
      { option: 'other_unidentified', label: 'Other / Unidentified' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_other_bagpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: true,
    },
  },
  {
    key: 'bagpipes_other_bagpipes_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'mouth', label: 'Mouth' },
      { option: 'bellows', label: 'Bellows' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_other_bagpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Blowing System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Blowing System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Blowing System',
      isRequired: false,
    },
  },
  {
    key: 'bagpipes_other_bagpipes_drones',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '0', label: '0' },
      { option: '1', label: '1' },
      { option: '2', label: '2' },
      { option: '3', label: '3+' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bagpipes_other_bagpipes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Drones',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Drones',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Drones',
      isRequired: false,
    },
  },
  {
    key: 'wind_reed_accordion_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ballone_burini', label: 'Ballone Burini' },
      { option: 'beltuna', label: 'Beltuna' },
      { option: 'borsini', label: 'Borsini' },
      { option: 'brandoni', label: 'Brandoni' },
      { option: 'briggs', label: 'Briggs' },
      { option: 'bugari', label: 'Bugari' },
      { option: 'cairdin', label: 'Cairdín' },
      { option: 'castagnari', label: 'Castagnari' },
      { option: 'paddy_clancy', label: 'Paddy Clancy' },
      { option: 'dallape', label: 'Dallapè' },
      { option: 'delicia', label: 'Delicia' },
      { option: 'excelsior', label: 'Excelsior' },
      { option: 'fisart', label: 'Fisart' },
      { option: 'fratelli_crosio', label: 'Fratelli Crosio' },
      { option: 'gabbanelli', label: 'Gabbanelli' },
      { option: 'gaillard', label: 'Gaillard' },
      { option: 'giulietti', label: 'Giulietti' },
      { option: 'guerrini', label: 'Guerrini' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'jupiter', label: 'Jupiter' },
      { option: 'kincora_martin_connolly', label: 'Kincora (Martin Connolly)' },
      { option: 'loffet', label: 'Loffet' },
      { option: 'marcus', label: 'Marcus' },
      { option: 'marinucci', label: 'Marinucci' },
      { option: 'maugein', label: 'Maugein' },
      { option: 'melodija', label: 'Melodija' },
      { option: 'mengascini', label: 'Mengascini' },
      { option: 'musictech', label: 'Musictech' },
      { option: 'paolo_soprani', label: 'Paolo Soprani' },
      { option: 'petosa', label: 'Petosa' },
      { option: 'piermaria', label: 'Piermaria' },
      { option: 'pigini', label: 'Pigini' },
      { option: 'roland', label: 'Roland' },
      { option: 'saltarelle', label: 'Saltarelle' },
      { option: 'savoia', label: 'Savoïa' },
      { option: 'scandalli', label: 'Scandalli' },
      { option: 'serenellini', label: 'Serenellini' },
      { option: 'sherwood', label: 'Sherwood' },
      { option: 'sonola', label: 'Sonola' },
      { option: 'strasser', label: 'Strasser' },
      { option: 'super_locatelli', label: 'Super Locatelli' },
      { option: 'titano', label: 'Titano' },
      { option: 'verde', label: 'Verde' },
      { option: 'victoria', label: 'Victoria' },
      { option: 'weltmeister', label: 'Weltmeister' },
      { option: 'zupan', label: 'Zupan' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_chromatic_button_accordion',
        'accordion_diatonic_button_accordion',
        'accordion_piano_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'accordion_diatonic_button_accordion_tuning',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'b_c', label: 'B/C' },
      { option: 'c_sharp_d', label: 'C#/D' },
      { option: 'b_c_c_sharp', label: 'B/C/C#' },
      { option: 'c_sharp_d_g', label: 'C#/D/G' },
      { option: 'd_g', label: 'D/G' },
      { option: 'g_c', label: 'G/C' },
      { option: 'a_d', label: 'A/D' },
      { option: 'g_c_f', label: 'G/C/F' },
      { option: 'f_b_flat_e_flat', label: 'F/Bb/Eb' },
      { option: 'e_a_d', label: 'E/A/D' },
      { option: 'a_d_g', label: 'A/D/G' },
      { option: 'b_flat_e_flat_a_flat', label: 'Bb/Eb/Ab' },
      { option: 'g_c_f_b_flat', label: 'G/C/F/Bb' },
      { option: 'a_d_g_c', label: 'A/D/G/C' },
      { option: 'c_single_row', label: 'C (single row)' },
      { option: 'd_single_row', label: 'D (single row)' },
      { option: 'g_single_row', label: 'G (single row)' },
      { option: 'b_flat_single_row', label: 'Bb (single row)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_diatonic_button_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning',
      isRequired: false,
    },
  },
  {
    key: 'accordion_diatonic_button_accordion_rows',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '1', label: '1' },
      { option: '2', label: '2' },
      { option: '2_5', label: '2.5' },
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_diatonic_button_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Rows',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Rows',
      isDetail: true,
    },
    saveConfig: {
      label: 'Rows',
      isRequired: false,
    },
  },
  {
    key: 'accordion_diatonic_button_accordion_voice',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '1', label: '1' },
      { option: '2', label: '2' },
      { option: '3', label: '3' },
      { option: '4', label: '4' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_diatonic_button_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Voices',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Voices',
      isDetail: true,
    },
    saveConfig: {
      label: 'Voices',
      isRequired: false,
    },
  },
  {
    key: 'accordion_diatonic_button_accordion_bass',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '2', label: '2' },
      { option: '4', label: '4' },
      { option: '8', label: '8' },
      { option: '12', label: '12' },
      { option: '18', label: '18' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_diatonic_button_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bass Buttons',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bass Buttons',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bass Buttons',
      isRequired: false,
    },
  },
  {
    key: 'accordion_chromatic_button_accordion_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'c', label: 'C' },
      { option: 'b', label: 'B' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_chromatic_button_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Button System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Button System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Button System',
      isRequired: false,
    },
  },
  {
    key: 'accordion_chromatic_button_accordion_bass',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '48', label: '48' },
      { option: '60', label: '60' },
      { option: '72', label: '72' },
      { option: '80', label: '80' },
      { option: '96', label: '96' },
      { option: '120', label: '120' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_chromatic_button_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bass Buttons',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bass Buttons',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bass Buttons',
      isRequired: false,
    },
  },
  {
    key: 'accordion_piano_accordion_bass',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '12', label: '12' },
      { option: '24', label: '24' },
      { option: '32', label: '32' },
      { option: '48', label: '48' },
      { option: '60', label: '60' },
      { option: '72', label: '72' },
      { option: '80', label: '80' },
      { option: '96', label: '96' },
      { option: '120', label: '120' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_piano_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bass Buttons',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bass Buttons',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bass Buttons',
      isRequired: false,
    },
  },
  {
    key: 'accordion_piano_accordion_treble',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '25', label: '25' },
      { option: '26', label: '26' },
      { option: '30', label: '30' },
      { option: '34', label: '34' },
      { option: '37', label: '37' },
      { option: '41', label: '41' },
      { option: '45', label: '45' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_piano_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Treble Keys',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Treble Keys',
      isDetail: true,
    },
    saveConfig: {
      label: 'Treble Keys',
      isRequired: false,
    },
  },
  {
    key: 'accordion_piano_accordion_voices',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '2', label: '2' },
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'accordion_piano_accordion',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Voices',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Voices',
      isDetail: true,
    },
    saveConfig: {
      label: 'Voices',
      isRequired: false,
    },
  },
  {
    key: 'concertina_anglo_concertina_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a_accordion_kay_albrecht', label: 'A Accordion (Kay Albrecht)' },
      { option: 'accordeons_du_marais', label: 'Accordéons du Marais' },
      { option: 'ap_james_claddagh', label: 'AP James (Claddagh)' },
      { option: 'bastari', label: 'Bastari' },
      { option: 'brunner', label: 'Brunner' },
      { option: 'burren_tim_collins', label: 'Burren (Tim Collins)' },
      { option: 'carroll_wally_carroll', label: 'Carroll (Wally Carroll)' },
      { option: 'rock_chidley_inc_edward_chidley', label: 'Rock Chidley (inc. Edward Chidley)' },
      { option: 'jose_claro', label: 'Jose Claro' },
      { option: 'concertina_connection', label: 'Concertina Connection' },
      { option: 'concertina_gk_kevin_garnier', label: 'Concertina GK (Kevin Garnier)' },
      { option: 'concertina_workshop_jurie_olckers', label: 'Concertina Workshop (Jurie Olckers)' },
      { option: 'concertine_italia', label: 'Concertine Italia' },
      { option: 'crab_flat_j_crab_flat_sons_h_crab_flat_sons', label: 'Crabb (J. Crabb & Sons / H. Crabb & Sons)' },
      { option: 'geoff_crab_flat_modern_revival', label: 'Geoff Crabb (Modern Revival)' },
      { option: 'crook_willie_crook', label: 'Crook (Willie Crook)' },
      { option: 'dipper_c_r_dipper_son', label: 'Dipper (C. & R. Dipper & Son)' },
      { option: 'edgley_heritage_frank_edgley', label: 'Edgley Heritage (Frank Edgley)' },
      { option: 'edgley_hybrid_frank_edgley', label: 'Edgley Hybrid (Frank Edgley)' },
      { option: 'faber', label: 'Faber' },
      { option: 'generic_unbranded_chinese', label: 'Generic / Unbranded Chinese' },
      { option: 'geuns_harry_geuns', label: 'Geuns (Harry Geuns)' },
      { option: 'allan_green', label: 'Allan Green' },
      { option: 'seth_hamon', label: 'Seth Hamon' },
      { option: 'hohner_d_40', label: 'Hohner (D-40)' },
      { option: 'holden_alex_holden', label: 'Holden (Alex Holden)' },
      { option: 'inkostini_david_jenkins', label: 'Inkostini (David Jenkins)' },
      { option: 'irish_concertina_company_clare', label: 'Irish Concertina Company (Clare)' },
      { option: 'irish_concertina_company_tina_swift_raven', label: 'Irish Concertina Company (Tina / Swift / Raven)' },
      { option: 'irish_concertina_company_vintage_eiru', label: 'Irish Concertina Company (Vintage / Eiru)' },
      { option: 'jay_edward_jay', label: 'Jay (Edward Jay)' },
      { option: 'jeffries_c_jeffries_maker', label: 'Jeffries (C. Jeffries, Maker)' },
      { option: 'jones_george_jones_sons', label: 'Jones (George Jones & Sons)' },
      { option: 'kensington_dana_johnson', label: 'Kensington (Dana Johnson)' },
      { option: 'lachenal_louis_lachenal_lachenal_co', label: 'Lachenal (Louis Lachenal / Lachenal & Co.)' },
      { option: 'gys_mans', label: 'Gys Mans' },
      { option: 'marcus_marcus_butler', label: 'Marcus (Marcus Butler)' },
      { option: 'mcneela_wren_swan', label: 'McNeela (Wren / Swan)' },
      { option: 'mcneela_phoenix', label: 'McNeela Phoenix' },
      { option: 'morse_rich_morse_the_button_box', label: 'Morse (Rich Morse — The Button Box)' },
      { option: 'nickolds_f_c_nickolds_nickolds_bros', label: 'Nickolds (F.C. Nickolds / Nickolds Bros.)' },
      { option: 'norman_andrew_norman', label: 'Norman (Andrew Norman)' },
      { option: 'o_shaughnessy', label: 'O\'Shaughnessy' },
      { option: 'scarlatti', label: 'Scarlatti' },
      { option: 'seven_mount_ralf_schlimm', label: 'Seven Mount (Ralf Schlimm)' },
      { option: 'sherwood_flynn_marion_hobgoblin', label: 'Sherwood Flynn / Marion (Hobgoblin)' },
      { option: 'sherwood_kirklees_hobgoblin', label: 'Sherwood Kirklees (Hobgoblin)' },
      { option: 'stagi', label: 'Stagi' },
      { option: 'stephanelli', label: 'Stephanelli' },
      { option: 'suttner_jurgen_suttner', label: 'Suttner (Jürgen Suttner)' },
      { option: 'tassey_tiger_david_hornett', label: 'Tassey Tiger (David Hornett)' },
      { option: 'tedrow_bob_tedrow', label: 'Tedrow (Bob Tedrow)' },
      { option: 'thomas_jeff_thomas', label: 'Thomas (Jeff Thomas)' },
      { option: 'wakker_wim_wakker', label: 'Wakker (Wim Wakker)' },
      { option: 'wheatstone_modern_steve_dickinson', label: 'Wheatstone (Modern — Steve Dickinson)' },
      { option: 'wheatstone_vintage_pre_dickinson', label: 'Wheatstone (Vintage — Pre-Dickinson)' },
      { option: 'wifra_willie_van_wyk', label: 'Wifra (Willie van Wyk)' },
      { option: 'wolverton_jake_middleton', label: 'Wolverton (Jake Middleton)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_anglo_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'concertina_anglo_concertina_buttons',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '20', label: '20' },
      { option: '30', label: '30' },
      { option: '38', label: '38' },
      { option: '40', label: '40' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_anglo_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Buttons',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Buttons',
      isDetail: true,
    },
    saveConfig: {
      label: 'Buttons',
      isRequired: false,
    },
  },
  {
    key: 'concertina_anglo_concertina_layout',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'jeffries', label: 'Jeffries' },
      { option: 'wheatstone_lachenal', label: 'Wheatstone / Lachenal' },
      { option: 'edgley_herrington', label: 'Edgley / Herrington' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_anglo_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Layout',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Layout',
      isDetail: true,
    },
    saveConfig: {
      label: 'Layout',
      isRequired: false,
    },
  },
  {
    key: 'concertina_anglo_concertina_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'c_g', label: 'C/G' },
      { option: 'g_d', label: 'G/D' },
      { option: 'b_flat_f', label: 'Bb/F' },
      { option: 'a_flat_e_flat', label: 'Ab/Eb' },
      { option: 'a_d', label: 'A/D' },
      { option: 'c_sharp_g_sharp', label: 'C#/G#' },
      { option: 'd_a', label: 'D/A' },
      { option: 'e_flat_b_flat', label: 'Eb/Bb' },
      { option: 'f_c', label: 'F/C' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_anglo_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'concertina_anglo_concertina_reed',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'traditional_concertina_reeds_steel', label: 'Traditional Concertina Reeds (Steel)' },
      { option: 'traditional_concertina_reeds_brass', label: 'Traditional Concertina Reeds (Brass)' },
      { option: 'hybrid_accordion_reeds', label: 'Hybrid (Accordion Reeds)' },
      { option: 'unknown_not_specified', label: 'Unknown / Not Specified' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_anglo_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Reed Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Reed Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Reed Type',
      isRequired: false,
    },
  },
  {
    key: 'concertina_english_concertina_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'jabez_austin', label: 'Jabez Austin' },
      { option: 'bastari', label: 'Bastari' },
      { option: 'brunner', label: 'Brunner' },
      { option: 'george_case', label: 'George Case' },
      { option: 'rock_edward_chidley', label: 'Rock & Edward Chidley' },
      { option: 'concertina_connection', label: 'Concertina Connection' },
      { option: 'concertine_italia', label: 'Concertine Italia' },
      { option: 'crab_flat_j_crab_flat_sons_h_crab_flat_son', label: 'Crabb (J. Crabb & Sons / H. Crabb & Son)' },
      { option: 'geoff_crab_flat_modern_revival', label: 'Geoff Crabb (Modern Revival)' },
      { option: 'dipper_c_r_dipper_son', label: 'Dipper (C. & R. Dipper & Son)' },
      { option: 'william_dove', label: 'William Dove' },
      { option: 'ebblewhite', label: 'Ebblewhite' },
      { option: 'generic_unbranded_chinese', label: 'Generic / Unbranded Chinese' },
      { option: 'chris_ghent', label: 'Chris Ghent' },
      { option: 'henry_harley', label: 'Henry Harley' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'holden', label: 'Holden' },
      { option: 'jones_george_jones_sons', label: 'Jones (George Jones & Sons)' },
      { option: 'keith_prowse_co', label: 'Keith, Prowse & Co.' },
      { option: 'lachenal_louis_lachenal_lachenal_co', label: 'Lachenal (Louis Lachenal / Lachenal & Co.)' },
      { option: 'marcus', label: 'Marcus' },
      { option: 'may_fair_wheatstone_budget_line', label: 'May Fair (Wheatstone Budget Line)' },
      { option: 'mcneela', label: 'McNeela' },
      { option: 'morse_rich_morse_the_button_box', label: 'Morse (Rich Morse / The Button Box)' },
      { option: 'nickolds_f_c_nickolds_nickolds_bros', label: 'Nickolds (F.C. Nickolds / Nickolds Bros.)' },
      { option: 'norman_andrew_norman', label: 'Norman (Andrew Norman)' },
      { option: 'william_prowse', label: 'William Prowse' },
      { option: 'scarlatti', label: 'Scarlatti' },
      { option: 'joseph_scates', label: 'Joseph Scates' },
      { option: 'shakespeare', label: 'Shakespeare' },
      { option: 'simpson', label: 'Simpson' },
      { option: 'stagi', label: 'Stagi' },
      { option: 'stephanelli', label: 'Stephanelli' },
      { option: 'suttner_jurgen_suttner', label: 'Suttner (Jürgen Suttner)' },
      { option: 'tedrow_bob_tedrow', label: 'Tedrow (Bob Tedrow)' },
      { option: 'john_alvey_turner', label: 'John Alvey Turner' },
      { option: 'wakker_wim_wakker', label: 'Wakker (Wim Wakker)' },
      { option: 'wheatstone_steve_dickinson_modern', label: 'Wheatstone (Steve Dickinson — Modern)' },
      { option: 'wheatstone_vintage_pre_dickinson', label: 'Wheatstone (Vintage — Pre-Dickinson)' },
      { option: 'wolverton_jake_middleton', label: 'Wolverton (Jake Middleton)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_english_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'concertina_english_concertina_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'piccolo', label: 'Piccolo' },
      { option: 'treble', label: 'Treble' },
      { option: 'tenor_treble', label: 'Tenor-Treble' },
      { option: 'baritone', label: 'Baritone' },
      { option: 'bass', label: 'Bass' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_english_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'concertina_english_concertina_buttons',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '30', label: '30' },
      { option: '37', label: '37' },
      { option: '48', label: '48' },
      { option: '56', label: '56' },
      { option: '64', label: '64' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_english_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Buttons',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Buttons',
      isDetail: true,
    },
    saveConfig: {
      label: 'Buttons',
      isRequired: false,
    },
  },
  {
    key: 'concertina_duet_concertina_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bastari', label: 'Bastari' },
      { option: 'concertina_connection', label: 'Concertina Connection' },
      { option: 'concertine_italia', label: 'Concertine Italia' },
      { option: 'crab_flat_j_crab_flat_sons_h_crab_flat_son', label: 'Crabb (J. Crabb & Sons, H. Crabb & Son)' },
      { option: 'crane_sons', label: 'Crane & Sons' },
      { option: 'dipper_c_r_dipper_son', label: 'Dipper (C. & R. Dipper & Son)' },
      { option: 'holden_alex_holden', label: 'Holden (Alex Holden)' },
      { option: 'jeffries_c_jeffries', label: 'Jeffries (C. Jeffries)' },
      { option: 'lachenal_louis_lachenal_lachenal_co', label: 'Lachenal (Louis Lachenal, Lachenal & Co.)' },
      { option: 'morse_beaumont_hayden', label: 'Morse (Beaumont — Hayden)' },
      { option: 'stagi', label: 'Stagi' },
      { option: 'suttner_jurgen_suttner', label: 'Suttner (Jürgen Suttner)' },
      { option: 'tedrow_bob_tedrow', label: 'Tedrow (Bob Tedrow)' },
      { option: 'unbranded_chinese', label: 'Unbranded Chinese' },
      { option: 'wakker_wim_wakker', label: 'Wakker (Wim Wakker)' },
      { option: 'wheatstone_steve_dickinson_modern', label: 'Wheatstone (Steve Dickinson — Modern)' },
      { option: 'wheatstone_vintage_pre_dickinson', label: 'Wheatstone (Vintage — Pre-Dickinson)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_duet_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'concertina_duet_concertina_system',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maccann', label: 'Maccann' },
      { option: 'crane_triumph', label: 'Crane (Triumph)' },
      { option: 'hayden_wicki', label: 'Hayden (Wicki)' },
      { option: 'jeffries_duet', label: 'Jeffries Duet' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_duet_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'System',
      isDetail: true,
    },
    saveConfig: {
      label: 'System',
      isRequired: false,
    },
  },
  {
    key: 'concertina_duet_concertina_buttons',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '35', label: '35' },
      { option: '40', label: '40' },
      { option: '46', label: '46' },
      { option: '48', label: '48' },
      { option: '52', label: '52' },
      { option: '55', label: '55' },
      { option: '57', label: '57' },
      { option: '62', label: '62' },
      { option: '65', label: '65+' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_duet_concertina',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Buttons',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Buttons',
      isDetail: true,
    },
    saveConfig: {
      label: 'Buttons',
      isRequired: false,
    },
  },
  {
    key: 'concertina_other_concertinas_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aa_alfred_arnold', label: 'AA (Alfred Arnold)' },
      { option: 'aa_bandoneons', label: 'AA Bandoneons' },
      { option: 'aa_campo_alfred_arnold', label: 'AA Campo (Alfred Arnold)' },
      { option: 'aa_premier_alfred_arnold', label: 'AA Premier (Alfred Arnold)' },
      { option: 'arno_arnold', label: 'Arno Arnold' },
      { option: 'baldoni', label: 'Baldoni' },
      { option: 'bastari', label: 'Bastari' },
      { option: 'brown_bill_brown', label: 'Brown (Bill Brown)' },
      { option: 'eagle', label: 'Eagle' },
      { option: 'echo_bob_novak', label: 'Echo (Bob Novak)' },
      { option: 'echo_stan_uhlir_bob_novak', label: 'Echo (Stan Uhlir & Bob Novak)' },
      { option: 'ela_ernst_louis_arnold', label: 'ELA (Ernst Louis Arnold)' },
      { option: 'gebr_meinel', label: 'Gebr. Meinel' },
      { option: 'harry_geuns', label: 'Harry Geuns' },
      { option: 'glass_glass_brothers', label: 'Glass (Glass Brothers)' },
      { option: 'grand_ozzie_guerini', label: 'Grand (Ozzie Guerini)' },
      { option: 'klaus_gutjahr', label: 'Klaus Gutjahr' },
      { option: 'uwe_hartenhauer', label: 'Uwe Hartenhauer' },
      { option: 'hengel_christy_hengel_vintage', label: 'Hengel (Christy Hengel — Vintage)' },
      { option: 'hengel_jerry_minar_jbm_sound', label: 'Hengel (Jerry Minar — JBM Sound)' },
      { option: 'hohner_tango_cardenal', label: 'Hohner (Tango & Cardenal)' },
      { option: 'international', label: 'International' },
      { option: 'karpek_andrew_karpek', label: 'Karpek (Andrew Karpek)' },
      { option: 'kosatka', label: 'Kosatka' },
      { option: 'lange_friedrich_lange', label: 'Lange (Friedrich Lange)' },
      { option: 'meinel_herold_tres_b', label: 'Meinel & Herold (Tres B)' },
      { option: 'morbidoni', label: 'Morbidoni' },
      { option: 'patek_rudy_patek', label: 'Patek (Rudy Patek)' },
      { option: 'pearl_king', label: 'Pearl King' },
      { option: 'pearl_queen_otto_schlicht_vitak_elsnic', label: 'Pearl Queen (Otto Schlicht & Vitak-Elsnic)' },
      { option: 'peerless_otto_schlicht_kosatka', label: 'Peerless (Otto Schlicht & Kosatka)' },
      { option: 'premier_bandoneonbau_peter_spende', label: 'Premier Bandoneonbau (Peter Spende)' },
      { option: 'royal_slim_maser', label: 'Royal ("Slim" Maser)' },
      { option: 'rw_bandoneon_robert_wallschlager', label: 'RW Bandoneon (Robert Wallschläger)' },
      { option: 'schreiber', label: 'Schreiber' },
      { option: 'stagi', label: 'Stagi' },
      { option: 'star_international_accordion_co', label: 'Star (International Accordion Co.)' },
      { option: 'wolfe_anton_wolfe', label: 'Wolfe (Anton Wolfe)' },
      { option: 'wunderlich', label: 'Wunderlich' },
      { option: 'zimmermann_carl_friedrich_zimmermann', label: 'Zimmermann (Carl Friedrich Zimmermann)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_other_concertinas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'concertina_other_concertinas_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'chemnitzer', label: 'Chemnitzer' },
      { option: 'bandoneon', label: 'Bandoneon' },
      { option: 'carlsfelder', label: 'Carlsfelder' },
      { option: 'other_unknown', label: 'Other / Unknown' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_other_concertinas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'concertina_other_concertinas_key',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a', label: 'A' },
      { option: 'b_flat', label: 'Bb' },
      { option: 'b', label: 'B' },
      { option: 'c', label: 'C' },
      { option: 'c_sharp_d_flat', label: 'C# / Db' },
      { option: 'd', label: 'D' },
      { option: 'e_flat', label: 'Eb' },
      { option: 'e', label: 'E' },
      { option: 'f', label: 'F' },
      { option: 'f_sharp', label: 'F#' },
      { option: 'g', label: 'G' },
      { option: 'a_flat', label: 'Ab' },
      { option: 'chromatic_multiple_keys', label: 'Chromatic / Multiple Keys' },
      { option: 'not_applicable', label: 'Not Applicable' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_other_concertinas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'concertina_other_concertinas_voices',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single', label: 'Single' },
      { option: 'double_2_reeds_per_note', label: 'Double (2 Reeds per Note)' },
      { option: 'triple_3_reeds_per_note', label: 'Triple (3 Reeds per Note)' },
      { option: 'quadruple_4_reeds_per_note', label: 'Quadruple (4 Reeds per Note)' },
      { option: 'other_unknown', label: 'Other / Unknown' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'concertina_other_concertinas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Voices',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Voices',
      isDetail: true,
    },
    saveConfig: {
      label: 'Voices',
      isRequired: false,
    },
  },
  {
    key: 'harmonica_diatonic_harmonica_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'joel_andersson', label: 'Joel Andersson' },
      { option: 'bushman', label: 'Bushman' },
      { option: 'dabell', label: 'DaBell' },
      { option: 'easttop', label: 'Easttop' },
      { option: 'fender', label: 'Fender' },
      { option: 'hammond', label: 'Hammond' },
      { option: 'harmo', label: 'Harmo' },
      { option: 'hering', label: 'Hering' },
      { option: 'hero', label: 'Hero' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'huang', label: 'Huang' },
      { option: 'kongsheng', label: 'Kongsheng' },
      { option: 'lee_oskar', label: 'Lee Oskar' },
      { option: 'mississippi_harmonica_co', label: 'Mississippi Harmonica Co.' },
      { option: 'brendan_power', label: 'Brendan Power' },
      { option: 'seydel', label: 'Seydel' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'swan', label: 'Swan' },
      { option: 'tombo', label: 'Tombo' },
      { option: 'turboharp_james_antaki', label: 'TurboHarp (James Antaki)' },
      { option: 'weltmeister', label: 'Weltmeister' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_diatonic_harmonica',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'harmonica_diatonic_harmonica_tuning',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'richter_standard', label: 'Richter (Standard)' },
      { option: 'country', label: 'Country' },
      { option: 'harmonic_minor', label: 'Harmonic Minor' },
      { option: 'melody_maker', label: 'Melody Maker' },
      { option: 'natural_minor', label: 'Natural Minor' },
      { option: 'paddy_richter', label: 'Paddy Richter' },
      { option: 'powerbender', label: 'PowerBender' },
      { option: 'powerdraw', label: 'PowerDraw' },
      { option: 'solo', label: 'Solo' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_diatonic_harmonica',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning',
      isRequired: false,
    },
  },
  {
    key: 'harmonica_chromatic_harmonica_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bushman', label: 'Bushman' },
      { option: 'easttop', label: 'Easttop' },
      { option: 'fender', label: 'Fender' },
      { option: 'harmo', label: 'Harmo' },
      { option: 'hering', label: 'Hering' },
      { option: 'hero', label: 'Hero' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'huang', label: 'Huang' },
      { option: 'kongsheng', label: 'Kongsheng' },
      { option: 'brendan_power', label: 'Brendan Power' },
      { option: 'seydel', label: 'Seydel' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'swan', label: 'Swan' },
      { option: 'tombo', label: 'Tombo' },
      { option: 'weltmeister', label: 'Weltmeister' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_chromatic_harmonica',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'harmonica_other_harmonicas_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'dabell', label: 'DaBell' },
      { option: 'easttop', label: 'Easttop' },
      { option: 'hering', label: 'Hering' },
      { option: 'hero', label: 'Hero' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'huang', label: 'Huang' },
      { option: 'kongsheng', label: 'Kongsheng' },
      { option: 'mississippi_harmonica_co', label: 'Mississippi Harmonica Co.' },
      { option: 'brendan_power', label: 'Brendan Power' },
      { option: 'seydel', label: 'Seydel' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'swan', label: 'Swan' },
      { option: 'tombo', label: 'Tombo' },
      { option: 'weltmeister', label: 'Weltmeister' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_other_harmonicas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'harmonica_diatonic_harmonica_comb',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'plastic', label: 'Plastic' },
      { option: 'wood', label: 'Wood' },
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'acrylic', label: 'Acrylic' },
      { option: 'brass', label: 'Brass' },
      { option: 'composite', label: 'Composite' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_diatonic_harmonica',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Comb Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Comb Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Comb Material',
      isRequired: false,
    },
  },
  {
    key: 'harmonica_chromatic_harmonica_holes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '10', label: '10' },
      { option: '12', label: '12' },
      { option: '14', label: '14' },
      { option: '16', label: '16' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_chromatic_harmonica',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Holes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Holes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Holes',
      isRequired: false,
    },
  },
  {
    key: 'harmonica_other_harmonicas_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'tremolo', label: 'Tremolo' },
      { option: 'octave', label: 'Octave' },
      { option: 'bass', label: 'Bass' },
      { option: 'chord', label: 'Chord' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_other_harmonicas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'harmonica_other_harmonicas_holes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '10', label: '10' },
      { option: '12', label: '12' },
      { option: '14', label: '14' },
      { option: '16', label: '16' },
      { option: '20', label: '20' },
      { option: '21', label: '21' },
      { option: '24', label: '24' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harmonica_other_harmonicas',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Holes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Holes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Holes',
      isRequired: false,
    },
  },
  {
    key: 'guitar_acoustic_steel_string_guitar_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'alberico', label: 'Alberico' },
      { option: 'alvarez', label: 'Alvarez' },
      { option: 'andersen', label: 'Andersen' },
      { option: 'anderson', label: 'Anderson' },
      { option: 'applegate', label: 'Applegate' },
      { option: 'aram', label: 'Aram' },
      { option: 'aria', label: 'Aria' },
      { option: 'ark_new_era', label: 'Ark New Era' },
      { option: 'arnold', label: 'Arnold' },
      { option: 'art_lutherie', label: 'Art & Lutherie' },
      { option: 'atkin', label: 'Atkin' },
      { option: 'b_c_kingston', label: 'B. C. Kingston' },
      { option: 'bagnasco_casati', label: 'Bagnasco & Casati' },
      { option: 'baranik', label: 'Baranik' },
      { option: 'barzilai', label: 'Barzilai' },
      { option: 'bashkin', label: 'Bashkin' },
      { option: 'batson', label: 'Batson' },
      { option: 'beardsell', label: 'Beardsell' },
      { option: 'beauregard', label: 'Beauregard' },
      { option: 'bedell', label: 'Bedell' },
      { option: 'beneteau', label: 'Beneteau' },
      { option: 'bent_twig', label: 'Bent Twig' },
      { option: 'berkowitz', label: 'Berkowitz' },
      { option: 'bilt', label: 'BilT' },
      { option: 'bills', label: 'Bills' },
      { option: 'blanchard', label: 'Blanchard' },
      { option: 'blueridge', label: 'Blueridge' },
      { option: 'borges', label: 'Borges' },
      { option: 'boucher', label: 'Boucher' },
      { option: 'bourgeois', label: 'Bourgeois' },
      { option: 'bown', label: 'Bown' },
      { option: 'bozo', label: 'Bozo' },
      { option: 'branzell', label: 'Branzell' },
      { option: 'breedlove', label: 'Breedlove' },
      { option: 'bresnan', label: 'Bresnan' },
      { option: 'brondel', label: 'Brondel' },
      { option: 'c_b_guitars', label: 'C. B. Guitars' },
      { option: 'c_fox', label: 'C. Fox' },
      { option: 'caldwell', label: 'Caldwell' },
      { option: 'campanella', label: 'Campanella' },
      { option: 'carruth', label: 'Carruth' },
      { option: 'carvin', label: 'Carvin' },
      { option: 'caton', label: 'Caton' },
      { option: 'charis', label: 'Charis' },
      { option: 'chasson', label: 'Chasson' },
      { option: 'circa', label: 'Circa' },
      { option: 'claxton', label: 'Claxton' },
      { option: 'cole_clark', label: 'Cole Clark' },
      { option: 'collings', label: 'Collings' },
      { option: 'connor', label: 'Connor' },
      { option: 'cort', label: 'Cort' },
      { option: 'crafter', label: 'Crafter' },
      { option: 'crook', label: 'Crook' },
      { option: 'd_ambrosio', label: 'D\'Ambrosio' },
      { option: 'd_angelico', label: 'D\'Angelico' },
      { option: 'd_pergo', label: 'D\'Pergo' },
      { option: 'd_t_guitars', label: 'D. T. Guitars' },
      { option: 'davis_luthiers', label: 'Davis Luthiers' },
      { option: 'ted_davis', label: 'Ted Davis' },
      { option: 'dejonge', label: 'DeJonge' },
      { option: 'delgado', label: 'Delgado' },
      { option: 'detemple', label: 'DeTemple' },
      { option: 'dobro', label: 'Dobro' },
      { option: 'doerr', label: 'Doerr' },
      { option: 'doolin', label: 'Doolin' },
      { option: 'druzhinin', label: 'Druzhinin' },
      { option: 'dudenbostel', label: 'Dudenbostel' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'eichelbaum', label: 'Eichelbaum' },
      { option: 'elysian', label: 'Elysian' },
      { option: 'ensor', label: 'Ensor' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'ernie_ball_music_man', label: 'Ernie Ball Music Man' },
      { option: 'everett', label: 'Everett' },
      { option: 'evergreen_mountain', label: 'Evergreen Mountain' },
      { option: 'fairbanks', label: 'Fairbanks' },
      { option: 'faith', label: 'Faith' },
      { option: 'fay', label: 'Fay' },
      { option: 'fender', label: 'Fender' },
      { option: 'flammang', label: 'Flammang' },
      { option: 'franklin', label: 'Franklin' },
      { option: 'fraulini', label: 'Fraulini' },
      { option: 'froggy_bottom', label: 'Froggy Bottom' },
      { option: 'fujii', label: 'Fujii' },
      { option: 'furch', label: 'Furch' },
      { option: 'fylde', label: 'Fylde' },
      { option: 'g_r_bear', label: 'G. R. Bear' },
      { option: 'gallagher', label: 'Gallagher' },
      { option: 'galloup', label: 'Galloup' },
      { option: 'gerber', label: 'Gerber' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'godin', label: 'Godin' },
      { option: 'goodall', label: 'Goodall' },
      { option: 'graf', label: 'Graf' },
      { option: 'great_lake', label: 'Great Lake' },
      { option: 'greenfield', label: 'Greenfield' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'greven', label: 'Greven' },
      { option: 'grimes', label: 'Grimes' },
      { option: 'guild', label: 'Guild' },
      { option: 'halland', label: 'Halland' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'richard_hebden', label: 'Richard Hebden' },
      { option: 'heindel', label: 'Heindel' },
      { option: 'heinonen', label: 'Heinonen' },
      { option: 'hemken', label: 'Hemken' },
      { option: 'henderson', label: 'Henderson' },
      { option: 'heritage', label: 'Heritage' },
      { option: 'hewett', label: 'Hewett' },
      { option: 'hoffman', label: 'Hoffman' },
      { option: 'hofner', label: 'Höfner' },
      { option: 'c_f_holcomb', label: 'C. F. Holcomb' },
      { option: 'hopkins', label: 'Hopkins' },
      { option: 'house', label: 'House' },
      { option: 'huss_dalton', label: 'Huss & Dalton' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'indian_hill', label: 'Indian Hill' },
      { option: 'ithaca_stringed_instruments', label: 'Ithaca Stringed Instruments' },
      { option: 'isaac_jang', label: 'Isaac Jang' },
      { option: 'james_neligan', label: 'James Neligan' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'william_jeffrey_jones', label: 'William Jeffrey Jones' },
      { option: 'kay', label: 'Kay' },
      { option: 'keller', label: 'Keller' },
      { option: 'kevin_michael', label: 'Kevin Michael' },
      { option: 'keystone', label: 'Keystone' },
      { option: 'dave_king', label: 'Dave King' },
      { option: 'kingslight', label: 'Kingslight' },
      { option: 'kinnaird', label: 'Kinnaird' },
      { option: 'kinscherff', label: 'Kinscherff' },
      { option: 'kiso_klein', label: 'Kiso Klein' },
      { option: 'klein', label: 'Klein' },
      { option: 'klein_kauffman', label: 'Klein Kauffman' },
      { option: 'kostal', label: 'Kostal' },
      { option: 'kragenbrink', label: 'Kragenbrink' },
      { option: 'kraut', label: 'Kraut' },
      { option: 'l_h_t_guitars', label: 'L. H. T. Guitars' },
      { option: 'lakewood', label: 'Lakewood' },
      { option: 'lame_horse', label: 'Lame Horse' },
      { option: 'langejans', label: 'Langejans' },
      { option: 'larrivee', label: 'Larrivée' },
      { option: 'larson_brothers', label: 'Larson Brothers' },
      { option: 'laskin', label: 'Laskin' },
      { option: 'leach', label: 'Leach' },
      { option: 'lichty', label: 'Lichty' },
      { option: 'loar', label: 'Loar' },
      { option: 'lowden', label: 'Lowden' },
      { option: 'lucas', label: 'Lucas' },
      { option: 'luna', label: 'Luna' },
      { option: 'maccubbin', label: 'MacCubbin' },
      { option: 'maingard', label: 'Maingard' },
      { option: 'majestic', label: 'Majestic' },
      { option: 'mannix', label: 'Mannix' },
      { option: 'manzer', label: 'Manzer' },
      { option: 'marchione', label: 'Marchione' },
      { option: 'martin', label: 'Martin' },
      { option: 'maton', label: 'Maton' },
      { option: 'matsuda', label: 'Matsuda' },
      { option: 'mcalister', label: 'McAlister' },
      { option: 'mccollum', label: 'McCollum' },
      { option: 'mcelroy', label: 'McElroy' },
      { option: 'mcgill', label: 'McGill' },
      { option: 'mcilroy', label: 'McIlroy' },
      { option: 'mcknight', label: 'McKnight' },
      { option: 'mcpherson', label: 'McPherson' },
      { option: 'merrill', label: 'Merrill' },
      { option: 'mervyn_davis_guitars', label: 'Mervyn Davis Guitars' },
      { option: 'moon', label: 'Moon' },
      { option: 'moonstone', label: 'Moonstone' },
      { option: 'moreira', label: 'Moreira' },
      { option: 'morgan', label: 'Morgan' },
      { option: 'mossman', label: 'Mossman' },
      { option: 'mustapick', label: 'Mustapick' },
      { option: 'n_k_forster', label: 'N. K. Forster' },
      { option: 'nashville_guitar_co', label: 'Nashville Guitar Co.' },
      { option: 'national', label: 'National' },
      { option: 'new_complexity', label: 'New Complexity' },
      { option: 'new_era_guitars', label: 'New Era Guitars' },
      { option: 'nickerson', label: 'Nickerson' },
      { option: 'noble', label: 'Noble' },
      { option: 'noemi', label: 'Noemi' },
      { option: 'northwood', label: 'Northwood' },
      { option: 'o_c_bear', label: 'O. C. Bear' },
      { option: 'james_olson', label: 'James Olson' },
      { option: 'orangewood', label: 'Orangewood' },
      { option: 'osthoff', label: 'Osthoff' },
      { option: 'ovation', label: 'Ovation' },
      { option: 'pellerin', label: 'Pellerin' },
      { option: 'petros', label: 'Petros' },
      { option: 'phillips_metal_guitars', label: 'Phillips Metal Guitars' },
      { option: 'preston_thompson', label: 'Preston Thompson' },
      { option: 'prs_paul_reed_smith', label: 'PRS (Paul Reed Smith)' },
      { option: 'r_taylor', label: 'R. Taylor' },
      { option: 'radicic', label: 'Radicic' },
      { option: 'rainsong', label: 'RainSong' },
      { option: 'rasmussen', label: 'Rasmussen' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'tim_reede', label: 'Tim Reede' },
      { option: 'regal', label: 'Regal' },
      { option: 'david_antony_reid', label: 'David Antony Reid' },
      { option: 'rein', label: 'Rein' },
      { option: 'rich_taylor', label: 'Rich & Taylor' },
      { option: 'rickenbacker', label: 'Rickenbacker' },
      { option: 'rothel', label: 'Rothel' },
      { option: 'roy_rogers', label: 'Roy Rogers' },
      { option: 'rs_muth', label: 'RS Muth' },
      { option: 'running_dog', label: 'Running Dog' },
      { option: 'kevin_ryan', label: 'Kevin Ryan' },
      { option: 'sand', label: 'Sand' },
      { option: 'tom_sands', label: 'Tom Sands' },
      { option: 'santa_cruz', label: 'Santa Cruz' },
      { option: 'schenk', label: 'Schenk' },
      { option: 'schoenberg', label: 'Schoenberg' },
      { option: 'schwartz', label: 'Schwartz' },
      { option: 'scott', label: 'Scott' },
      { option: 'seagull', label: 'Seagull' },
      { option: 'sec', label: 'SeC' },
      { option: 'sedgwick', label: 'Sedgwick' },
      { option: 'serracini', label: 'Serracini' },
      { option: 'sexauer', label: 'Sexauer' },
      { option: 'shanti', label: 'Shanti' },
      { option: 'sharp', label: 'Sharp' },
      { option: 'sheeran_by_lowden', label: 'Sheeran by Lowden' },
      { option: 'sheppard', label: 'Sheppard' },
      { option: 'sigma', label: 'Sigma' },
      { option: 'simon_patrick', label: 'Simon & Patrick' },
      { option: 'simpson', label: 'Simpson' },
      { option: 'skytop', label: 'Skytop' },
      { option: 'sobell', label: 'Sobell' },
      { option: 'somogyi', label: 'Somogyi' },
      { option: 'square_deal', label: 'Square Deal' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'stehr', label: 'Stehr' },
      { option: 'stella', label: 'Stella' },
      { option: 'stetson', label: 'Stetson' },
      { option: 'strahm', label: 'Strahm' },
      { option: 'striebel', label: 'Striebel' },
      { option: 'tacoma', label: 'Tacoma' },
      { option: 'takamine', label: 'Takamine' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'taylor', label: 'Taylor' },
      { option: 'teton', label: 'Teton' },
      { option: 'thames', label: 'Thames' },
      { option: 'thorell', label: 'Thorell' },
      { option: 'threet', label: 'Threet' },
      { option: 'tippin', label: 'Tippin' },
      { option: 'traphagen', label: 'Traphagen' },
      { option: 'traugott', label: 'Traugott' },
      { option: 'treehouse', label: 'TreeHouse' },
      { option: 'triggs', label: 'Triggs' },
      { option: 'pete_turner', label: 'Pete Turner' },
      { option: 'turnstone', label: 'Turnstone' },
      { option: 'urlacher', label: 'Urlacher' },
      { option: 'vega', label: 'Vega' },
      { option: 'veillette', label: 'Veillette' },
      { option: 'vines', label: 'Vines' },
      { option: 'visesnut', label: 'Visesnut' },
      { option: 'john_walker', label: 'John Walker' },
      { option: 'kim_walker', label: 'Kim Walker' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'waterloo_collings', label: 'Waterloo (Collings)' },
      { option: 'webber', label: 'Webber' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'white', label: 'White' },
      { option: 'peggy_white', label: 'Peggy White' },
      { option: 'wilborn', label: 'Wilborn' },
      { option: 'williams', label: 'Williams' },
      { option: 'wingert', label: 'Wingert' },
      { option: 'woolson', label: 'Woolson' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'yamamoto', label: 'Yamamoto' },
      { option: 'yong', label: 'Yong' },
      { option: 'zimnicki', label: 'Zimnicki' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_acoustic_steel_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'guitar_acoustic_steel_string_guitar_shape',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'dreadnought', label: 'Dreadnought' },
      { option: 'slope_shoulder_dreadnought', label: 'Slope-Shoulder Dreadnought' },
      { option: 'concert_0_00', label: 'Concert (0/00)' },
      { option: 'grand_concert', label: 'Grand Concert' },
      { option: 'auditorium_000', label: 'Auditorium (000)' },
      { option: 'orchestra_model_om', label: 'Orchestra Model (OM)' },
      { option: 'grand_auditorium', label: 'Grand Auditorium' },
      { option: 'jumbo', label: 'Jumbo' },
      { option: 'parlor', label: 'Parlor' },
      { option: 'mini_travel', label: 'Mini/Travel' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_acoustic_steel_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Shape',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Shape',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Shape',
      isRequired: false,
    },
  },
  {
    key: 'guitar_acoustic_steel_string_guitar_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'sitka_spruce', label: 'Sitka Spruce' },
      { option: 'adirondack_red_spruce', label: 'Adirondack (Red) Spruce' },
      { option: 'engelmann_spruce', label: 'Engelmann Spruce' },
      { option: 'european_spruce', label: 'European Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'koa', label: 'Koa' },
      { option: 'maple', label: 'Maple' },
      { option: 'redwood', label: 'Redwood' },
      { option: 'laminate_hpl', label: 'Laminate/HPL' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_acoustic_steel_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'guitar_acoustic_steel_string_guitar_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'indian_rosewood', label: 'Indian Rosewood' },
      { option: 'brazilian_rosewood', label: 'Brazilian Rosewood' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'maple', label: 'Maple' },
      { option: 'koa', label: 'Koa' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'ovangkol', label: 'Ovangkol' },
      { option: 'laminate_hpl', label: 'Laminate / HPL' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_acoustic_steel_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'guitar_acoustic_steel_string_guitar_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'no_cutaway', label: 'No Cutaway' },
      { option: 'cutaway', label: 'Cutaway' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_acoustic_steel_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'guitar_classical_nylon_string_guitar_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'manuel_adalid', label: 'Manuel Adalid' },
      { option: 'admira', label: 'Admira' },
      { option: 'alhambra', label: 'Alhambra' },
      { option: 'alvarez', label: 'Alvarez' },
      { option: 'aria_arai', label: 'Aria (Arai)' },
      { option: 'vicente_arias', label: 'Vicente Arias' },
      { option: 'asturias', label: 'Asturias' },
      { option: 'marcelo_barbero', label: 'Marcelo Barbero' },
      { option: 'paulino_bernabe', label: 'Paulino Bernabé' },
      { option: 'robert_bouchet', label: 'Robert Bouchet' },
      { option: 'amalio_burguet', label: 'Amalio Burguet' },
      { option: 'bastien_burlot', label: 'Bastien Burlot' },
      { option: 'cyndy_burton', label: 'Cyndy Burton' },
      { option: 'gregory_byers', label: 'Gregory Byers' },
      { option: 'camps', label: 'Camps' },
      { option: 'cervantes', label: 'Cervantes' },
      { option: 'daniele_chiesa', label: 'Daniele Chiesa' },
      { option: 'conde_hermanos_conde_de_paz', label: 'Conde Hermanos (Conde de Paz)' },
      { option: 'stephan_connor', label: 'Stephan Connor' },
      { option: 'manuel_contreras', label: 'Manuel Contreras' },
      { option: 'cordoba', label: 'Cordoba' },
      { option: 'matthias_dammann', label: 'Matthias Dammann' },
      { option: 'sergei_de_jonge', label: 'Sergei de Jonge' },
      { option: 'antonio_de_torres', label: 'Antonio de Torres' },
      { option: 'lester_devoe', label: 'Lester DeVoe' },
      { option: 'marcus_dominelli', label: 'Marcus Dominelli' },
      { option: 'stephen_eden', label: 'Stephen Eden' },
      { option: 'jeffrey_elliott', label: 'Jeffrey Elliott' },
      { option: 'domingo_esteso', label: 'Domingo Esteso' },
      { option: 'esteve', label: 'Esteve' },
      { option: 'fender', label: 'Fender' },
      { option: 'dominique_field', label: 'Dominique Field' },
      { option: 'paul_fischer', label: 'Paul Fischer' },
      { option: 'ignacio_fleta_sons', label: 'Ignacio Fleta & Sons' },
      { option: 'thomas_fredholm', label: 'Thomas Fredholm' },
      { option: 'enrique_garcia', label: 'Enrique García' },
      { option: 'giannini', label: 'Giannini' },
      { option: 'godin', label: 'Godin' },
      { option: 'guild', label: 'Guild' },
      { option: 'hermann_hauser_i_ii_iii', label: 'Hermann Hauser (I, II, III)' },
      { option: 'juan_hernandez', label: 'Juan Hernández' },
      { option: 'santos_hernandez', label: 'Santos Hernández' },
      { option: 'kenny_hill_hill_guitar_company', label: 'Kenny Hill (Hill Guitar Company)' },
      { option: 'hirade', label: 'Hirade' },
      { option: 'hofner', label: 'Höfner' },
      { option: 'dieter_hopf', label: 'Dieter Hopf' },
      { option: 'thomas_humphrey', label: 'Thomas Humphrey' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'k_yairi_alvarez_yairi', label: 'K. Yairi (Alvarez Yairi)' },
      { option: 'kodaira', label: 'Kodaira' },
      { option: 'kohno', label: 'Kohno' },
      { option: 'kremona', label: 'Kremona' },
      { option: 'la_patrie', label: 'La Patrie' },
      { option: 'angel_lopez', label: 'Ángel López' },
      { option: 'antonio_marin_montero', label: 'Antonio Marín Montero' },
      { option: 'martin', label: 'Martin' },
      { option: 'simon_marty', label: 'Simon Marty' },
      { option: 'ryoji_matsuoka', label: 'Ryoji Matsuoka' },
      { option: 'fritz_mueller', label: 'Fritz Mueller' },
      { option: 'ortega', label: 'Ortega' },
      { option: 'daryl_perry', label: 'Daryl Perry' },
      { option: 'john_price', label: 'John Price' },
      { option: 'raimundo', label: 'Raimundo' },
      { option: 'jose_ramirez', label: 'José Ramírez' },
      { option: 'jim_redgate', label: 'Jim Redgate' },
      { option: 'manuel_rodriguez', label: 'Manuel Rodríguez' },
      { option: 'jose_romanillos', label: 'José Romanillos' },
      { option: 'jean_rompre', label: 'Jean Rompré' },
      { option: 'robert_ruck', label: 'Robert Ruck' },
      { option: 'prudencio_saez', label: 'Prudencio Sáez' },
      { option: 'eric_sahlin', label: 'Eric Sahlin' },
      { option: 'masaki_sakurai', label: 'Masaki Sakurai' },
      { option: 'antonio_sanchez', label: 'Antonio Sánchez' },
      { option: 'douglass_scott', label: 'Douglass Scott' },
      { option: 'greg_smallman_sons', label: 'Greg Smallman & Sons' },
      { option: 'gary_southwell', label: 'Gary Southwell' },
      { option: 'takamine', label: 'Takamine' },
      { option: 'taylor', label: 'Taylor' },
      { option: 'manuel_velazquez', label: 'Manuel Velázquez' },
      { option: 'otto_vowinkel', label: 'Otto Vowinkel' },
      { option: 'gernot_wagner', label: 'Gernot Wagner' },
      { option: 'jason_wolverton', label: 'Jason Wolverton' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_classical_nylon_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'guitar_classical_nylon_string_guitar_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4_4_full_size', label: '4/4 (Full Size)' },
      { option: '7_8', label: '7/8' },
      { option: '3_4', label: '3/4' },
      { option: '1_2', label: '1/2' },
      { option: '1_4', label: '1/4' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_classical_nylon_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Size',
      isRequired: false,
    },
  },
  {
    key: 'guitar_classical_nylon_string_guitar_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cedar', label: 'Cedar' },
      { option: 'spruce', label: 'Spruce' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_classical_nylon_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top Wood',
      isRequired: false,
    },
  },
  {
    key: 'guitar_classical_nylon_string_guitar_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'indian_rosewood', label: 'Indian Rosewood' },
      { option: 'brazilian_rosewood', label: 'Brazilian Rosewood' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'cypress', label: 'Cypress' },
      { option: 'maple', label: 'Maple' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'ziricote', label: 'Ziricote' },
      { option: 'pau_ferro', label: 'Pau Ferro' },
      { option: 'koa', label: 'Koa' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_classical_nylon_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'guitar_classical_nylon_string_guitar_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'no_cutaway', label: 'No Cutaway' },
      { option: 'cutaway', label: 'Cutaway' },
      { option: 'thinline_crossover', label: 'Thinline / Crossover' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_classical_nylon_string_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'guitar_resonator_guitar_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'amistar', label: 'Amistar' },
      { option: 'appalachian', label: 'Appalachian' },
      { option: 'beard', label: 'Beard' },
      { option: 'clinesmith', label: 'Clinesmith' },
      { option: 'copley', label: 'Copley' },
      { option: 'crafters_of_tennessee', label: 'Crafters of Tennessee' },
      { option: 'danelectro', label: 'Danelectro' },
      { option: 'dean', label: 'Dean' },
      { option: 'dobro', label: 'Dobro' },
      { option: 'donmo', label: 'Donmo' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'fender', label: 'Fender' },
      { option: 'fine_resophonic', label: 'Fine Resophonic' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'harley_benton', label: 'Harley Benton' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'meredith', label: 'Meredith' },
      { option: 'michael_messer', label: 'Michael Messer' },
      { option: 'morgan_monroe', label: 'Morgan Monroe' },
      { option: 'mule_resophonic', label: 'Mule Resophonic' },
      { option: 'national_vintage_pre_war', label: 'National (Vintage/Pre-War)' },
      { option: 'national_reso_phonic', label: 'National Reso-Phonic' },
      { option: 'ortega', label: 'Ortega' },
      { option: 'rayco_resophonics', label: 'Rayco Resophonics' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'redline', label: 'Redline' },
      { option: 'regal', label: 'Regal' },
      { option: 'republic', label: 'Republic' },
      { option: 'rogue', label: 'Rogue' },
      { option: 'royall', label: 'Royall' },
      { option: 's_b_macdonald', label: 'S.B. MacDonald' },
      { option: 'scheerhorn', label: 'Scheerhorn' },
      { option: 'schoonover', label: 'Schoonover' },
      { option: 'terraplane', label: 'Terraplane' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weber', label: 'Weber' },
      { option: 'wechter', label: 'Wechter' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_resonator_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'guitar_resonator_guitar_cone',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'biscuit_bridge_single_cone', label: 'Biscuit Bridge (Single Cone)' },
      { option: 'spider_bridge_single_cone', label: 'Spider Bridge (Single Cone)' },
      { option: 'tricone', label: 'Tricone' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_resonator_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Cone / Bridge Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Cone / Bridge Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Cone / Bridge Type',
      isRequired: false,
    },
  },
  {
    key: 'guitar_resonator_guitar_neck',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'round_neck', label: 'Round Neck' },
      { option: 'square_neck', label: 'Square Neck' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_resonator_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Neck Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Neck Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Neck Type',
      isRequired: false,
    },
  },
  {
    key: 'guitar_resonator_guitar_body_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'brass', label: 'Brass' },
      { option: 'steel', label: 'Steel' },
      { option: 'nickel_silver_german_silver', label: 'Nickel Silver / German Silver' },
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'other_wood', label: 'Other Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_resonator_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Material',
      isRequired: false,
    },
  },
  {
    key: 'guitar_tenor_guitar_body_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'flat_top_acoustic', label: 'Flat Top Acoustic' },
      { option: 'archtop_acoustic', label: 'Archtop Acoustic' },
      { option: 'resonator', label: 'Resonator' },
      { option: 'hollow_semi_hollow_body_electric', label: 'Hollow / Semi-Hollow Body Electric' },
      { option: 'solid_body_electric', label: 'Solid Body Electric' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_tenor_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Type',
      isRequired: false,
    },
  },
  {
    key: 'guitar_tenor_guitar_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'amistar', label: 'Amistar' },
      { option: 'aria', label: 'Aria' },
      { option: 'arrow_guitars', label: 'Arrow Guitars' },
      { option: 'ashbury', label: 'Ashbury' },
      { option: 'atkin_guitars', label: 'Atkin Guitars' },
      { option: 'beeton', label: 'Beeton' },
      { option: 'benedetto', label: 'Benedetto' },
      { option: 'biltmore', label: 'Biltmore' },
      { option: 'blueridge', label: 'Blueridge' },
      { option: 'bowerman_guitars', label: 'Bowerman Guitars' },
      { option: 'breedlove', label: 'Breedlove' },
      { option: 'c_f_martin', label: 'C.F. Martin' },
      { option: 'canora', label: 'Canora' },
      { option: 'collings', label: 'Collings' },
      { option: 'craven_tenor_guitars', label: 'Craven Tenor Guitars' },
      { option: 'd_angelico', label: 'D\'Angelico' },
      { option: 'darkstar_guitars', label: 'DarkStar Guitars' },
      { option: 'd_rickert', label: 'D. Rickert' },
      { option: 'dobro', label: 'Dobro' },
      { option: 'earnest_instruments', label: 'Earnest Instruments' },
      { option: 'eastwood', label: 'Eastwood' },
      { option: 'egan_custom_guitars', label: 'Egan Custom Guitars' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'evergreen_mountain_instruments', label: 'Evergreen Mountain Instruments' },
      { option: 'fender', label: 'Fender' },
      { option: 'fletcher_tenor_guitars', label: 'Fletcher Tenor Guitars' },
      { option: 'fylde', label: 'Fylde' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'guild', label: 'Guild' },
      { option: 'harley_benton', label: 'Harley Benton' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'kala', label: 'Kala' },
      { option: 'kay', label: 'Kay' },
      { option: 'kent', label: 'Kent' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'moon_guitars', label: 'Moon Guitars' },
      { option: 'national', label: 'National' },
      { option: 'nk_forster', label: 'NK Forster' },
      { option: 'norma', label: 'Norma' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'paramount', label: 'Paramount' },
      { option: 'prs', label: 'PRS' },
      { option: 'regal', label: 'Regal' },
      { option: 'republic', label: 'Republic' },
      { option: 'rickenbacker', label: 'Rickenbacker' },
      { option: 'selmer', label: 'Selmer' },
      { option: 'slingerland', label: 'Slingerland' },
      { option: 'soares_y_guitars', label: 'Soares\'y Guitars' },
      { option: 'stella', label: 'Stella' },
      { option: 'thomann', label: 'Thomann' },
      { option: 'vega', label: 'Vega' },
      { option: 'webber_guitars', label: 'Webber Guitars' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_tenor_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'guitar_tenor_guitar_scale_length',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'short_scale_under_22', label: 'Short Scale (Under 22")' },
      { option: 'standard_22_23', label: 'Standard (22"–23")' },
      { option: 'long_scale_over_23', label: 'Long Scale (Over 23")' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_tenor_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Length',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Length',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Length',
      isRequired: false,
    },
  },
  {
    key: 'guitar_tenor_guitar_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'koa', label: 'Koa' },
      { option: 'metal', label: 'Metal' },
      { option: 'laminate_plywood', label: 'Laminate / Plywood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_tenor_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'guitar_tenor_guitar_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'maple', label: 'Maple' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'koa', label: 'Koa' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'metal', label: 'Metal' },
      { option: 'laminate_plywood', label: 'Laminate / Plywood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_tenor_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'guitar_archtop_guitar_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'american_archtop', label: 'American Archtop' },
      { option: 'andersen', label: 'Andersen' },
      { option: 'aria', label: 'Aria' },
      { option: 'benedetto', label: 'Benedetto' },
      { option: 'buscarino', label: 'Buscarino' },
      { option: 'campellone', label: 'Campellone' },
      { option: 'collings', label: 'Collings' },
      { option: 'comins', label: 'Comins' },
      { option: 'd_angelico', label: 'D\'Angelico' },
      { option: 'd_aquisto', label: 'D\'Aquisto' },
      { option: 'decava', label: 'DeCava' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'framus', label: 'Framus' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'godin', label: 'Godin' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'grimes', label: 'Grimes' },
      { option: 'guild', label: 'Guild' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'heritage', label: 'Heritage' },
      { option: 'hofner', label: 'Höfner' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'kay', label: 'Kay' },
      { option: 'the_loar', label: 'The Loar' },
      { option: 'manzer', label: 'Manzer' },
      { option: 'marchione', label: 'Marchione' },
      { option: 'martin', label: 'Martin' },
      { option: 'megas', label: 'Megas' },
      { option: 'mirabella', label: 'Mirabella' },
      { option: 'moll', label: 'Moll' },
      { option: 'monteleone', label: 'Monteleone' },
      { option: 'national', label: 'National' },
      { option: 'peerless', label: 'Peerless' },
      { option: 'prs', label: 'PRS' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'regal', label: 'Regal' },
      { option: 'ribbecke', label: 'Ribbecke' },
      { option: 'sadowsky', label: 'Sadowsky' },
      { option: 'scharpach', label: 'Scharpach' },
      { option: 'silvertone', label: 'Silvertone' },
      { option: 'solomon', label: 'Solomon' },
      { option: 'stromberg', label: 'Stromberg' },
      { option: 'trenier', label: 'Trenier' },
      { option: 'triggs', label: 'Triggs' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_archtop_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'guitar_archtop_guitar_body_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'full_depth_hollow', label: 'Full-Depth Hollow' },
      { option: 'thinline_hollow', label: 'Thinline Hollow' },
      { option: 'semi_hollow', label: 'Semi-Hollow' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_archtop_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Type',
      isRequired: false,
    },
  },
  {
    key: 'guitar_archtop_guitar_top_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'carved_solid', label: 'Carved Solid' },
      { option: 'pressed_solid', label: 'Pressed Solid' },
      { option: 'laminate', label: 'Laminate' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_archtop_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top Construction',
      isRequired: false,
    },
  },
  {
    key: 'guitar_archtop_guitar_body_width',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '15_or_under', label: '15" or Under' },
      { option: '16', label: '16"' },
      { option: '17', label: '17"' },
      { option: '18_or_over', label: '18" or Over' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_archtop_guitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Width',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Width',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Width',
      isRequired: false,
    },
  },
  {
    key: 'guitar_lap_steel_weissenborn_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'airline', label: 'Airline' },
      { option: 'sachar_amos', label: 'Sachar Amos' },
      { option: 'anderwood', label: 'Anderwood' },
      { option: 'asher', label: 'Asher' },
      { option: 'audiovox', label: 'Audiovox' },
      { option: 'bales', label: 'Bales' },
      { option: 'bear_creek', label: 'Bear Creek' },
      { option: 'bediaz', label: 'Bediaz' },
      { option: 'bluestem', label: 'Bluestem' },
      { option: 'bowerman', label: 'Bowerman' },
      { option: 'breedlove', label: 'Breedlove' },
      { option: 'bronson', label: 'Bronson' },
      { option: 'paddy_burgin', label: 'Paddy Burgin' },
      { option: 'canopus', label: 'Canopus' },
      { option: 'carvin', label: 'Carvin' },
      { option: 'celtic_cross', label: 'Celtic Cross' },
      { option: 'chandler', label: 'Chandler' },
      { option: 'clearwater', label: 'Clearwater' },
      { option: 'clinesmith', label: 'Clinesmith' },
      { option: 'cole_clark', label: 'Cole Clark' },
      { option: 'cruztone', label: 'CruzTone' },
      { option: 'dean', label: 'Dean' },
      { option: 'dickerson', label: 'Dickerson' },
      { option: 'dobro', label: 'Dobro' },
      { option: 'duesenberg', label: 'Duesenberg' },
      { option: 'michael_dunn', label: 'Michael Dunn' },
      { option: 'dyson', label: 'Dyson' },
      { option: 'eastwood', label: 'Eastwood' },
      { option: 'electromuse', label: 'Electromuse' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'fender', label: 'Fender' },
      { option: 'fouke', label: 'Fouke' },
      { option: 'tony_francis', label: 'Tony Francis' },
      { option: 'fuzzy', label: 'Fuzzy' },
      { option: 'georgeboards', label: 'GeorgeBoards' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gosden', label: 'Gosden' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'guyatone', label: 'Guyatone' },
      { option: 'harmos', label: 'Harmos' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'herrmann', label: 'Herrmann' },
      { option: 'iseman', label: 'Iseman' },
      { option: 'kay', label: 'Kay' },
      { option: 'kiesel', label: 'Kiesel' },
      { option: 'tim_kill', label: 'Tim Kill' },
      { option: 'chris_knutsen', label: 'Chris Knutsen' },
      { option: 'kona', label: 'Kona' },
      { option: 'lap_king', label: 'Lap King' },
      { option: 'lazy_river', label: 'Lazy River' },
      { option: 'magnatone', label: 'Magnatone' },
      { option: 'melbert', label: 'Melbert' },
      { option: 'melobar', label: 'Melobar' },
      { option: 'joe_morrell', label: 'Joe Morrell' },
      { option: 'national', label: 'National' },
      { option: 'oahu', label: 'Oahu' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'peavey', label: 'Peavey' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'regal', label: 'Regal' },
      { option: 'remington_steel', label: 'Remington Steel' },
      { option: 'republic', label: 'Republic' },
      { option: 'rickenbacker', label: 'Rickenbacker' },
      { option: 'rogue', label: 'Rogue' },
      { option: 'sierra', label: 'Sierra' },
      { option: 'silvertone', label: 'Silvertone' },
      { option: 'squier', label: 'Squier' },
      { option: 'superior', label: 'Superior' },
      { option: 'supro', label: 'Supro' },
      { option: 'sx', label: 'SX' },
      { option: 'teisco', label: 'Teisco' },
      { option: 'twisted_wood', label: 'Twisted Wood' },
      { option: 'tyyster', label: 'Tyyster' },
      { option: 'valco', label: 'Valco' },
      { option: 'vega', label: 'Vega' },
      { option: 'vorson', label: 'Vorson' },
      { option: 'hermann_weissenborn', label: 'Hermann Weissenborn' },
      { option: 'west_coast_steel', label: 'West Coast Steel' },
      { option: 'richard_wilson', label: 'Richard Wilson' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'yanuziello', label: 'Yanuziello' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_lap_steel_weissenborn',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'guitar_lap_steel_weissenborn_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'electric_solid_body', label: 'Electric Solid Body' },
      { option: 'electric_semi_hollow', label: 'Electric Semi-Hollow' },
      { option: 'acoustic_weissenborn_style', label: 'Acoustic (Weissenborn-Style)' },
      { option: 'acoustic_electric', label: 'Acoustic-Electric' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_lap_steel_weissenborn',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'guitar_lap_steel_weissenborn_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_lap_steel_weissenborn',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'guitar_lap_steel_weissenborn_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'koa', label: 'Koa' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'maple', label: 'Maple' },
      { option: 'blackwood', label: 'Blackwood' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'ash', label: 'Ash' },
      { option: 'alder', label: 'Alder' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'bakelite_composite', label: 'Bakelite / Composite' },
      { option: 'metal', label: 'Metal' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_lap_steel_weissenborn',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top Wood',
      isRequired: false,
    },
  },
  {
    key: 'guitar_lap_steel_weissenborn_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'koa', label: 'Koa' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'blackwood', label: 'Blackwood' },
      { option: 'maple', label: 'Maple' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'ash', label: 'Ash' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'spruce', label: 'Spruce' },
      { option: 'alder', label: 'Alder' },
      { option: 'not_applicable', label: 'Not Applicable' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'guitar_lap_steel_weissenborn',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Side Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Side Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Side Wood',
      isRequired: false,
    },
  },
  {
    key: 'banjo_5_string_banjo_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'alvarez', label: 'Alvarez' },
      { option: 'aria', label: 'Aria' },
      { option: 'bacon_day', label: 'Bacon & Day' },
      { option: 'baldwin', label: 'Baldwin' },
      { option: 'bishline', label: 'Bishline' },
      { option: 'blackberry', label: 'Blackberry' },
      { option: 'noel_booth', label: 'Noel Booth' },
      { option: 'brooks', label: 'Brooks' },
      { option: 'buckeye', label: 'Buckeye' },
      { option: 'cedar_mountain', label: 'Cedar Mountain' },
      { option: 'clareen', label: 'Clareen' },
      { option: 'noah_cline', label: 'Noah Cline' },
      { option: 'cloverlick', label: 'Cloverlick' },
      { option: 'cox', label: 'Cox' },
      { option: 'crafters_of_tennessee', label: 'Crafters of Tennessee' },
      { option: 'davis', label: 'Davis' },
      { option: 'deering', label: 'Deering' },
      { option: 'enoch', label: 'Enoch' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'fairbanks', label: 'Fairbanks' },
      { option: 'fender', label: 'Fender' },
      { option: 'flinthill', label: 'Flinthill' },
      { option: 'franzke', label: 'Franzke' },
      { option: 'george', label: 'George' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_star', label: 'Gold Star' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'grundy', label: 'Grundy' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'hatfield', label: 'Hatfield' },
      { option: 'heartland', label: 'Heartland' },
      { option: 'hendricks', label: 'Hendricks' },
      { option: 'd_p_hopkins_louzee', label: 'D.P. Hopkins (Louzee)' },
      { option: 'huber', label: 'Huber' },
      { option: 'huss_dalton', label: 'Huss & Dalton' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'iida', label: 'Iida' },
      { option: 'kay', label: 'Kay' },
      { option: 'kel_kroydon', label: 'Kel Kroydon' },
      { option: 'chuck_lee', label: 'Chuck Lee' },
      { option: 'levan', label: 'LeVan' },
      { option: 'ludwig', label: 'Ludwig' },
      { option: 'morgan_monroe', label: 'Morgan Monroe' },
      { option: 'neat', label: 'Neat' },
      { option: 'nechville', label: 'Nechville' },
      { option: 'ode', label: 'ODE' },
      { option: 'ome', label: 'Ome' },
      { option: 'orpheum', label: 'Orpheum' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'paramount', label: 'Paramount' },
      { option: 'pisgah', label: 'Pisgah' },
      { option: 'prucha', label: 'Prucha' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'bart_reiter', label: 'Bart Reiter' },
      { option: 'rich_taylor', label: 'Rich & Taylor' },
      { option: 'rickard', label: 'Rickard' },
      { option: 'rogue', label: 'Rogue' },
      { option: 'romero', label: 'Romero' },
      { option: 'saga', label: 'Saga' },
      { option: 'samick', label: 'Samick' },
      { option: 'savannah', label: 'Savannah' },
      { option: 'slingerland', label: 'Slingerland' },
      { option: 'sloan', label: 'Sloan' },
      { option: 'dale_small', label: 'Dale Small' },
      { option: 's_s_stewart', label: 'S.S. Stewart' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'stealth', label: 'Stealth' },
      { option: 'stelling', label: 'Stelling' },
      { option: 'stone', label: 'Stone' },
      { option: 'sullivan', label: 'Sullivan' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'doug_unger', label: 'Doug Unger' },
      { option: 'vance', label: 'Vance' },
      { option: 'vega', label: 'Vega' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'wildwood', label: 'Wildwood' },
      { option: 'yates', label: 'Yates' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_5_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'banjo_5_string_banjo_back_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'resonator_closed_back', label: 'Resonator (Closed Back)' },
      { option: 'open_back', label: 'Open Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_5_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_5_string_banjo_tone_ring',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'none_wood_rim_only', label: 'None (Wood Rim Only)' },
      { option: 'flathead', label: 'Flathead' },
      { option: 'archtop', label: 'Archtop' },
      { option: 'tubaphone', label: 'Tubaphone' },
      { option: 'whyte_laydie', label: 'Whyte Laydie' },
      { option: 'dobson', label: 'Dobson' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_5_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tone Ring Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tone Ring Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tone Ring Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_5_string_banjo_rim_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '11', label: '11"' },
      { option: '12', label: '12"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_5_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Rim Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Rim Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Rim Size',
      isRequired: false,
    },
  },
  {
    key: 'banjo_5_string_banjo_neck_wood',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_5_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Neck Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Neck Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Neck Wood',
      isRequired: false,
    },
  },
  {
    key: 'banjo_tenor_banjo_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'als_banjos', label: 'ALS Banjos' },
      { option: 'aria', label: 'Aria' },
      { option: 'bacon_day', label: 'Bacon & Day' },
      { option: 'david_boyle', label: 'David Boyle' },
      { option: 'clareen', label: 'Clareen' },
      { option: 'clifford_essex', label: 'Clifford Essex' },
      { option: 'tom_cussen', label: 'Tom Cussen' },
      { option: 'deering', label: 'Deering' },
      { option: 'emerald', label: 'Emerald' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'framus', label: 'Framus' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'kay', label: 'Kay' },
      { option: 'levin', label: 'Levin' },
      { option: 'ludwig', label: 'Ludwig' },
      { option: 'maybell', label: 'Maybell' },
      { option: 'mcneela_paraic_mcneela', label: 'McNeela (Paraic McNeela)' },
      { option: 'nechville', label: 'Nechville' },
      { option: 'oakwood', label: 'Oakwood' },
      { option: 'ome', label: 'Ome' },
      { option: 'orpheum', label: 'Orpheum' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'paramount', label: 'Paramount' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'regal', label: 'Regal' },
      { option: 'richelieu', label: 'Richelieu' },
      { option: 'rioga', label: 'Rioga' },
      { option: 'rover', label: 'Rover' },
      { option: 's_s_stewart', label: 'S.S. Stewart' },
      { option: 'savannah', label: 'Savannah' },
      { option: 'slingerland', label: 'Slingerland' },
      { option: 'sully_sullivan', label: 'Sully (Sullivan)' },
      { option: 'vega', label: 'Vega' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_tenor_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'banjo_tenor_banjo_frets',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '17_fret', label: '17-Fret' },
      { option: '19_fret', label: '19-Fret' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_tenor_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Frets',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Frets',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Frets',
      isRequired: false,
    },
  },
  {
    key: 'banjo_tenor_banjo_back_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'resonator', label: 'Resonator' },
      { option: 'open_back', label: 'Open Back' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_tenor_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_tenor_banjo_tone_ring',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'archtop', label: 'Archtop' },
      { option: 'flathead', label: 'Flathead' },
      { option: 'tubaphone', label: 'Tubaphone' },
      { option: 'none_no_tone_ring', label: 'None (No Tone Ring)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_tenor_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tone Ring Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tone Ring Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tone Ring Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_tenor_banjo_pot_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '11', label: '11"' },
      { option: '12', label: '12"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_tenor_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Pot Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Pot Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Pot Diameter',
      isRequired: false,
    },
  },
  {
    key: 'banjo_plectrum_banjo_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a_c_fairbanks', label: 'A.C. Fairbanks' },
      { option: 'bacon_day', label: 'Bacon & Day' },
      { option: 'bishline', label: 'Bishline' },
      { option: 'blackberry', label: 'Blackberry' },
      { option: 'noel_booth', label: 'Noel Booth' },
      { option: 'dave_boyle', label: 'Dave Boyle' },
      { option: 'nate_calkins', label: 'Nate Calkins' },
      { option: 'clareen_tom_cussen', label: 'Clareen (Tom Cussen)' },
      { option: 'noah_cline', label: 'Noah Cline' },
      { option: 'deering', label: 'Deering' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'fairchild', label: 'Fairchild' },
      { option: 'fender', label: 'Fender' },
      { option: 'franzke', label: 'Franzke' },
      { option: 'tommy_george_george_banjos', label: 'Tommy George (George Banjos)' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'hopkins', label: 'Hopkins' },
      { option: 'zachary_hoyt', label: 'Zachary Hoyt' },
      { option: 'kay', label: 'Kay' },
      { option: 'kel_kroydon', label: 'Kel Kroydon' },
      { option: 'chuck_lee', label: 'Chuck Lee' },
      { option: 'leedy', label: 'Leedy' },
      { option: 'ludwig', label: 'Ludwig' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'brooks_masten', label: 'Brooks Masten' },
      { option: 'nechville', label: 'Nechville' },
      { option: 'ode', label: 'ODE' },
      { option: 'ome', label: 'Ome' },
      { option: 'orpheum', label: 'Orpheum' },
      { option: 'paramount', label: 'Paramount' },
      { option: 'prucha', label: 'Prucha' },
      { option: 'ramsey', label: 'Ramsey' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'rettburg_lange', label: 'Rettburg & Lange' },
      { option: 'richelieu', label: 'Richelieu' },
      { option: 'rickard', label: 'Rickard' },
      { option: 'jason_romero_j_romero_banjos', label: 'Jason Romero (J. Romero Banjos)' },
      { option: 'slingerland', label: 'Slingerland' },
      { option: 'dale_small', label: 'Dale Small' },
      { option: 's_s_stewart', label: 'S.S. Stewart' },
      { option: 'stelling', label: 'Stelling' },
      { option: 'sullivan', label: 'Sullivan' },
      { option: 'doug_unger', label: 'Doug Unger' },
      { option: 'vega', label: 'Vega' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'wildwood', label: 'Wildwood' },
      { option: 'yates', label: 'Yates' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_plectrum_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'banjo_plectrum_banjo_back_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'resonator', label: 'Resonator' },
      { option: 'open_back', label: 'Open Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_plectrum_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_plectrum_banjo_tone_ring',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'tubaphone', label: 'Tubaphone' },
      { option: 'flathead_cast_bronze', label: 'Flathead (Cast Bronze)' },
      { option: 'archtop', label: 'Archtop' },
      { option: 'rolled_brass', label: 'Rolled Brass' },
      { option: 'none_wood_rim_only', label: 'None (Wood Rim Only)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_plectrum_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tone Ring',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tone Ring',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tone Ring',
      isRequired: false,
    },
  },
  {
    key: 'banjo_plectrum_banjo_neck_wood',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_plectrum_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Neck Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Neck Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Neck Wood',
      isRequired: false,
    },
  },
  {
    key: 'banjo_banjo_ukulele_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'abbott_j_g_abbott_co', label: 'Abbott (J.G. Abbott & Co.)' },
      { option: 'aklot', label: 'Aklot' },
      { option: 'bacon_day', label: 'Bacon & Day' },
      { option: 'dave_barnes', label: 'Dave Barnes' },
      { option: 'barnes_mullins', label: 'Barnes & Mullins' },
      { option: 'beansprout', label: 'Beansprout' },
      { option: 'brown_banjos_richard_brown', label: 'Brown Banjos (Richard Brown)' },
      { option: 'cartwright_phil_cartwright', label: 'Cartwright (Phil Cartwright)' },
      { option: 'dallas', label: 'Dallas' },
      { option: 'phil_davidson', label: 'Phil Davidson' },
      { option: 'dayton', label: 'Dayton' },
      { option: 'deering', label: 'Deering' },
      { option: 'dixie', label: 'Dixie' },
      { option: 'duke', label: 'Duke' },
      { option: 'eddy_finn', label: 'Eddy Finn' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'g_h_s_george_houghton_sons', label: 'G.H. & S. (George Houghton & Sons)' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'john_grey', label: 'John Grey' },
      { option: 'kala', label: 'Kala' },
      { option: 'kay', label: 'Kay' },
      { option: 'keech_banjulele', label: 'Keech (Banjulele)' },
      { option: 'kmise', label: 'Kmise' },
      { option: 'lanikai', label: 'Lanikai' },
      { option: 'ludwig', label: 'Ludwig' },
      { option: 'luna', label: 'Luna' },
      { option: 'magic_fluke_firefly', label: 'Magic Fluke (Firefly)' },
      { option: 'markendale', label: 'Markendale' },
      { option: 'martin_c_f_martin', label: 'Martin (C.F. Martin)' },
      { option: 'm_l_neal', label: 'M.L. Neal' },
      { option: 'mya_moe', label: 'Mya-Moe' },
      { option: 'ohana', label: 'Ohana' },
      { option: 'orpheum', label: 'Orpheum' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'paramount', label: 'Paramount' },
      { option: 'rally', label: 'Rally' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'regal', label: 'Regal' },
      { option: 'romero_creations', label: 'Romero Creations' },
      { option: 'shellard', label: 'Shellard' },
      { option: 'slingerland_may_bell', label: 'Slingerland (May Bell)' },
      { option: 'songbird_stringed_instruments', label: 'Songbird Stringed Instruments' },
      { option: 'southern_cross', label: 'Southern Cross' },
      { option: 'spanky', label: 'Spanky' },
      { option: 's_s_stewart', label: 'S.S. Stewart' },
      { option: 'stromberg_voisinet', label: 'Stromberg-Voisinet' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'bruno_torres', label: 'Bruno Torres' },
      { option: 'vega', label: 'Vega' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'windsor', label: 'Windsor' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_banjo_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'banjo_banjo_ukulele_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'soprano', label: 'Soprano' },
      { option: 'concert', label: 'Concert' },
      { option: 'tenor', label: 'Tenor' },
      { option: 'baritone', label: 'Baritone' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_banjo_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'banjo_banjo_ukulele_back_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'open_back', label: 'Open Back' },
      { option: 'resonator_closed_back', label: 'Resonator (Closed Back)' },
      { option: 'flat_back', label: 'Flat Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_banjo_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_banjo_ukulele_head',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'synthetic', label: 'Synthetic' },
      { option: 'natural_skin_vellum', label: 'Natural Skin (Vellum)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_banjo_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Material',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandolin_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'altman', label: 'Altman' },
      { option: 'alvarez', label: 'Alvarez' },
      { option: 'apitius', label: 'Apitius' },
      { option: 'big_muddy', label: 'Big Muddy' },
      { option: 'bourgeois', label: 'Bourgeois' },
      { option: 'boyd', label: 'Boyd' },
      { option: 'breedlove', label: 'Breedlove' },
      { option: 'brentrup', label: 'Brentrup' },
      { option: 'brw', label: 'BRW' },
      { option: 'bulas', label: 'Bulas' },
      { option: 'calace', label: 'Calace' },
      { option: 'campanella', label: 'Campanella' },
      { option: 'cedar_mountain', label: 'Cedar Mountain' },
      { option: 'clark', label: 'Clark' },
      { option: 'collings', label: 'Collings' },
      { option: 'd_angelico', label: 'D\'Angelico' },
      { option: 'd_aquisto', label: 'D\'Aquisto' },
      { option: 'davidson', label: 'Davidson' },
      { option: 'brian_dean', label: 'Brian Dean' },
      { option: 'dearstone', label: 'Dearstone' },
      { option: 'dudenbostel', label: 'Dudenbostel' },
      { option: 'duff', label: 'Duff' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'ellis', label: 'Ellis' },
      { option: 'embergher', label: 'Embergher' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'favilla', label: 'Favilla' },
      { option: 'fender', label: 'Fender' },
      { option: 'flatiron', label: 'Flatiron' },
      { option: 'franklin', label: 'Franklin' },
      { option: 'giacomel', label: 'Giacomel' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gilchrist', label: 'Gilchrist' },
      { option: 'girouard', label: 'Girouard' },
      { option: 'godin', label: 'Godin' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'brad_goodman', label: 'Brad Goodman' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'heiden', label: 'Heiden' },
      { option: 'henderson', label: 'Henderson' },
      { option: 'hinde', label: 'Hinde' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'hutto', label: 'Hutto' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'jay_turser', label: 'Jay Turser' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'kala', label: 'Kala' },
      { option: 'kay', label: 'Kay' },
      { option: 'thomas_skip_kelley', label: 'Thomas "Skip" Kelley' },
      { option: 'kentucky', label: 'Kentucky' },
      { option: 'kimble', label: 'Kimble' },
      { option: 'klos', label: 'KLOS' },
      { option: 'kr_strings_kilin_reece', label: 'KR Strings (Kilin Reece)' },
      { option: 'larson_brothers', label: 'Larson Brothers' },
      { option: 'lewandowski', label: 'Lewandowski' },
      { option: 'lime_creek', label: 'Lime Creek' },
      { option: 'lippi', label: 'Lippi' },
      { option: 'luna', label: 'Luna' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'martin_c_f_martin', label: 'Martin (C.F. Martin)' },
      { option: 'mclanahan', label: 'McLanahan' },
      { option: 'michael_kelly', label: 'Michael Kelly' },
      { option: 'monteleone', label: 'Monteleone' },
      { option: 'morgan_monroe', label: 'Morgan Monroe' },
      { option: 'mowry', label: 'Mowry' },
      { option: 'national', label: 'National' },
      { option: 'newell', label: 'Newell' },
      { option: 'northfield', label: 'Northfield' },
      { option: 'nugget_mike_kemnitzer', label: 'Nugget (Mike Kemnitzer)' },
      { option: 'old_wave', label: 'Old Wave' },
      { option: 'ortega', label: 'Ortega' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'paganoni', label: 'Paganoni' },
      { option: 'pandini', label: 'Pandini' },
      { option: 'parsons', label: 'Parsons' },
      { option: 'pava', label: 'Pava' },
      { option: 'phoenix_rolfe_gerhardt', label: 'Phoenix (Rolfe Gerhardt)' },
      { option: 'ratliff', label: 'Ratliff' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'red_diamond_don_macrostie', label: 'Red Diamond (Don MacRostie)' },
      { option: 'regal', label: 'Regal' },
      { option: 'rigel', label: 'Rigel' },
      { option: 'rogue', label: 'Rogue' },
      { option: 'rover', label: 'Rover' },
      { option: 'saga', label: 'Saga' },
      { option: 'savannah', label: 'Savannah' },
      { option: 'seiffert', label: 'Seiffert' },
      { option: 'silverangel_ken_ratcliff', label: 'Silverangel (Ken Ratcliff)' },
      { option: 'a_lawrence_smart', label: 'A. Lawrence Smart' },
      { option: 'sobell', label: 'Sobell' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'stiver', label: 'Stiver' },
      { option: 'stradolin', label: 'Stradolin' },
      { option: 'summit', label: 'Summit' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'the_loar', label: 'The Loar' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'tucker', label: 'Tucker' },
      { option: 'van_den_broek', label: 'Van den Broek' },
      { option: 'vega', label: 'Vega' },
      { option: 'vinaccia', label: 'Vinaccia' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weber', label: 'Weber' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'wiens', label: 'Wiens' },
      { option: 'randy_wood', label: 'Randy Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'mandolin_family_mandolin_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a_style', label: 'A-Style' },
      { option: 'f_style', label: 'F-Style' },
      { option: 'bowlback_roundback', label: 'Bowlback / Roundback' },
      { option: 'flatback', label: 'Flatback' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'strings_mandolin_family_soundhole',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'f_holes', label: 'F-Holes' },
      { option: 'oval_hole', label: 'Oval Hole' },
      { option: 'round_hole', label: 'Round Hole' },
      { option: 'd_hole', label: 'D-Hole' },
      { option: 'no_soundhole_electric', label: 'No Soundhole (Electric)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandocello',
        'mandolin_family_mandolin',
        'mandolin_family_octave_mandolin',
        'mandolin_family_mandola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Soundhole Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Soundhole Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Soundhole Type',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandolin_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'adirondack_red_spruce', label: 'Adirondack / Red Spruce' },
      { option: 'sitka_spruce', label: 'Sitka Spruce' },
      { option: 'engelmann_spruce', label: 'Engelmann Spruce' },
      { option: 'european_german_spruce', label: 'European / German Spruce' },
      { option: 'carpathian_spruce', label: 'Carpathian Spruce' },
      { option: 'italian_alpine_spruce', label: 'Italian / Alpine Spruce' },
      { option: 'lutz_spruce', label: 'Lutz Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'redwood', label: 'Redwood' },
      { option: 'torrefied_spruce', label: 'Torrefied Spruce' },
      { option: 'laminated_veneer', label: 'Laminated / Veneer' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandolin_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'koa', label: 'Koa' },
      { option: 'mixed_woods_bowlback', label: 'Mixed Woods (Bowlback)' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandola_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'abnett_peter_abnett', label: 'Abnett (Peter Abnett)' },
      { option: 'altman', label: 'Altman' },
      { option: 'alvarez', label: 'Alvarez' },
      { option: 'apitius', label: 'Apitius' },
      { option: 'big_muddy', label: 'Big Muddy' },
      { option: 'bourgeois', label: 'Bourgeois' },
      { option: 'boyd', label: 'Boyd' },
      { option: 'breedlove', label: 'Breedlove' },
      { option: 'brentrup', label: 'Brentrup' },
      { option: 'brw_ben_wilcox', label: 'BRW (Ben Wilcox)' },
      { option: 'bulas', label: 'Bulas' },
      { option: 'calace', label: 'Calace' },
      { option: 'campanella', label: 'Campanella' },
      { option: 'cedar_mountain', label: 'Cedar Mountain' },
      { option: 'clark', label: 'Clark' },
      { option: 'collings', label: 'Collings' },
      { option: 'd_angelico', label: 'D\'Angelico' },
      { option: 'd_aquisto', label: 'D\'Aquisto' },
      { option: 'davidson', label: 'Davidson' },
      { option: 'dean_brian_dean', label: 'Dean (Brian Dean)' },
      { option: 'dearstone', label: 'Dearstone' },
      { option: 'dudenbostel', label: 'Dudenbostel' },
      { option: 'duff', label: 'Duff' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'ellis', label: 'Ellis' },
      { option: 'embergher', label: 'Embergher' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'favilla', label: 'Favilla' },
      { option: 'fender', label: 'Fender' },
      { option: 'flatiron', label: 'Flatiron' },
      { option: 'franklin', label: 'Franklin' },
      { option: 'franzke', label: 'Franzke' },
      { option: 'fylde', label: 'Fylde' },
      { option: 'giacomel', label: 'Giacomel' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gilchrist', label: 'Gilchrist' },
      { option: 'girouard', label: 'Girouard' },
      { option: 'godin', label: 'Godin' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'brad_goodman', label: 'Brad Goodman' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'heiden', label: 'Heiden' },
      { option: 'henderson', label: 'Henderson' },
      { option: 'hinde', label: 'Hinde' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'hutto', label: 'Hutto' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'jay_turser', label: 'Jay Turser' },
      { option: 'jj_ryan_jill_ryan', label: 'JJ Ryan (Jill Ryan)' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'kala', label: 'Kala' },
      { option: 'kay', label: 'Kay' },
      { option: 'thomas_skip_kelley', label: 'Thomas "Skip" Kelley' },
      { option: 'kentucky', label: 'Kentucky' },
      { option: 'kimble', label: 'Kimble' },
      { option: 'klos', label: 'KLOS' },
      { option: 'kr_strings_kilin_reece', label: 'KR Strings (Kilin Reece)' },
      { option: 'larson_brothers', label: 'Larson Brothers' },
      { option: 'lewandowski', label: 'Lewandowski' },
      { option: 'lime_creek', label: 'Lime Creek' },
      { option: 'lippi', label: 'Lippi' },
      { option: 'luna', label: 'Luna' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'martin_c_f_martin', label: 'Martin (C.F. Martin)' },
      { option: 'mclanahan', label: 'McLanahan' },
      { option: 'michael_kelly', label: 'Michael Kelly' },
      { option: 'monteleone', label: 'Monteleone' },
      { option: 'morgan_monroe', label: 'Morgan Monroe' },
      { option: 'mowry', label: 'Mowry' },
      { option: 'national', label: 'National' },
      { option: 'newell', label: 'Newell' },
      { option: 'northfield', label: 'Northfield' },
      { option: 'nugget_mike_kemnitzer', label: 'Nugget (Mike Kemnitzer)' },
      { option: 'lawrence_nyberg', label: 'Lawrence Nyberg' },
      { option: 'old_wave', label: 'Old Wave' },
      { option: 'ortega', label: 'Ortega' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'paganoni', label: 'Paganoni' },
      { option: 'pandini', label: 'Pandini' },
      { option: 'parsons', label: 'Parsons' },
      { option: 'pava_pava_knezevic', label: 'Pava (Pava Knezevic)' },
      { option: 'phoenix_rolfe_gerhardt', label: 'Phoenix (Rolfe Gerhardt)' },
      { option: 'prucha', label: 'Prucha' },
      { option: 'ratliff', label: 'Ratliff' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'red_diamond_don_macrostie', label: 'Red Diamond (Don MacRostie)' },
      { option: 'regal', label: 'Regal' },
      { option: 'rigel', label: 'Rigel' },
      { option: 'rogue', label: 'Rogue' },
      { option: 'rose_mandolins_darby_boofer', label: 'Rose Mandolins (Darby Boofer)' },
      { option: 'rover', label: 'Rover' },
      { option: 'saga', label: 'Saga' },
      { option: 'savannah', label: 'Savannah' },
      { option: 'seiffert', label: 'Seiffert' },
      { option: 'silverangel_ken_ratcliff', label: 'Silverangel (Ken Ratcliff)' },
      { option: 'a_lawrence_smart', label: 'A. Lawrence Smart' },
      { option: 'sobell', label: 'Sobell' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'stiver', label: 'Stiver' },
      { option: 'stradolin', label: 'Stradolin' },
      { option: 'davy_stuart', label: 'Davy Stuart' },
      { option: 'summit', label: 'Summit' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'the_loar', label: 'The Loar' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'tucker', label: 'Tucker' },
      { option: 'van_den_broek', label: 'Van den Broek' },
      { option: 'vega', label: 'Vega' },
      { option: 'vinaccia', label: 'Vinaccia' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weber', label: 'Weber' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'wiens', label: 'Wiens' },
      { option: 'wyatt_wilkie', label: 'Wyatt Wilkie' },
      { option: 'randy_wood', label: 'Randy Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'mandolin_family_mandola_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'f_style_scroll', label: 'F-Style (Scroll)' },
      { option: 'a_style_teardrop_pear', label: 'A-Style (Teardrop / Pear)' },
      { option: 'bowlback_round_back', label: 'Bowlback (Round-back)' },
      { option: 'flatback_celtic_folk', label: 'Flatback (Celtic / Folk)' },
      { option: 'electric_solid_body', label: 'Electric (Solid Body)' },
      { option: 'electric_semi_hollow_hollow', label: 'Electric (Semi-Hollow / Hollow)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandola_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'maple', label: 'Maple' },
      { option: 'adirondack_spruce', label: 'Adirondack Spruce' },
      { option: 'engelmann_spruce', label: 'Engelmann Spruce' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandola_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'birch', label: 'Birch' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_octave_mandolin_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bob_abrams_trillium', label: 'Bob Abrams (Trillium)' },
      { option: 'apc_instruments', label: 'APC Instruments' },
      { option: 'ashbury', label: 'Ashbury' },
      { option: 'big_muddy', label: 'Big Muddy' },
      { option: 'eddie_blevins_ebm_works', label: 'Eddie Blevins (EBM Works)' },
      { option: 'daniel_brauchli', label: 'Daniel Brauchli' },
      { option: 'breedlove', label: 'Breedlove' },
      { option: 'fletcher_brock', label: 'Fletcher Brock' },
      { option: 'micah_bruce', label: 'Micah Bruce' },
      { option: 'tom_buchanan', label: 'Tom Buchanan' },
      { option: 'paddy_burgin', label: 'Paddy Burgin' },
      { option: 'campanella', label: 'Campanella' },
      { option: 'clark_mandolins', label: 'Clark Mandolins' },
      { option: 'collings', label: 'Collings' },
      { option: 'de_faoite_david_white', label: 'De Faoite (David White)' },
      { option: 'gil_draper', label: 'Gil Draper' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'paul_evans_siveen', label: 'Paul Evans (Siveen)' },
      { option: 'flatiron', label: 'Flatiron' },
      { option: 'joe_foley', label: 'Joe Foley' },
      { option: 'davey_freshwater', label: 'Davey Freshwater' },
      { option: 'fylde', label: 'Fylde' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'girouard', label: 'Girouard' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'hathway', label: 'Hathway' },
      { option: 'hora', label: 'Hora' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'thomas_skip_kelley', label: 'Thomas "Skip" Kelley' },
      { option: 'kentucky_saga', label: 'Kentucky (Saga)' },
      { option: 'gary_lewandowski_cedar_mountain', label: 'Gary Lewandowski (Cedar Mountain)' },
      { option: 'mid_missouri', label: 'Mid-Missouri' },
      { option: 'jimmy_moon', label: 'Jimmy Moon' },
      { option: 'morgan_monroe', label: 'Morgan Monroe' },
      { option: 'mowry', label: 'Mowry' },
      { option: 'nk_forster', label: 'NK Forster' },
      { option: 'northfield', label: 'Northfield' },
      { option: 'macdara_o_faolain', label: 'Macdara Ó Faoláin' },
      { option: 'old_wave', label: 'Old Wave' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'petersen', label: 'Petersen' },
      { option: 'pono', label: 'Pono' },
      { option: 'red_valley_lafferty', label: 'Red Valley (Lafferty)' },
      { option: 'rickert_hale_d_rickert', label: 'Rickert & Hale (D. Rickert)' },
      { option: 'rigel', label: 'Rigel' },
      { option: 'paul_shippey', label: 'Paul Shippey' },
      { option: 'sobell', label: 'Sobell' },
      { option: 'springwell_taran_guitars', label: 'Springwell (Taran Guitars)' },
      { option: 'taran_guitars', label: 'Taran Guitars' },
      { option: 'frank_tate', label: 'Frank Tate' },
      { option: 'the_loar', label: 'The Loar' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weber', label: 'Weber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_octave_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'mandolin_family_octave_mandolin_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a_style_pear_teardrop', label: 'A-Style (Pear / Teardrop)' },
      { option: 'f_style_scroll_points', label: 'F-Style (Scroll / Points)' },
      { option: 'guitar_bodied_hourglass', label: 'Guitar-Bodied (Hourglass)' },
      { option: 'celtic_irish_teardrop_flat_top', label: 'Celtic/Irish Teardrop (Flat-Top)' },
      { option: 'bowl_back', label: 'Bowl-Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_octave_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_octave_mandolin_top_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'carved_archtop', label: 'Carved Archtop' },
      { option: 'flat_top', label: 'Flat Top' },
      { option: 'pressed_archtop', label: 'Pressed Archtop' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_octave_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top Construction',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_octave_mandolin_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'sitka_spruce', label: 'Sitka Spruce' },
      { option: 'engelmann_spruce', label: 'Engelmann Spruce' },
      { option: 'adirondack_red_spruce', label: 'Adirondack (Red) Spruce' },
      { option: 'european_alpine_carpathian_spruce', label: 'European / Alpine / Carpathian Spruce' },
      { option: 'western_red_cedar', label: 'Western Red Cedar' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'laminate_plywood', label: 'Laminate/Plywood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_octave_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_octave_mandolin_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'blackwood', label: 'Blackwood' },
      { option: 'laminate_plywood', label: 'Laminate/Plywood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_octave_mandolin',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandocello_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'arrow_paul_lestock', label: 'Arrow (Paul Lestock)' },
      { option: 'baxendale_scott_baxendale', label: 'Baxendale (Scott Baxendale)' },
      { option: 'calace_raffaele_calace', label: 'Calace (Raffaele Calace)' },
      { option: 'campanella_joe_campanella_cleary', label: 'Campanella (Joe Campanella Cleary)' },
      { option: 'ceccherini', label: 'Ceccherini' },
      { option: 'cerrone', label: 'Cerrone' },
      { option: 'cohen_david_cohen', label: 'Cohen (David Cohen)' },
      { option: 'collings', label: 'Collings' },
      { option: 'condino_j_j_condino', label: 'Condino (J.J. Condino)' },
      { option: 'dammann_ray_varona', label: 'Dammann (Ray Varona)' },
      { option: 'dart_dave_dart', label: 'Dart (Dave Dart)' },
      { option: 'dudenbostel_lynn_dudenbostel', label: 'Dudenbostel (Lynn Dudenbostel)' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'embergher_luigi_embergher', label: 'Embergher (Luigi Embergher)' },
      { option: 'flatiron', label: 'Flatiron' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gilchrist_steve_gilchrist', label: 'Gilchrist (Steve Gilchrist)' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'hinde_steve_hinde', label: 'Hinde (Steve Hinde)' },
      { option: 'kentucky', label: 'Kentucky' },
      { option: 'kimble_will_kimble', label: 'Kimble (Will Kimble)' },
      { option: 'langdell_pete_langdell', label: 'Langdell (Pete Langdell)' },
      { option: 'lebeda_jiri_lebeda', label: 'Lebeda (Jiří Lebeda)' },
      { option: 'martin', label: 'Martin' },
      { option: 'monteleone_john_monteleone', label: 'Monteleone (John Monteleone)' },
      { option: 'mowry_andrew_mowry', label: 'Mowry (Andrew Mowry)' },
      { option: 'northfield', label: 'Northfield' },
      { option: 'pecoraro', label: 'Pecoraro' },
      { option: 'rattlesnake_olin_davis', label: 'Rattlesnake (Olin Davis)' },
      { option: 'redhead_mandolins_nev_fahs', label: 'Redhead Mandolins (Nev Fahs)' },
      { option: 'rickert_d_rickert_musical_instruments', label: 'Rickert (D. Rickert Musical Instruments)' },
      { option: 'rigel', label: 'Rigel' },
      { option: 'santa_cruz_guitar_company', label: 'Santa Cruz Guitar Company' },
      { option: 'scaparelli_antonio_tsai', label: 'Scaparelli (Antonio Tsai)' },
      { option: 'smart_lawrence_smart', label: 'Smart (Lawrence Smart)' },
      { option: 'sobell_stefan_sobell', label: 'Sobell (Stefan Sobell)' },
      { option: 'stradolin', label: 'Stradolin' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'the_loar', label: 'The Loar' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'vega', label: 'Vega' },
      { option: 'vinaccia', label: 'Vinaccia' },
      { option: 'wade_yoichi_ueda', label: 'Wade (Yoichi Ueda)' },
      { option: 'washburn_lyon_healy', label: 'Washburn (Lyon & Healy)' },
      { option: 'weber', label: 'Weber' },
      { option: 'wood_randy_wood', label: 'Wood (Randy Wood)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandocello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'mandolin_family_mandocello_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bowl_back_neapolitan', label: 'Bowl-Back (Neapolitan)' },
      { option: 'a_style_oval_teardrop', label: 'A-Style (Oval/Teardrop)' },
      { option: 'f_style_scroll_florentine', label: 'F-Style (Scroll/Florentine)' },
      { option: 'guitar_body', label: 'Guitar-Body' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandocello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandocello_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4_courses_8_strings', label: '4 Courses (8 Strings)' },
      { option: '5_courses_10_strings', label: '5 Courses (10 Strings)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandocello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandocello_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'redwood', label: 'Redwood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandocello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'mandolin_family_mandocello_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'birch', label: 'Birch' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'mandolin_family_mandocello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_violin_fiddle_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3dvarius', label: '3DVarius' },
      { option: 'adm_all_days_music', label: 'ADM (All Days Music)' },
      { option: 'aileen', label: 'Aileen' },
      { option: 'amati_family', label: 'Amati (Family)' },
      { option: 'luiz_amorim', label: 'Luiz Amorim' },
      { option: 'antoniazzi_family', label: 'Antoniazzi (Family)' },
      { option: 'georges_apparut', label: 'Georges Apparut' },
      { option: 'auro', label: 'Auro' },
      { option: 'aurora', label: 'Aurora' },
      { option: 'barcus_berry', label: 'Barcus-Berry' },
      { option: 'gonzalo_bayolo', label: 'Gonzalo Bayolo' },
      { option: 'becker_family', label: 'Becker (Family)' },
      { option: 'bein_fushi', label: 'Bein & Fushi' },
      { option: 'carlo_bergonzi', label: 'Carlo Bergonzi' },
      { option: 'riccardo_bergonzi_modern', label: 'Riccardo Bergonzi (modern)' },
      { option: 'leandro_bisiach', label: 'Leandro Bisiach' },
      { option: 'bissolotti_family', label: 'Bissolotti (Family)' },
      { option: 'paul_bradley', label: 'Paul Bradley' },
      { option: 'braid_river', label: 'Braid River' },
      { option: 'bridge_instruments', label: 'Bridge Instruments' },
      { option: 'calin_wultur', label: 'Calin Wultur' },
      { option: 'cantini', label: 'Cantini' },
      { option: 'carlo_lamberti_shar', label: 'Carlo Lamberti (Shar)' },
      { option: 'carlo_robelli', label: 'Carlo Robelli' },
      { option: 'cecilio', label: 'Cecilio' },
      { option: 'ceruti_family', label: 'Ceruti (Family)' },
      { option: 'chanot_family', label: 'Chanot (Family)' },
      { option: 'collin_mezin', label: 'Collin-Mézin' },
      { option: 'cremona', label: 'Cremona' },
      { option: 'joseph_curtin', label: 'Joseph Curtin' },
      { option: 'd_z_strad', label: 'D Z Strad' },
      { option: 'darte', label: 'Darte' },
      { option: 'justin_derazey', label: 'Justin Derazey' },
      { option: 'eastman_strings', label: 'Eastman Strings' },
      { option: 'plamen_edrev', label: 'Plamen Edrev' },
      { option: 'engelhardt', label: 'Engelhardt' },
      { option: 'ernst_heinrich_roth', label: 'Ernst Heinrich Roth' },
      { option: 'eugen_meinel', label: 'Eugen Meinel' },
      { option: 'annibale_fagnola', label: 'Annibale Fagnola' },
      { option: 'fiddlerman_holstein', label: 'Fiddlerman (Holstein)' },
      { option: 'fiddlershop', label: 'Fiddlershop' },
      { option: 'fortevio', label: 'ForteVio' },
      { option: 'fourness', label: 'Fourness' },
      { option: 'franz_hoffmann_shar', label: 'Franz Hoffmann (Shar)' },
      { option: 'franz_sandner', label: 'Franz Sandner' },
      { option: 'benedicte_friedmann', label: 'Bénédicte Friedmann' },
      { option: 'gagliano_family', label: 'Gagliano (Family)' },
      { option: 'gand_bernardel', label: 'Gand & Bernardel' },
      { option: 'gasparo_da_salo', label: 'Gasparo da Salò' },
      { option: 'george_gemunder', label: 'George Gemünder' },
      { option: 'gewa', label: 'Gewa' },
      { option: 'gliga', label: 'Gliga' },
      { option: 'glasser', label: 'Glasser' },
      { option: 'grancino_family', label: 'Grancino (Family)' },
      { option: 'stefan_peter_greiner', label: 'Stefan-Peter Greiner' },
      { option: 'giovanni_battista_guadagnini', label: 'Giovanni Battista Guadagnini' },
      { option: 'guarneri_family', label: 'Guarneri (Family)' },
      { option: 'harley_benton', label: 'Harley Benton' },
      { option: 'heberlein_family', label: 'Heberlein (Family)' },
      { option: 'lloyd_heikes', label: 'Lloyd Heikes' },
      { option: 'hermann_todt', label: 'Hermann Todt' },
      { option: 'hofner', label: 'Höfner' },
      { option: 'hopf_family', label: 'Hopf (Family)' },
      { option: 'j_t_l_jerome_thibouville_lamy', label: 'J.T.L. (Jérôme Thibouville-Lamy)' },
      { option: 'jay_haide', label: 'Jay Haide' },
      { option: 'jordan', label: 'Jordan' },
      { option: 'keebler', label: 'Keebler' },
      { option: 'kennedy_violins_bunnel', label: 'Kennedy Violins (Bunnel)' },
      { option: 'kinglos', label: 'Kinglos' },
      { option: 'klaus_clement', label: 'Klaus Clement' },
      { option: 'klotz_family', label: 'Klotz (Family)' },
      { option: 'knilling', label: 'Knilling' },
      { option: 'laberte_humbert', label: 'Laberte-Humbert' },
      { option: 'luis_and_clark', label: 'Luis and Clark' },
      { option: 'nicolas_lupot', label: 'Nicolas Lupot' },
      { option: 'giovanni_paolo_maggini', label: 'Giovanni Paolo Maggini' },
      { option: 'maple_leaf_strings', label: 'Maple Leaf Strings' },
      { option: 'andres_modesto_martinez_bilva_a_m_bilva', label: 'Andrés Modesto Martinez Bilva (A.M. Bilva)' },
      { option: 'mcneela', label: 'McNeela' },
      { option: 'mendini_by_cecilio', label: 'Mendini (by Cecilio)' },
      { option: 'merano', label: 'Merano' },
      { option: 'mezzo_forte', label: 'Mezzo-Forte' },
      { option: 'domenico_montagnana', label: 'Domenico Montagnana' },
      { option: 'msi', label: 'MSI' },
      { option: 'neuner_hornsteiner', label: 'Neuner & Hornsteiner' },
      { option: 'ns_design', label: 'NS Design' },
      { option: 'charlie_ogle_workshop', label: 'Charlie Ogle (Workshop)' },
      { option: 'openfabpdx', label: 'OpenFabPDX' },
      { option: 'giuseppe_ornati', label: 'Giuseppe Ornati' },
      { option: 'paesold', label: 'Paesold' },
      { option: 'palatino', label: 'Palatino' },
      { option: 'panormo_family', label: 'Panormo (Family)' },
      { option: 'thomas_perry', label: 'Thomas Perry' },
      { option: 'francois_louis_pique', label: 'François-Louis Pique' },
      { option: 'ansaldo_poggi', label: 'Ansaldo Poggi' },
      { option: 'giovanni_francesco_pressenda', label: 'Giovanni Francesco Pressenda' },
      { option: 'primavera', label: 'Primavera' },
      { option: 'raggetti', label: 'Raggetti' },
      { option: 'realist', label: 'Realist' },
      { option: 'revelle', label: 'Revelle' },
      { option: 'giovanni_battista_rogeri', label: 'Giovanni Battista Rogeri' },
      { option: 'francesco_ruggieri_casa_ruggieri', label: 'Francesco Ruggieri (Casa Ruggieri)' },
      { option: 'edgar_russ', label: 'Edgar Russ' },
      { option: 'stefano_scarampella', label: 'Stefano Scarampella' },
      { option: 'scherl_roth', label: 'Scherl & Roth' },
      { option: 'carrie_scoggins', label: 'Carrie Scoggins' },
      { option: 'scott_cao', label: 'Scott Cao' },
      { option: 'renato_scrollavezza', label: 'Renato Scrollavezza' },
      { option: 'gaetano_pietro_sgarabotto', label: 'Gaetano & Pietro Sgarabotto' },
      { option: 'samuel_shen_workshop', label: 'Samuel Shen (Workshop)' },
      { option: 'silvestre_family', label: 'Silvestre (Family)' },
      { option: 'snow', label: 'Snow' },
      { option: 'walter_solon', label: 'Walter Solon' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'jacob_stainer', label: 'Jacob Stainer' },
      { option: 'stentor', label: 'Stentor' },
      { option: 'lorenzo_storioni', label: 'Lorenzo Storioni' },
      { option: 'stradivari_stradivarius', label: 'Stradivari (Stradivarius)' },
      { option: 'stratton', label: 'Stratton' },
      { option: 'strikell', label: 'Strikell' },
      { option: 'strunal_schonbach', label: 'Strunal (Schönbach)' },
      { option: 'testore_family', label: 'Testore (Family)' },
      { option: 'daniele_tonarelli', label: 'Daniele Tonarelli' },
      { option: 'francesco_toto', label: 'Francesco Toto' },
      { option: 'tower_strings', label: 'Tower Strings' },
      { option: 'paolo_vettori', label: 'Paolo Vettori' },
      { option: 'voller_brothers', label: 'Voller Brothers' },
      { option: 'jean_baptiste_vuillaume', label: 'Jean-Baptiste Vuillaume' },
      { option: 'w_e_hill_sons', label: 'W.E. Hill & Sons' },
      { option: 'peter_white', label: 'Peter White' },
      { option: 'windsor', label: 'Windsor' },
      { option: 'wood_violins_mark_wood', label: 'Wood Violins (Mark Wood)' },
      { option: 'wulffenstejn', label: 'Wulffenstejn' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'zeta', label: 'Zeta' },
      { option: 'ming_jiang_zhu', label: 'Ming Jiang Zhu' },
      { option: 'samuel_zygmuntowicz', label: 'Samuel Zygmuntowicz' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_violin_fiddle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bowed_strings_violin_fiddle_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4_4_full_size', label: '4/4 (Full Size)' },
      { option: '7_8_lady_s_intermediate', label: '7/8 (Lady\'s / Intermediate)' },
      { option: '3_4', label: '3/4' },
      { option: '1_2', label: '1/2' },
      { option: '1_4', label: '1/4' },
      { option: '1_8', label: '1/8' },
      { option: '1_10', label: '1/10' },
      { option: '1_16', label: '1/16' },
      { option: '1_32', label: '1/32' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_violin_fiddle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_violin_fiddle_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acoustic', label: 'Acoustic' },
      { option: 'electric_solid_body', label: 'Electric (Solid Body)' },
      { option: 'acoustic_electric_semi_acoustic', label: 'Acoustic-Electric / Semi-Acoustic' },
      { option: 'baroque', label: 'Baroque' },
      { option: 'silent_practice', label: 'Silent / Practice' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_violin_fiddle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_violin_fiddle_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_violin_fiddle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_viola_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3dvarius', label: '3DVarius' },
      { option: 'a_e_smith', label: 'A.E. Smith' },
      { option: 'albert_nebel', label: 'Albert Nebel' },
      { option: 'amati_family', label: 'Amati (family)' },
      { option: 'luiz_amorim', label: 'Luiz Amorim' },
      { option: 'leo_aschauer', label: 'Leo Aschauer' },
      { option: 'aurora_violins', label: 'Aurora Violins' },
      { option: 'barcus_berry', label: 'Barcus-Berry' },
      { option: 'gonzalo_bayolo', label: 'Gonzalo Bayolo' },
      { option: 'martin_beck', label: 'Martin Beck' },
      { option: 'becker_carl_becker_son', label: 'Becker (Carl Becker & Son)' },
      { option: 'otto_benjamin', label: 'Otto Benjamin' },
      { option: 'herman_beyer', label: 'Herman Beyer' },
      { option: 'francesco_bissolotti', label: 'Francesco Bissolotti' },
      { option: 'bridge_uk', label: 'Bridge (UK)' },
      { option: 'calin_wultur', label: 'Calin Wultur' },
      { option: 'cantini', label: 'Cantini' },
      { option: 'scott_cao', label: 'Scott Cao' },
      { option: 'cecilio', label: 'Cecilio' },
      { option: 'collin_mezin', label: 'Collin-Mézin' },
      { option: 'cremona', label: 'Cremona' },
      { option: 'colin_cross', label: 'Colin Cross' },
      { option: 'joseph_curtin', label: 'Joseph Curtin' },
      { option: 'd_z_strad', label: 'D Z Strad' },
      { option: 'rudolph_doetsch', label: 'Rudolph Doetsch' },
      { option: 'ivan_dunov', label: 'Ivan Dunov' },
      { option: 'eastman_strings', label: 'Eastman Strings' },
      { option: 'samuel_eastman', label: 'Samuel Eastman' },
      { option: 'raul_emiliani', label: 'Raúl Emiliani' },
      { option: 'f_r_enders', label: 'F. & R. Enders' },
      { option: 'gustav_august_ficker', label: 'Gustav August Ficker' },
      { option: 'fortevio', label: 'Fortevio' },
      { option: 'august_gemunder', label: 'August Gemünder' },
      { option: 'hermann_geipel', label: 'Hermann Geipel' },
      { option: 'gewa', label: 'GEWA' },
      { option: 'glasser', label: 'Glasser' },
      { option: 'gliga', label: 'Gliga' },
      { option: 'guarneri_family', label: 'Guarneri (family)' },
      { option: 'kurt_gutter', label: 'Kurt Gutter' },
      { option: 'jay_haide', label: 'Jay Haide' },
      { option: 'heinrich_theodor_heberlein', label: 'Heinrich Theodor Heberlein' },
      { option: 'karl_hofner', label: 'Karl Höfner' },
      { option: 'holstein', label: 'Holstein' },
      { option: 'jacobus_hornsteiner', label: 'Jacobus Hornsteiner' },
      { option: 'hiroshi_iizuka', label: 'Hiroshi Iizuka' },
      { option: 'jerome_thibouville_lamy_jtl', label: 'Jérôme Thibouville-Lamy (JTL)' },
      { option: 'jordan', label: 'Jordan' },
      { option: 'john_juzek', label: 'John Juzek' },
      { option: 'jurgen_klier', label: 'Jürgen Klier' },
      { option: 'wilhelm_klier', label: 'Wilhelm Klier' },
      { option: 'knilling', label: 'Knilling' },
      { option: 'frantisek_kus', label: 'František Kůs' },
      { option: 'la_lutherie_d_art', label: 'La Lutherie D\'Art' },
      { option: 'maple_leaf_strings', label: 'Maple Leaf Strings' },
      { option: 'pierre_marcel', label: 'Pierre Marcel' },
      { option: 'oskar_meinel', label: 'Oskar Meinel' },
      { option: 'klaus_mueller', label: 'Klaus Mueller' },
      { option: 'msi_dan_maloney', label: 'MSI (Dan Maloney)' },
      { option: 'albert_nebel_2', label: 'Albert Nebel' },
      { option: 'neuner_hornsteiner', label: 'Neuner & Hornsteiner' },
      { option: 'ns_design', label: 'NS Design' },
      { option: 'e_r_pfretzschner', label: 'E.R. Pfretzschner' },
      { option: 'gunther_prager', label: 'Gunther Prager' },
      { option: 'primavera', label: 'Primavera' },
      { option: 'guy_rabut', label: 'Guy Rabut' },
      { option: 'realist', label: 'Realist' },
      { option: 'revelle', label: 'Revelle' },
      { option: 'david_rivinus', label: 'David Rivinus' },
      { option: 'ernst_heinrich_roth', label: 'Ernst Heinrich Roth' },
      { option: 'wilf_saunders', label: 'Wilf Saunders' },
      { option: 'scherl_roth', label: 'Scherl & Roth' },
      { option: 'anton_schroetter', label: 'Anton Schroetter' },
      { option: 'ren_wei_shi', label: 'Ren Wei Shi' },
      { option: 'snow', label: 'Snow' },
      { option: 'stentor', label: 'Stentor' },
      { option: 'stradivari_antonio_stradivari', label: 'Stradivari (Antonio Stradivari)' },
      { option: 'stratton', label: 'Stratton' },
      { option: 'stringworks', label: 'StringWorks' },
      { option: 'wojciech_topa', label: 'Wojciech Topa' },
      { option: 'francesco_toto', label: 'Francesco Toto' },
      { option: 'v_richelieu', label: 'V. Richelieu' },
      { option: 'vector', label: 'Vector' },
      { option: 'paolo_vettori', label: 'Paolo Vettori' },
      { option: 'w_e_hill_sons', label: 'W.E. Hill & Sons' },
      { option: 'william_lewis_son', label: 'William Lewis & Son' },
      { option: 'william_wilkanowski', label: 'William Wilkanowski' },
      { option: 'wood_violins_mark_wood', label: 'Wood Violins (Mark Wood)' },
      { option: 'frederich_wyss_eastman', label: 'Frederich Wyss (Eastman)' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'zeta', label: 'ZETA' },
      { option: 'ming_jiang_zhu', label: 'Ming Jiang Zhu' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bowed_strings_viola_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '11_child', label: '11" (child)' },
      { option: '12_child', label: '12" (child)' },
      { option: '13_child', label: '13" (child)' },
      { option: '14_child_same_as_4_4_violin', label: '14" (child / same as 4/4 violin)' },
      { option: '15_small_adult', label: '15" (small adult)' },
      { option: '15_5_medium_adult', label: '15.5" (medium adult)' },
      { option: '16_standard_adult', label: '16" (standard adult)' },
      { option: '16_5_large_adult', label: '16.5" (large adult)' },
      { option: '17_extra_large', label: '17"+ (extra large)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Size',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_viola_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acoustic', label: 'Acoustic' },
      { option: 'electric_solid_body', label: 'Electric (Solid Body)' },
      { option: 'acoustic_electric', label: 'Acoustic-Electric' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_cello_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3dvarius', label: '3DVarius' },
      { option: 'alessandro', label: 'Alessandro' },
      { option: 'pablo_alfaro', label: 'Pablo Alfaro' },
      { option: 'amati', label: 'Amati' },
      { option: 'ammoon', label: 'Ammoon' },
      { option: 'luiz_amorim', label: 'Luiz Amorim' },
      { option: 'antoni', label: 'Antoni' },
      { option: 'antonio_strad', label: 'Antonio Strad' },
      { option: 'aurora_auro', label: 'Aurora (Auro)' },
      { option: 'bergonzi', label: 'Bergonzi' },
      { option: 'bridge_london', label: 'Bridge (London)' },
      { option: 'cantini', label: 'Cantini' },
      { option: 'scott_cao', label: 'Scott Cao' },
      { option: 'cecilio', label: 'Cecilio' },
      { option: 'frederic_chaudiere', label: 'Frédéric Chaudière' },
      { option: 'core', label: 'Core' },
      { option: 'cremona', label: 'Cremona' },
      { option: 'd_luca', label: 'D\'Luca' },
      { option: 'rudoulf_doetsch', label: 'Rudoulf Doetsch' },
      { option: 'dz_strad', label: 'DZ Strad' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'engelhardt', label: 'Engelhardt' },
      { option: 'fiddlerman', label: 'Fiddlerman' },
      { option: 'forenza', label: 'Forenza' },
      { option: 'gagliano', label: 'Gagliano' },
      { option: 'gear4music', label: 'Gear4music' },
      { option: 'gewa', label: 'GEWA' },
      { option: 'glasser', label: 'Glasser' },
      { option: 'gliga', label: 'Gliga' },
      { option: 'gofriller', label: 'Gofriller' },
      { option: 'grancino', label: 'Grancino' },
      { option: 'stefan_peter_greiner', label: 'Stefan-Peter Greiner' },
      { option: 'guarneri', label: 'Guarneri' },
      { option: 'hidersine', label: 'Hidersine' },
      { option: 'holstein', label: 'Holstein' },
      { option: 'jay_haide', label: 'Jay Haide' },
      { option: 'baptiste_juguera', label: 'Baptiste Juguera' },
      { option: 'wilhelm_klier', label: 'Wilhelm Klier' },
      { option: 'knilling_nicolo_gabrieli', label: 'Knilling (Nicolo Gabrieli)' },
      { option: 'la_scala', label: 'La Scala' },
      { option: 'leonhardt', label: 'Leonhardt' },
      { option: 'jonathan_li', label: 'Jonathan Li' },
      { option: 'luis_clark', label: 'Luis & Clark' },
      { option: 'stefano_marzi', label: 'Stefano Marzi' },
      { option: 'james_mckean', label: 'James McKean' },
      { option: 'mendini_by_cecilio', label: 'Mendini (by Cecilio)' },
      { option: 'merano', label: 'Merano' },
      { option: 'mezzo_forte', label: 'Mezzo-Forte' },
      { option: 'mario_miralles', label: 'Mario Miralles' },
      { option: 'montagnana', label: 'Montagnana' },
      { option: 'ns_design', label: 'NS Design' },
      { option: 'palatino', label: 'Palatino' },
      { option: 'davide_pizzolato', label: 'Davide Pizzolato' },
      { option: 'primavera', label: 'Primavera' },
      { option: 'josef_regh', label: 'Josef Regh' },
      { option: 'resonance', label: 'Resonance' },
      { option: 'rugeri', label: 'Rugeri' },
      { option: 'edgar_russ', label: 'Edgar Russ' },
      { option: 'scherl_roth', label: 'Scherl & Roth' },
      { option: 'segue', label: 'Segue' },
      { option: 'snow', label: 'Snow' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'stentor', label: 'Stentor' },
      { option: 'stradivari_stradivarius', label: 'Stradivari (Stradivarius)' },
      { option: 'strunal_schonbach', label: 'Strunal (Schönbach)' },
      { option: 'tecchler', label: 'Tecchler' },
      { option: 'francesco_toto', label: 'Francesco Toto' },
      { option: 'paolo_vettori', label: 'Paolo Vettori' },
      { option: 'vuillaume', label: 'Vuillaume' },
      { option: 'antonio_wang', label: 'Antonio Wang' },
      { option: 'windsor', label: 'Windsor' },
      { option: 'wood_violins', label: 'Wood Violins' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'zeta', label: 'ZETA' },
      { option: 'ming_jiang_zhu', label: 'Ming Jiang Zhu' },
      { option: 'samuel_zygmuntowicz', label: 'Samuel Zygmuntowicz' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_cello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bowed_strings_cello_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4_4_full_size', label: '4/4 (Full Size)' },
      { option: '7_8', label: '7/8' },
      { option: '3_4', label: '3/4' },
      { option: '1_2', label: '1/2' },
      { option: '1_4', label: '1/4' },
      { option: '1_8', label: '1/8' },
      { option: '1_10', label: '1/10' },
      { option: '1_16', label: '1/16' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_cello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_cello_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acoustic', label: 'Acoustic' },
      { option: 'electric_solid_body', label: 'Electric (Solid Body)' },
      { option: 'acoustic_electric', label: 'Acoustic-Electric' },
      { option: 'silent_practice', label: 'Silent / Practice' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_cello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_cello_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: 'other', label: 'Other' },
      { option: '4_2', label: '4' },
      { option: '5_2', label: '5' },
      { option: '6_2', label: '6' },
      { option: 'other_2', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_cello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_cello_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'fully_carved_solid_wood', label: 'Fully Carved (Solid Wood)' },
      { option: 'hybrid_solid_top_laminated_back_sides', label: 'Hybrid (Solid Top, Laminated Back & Sides)' },
      { option: 'fully_laminated', label: 'Fully Laminated' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_cello',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Construction',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_double_bass_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aes_fine_instruments_arnold_schnitzer', label: 'AES Fine Instruments (Arnold Schnitzer)' },
      { option: 'alienbow', label: 'Alienbow' },
      { option: 'american_standard_h_n_white', label: 'American Standard (H.N. White)' },
      { option: 'thomas_andres_wolf_instruments', label: 'Thomas Andres (Wolf Instruments)' },
      { option: 'aria', label: 'Aria' },
      { option: 'arvi_kai_arvi', label: 'Arvi (Kai Arvi)' },
      { option: 'azola', label: 'Azola' },
      { option: 'martyn_bailey', label: 'Martyn Bailey' },
      { option: 'bellafina', label: 'Bellafina' },
      { option: 'jeff_bollbach', label: 'Jeff Bollbach' },
      { option: 'bsx_bass', label: 'BSX Bass' },
      { option: 'bryant_basses_paul_bryant', label: 'Bryant Basses (Paul Bryant)' },
      { option: 'andrew_carruthers', label: 'Andrew Carruthers' },
      { option: 'cecilio', label: 'Cecilio' },
      { option: 'christopher', label: 'Christopher' },
      { option: 'clevinger', label: 'Clevinger' },
      { option: 'concord', label: 'Concord' },
      { option: 'james_condino', label: 'James Condino' },
      { option: 'cremona', label: 'Cremona' },
      { option: 'czech_ease', label: 'Czech-Ease' },
      { option: 'dean', label: 'Dean' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'e_h_roth', label: 'E.H. Roth' },
      { option: 'peter_elias', label: 'Peter Elias' },
      { option: 'eminence', label: 'Eminence' },
      { option: 'engelhardt', label: 'Engelhardt' },
      { option: 'luciano_golia', label: 'Luciano Golia' },
      { option: 'gotz', label: 'Götz' },
      { option: 'grisales_giorgio_grisales', label: 'Grisales (Giorgio Grisales)' },
      { option: 'horst_grunert', label: 'Horst Grünert' },
      { option: 'hallstatt', label: 'Hallstatt' },
      { option: 'hawkes_son', label: 'Hawkes & Son' },
      { option: 'neal_heppleston', label: 'Neal Heppleston' },
      { option: 'herold', label: 'Herold' },
      { option: 'h_luger', label: 'H. Luger' },
      { option: 'hofner', label: 'Höfner' },
      { option: 'hora', label: 'Hora' },
      { option: 'howard_core', label: 'Howard Core' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'jakstadt', label: 'Jakstadt' },
      { option: 'jay_haide', label: 'Jay Haide' },
      { option: 'jean_auray', label: 'Jean Auray' },
      { option: 'juzek', label: 'Juzek' },
      { option: 'kay', label: 'Kay' },
      { option: 'seth_kimmel', label: 'Seth Kimmel' },
      { option: 'king_doublebass_kd_flat', label: 'King Doublebass (KDB)' },
      { option: 'king_mortone_h_n_white', label: 'King Mortone (H.N. White)' },
      { option: 'knilling', label: 'Knilling' },
      { option: 'kolstein', label: 'Kolstein' },
      { option: 'jed_kriegel', label: 'Jed Kriegel' },
      { option: 'anton_krutz', label: 'Anton Krutz' },
      { option: 'krutz_kc_strings', label: 'Krutz (KC Strings)' },
      { option: 'william_harris_lee', label: 'William Harris Lee' },
      { option: 'nick_lloyd', label: 'Nick Lloyd' },
      { option: 'maple_leaf_strings', label: 'Maple Leaf Strings' },
      { option: 'martin_thomas_george_martin', label: 'Martin (Thomas & George Martin)' },
      { option: 'hannah_mayne', label: 'Hannah Mayne' },
      { option: 'gael_mckeon', label: 'Gael McKeon' },
      { option: 'eberhard_meinel', label: 'Eberhard Meinel' },
      { option: 'merano', label: 'Merano' },
      { option: 'musima', label: 'Musima' },
      { option: 'new_standard', label: 'New Standard' },
      { option: 'ns_design_ned_steinberger', label: 'NS Design (Ned Steinberger)' },
      { option: 'paesold', label: 'Paesold' },
      { option: 'palatino', label: 'Palatino' },
      { option: 'pfretzschner', label: 'Pfretzschner' },
      { option: 'pollmann', label: 'Pöllmann' },
      { option: 'abraham_prescott', label: 'Abraham Prescott' },
      { option: 'benedict_puglisi', label: 'Benedict Puglisi' },
      { option: 'oliver_radke', label: 'Oliver Radke' },
      { option: 'rata', label: 'Rata' },
      { option: 'rubner', label: 'Rubner' },
      { option: 'sergio_scaramelli', label: 'Sergio Scaramelli' },
      { option: 'scherl_roth', label: 'Scherl & Roth' },
      { option: 'lothar_semmlinger', label: 'Lothar Semmlinger' },
      { option: 'samuel_shen', label: 'Samuel Shen' },
      { option: 'ren_wei_shi', label: 'Ren Wei Shi' },
      { option: 'silver_creek', label: 'Silver Creek' },
      { option: 'andrea_spada', label: 'Andrea Spada' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'stentor', label: 'Stentor' },
      { option: 'bjorn_stoll', label: 'Björn Stoll' },
      { option: 'strobel', label: 'Strobel' },
      { option: 'strunal', label: 'Strunal' },
      { option: 'xuechang_sun', label: 'Xuechang Sun' },
      { option: 'tetsu_suzuki', label: 'Tetsu Suzuki' },
      { option: 'upton_bass', label: 'Upton Bass' },
      { option: 'wan_bernadel', label: 'Wan Bernadel' },
      { option: 'wilfer', label: 'Wilfer' },
      { option: 'calin_wultur', label: 'Calin Wultur' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_double_bass',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bowed_strings_double_bass_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acoustic', label: 'Acoustic' },
      { option: 'acoustic_electric', label: 'Acoustic-Electric' },
      { option: 'electric_upright_eub', label: 'Electric Upright (EUB)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_double_bass',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_double_bass_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '1_8', label: '1/8' },
      { option: '1_4', label: '1/4' },
      { option: '1_2', label: '1/2' },
      { option: '5_8', label: '5/8' },
      { option: '3_4', label: '3/4' },
      { option: '7_8', label: '7/8' },
      { option: '4_4', label: '4/4' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_double_bass',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_double_bass_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'laminated_plywood', label: 'Laminated (Plywood)' },
      { option: 'hybrid_solid_top_laminated_back_sides', label: 'Hybrid (Solid Top / Laminated Back & Sides)' },
      { option: 'fully_carved_solid_wood', label: 'Fully Carved (Solid Wood)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_double_bass',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Construction',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_double_bass_back_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'round_carved_back', label: 'Round (Carved) Back' },
      { option: 'flat_back', label: 'Flat Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_double_bass',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_double_bass_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_double_bass',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_bowed_psaltery_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'appalachian_strings_thomas_cook', label: 'Appalachian Strings (Thomas Cook)' },
      { option: 'cedar_creek_dulcimers', label: 'Cedar Creek Dulcimers' },
      { option: 'folkcraft_instruments', label: 'Folkcraft Instruments' },
      { option: 'hora', label: 'HORA' },
      { option: 'james_jones_instruments', label: 'James Jones Instruments' },
      { option: 'june_apple_dulcimers_gotzmer_family', label: 'June Apple Dulcimers (Gotzmer Family)' },
      { option: 'kelischek_workshop', label: 'Kelischek Workshop' },
      { option: 'michael_j_king', label: 'Michael J King' },
      { option: 'lark_in_the_morning', label: 'Lark in the Morning' },
      { option: 'master_works_russell_cook', label: 'Master Works (Russell Cook)' },
      { option: 'mid_east', label: 'Mid-East' },
      { option: 'mountain_made_music_bill_berg', label: 'Mountain Made Music (Bill Berg)' },
      { option: 'musicmakers', label: 'Musicmakers' },
      { option: 'phantasy_psalteries_eric_meier', label: 'Phantasy Psalteries (Eric Meier)' },
      { option: 'ringing_strings_rick_long', label: 'Ringing Strings (Rick Long)' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'sangineto', label: 'Sangineto' },
      { option: 'song_of_the_wood_jerry_read_smith', label: 'Song of the Wood (Jerry Read Smith)' },
      { option: 'spring_creek_psalteries_terry_sharon_kirby', label: 'Spring Creek Psalteries (Terry & Sharon Kirby)' },
      { option: 'tk_o_brien_s_craggy_mountain_music', label: 'TK O\'Brien\'s (Craggy Mountain Music)' },
      { option: 'unicorn_strings_gene_jessica_jaeger', label: 'Unicorn Strings (Gene & Jessica Jaeger)' },
      { option: 'westman_instruments_tish_greg_westman', label: 'Westman Instruments (Tish & Greg Westman)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_bowed_psaltery',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bowed_strings_bowed_psaltery_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'soprano', label: 'Soprano' },
      { option: 'alto', label: 'Alto' },
      { option: 'tenor', label: 'Tenor' },
      { option: 'baritone', label: 'Baritone' },
      { option: 'bass', label: 'Bass' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_bowed_psaltery',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_bowed_psaltery_scale_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'chromatic', label: 'Chromatic' },
      { option: 'diatonic', label: 'Diatonic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_bowed_psaltery',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_bowed_psaltery_soundboard',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'redwood', label: 'Redwood' },
      { option: 'maple', label: 'Maple' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'birch_plywood', label: 'Birch Plywood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_bowed_psaltery',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Soundboard Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Soundboard Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Soundboard Material',
      isRequired: false,
    },
  },
  {
    key: 'harp_lever_celtic_harp_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'another_era', label: 'Another Era' },
      { option: 'ardival', label: 'Ardival' },
      { option: 'argent_fox', label: 'Argent Fox' },
      { option: 'backyard_music_fireside_harp', label: 'Backyard Music (Fireside Harp)' },
      { option: 'blevins', label: 'Blevins' },
      { option: 'camac', label: 'Camac' },
      { option: 'caswell', label: 'Caswell' },
      { option: 'clark_irish_harps', label: 'Clark Irish Harps' },
      { option: 'clive_morley_harps', label: 'Clive Morley Harps' },
      { option: 'dusty_strings', label: 'Dusty Strings' },
      { option: 'john_egan', label: 'John Egan' },
      { option: 'eriu_harps', label: 'Eriu Harps' },
      { option: 'fisher_harps', label: 'Fisher Harps' },
      { option: 'harps_of_lorien', label: 'Harps of Lorien' },
      { option: 'harpy_harps', label: 'Harpy Harps' },
      { option: 'harrington', label: 'Harrington' },
      { option: 'harrison', label: 'Harrison' },
      { option: 'heartland_harps', label: 'Heartland Harps' },
      { option: 'james_jones', label: 'James Jones' },
      { option: 'david_kortier', label: 'David Kortier' },
      { option: 'lewis_creek', label: 'Lewis Creek' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'marini_made', label: 'Marini Made' },
      { option: 'mcneela', label: 'McNeela' },
      { option: 'mikel_harps', label: 'Mikel Harps' },
      { option: 'mountain_glen', label: 'Mountain Glen' },
      { option: 'musicmakers', label: 'Musicmakers' },
      { option: 'noteworthy', label: 'Noteworthy' },
      { option: 'october_mountain', label: 'October Mountain' },
      { option: 'pilgrim', label: 'Pilgrim' },
      { option: 'rees_harps_harpsicle', label: 'Rees Harps (Harpsicle)' },
      { option: 'resonance_harps', label: 'Resonance Harps' },
      { option: 'roosebeck_mid_east', label: 'Roosebeck (Mid-East)' },
      { option: 'salvi', label: 'Salvi' },
      { option: 'sandpiper', label: 'Sandpiper' },
      { option: 'sligo_harps_rick_kemper', label: 'Sligo Harps (Rick Kemper)' },
      { option: 'starfish', label: 'Starfish' },
      { option: 'steen', label: 'Steen' },
      { option: 'stoney_end', label: 'Stoney End' },
      { option: 'davy_stuart', label: 'Davy Stuart' },
      { option: 'teifi', label: 'Teifi' },
      { option: 'thormahlen', label: 'Thormahlen' },
      { option: 'triplett', label: 'Triplett' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'turmennan', label: 'Turmennan' },
      { option: 'vavra', label: 'Vavra' },
      { option: 'wind_river', label: 'Wind River' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_lever_celtic_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'harp_lever_celtic_harp_string_number',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '12', label: '12' },
      { option: '15', label: '15' },
      { option: '19', label: '19' },
      { option: '22', label: '22' },
      { option: '24', label: '24' },
      { option: '26', label: '26' },
      { option: '27', label: '27' },
      { option: '29', label: '29' },
      { option: '30', label: '30' },
      { option: '32', label: '32' },
      { option: '34', label: '34' },
      { option: '36', label: '36' },
      { option: '38', label: '38' },
      { option: '40', label: '40' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_lever_celtic_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'harp_lever_celtic_harp_string_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'nylon', label: 'Nylon' },
      { option: 'gut_natural_gut', label: 'Gut (Natural Gut)' },
      { option: 'fluorocarbon_carbon', label: 'Fluorocarbon / Carbon' },
      { option: 'wire_metal', label: 'Wire (Metal)' },
      { option: 'nylgut_synthetic_gut', label: 'Nylgut / Synthetic Gut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_lever_celtic_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'String Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'String Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'String Material',
      isRequired: false,
    },
  },
  {
    key: 'harp_lever_celtic_harp_lever',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'camac', label: 'Camac' },
      { option: 'loveland', label: 'Loveland' },
      { option: 'truitt', label: 'Truitt' },
      { option: 'rees', label: 'Rees' },
      { option: 'none_no_levers', label: 'None (No Levers)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_lever_celtic_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Lever Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Lever Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Lever Type',
      isRequired: false,
    },
  },
  {
    key: 'harp_lever_celtic_harp_body',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'bubinga', label: 'Bubinga' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'birch_laminate', label: 'Birch (Laminate)' },
      { option: 'ash', label: 'Ash' },
      { option: 'beech', label: 'Beech' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'rosewood_sheesham', label: 'Rosewood / Sheesham' },
      { option: 'koa', label: 'Koa' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'spruce', label: 'Spruce' },
      { option: 'poplar', label: 'Poplar' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_lever_celtic_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body',
      isRequired: false,
    },
  },
  {
    key: 'harp_pedal_harp_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aoyama', label: 'Aoyama' },
      { option: 'camac', label: 'Camac' },
      { option: 'clark', label: 'Clark' },
      { option: 'david', label: 'David' },
      { option: 'erat', label: 'Erat' },
      { option: 'erard', label: 'Érard' },
      { option: 'grosjean', label: 'Grosjean' },
      { option: 'heartland_harps', label: 'Heartland Harps' },
      { option: 'hochbrucker', label: 'Hochbrucker' },
      { option: 'horngacher', label: 'Horngacher' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'j_g_morley', label: 'J.G. Morley' },
      { option: 'naderman', label: 'Naderman' },
      { option: 'obermayer', label: 'Obermayer' },
      { option: 'pilgrim', label: 'Pilgrim' },
      { option: 'pleyel', label: 'Pleyel' },
      { option: 'resonance', label: 'Resonance' },
      { option: 'salvi', label: 'Salvi' },
      { option: 'simson', label: 'Simson' },
      { option: 'swanson', label: 'Swanson' },
      { option: 'thurau', label: 'Thurau' },
      { option: 'venus', label: 'Venus' },
      { option: 'wurlitzer', label: 'Wurlitzer' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_pedal_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'harp_pedal_harp_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'concert_grand_47_strings', label: 'Concert Grand (47 strings)' },
      { option: 'semi_grand_46_47_strings', label: 'Semi-Grand (46–47 strings)' },
      { option: 'grand_petite_44_strings', label: 'Grand Petite (44 strings)' },
      { option: 'petite_40_strings', label: 'Petite (40 strings)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_pedal_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size/Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size/Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size/Type',
      isRequired: false,
    },
  },
  {
    key: 'harp_pedal_harp_profile',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'extended', label: 'Extended' },
      { option: 'straight', label: 'Straight' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_pedal_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Soundboard Profile',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Soundboard Profile',
      isDetail: true,
    },
    saveConfig: {
      label: 'Soundboard Profile',
      isRequired: false,
    },
  },
  {
    key: 'harp_pedal_harp_finish',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'natural_maple', label: 'Natural Maple' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'black', label: 'Black' },
      { option: 'red', label: 'Red' },
      { option: 'blue', label: 'Blue' },
      { option: 'gold_leaf', label: 'Gold Leaf' },
      { option: 'bronze', label: 'Bronze' },
      { option: 'custom_special_color', label: 'Custom / Special Color' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'harp_pedal_harp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Finish',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Finish',
      isDetail: true,
    },
    saveConfig: {
      label: 'Finish',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_hammered_dulcimer_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'william_berg', label: 'William Berg' },
      { option: 'doug_berch', label: 'Doug Berch' },
      { option: 'nicholas_blanton', label: 'Nicholas Blanton' },
      { option: 'cloud_nine_michael_allen', label: 'Cloud Nine (Michael Allen)' },
      { option: 'david_s_dulcimers_david_lindsey', label: 'David\'s Dulcimers (David Lindsey)' },
      { option: 'the_dulcimer_factory_john_shirley_naylor', label: 'The Dulcimer Factory (John & Shirley Naylor)' },
      { option: 'dusty_strings', label: 'Dusty Strings' },
      { option: 'jake_s_cabin_dulcimers_jim_newman', label: 'Jake\'s Cabin Dulcimers (Jim Newman)' },
      { option: 'james_jones', label: 'James Jones' },
      { option: 'masterworks_russell_cook', label: 'MasterWorks (Russell Cook)' },
      { option: 'musicmakers_harpkit', label: 'Musicmakers (Harpkit)' },
      { option: 'sam_rizzetta', label: 'Sam Rizzetta' },
      { option: 'roosebeck_emi', label: 'Roosebeck (EMI)' },
      { option: 'round_dulcimers', label: 'Round Dulcimers' },
      { option: 'sala_muzik', label: 'Sala Muzik' },
      { option: 'jerry_read_smith_song_of_the_wood', label: 'Jerry Read Smith (Song of the Wood)' },
      { option: 'songbird_dulcimers_chris_foss', label: 'Songbird Dulcimers (Chris Foss)' },
      { option: 'lee_spears', label: 'Lee Spears' },
      { option: 'string_fever_music', label: 'String Fever Music' },
      { option: 'bob_tack', label: 'Bob Tack' },
      { option: 'rick_thum', label: 'Rick Thum' },
      { option: 'tk_o_brien_s', label: 'TK O\'Brien\'s' },
      { option: 'triskelion_tom_swyney', label: 'Triskelion (Tom Swyney)' },
      { option: 'bill_webster', label: 'Bill Webster' },
      { option: 'whamdiddle_rick_fogel', label: 'Whamdiddle (Rick Fogel)' },
      { option: 'wild_meadow_dulcimers_dale_penrose', label: 'Wild Meadow Dulcimers (Dale Penrose)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_hammered_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'dulcimer_hammered_dulcimer_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '12_11', label: '12/11' },
      { option: '13_12', label: '13/12' },
      { option: '14_13', label: '14/13' },
      { option: '15_14', label: '15/14' },
      { option: '16_15', label: '16/15' },
      { option: '17_16', label: '17/16' },
      { option: '19_18', label: '19/18' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_hammered_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size (Courses)',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size (Courses)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size (Courses)',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_hammered_dulcimer_tuning',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'diatonic_traditional', label: 'Diatonic (Traditional)' },
      { option: 'chromatic_5th_interval', label: 'Chromatic (5th Interval)' },
      { option: 'chromatic_piano_layout', label: 'Chromatic (Piano Layout)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_hammered_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning Type',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_hammered_dulcimer_soundboard',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'laminated_birch_plywood', label: 'Laminated Birch / Plywood' },
      { option: 'solid_sapele', label: 'Solid Sapele' },
      { option: 'solid_mahogany', label: 'Solid Mahogany' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'solid_redwood', label: 'Solid Redwood' },
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'carbon_fiber_composite', label: 'Carbon Fiber / Composite' },
      { option: 'other_solid_wood', label: 'Other Solid Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_hammered_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Soundboard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Soundboard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Soundboard',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_hammered_dulcimer_features',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'dampers', label: 'Dampers' },
      { option: 'pickup_transducer', label: 'Pickup / Transducer' },
      { option: 'super_bass_bridge_s', label: 'Super Bass Bridge(s)' },
      { option: 'floating_soundboard', label: 'Floating Soundboard' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_hammered_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Features',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Features',
      isDetail: true,
    },
    saveConfig: {
      label: 'Features',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_lap_mountain_dulcimer_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'jethro_amburgey', label: 'Jethro Amburgey' },
      { option: 'apple_creek', label: 'Apple Creek' },
      { option: 'backyard_music', label: 'Backyard Music' },
      { option: 'fred_battershell', label: 'Fred Battershell' },
      { option: 'doug_berch', label: 'Doug Berch' },
      { option: 'bill_berg_mountain_made_music', label: 'Bill Berg (Mountain Made Music)' },
      { option: 'black_mountain', label: 'Black Mountain' },
      { option: 'blue_lion', label: 'Blue Lion' },
      { option: 'david_brunner', label: 'David Brunner' },
      { option: 'sam_carrell', label: 'Sam Carrell' },
      { option: 'cedar_creek', label: 'Cedar Creek' },
      { option: 'mike_clemmer', label: 'Mike Clemmer' },
      { option: 'cripple_creek', label: 'Cripple Creek' },
      { option: 'dan_daniels_new_traditions', label: 'Dan Daniels (New Traditions)' },
      { option: 'dulcimore_dan_dan_cox', label: 'Dulcimore Dan (Dan Cox)' },
      { option: 'e_dale_eckard', label: 'E. Dale Eckard' },
      { option: 'ron_ewing', label: 'Ron Ewing' },
      { option: 'folkcraft', label: 'Folkcraft' },
      { option: 'folkroots', label: 'FolkRoots' },
      { option: 'gary_gallier', label: 'Gary Gallier' },
      { option: 'bob_gerard', label: 'Bob Gerard' },
      { option: 'gerry_heinrich', label: 'Gerry Heinrich' },
      { option: 'stanley_hicks', label: 'Stanley Hicks' },
      { option: 'hora', label: 'HORA' },
      { option: 'a_w_jeffreys', label: 'A.W. Jeffreys' },
      { option: 'jeffrey_lambert', label: 'Jeffrey Lambert' },
      { option: 'curt_lane_big_daddy_lane', label: 'Curt Lane (Big Daddy Lane)' },
      { option: 'joellen_lapidus', label: 'Joellen Lapidus' },
      { option: 'homer_ledford', label: 'Homer Ledford' },
      { option: 'david_harpmaker_lynch_sweet_woods', label: 'David "Harpmaker" Lynch (Sweet Woods)' },
      { option: 'bob_magowan', label: 'Bob Magowan' },
      { option: 'edsel_martin', label: 'Edsel Martin' },
      { option: 'fred_martin', label: 'Fred Martin' },
      { option: 'warren_a_may', label: 'Warren A. May' },
      { option: 'terry_mccafferty', label: 'Terry McCafferty' },
      { option: 'mcnally_strumstick', label: 'McNally (Strumstick)' },
      { option: 'mcspadden', label: 'McSpadden' },
      { option: 'modern_mountain_dulcimer', label: 'Modern Mountain Dulcimer' },
      { option: 'musicmakers', label: 'Musicmakers' },
      { option: 'new_harmony_rick_gooden', label: 'New Harmony (Rick Gooden)' },
      { option: 'tk_o_brien', label: 'TK O\'Brien' },
      { option: 'don_pedi', label: 'Don Pedi' },
      { option: 'dale_penrose_wild_meadow', label: 'Dale Penrose (Wild Meadow)' },
      { option: 'edd_presnell', label: 'Edd Presnell' },
      { option: 'frank_proffitt', label: 'Frank Proffitt' },
      { option: 'red_kite', label: 'Red Kite' },
      { option: 'arthur_rob_flat', label: 'Arthur Robb' },
      { option: 'jerry_rockwell_j_c_rockwell', label: 'Jerry Rockwell (J.C. Rockwell)' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'seagull_merlin', label: 'Seagull (Merlin)' },
      { option: 'robert_stephens_stephens_lutherie', label: 'Robert Stephens (Stephens Lutherie)' },
      { option: 'stoney_end', label: 'Stoney End' },
      { option: 'bristol_taylor', label: 'Bristol Taylor' },
      { option: 'j_edward_thomas', label: 'J. Edward Thomas' },
      { option: 'john_jacob_thomas', label: 'John Jacob Thomas' },
      { option: 'tony_vines', label: 'Tony Vines' },
      { option: 'walnut_valley', label: 'Walnut Valley' },
      { option: 'dwain_wilder_bear_meadow', label: 'Dwain Wilder (Bear Meadow)' },
      { option: 'keith_young', label: 'Keith Young' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_lap_mountain_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'dulcimer_lap_mountain_dulcimer_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'standard', label: 'Standard' },
      { option: 'baritone', label: 'Baritone' },
      { option: 'bass', label: 'Bass' },
      { option: 'dulcimette_soprano', label: 'Dulcimette / Soprano' },
      { option: 'courting_double_fretboard', label: 'Courting (Double Fretboard)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_lap_mountain_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type / Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type / Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type / Size',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_lap_mountain_dulcimer_strings_number',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3', label: '3' },
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_lap_mountain_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_lap_mountain_dulcimer_shape',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'hourglass', label: 'Hourglass' },
      { option: 'teardrop', label: 'Teardrop' },
      { option: 'elliptical_galax_style', label: 'Elliptical (Galax-Style)' },
      { option: 'rectangular_box', label: 'Rectangular / Box' },
      { option: 'violin_fiddle', label: 'Violin / Fiddle' },
      { option: 'trapezoid', label: 'Trapezoid' },
      { option: 'aorell_hourdrop', label: 'Aorell / Hourdrop' },
      { option: 'guitar_style', label: 'Guitar-Style' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_lap_mountain_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Shape (SS)',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Shape (SS)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Shape (SS)',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_lap_mountain_dulcimer_fret_pattern',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'diatonic_standard_with_61_2_fret', label: 'Diatonic (Standard with 6½ Fret)' },
      { option: 'diatonic_no_extra_frets', label: 'Diatonic (No Extra Frets)' },
      { option: 'diatonic_extended_11_2_61_2_81_2_131_2', label: 'Diatonic (Extended — 1½, 6½, 8½, 13½)' },
      { option: 'chromatic', label: 'Chromatic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_lap_mountain_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Fret Pattern',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Fret Pattern',
      isDetail: true,
    },
    saveConfig: {
      label: 'Fret Pattern',
      isRequired: false,
    },
  },
  {
    key: 'dulcimer_lap_mountain_dulcimer_soundboard',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'maple', label: 'Maple' },
      { option: 'butternut', label: 'Butternut' },
      { option: 'redwood', label: 'Redwood' },
      { option: 'poplar_tulip_poplar', label: 'Poplar/Tulip Poplar' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'koa', label: 'Koa' },
      { option: 'sycamore', label: 'Sycamore' },
      { option: 'birch_laminate', label: 'Birch (Laminate)' },
      { option: 'other_solid_wood', label: 'Other Solid Wood' },
      { option: 'other_laminate', label: 'Other Laminate' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'dulcimer_lap_mountain_dulcimer',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Soundboard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Soundboard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Soundboard',
      isRequired: false,
    },
  },
  {
    key: 'zither_autoharp_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'arkansas_autoharps_chuck_daniels', label: 'Arkansas Autoharps (Chuck Daniels)' },
      { option: 'ashbury', label: 'Ashbury' },
      { option: 'autoharp_works_bob_lewis', label: 'Autoharp Works (Bob Lewis)' },
      { option: 'blue_ridge_autoharps_john_hollandsworth', label: 'Blue Ridge Autoharps (John Hollandsworth)' },
      { option: 'c_f_zimmermann', label: 'C.F. Zimmermann' },
      { option: 'ray_choi_song_bird_autoharps', label: 'Ray Choi (Song Bird Autoharps)' },
      { option: 'chromaharp_rhythm_band', label: 'Chromaharp (Rhythm Band)' },
      { option: 'paul_cram_harmony_harps', label: 'Paul Cram (Harmony Harps)' },
      { option: 'd_aigle_autoharps_pete_d_aigle', label: 'd\'Aigle Autoharps (Pete d\'Aigle)' },
      { option: 'evoharp_evo_bluestein', label: 'EvoHarp (Evo Bluestein)' },
      { option: 'fisher_instruments_warren_fisher', label: 'Fisher Instruments (Warren Fisher)' },
      { option: 'fladmark_woodworks_tom_fladmark', label: 'Fladmark Woodworks (Tom Fladmark)' },
      { option: 'framus', label: 'Framus' },
      { option: 'fret_or_fret_not', label: 'Fret Or Fret Not' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'goose_acres', label: 'Goose Acres' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'harp_doctor_jeff_dantzler', label: 'Harp Doctor (Jeff Dantzler)' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'jubel_tone', label: 'Jubel Tone' },
      { option: 'lumbert_mountain_music_buck_lumbert', label: 'Lumbert Mountain Music (Buck Lumbert)' },
      { option: 'musima', label: 'Musima' },
      { option: 'orthey_instruments_george_orthey', label: 'Orthey Instruments (George Orthey)' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'samick', label: 'Samick' },
      { option: 'schoenhut', label: 'Schoenhut' },
      { option: 'schreiber_autoharps_greg_schreiber', label: 'Schreiber Autoharps (Greg Schreiber)' },
      { option: 'silvertone', label: 'Silvertone' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'timbre_line_music_mitch_pingel', label: 'Timbre Line Music (Mitch Pingel)' },
      { option: 'whippoorwill_acoustics_ken_ellis', label: 'Whippoorwill Acoustics (Ken Ellis)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'zither_autoharp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'zither_autoharp_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'chromatic', label: 'Chromatic' },
      { option: 'diatonic_single_key', label: 'Diatonic (Single-Key)' },
      { option: 'diatonic_two_key', label: 'Diatonic (Two-Key)' },
      { option: 'diatonic_three_key', label: 'Diatonic (Three-Key)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'zither_autoharp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'zither_autoharp_chord_bars',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '12', label: '12' },
      { option: '15', label: '15' },
      { option: '21', label: '21' },
      { option: '27', label: '27+' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'zither_autoharp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Chord Bars',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Chord Bars',
      isDetail: true,
    },
    saveConfig: {
      label: 'Chord Bars',
      isRequired: false,
    },
  },
  {
    key: 'zither_autoharp_strings_number',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '36', label: '36' },
      { option: '37', label: '37' },
      { option: '43', label: '43' },
      { option: '47_48', label: '47-48' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'zither_autoharp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'abasi', label: 'ABASI' },
      { option: 'peter_abnett', label: 'Peter Abnett' },
      { option: 'alden', label: 'Alden' },
      { option: 'sakis_anastasiadis', label: 'Sakis Anastasiadis' },
      { option: 'john_bailey', label: 'John Bailey' },
      { option: 'barnes_mullins', label: 'Barnes & Mullins' },
      { option: 'tom_buchanan', label: 'Tom Buchanan' },
      { option: 'paddy_burgin', label: 'Paddy Burgin' },
      { option: 'antonio_carvalho', label: 'Antonio Carvalho' },
      { option: 'p_w_crump', label: 'P.W. Crump' },
      { option: 'd_rickert_donald_rickert', label: 'D. Rickert (Donald Rickert)' },
      { option: 'david_dart', label: 'David Dart' },
      { option: 'defotoe', label: 'DeFotoe' },
      { option: 'kostas_dekavalas', label: 'Kostas Dekavalas' },
      { option: 'paul_doyle', label: 'Paul Doyle' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'erin_steve_agnew', label: 'Erin (Steve Agnew)' },
      { option: 'martin_faherty', label: 'Martin Faherty' },
      { option: 'fender', label: 'Fender' },
      { option: 'flatiron', label: 'Flatiron' },
      { option: 'joe_foley', label: 'Joe Foley' },
      { option: 'dave_freshwater', label: 'Dave Freshwater' },
      { option: 'nikos_fronimopoulos', label: 'Nikos Fronimopoulos' },
      { option: 'fylde_roger_bucknall', label: 'Fylde (Roger Bucknall)' },
      { option: 'bob_gernandt', label: 'Bob Gernandt' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'ged_green', label: 'Ged Green' },
      { option: 'paul_hathway', label: 'Paul Hathway' },
      { option: 'heartland_muzikkon', label: 'Heartland (Muzikkon)' },
      { option: 'hora', label: 'Hora' },
      { option: 'j_j_ryan_jill_ryan', label: 'J.J. Ryan (Jill Ryan)' },
      { option: 'johnson', label: 'Johnson' },
      { option: 'k_yairi', label: 'K. Yairi' },
      { option: 'panayiotis_kafetzopoulos', label: 'Panayiotis Kafetzopoulos' },
      { option: 'george_karellas', label: 'George Karellas' },
      { option: 'tasos_katsifis', label: 'Tasos Katsifis' },
      { option: 'kennaquhair_douglas_dieter', label: 'Kennaquhair (Douglas Dieter)' },
      { option: 'david_kilpatrick', label: 'David Kilpatrick' },
      { option: 'koda', label: 'Koda' },
      { option: 'kopeliadis', label: 'Kopeliadis' },
      { option: 'caroline_lacombe', label: 'Caroline Lacombe' },
      { option: 'chris_larkin', label: 'Chris Larkin' },
      { option: 'luna_guitars', label: 'Luna Guitars' },
      { option: 'andy_manson', label: 'Andy Manson' },
      { option: 'matsikas', label: 'Matsikas' },
      { option: 'mcneela', label: 'McNeela' },
      { option: 'moon_guitars_jimmy_moon', label: 'Moon Guitars (Jimmy Moon)' },
      { option: 'jan_mulyeart', label: 'Jan Mulyeart' },
      { option: 'gary_nava', label: 'Gary Nava' },
      { option: 'derek_nelson_danvel_instruments', label: 'Derek Nelson (Danvel Instruments)' },
      { option: 'nk_forster_nigel_forster', label: 'NK Forster (Nigel Forster)' },
      { option: 'lawrence_nyberg', label: 'Lawrence Nyberg' },
      { option: 'richard_osborne', label: 'Richard Osborne' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'isidoros_andreas_papadamou', label: 'Isidoros & Andreas Papadamou' },
      { option: 'bill_petersen', label: 'Bill Petersen' },
      { option: 'dimitrios_polydorou_the_bouzouki_shop', label: 'Dimitrios Polydorou (The Bouzouki Shop)' },
      { option: 'possum_hall_mac_ritchey', label: 'Possum Hall (Mac Ritchey)' },
      { option: 'dimitris_rapakousios', label: 'Dimitris Rapakousios' },
      { option: 'andrew_robinson', label: 'Andrew Robinson' },
      { option: 'seagull', label: 'Seagull' },
      { option: 'paul_shippey', label: 'Paul Shippey' },
      { option: 'siveen_instruments', label: 'Siveen Instruments' },
      { option: 'stefan_sobell', label: 'Stefan Sobell' },
      { option: 'mike_spicer', label: 'Mike Spicer' },
      { option: 'christos_spourdalakis', label: 'Christos Spourdalakis' },
      { option: 'stathopoulo_house_of', label: 'Stathopoulo (House of)' },
      { option: 'davy_stuart', label: 'Davy Stuart' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'frank_tate', label: 'Frank Tate' },
      { option: 'herb_taylor', label: 'Herb Taylor' },
      { option: 'zozef_terjivasian', label: 'Zozef Terjivasian' },
      { option: 'tasos_theodorakis', label: 'Tasos Theodorakis' },
      { option: 'tobin_instruments', label: 'Tobin Instruments' },
      { option: 'kai_tonjes', label: 'Kai Tönjes' },
      { option: 'trillium_robert_abrams', label: 'Trillium (Robert Abrams)' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'giannis_tsoulogiannis', label: 'Giannis Tsoulogiannis' },
      { option: 'ultan_walsh', label: 'Ultan Walsh' },
      { option: 'waltons', label: 'Waltons' },
      { option: 'weber', label: 'Weber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'strings_bouzouki_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'greek_trichordo_3_course_6_string', label: 'Greek Trichordo (3-Course / 6-String)' },
      { option: 'greek_tetrachordo_4_course_8_string', label: 'Greek Tetrachordo (4-Course / 8-String)' },
      { option: 'irish_celtic_4_course_8_string', label: 'Irish / Celtic (4-Course / 8-String)' },
      { option: '5_course_10_string', label: '5-Course / 10-String' },
      { option: 'electric_solid_body', label: 'Electric (Solid Body)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_top_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'flat_top', label: 'Flat Top' },
      { option: 'carved_arched_top', label: 'Carved / Arched Top' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top Construction',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_back_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'flat_back', label: 'Flat Back' },
      { option: 'three_piece_back', label: 'Three-Piece Back' },
      { option: 'staved_bowl_back', label: 'Staved / Bowl Back' },
      { option: 'carved_back', label: 'Carved Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Construction',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_scale_length',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'under_22', label: 'Under 22"' },
      { option: '22_to_23', label: '22" to 23"' },
      { option: '23_to_24', label: '23" to 24"' },
      { option: '24_to_25', label: '24" to 25"' },
      { option: '25_to_26', label: '25" to 26"' },
      { option: '26_to_27', label: '26" to 27"' },
      { option: 'over_27', label: 'Over 27"' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Length',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Length',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Length',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_bridge_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'fixed_glued', label: 'Fixed (Glued)' },
      { option: 'floating', label: 'Floating' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bridge Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bridge Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bridge Type',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'laminate_spruce', label: 'Laminate Spruce' },
      { option: 'laminate_cedar', label: 'Laminate Cedar' },
      { option: 'other_solid_wood', label: 'Other Solid Wood' },
      { option: 'other_laminate', label: 'Other Laminate' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'strings_bouzouki_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cherry_solid', label: 'Cherry (Solid)' },
      { option: 'koa_solid', label: 'Koa (Solid)' },
      { option: 'mahogany_solid', label: 'Mahogany (Solid)' },
      { option: 'mahogany_laminate', label: 'Mahogany (Laminate)' },
      { option: 'maple_solid', label: 'Maple (Solid)' },
      { option: 'maple_laminate', label: 'Maple (Laminate)' },
      { option: 'mulberry_solid', label: 'Mulberry (Solid)' },
      { option: 'rosewood_solid', label: 'Rosewood (Solid)' },
      { option: 'rosewood_laminate', label: 'Rosewood (Laminate)' },
      { option: 'walnut_solid', label: 'Walnut (Solid)' },
      { option: 'walnut_laminate', label: 'Walnut (Laminate)' },
      { option: 'other_solid_wood', label: 'Other Solid Wood' },
      { option: 'other_laminate', label: 'Other Laminate' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_bouzouki',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'strings_cittern_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'peter_abnett', label: 'Peter Abnett' },
      { option: 'nikos_apollonio', label: 'Nikos Apollonio' },
      { option: 'stephen_barber_sandi_harris', label: 'Stephen Barber & Sandi Harris' },
      { option: 'daniel_betsill', label: 'Daniel Betsill' },
      { option: 'james_bisgood', label: 'James Bisgood' },
      { option: 'bruce_brook', label: 'Bruce Brook' },
      { option: 'buchanan', label: 'Buchanan' },
      { option: 'paddy_burgin', label: 'Paddy Burgin' },
      { option: 'carlo_cecconi', label: 'Carlo Cecconi' },
      { option: 'peter_cox', label: 'Peter Cox' },
      { option: 'p_w_crump', label: 'P.W. Crump' },
      { option: 'd_rickert', label: 'D. Rickert' },
      { option: 'dammann_custom', label: 'Dammann Custom' },
      { option: 'defotoe', label: 'DeFotoe' },
      { option: 'bob_duffy', label: 'Bob Duffy' },
      { option: 'early_music_shop', label: 'Early Music Shop' },
      { option: 'arjen_everts', label: 'Arjen Everts' },
      { option: 'flatiron', label: 'Flatiron' },
      { option: 'joe_foley', label: 'Joe Foley' },
      { option: 'folkfriends', label: 'Folkfriends' },
      { option: 'peter_forrester', label: 'Peter Forrester' },
      { option: 'dave_freshwater', label: 'Dave Freshwater' },
      { option: 'fylde', label: 'Fylde' },
      { option: 'bob_gernandt', label: 'Bob Gernandt' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'paul_hathway', label: 'Paul Hathway' },
      { option: 'hora', label: 'Hora' },
      { option: 'james_jones', label: 'James Jones' },
      { option: 'don_kawlek', label: 'Don Kawlek' },
      { option: 'kr_strings_kilin_reece', label: 'KR Strings (Kilin Reece)' },
      { option: 'chris_larkin', label: 'Chris Larkin' },
      { option: 'gesina_liedmeier', label: 'Gesina Liedmeier' },
      { option: 'graham_mcdonald', label: 'Graham McDonald' },
      { option: 'christian_magdeleine_liutera', label: 'Christian Magdeleine (Liutera)' },
      { option: 'jimmy_moon_moon_guitars', label: 'Jimmy Moon (Moon Guitars)' },
      { option: 'nk_forster', label: 'NK Forster' },
      { option: 'sebastian_nunez', label: 'Sebastián Núñez' },
      { option: 'lawrence_nyberg_nyberg_instruments', label: 'Lawrence Nyberg (Nyberg Instruments)' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'paul_shippey', label: 'Paul Shippey' },
      { option: 'stefan_sobell', label: 'Stefan Sobell' },
      { option: 'george_stevens', label: 'George Stevens' },
      { option: 'davy_stuart', label: 'Davy Stuart' },
      { option: 'herb_taylor', label: 'Herb Taylor' },
      { option: 'trinity_college_saga', label: 'Trinity College (Saga)' },
      { option: 'troubadour_instruments', label: 'Troubadour Instruments' },
      { option: 'weber', label: 'Weber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_cittern',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'strings_cittern_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4_courses_8_strings', label: '4 Courses (8 Strings)' },
      { option: '5_courses_10_strings', label: '5 Courses (10 Strings)' },
      { option: '5_courses_9_strings', label: '5 Courses (9 Strings)' },
      { option: '6_courses_12_strings', label: '6 Courses (12 Strings)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_cittern',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'strings_cittern_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'archtop_carved', label: 'Archtop (Carved)' },
      { option: 'flat_top', label: 'Flat-Top' },
      { option: 'historical_period_reproduction', label: 'Historical / Period Reproduction' },
      { option: 'waldzither', label: 'Waldzither' },
      { option: 'guitar_bodied', label: 'Guitar-Bodied' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_cittern',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'strings_cittern_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'pine', label: 'Pine' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_cittern',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'strings_cittern_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'maple', label: 'Maple' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'koa', label: 'Koa' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_cittern',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'strings_cittern_scale_length',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'short_under_21', label: 'Short (Under 21")' },
      { option: 'medium_21_23_5', label: 'Medium (21"–23.5")' },
      { option: 'long_24_and_above', label: 'Long (24" and Above)' },
      { option: 'other_unknown', label: 'Other / Unknown' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'strings_cittern',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Length',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Length',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Length',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_bajo_quinto_sexto_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acosta', label: 'Acosta' },
      { option: 'candelas', label: 'Candelas' },
      { option: 'casa_cardiel_mario_cardiel', label: 'Casa Cardiel (Mario Cardiel)' },
      { option: 'cascabel', label: 'Cascabel' },
      { option: 'cordoba', label: 'Córdoba' },
      { option: 'cordovas', label: 'Cordovas' },
      { option: 'd_luca', label: 'D\'Luca' },
      { option: 'daniel', label: 'Daniel' },
      { option: 'del_rio', label: 'Del Rio' },
      { option: 'don_cortez', label: 'Don Cortez' },
      { option: 'don_pedro', label: 'Don Pedro' },
      { option: 'fender', label: 'Fender' },
      { option: 'fortaleza', label: 'Fortaleza' },
      { option: 'fraulini_todd_cambio', label: 'Fraulini (Todd Cambio)' },
      { option: 'gabbanelli', label: 'Gabbanelli' },
      { option: 'guerrero', label: 'Guerrero' },
      { option: 'h_jimenez_hilario_layo_jimenez', label: 'H. Jimenez (Hilario "Layo" Jimenez)' },
      { option: 'herrera', label: 'Herrera' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'lone_star', label: 'Lone Star' },
      { option: 'lucida', label: 'Lucida' },
      { option: 'macias', label: 'Macias' },
      { option: 'medher', label: 'Medher' },
      { option: 'navarro', label: 'Navarro' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'panuco', label: 'Panuco' },
      { option: 'paracho_elite', label: 'Paracho Elite' },
      { option: 'pasaye', label: 'Pasaye' },
      { option: 'rubens', label: 'Rubens' },
      { option: 'santiago', label: 'Santiago' },
      { option: 'jesus_sevillano', label: 'Jesús Sevillano' },
      { option: 'takamine', label: 'Takamine' },
      { option: 'taylor', label: 'Taylor' },
      { option: 'tetton', label: 'Tetton' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_bajo_quinto_sexto',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_bajo_quinto_sexto_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bajo_sexto_12_strings_6_courses', label: 'Bajo Sexto (12 Strings / 6 Courses)' },
      { option: 'bajo_quinto_10_strings_5_courses', label: 'Bajo Quinto (10 Strings / 5 Courses)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_bajo_quinto_sexto',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_bajo_quinto_sexto_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'full_body_non_cutaway', label: 'Full Body (Non-Cutaway)' },
      { option: 'single_cutaway_florentine', label: 'Single Cutaway (Florentine)' },
      { option: 'double_cutaway', label: 'Double Cutaway' },
      { option: 'thin_body_single_cutaway', label: 'Thin Body - Single Cutaway' },
      { option: 'thin_body_non_cutaway', label: 'Thin Body - Non-Cutaway' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_bajo_quinto_sexto',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_bajo_quinto_sexto_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cedar', label: 'Cedar' },
      { option: 'maple', label: 'Maple' },
      { option: 'pine', label: 'Pine' },
      { option: 'spruce_sitka', label: 'Spruce (Sitka)' },
      { option: 'spruce_adirondack', label: 'Spruce (Adirondack)' },
      { option: 'spruce_other_unspecified', label: 'Spruce (Other/Unspecified)' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'laminate_composite', label: 'Laminate / Composite' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_bajo_quinto_sexto',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_bajo_quinto_sexto_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'palo_escrito', label: 'Palo Escrito' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'ziricote', label: 'Ziricote' },
      { option: 'laminate_composite', label: 'Laminate / Composite' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_bajo_quinto_sexto',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_llanero_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'abraham_luthier', label: 'Abraham Luthier' },
      { option: 'jose_abreu_blanco', label: 'José Abreu Blanco' },
      { option: 'aileen_music', label: 'Aileen Music' },
      { option: 'apc', label: 'APC' },
      { option: 'rafael_arcia', label: 'Rafael Arcia' },
      { option: 'jose_baritto', label: 'José Baritto' },
      { option: 'carlos_becerra', label: 'Carlos Becerra' },
      { option: 'crispin_becerra', label: 'Crispín Becerra' },
      { option: 'ramon_blanco', label: 'Ramón Blanco' },
      { option: 'abrahan_blasco', label: 'Abrahán Blasco' },
      { option: 'roberto_bonaccorso', label: 'Roberto Bonaccorso' },
      { option: 'javier_jesus_camacaro', label: 'Javier Jesús Camacaro' },
      { option: 'miguel_castillo', label: 'Miguel Castillo' },
      { option: 'melvin_colina', label: 'Melvin Colina' },
      { option: 'yasuji_d_gucci', label: 'Yasuji D\'Gucci' },
      { option: 'gustavo_gomez', label: 'Gustavo Gómez' },
      { option: 'carlos_gonzalez', label: 'Carlos González' },
      { option: 'rafael_yuka_gonzalez', label: 'Rafael "Yuka" González' },
      { option: 'instrumentos_musicales_sermeno_richard_sermeno', label: 'Instrumentos Musicales Sermeño (Richard Sermeño)' },
      { option: 'claudio_lazcano', label: 'Claudio Lazcano' },
      { option: 'cosme_lopez', label: 'Cosme López' },
      { option: 'antonio_saulo_navarro', label: 'Antonio & Saulo Navarro' },
      { option: 'eladio_perez_chirinos', label: 'Eladio Pérez Chirinos' },
      { option: 'luis_ramirez', label: 'Luis Ramírez' },
      { option: 'edgar_ramirez_roa', label: 'Edgar Ramírez Roa' },
      { option: 'cesar_david_rojas_diaz', label: 'César David Rojas Díaz' },
      { option: 'alfonso_sandoval', label: 'Alfonso Sandoval' },
      { option: 'jorge_sandoval', label: 'Jorge Sandoval' },
      { option: 'thomann', label: 'Thomann' },
      { option: 'tucuatro', label: 'TuCuatro' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_llanero',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_cuatro_llanero_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: '8', label: '8' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_llanero',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_llanero_frets',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '14', label: '14' },
      { option: '15', label: '15' },
      { option: '17', label: '17' },
      { option: '19', label: '19' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_llanero',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Frets',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Frets',
      isDetail: true,
    },
    saveConfig: {
      label: 'Frets',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_llanero_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'solid_pine', label: 'Solid Pine' },
      { option: 'solid_kiri_paulownia', label: 'Solid Kiri (Paulownia)' },
      { option: 'laminate', label: 'Laminate' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_llanero',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_llanero_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bubinga', label: 'Bubinga' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'cypress', label: 'Cypress' },
      { option: 'jacaranda', label: 'Jacaranda' },
      { option: 'laminate', label: 'Laminate' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'rosewood_palo_santo', label: 'Rosewood (Palo Santo)' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'tulipwood', label: 'Tulipwood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'white_oak', label: 'White Oak' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_llanero',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_puertorriqueno_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'miguel_acevedo_flores', label: 'Miguel Acevedo Flores' },
      { option: 'rosendo_acosta', label: 'Rosendo Acosta' },
      { option: 'jaime_alicea', label: 'Jaime Alicea' },
      { option: 'apc', label: 'APC' },
      { option: 'rafael_aviles_vazquez', label: 'Rafael Avilés Vázquez' },
      { option: 'tito_baez', label: 'Tito Báez' },
      { option: 'carlos_barquero', label: 'Carlos Barquero' },
      { option: 'freddy_wilfredo_burgos_nieves', label: 'Freddy & Wilfredo Burgos Nieves' },
      { option: 'frank_cruz', label: 'Frank Cruz' },
      { option: 'aurelio_cruz_pagan', label: 'Aurelio Cruz Pagán' },
      { option: 'jose_cuevas', label: 'José Cuevas' },
      { option: 'william_cumpiano', label: 'William Cumpiano' },
      { option: 'william_del_pilar', label: 'William Del Pilar' },
      { option: 'efrain_figueroa_cabrera', label: 'Efraín Figueroa Cabrera' },
      { option: 'harmonia', label: 'Harmonia' },
      { option: 'manuel_henriquez_zapata', label: 'Manuel Henríquez Zapata' },
      { option: 'miguel_hernandez', label: 'Miguel Hernández' },
      { option: 'martin_marrero', label: 'Martín Marrero' },
      { option: 'carmelo_martell', label: 'Carmelo Martell' },
      { option: 'diomedes_yomi_matos', label: 'Diómedes "Yomi" Matos' },
      { option: 'pellin_medina', label: 'Pellín Medina' },
      { option: 'eugenio_mendez', label: 'Eugenio Méndez' },
      { option: 'miguel_mendez', label: 'Miguel Méndez' },
      { option: 'graciano_montesinos', label: 'Graciano Montesinos' },
      { option: 'roque_navarro', label: 'Roque Navarro' },
      { option: 'juan_olivera', label: 'Juan Olivera' },
      { option: 'leon_ortiz', label: 'León Ortiz' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'rosario_sayo_otero', label: 'Rosario "Sayo" Otero' },
      { option: 'maximo_elvin_perez', label: 'Máximo & Elvin Pérez' },
      { option: 'benjamin_phi_velazquez', label: 'Benjamín Phi Velázquez' },
      { option: 'juan_reyes_torres', label: 'Juan Reyes Torres' },
      { option: 'angel_luis_wimbo_rivera', label: 'Ángel Luis "Wimbo" Rivera' },
      { option: 'salustiano_rodriguez', label: 'Salustiano Rodríguez' },
      { option: 'manuel_rodriguez_feneque', label: 'Manuel Rodríguez Feneque' },
      { option: 'efrain_ronda', label: 'Efraín Ronda' },
      { option: 'cristobal_santiago', label: 'Cristóbal Santiago' },
      { option: 'jorge_santiago_mendoza', label: 'Jorge Santiago Mendoza' },
      { option: 'taller_del_cuatro', label: 'Taller del Cuatro' },
      { option: 'thomann', label: 'Thomann' },
      { option: 'natividad_tirado', label: 'Natividad Tirado' },
      { option: 'vicente_valentin_rivera', label: 'Vicente Valentín Rivera' },
      { option: 'candelario_vasquez', label: 'Candelario Vásquez' },
      { option: 'miguel_angel_guilin_vazquez', label: 'Miguel Ángel "Guilín" Vázquez' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_puertorriqueno',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_cuatro_puertorriqueno_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'soprano', label: 'Soprano' },
      { option: 'alto', label: 'Alto' },
      { option: 'tradicional_tenor', label: 'Tradicional (Tenor)' },
      { option: 'bajo_bass', label: 'Bajo (Bass)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_puertorriqueno',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_puertorriqueno_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aviolinado_violin_shaped', label: 'Aviolinado (Violin-Shaped)' },
      { option: 'antiguo_keyhole_cuadrao', label: 'Antiguo (Keyhole / Cuadrao)' },
      { option: 'tulipan', label: 'Tulipán' },
      { option: 'dos_puntos', label: 'Dos Puntos' },
      { option: 'higuera_gourd', label: 'Higuera (Gourd)' },
      { option: 'cutaway', label: 'Cutaway' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_puertorriqueno',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_puertorriqueno_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4_4_single_strings', label: '4 (4 single strings)' },
      { option: '8_4_courses_x_2', label: '8 (4 courses x 2)' },
      { option: '10_5_courses_x_2', label: '10 (5 courses x 2)' },
      { option: '12_6_courses_x_2', label: '12 (6 courses x 2)' },
      { option: '15_5_courses_x_3', label: '15 (5 courses x 3)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_puertorriqueno',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Strings',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_puertorriqueno_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'laminate', label: 'Laminate' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'solid_engelmann_spruce', label: 'Solid Engelmann Spruce' },
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'solid_pine', label: 'Solid Pine' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_puertorriqueno',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_cuatro_puertorriqueno_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acacia', label: 'Acacia' },
      { option: 'alamo_poplar', label: 'Álamo (Poplar)' },
      { option: 'algarrobo', label: 'Algarrobo' },
      { option: 'guaraguao', label: 'Guaraguao' },
      { option: 'laminate', label: 'Laminate' },
      { option: 'laurel', label: 'Laurel' },
      { option: 'maga', label: 'Maga' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_cuatro_puertorriqueno',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_guitarron_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aria', label: 'Aria' },
      { option: 'campos', label: 'Campos' },
      { option: 'candelas', label: 'Candelas' },
      { option: 'ernesto_caro', label: 'Ernesto Caro' },
      { option: 'caro_topete', label: 'Caro & Topete' },
      { option: 'david_caro_leonardo', label: 'David Caro Leonardo' },
      { option: 'salvador_castillo', label: 'Salvador Castillo' },
      { option: 'delgado_guitars', label: 'Delgado Guitars' },
      { option: 'don_cortez', label: 'Don Cortez' },
      { option: 'abel_garcia', label: 'Abel García' },
      { option: 'fernando_gonzalez', label: 'Fernando González' },
      { option: 'gabriel_hernandez', label: 'Gabriel Hernández' },
      { option: 'h_jimenez', label: 'H. Jiménez' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'benito_huipe', label: 'Benito Huipe' },
      { option: 'juan_carlos_huipe', label: 'Juan Carlos Huipe' },
      { option: 'indiana', label: 'Indiana' },
      { option: 'j_reynolds', label: 'J. Reynolds' },
      { option: 'lemus', label: 'Lemus' },
      { option: 'lucida', label: 'Lucida' },
      { option: 'morales', label: 'Morales' },
      { option: 'navarro', label: 'Navarro' },
      { option: 'paracho_elite', label: 'Paracho Elite' },
      { option: 'javier_pascual_jasso', label: 'Javier Pascual Jasso' },
      { option: 'german_vazquez_rubio', label: 'Germán Vázquez Rubio' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_guitarron',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_guitarron_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'full_size', label: 'Full Size' },
      { option: '3_4_size', label: '3/4 Size' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_guitarron',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_guitarron_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'basswood', label: 'Basswood' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'spruce', label: 'Spruce' },
      { option: 'tacote', label: 'Tacote' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_guitarron',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_guitarron_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cedar', label: 'Cedar' },
      { option: 'luan', label: 'Luan' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'palo_escrito', label: 'Palo Escrito' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_guitarron',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_guitarron_body_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'all_solid_wood', label: 'All Solid Wood' },
      { option: 'solid_top_laminated_back_sides', label: 'Solid Top / Laminated Back & Sides' },
      { option: 'all_laminated', label: 'All Laminated' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_guitarron',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Construction',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Construction',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Construction',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_vihuela_mexicana_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'caraya', label: 'Caraya' },
      { option: 'delgado', label: 'Delgado' },
      { option: 'don_cortez', label: 'Don Cortez' },
      { option: 'guitarras_azteca', label: 'Guitarras Azteca' },
      { option: 'h_jimenez', label: 'H. Jimenez' },
      { option: 'hernandez', label: 'Hernandez' },
      { option: 'krutz', label: 'Krutz' },
      { option: 'la_tradicion', label: 'La Tradición' },
      { option: 'lemus', label: 'Lemus' },
      { option: 'lucida', label: 'Lucida' },
      { option: 'morales', label: 'Morales' },
      { option: 'paracho_elite', label: 'Paracho Elite' },
      { option: 'luis_sevillano', label: 'Luis Sevillano' },
      { option: 'jose_zalapa_aleman', label: 'José Zalapa Alemán' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_vihuela_mexicana',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_vihuela_mexicana_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'size_1_small', label: 'Size 1 (Small)' },
      { option: 'size_2_medium_standard', label: 'Size 2 (Medium / Standard)' },
      { option: 'size_3_large', label: 'Size 3 (Large)' },
      { option: 'vihulelon_oversized', label: 'Vihulelón (Oversized)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_vihuela_mexicana',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_vihuela_mexicana_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cedar_cedro', label: 'Cedar (Cedro)' },
      { option: 'spruce_primavera', label: 'Spruce (Primavera)' },
      { option: 'tacote', label: 'Tacote' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_vihuela_mexicana',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_vihuela_mexicana_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cedar_cedro', label: 'Cedar (Cedro)' },
      { option: 'cirimo', label: 'Cirimo' },
      { option: 'encino_oak', label: 'Encino (Oak)' },
      { option: 'haya_beech', label: 'Haya (Beech)' },
      { option: 'maple', label: 'Maple' },
      { option: 'nogal_walnut', label: 'Nogal (Walnut)' },
      { option: 'palo_escrito', label: 'Palo Escrito' },
      { option: 'parota', label: 'Parota' },
      { option: 'rosewood_palo_de_rosa', label: 'Rosewood (Palo de Rosa)' },
      { option: 'zopilote', label: 'Zopilote' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_vihuela_mexicana',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_vihuela_mexicana_tuners',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'machine_heads_geared_tuners', label: 'Machine Heads (Geared Tuners)' },
      { option: 'wooden_pegs_clavijas', label: 'Wooden Pegs (Clavijas)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_vihuela_mexicana',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning Mechanism',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning Mechanism',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning Mechanism',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_huasteca_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'candelas_guitars_tomas_delgado', label: 'Candelas Guitars (Tomás Delgado)' },
      { option: 'don_cortez', label: 'Don Cortez' },
      { option: 'enrique_enriquez', label: 'Enrique Enríquez' },
      { option: 'jd_salazar_music', label: 'JD Salazar Music' },
      { option: 'la_tradicion_music', label: 'La Tradición Music' },
      { option: 'lauderia_cobos_cruz', label: 'Laudería Cobos Cruz' },
      { option: 'paracho_elite_guitars', label: 'Paracho Elite Guitars' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_huasteca',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_jarana_huasteca_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'palo_escrito', label: 'Palo Escrito' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_huasteca',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_huasteca_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'palo_escrito', label: 'Palo Escrito' },
      { option: 'cedar_cedro_rojo', label: 'Cedar (Cedro Rojo)' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_huasteca',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_huasteca_tuners',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'wooden_friction_pegs', label: 'Wooden Friction Pegs' },
      { option: 'geared_machine_heads', label: 'Geared Machine Heads' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_huasteca',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning Hardware',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning Hardware',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning Hardware',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_jarocha_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'pablo_elias_arboleyda_castro_taller_el_chejere', label: 'Pablo Elías Arboleyda Castro (Taller El Chéjere)' },
      { option: 'josue_ro_arboleyda_valdovinos', label: 'Josué "Ro" Arboleyda Valdovinos' },
      { option: 'miguel_angel_bravo_hernandez_instrumentos_miguel_angel', label: 'Miguel Ángel Bravo Hernández (Instrumentos Miguel Ángel)' },
      { option: 'pablo_campechano', label: 'Pablo Campechano' },
      { option: 'candelas_guitars_tomas_delgado', label: 'Candelas Guitars (Tomás Delgado)' },
      { option: 'cesar_castro', label: 'César Castro' },
      { option: 'victor_castro', label: 'Víctor Castro' },
      { option: 'asuncion_chon_cobos', label: 'Asunción "Chon" Cobos' },
      { option: 'don_cortez', label: 'Don Cortez' },
      { option: 'ramon_gutierrez_hernandez', label: 'Ramón Gutiérrez Hernández' },
      { option: 'gilberto_gutierrez_silva', label: 'Gilberto Gutiérrez Silva' },
      { option: 'lauderia_cobos_cruz', label: 'Laudería Cobos Cruz' },
      { option: 'ali_medrano', label: 'Alí Medrano' },
      { option: 'quirino_montalvo_corro', label: 'Quirino Montalvo Corro' },
      { option: 'felix_liche_oseguera_rueda', label: 'Félix "Liche" Oseguera Rueda' },
      { option: 'paracho_elite_guitars', label: 'Paracho Elite Guitars' },
      { option: 'angel_mario_rosales', label: 'Ángel Mario Rosales' },
      { option: 'mario_rosales', label: 'Mario Rosales' },
      { option: 'victor_francisco_siono_taller_de_lauderia', label: 'Víctor Francisco Siono (Taller de Laudería)' },
      { option: 'anastasio_tacho_utrera', label: 'Anastasio "Tacho" Utrera' },
      { option: 'camerino_utrera', label: 'Camerino Utrera' },
      { option: 'octavio_vega_hernandez', label: 'Octavio Vega Hernández' },
      { option: 'julio_viveros', label: 'Julio Viveros' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_jarocha',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_strings_jarana_jarocha_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'chaquiste', label: 'Chaquiste' },
      { option: 'mosquito', label: 'Mosquito' },
      { option: 'primera', label: 'Primera' },
      { option: 'segunda', label: 'Segunda' },
      { option: 'tercera', label: 'Tercera' },
      { option: 'tercerola_cuarta', label: 'Tercerola / Cuarta' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_jarocha',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_jarocha_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'traditional_carved_hollowed_single_block', label: 'Traditional Carved / Hollowed (Single Block)' },
      { option: 'spanish_method_assembled_bent_sides', label: 'Spanish Method / Assembled (Bent Sides)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_jarocha',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Construction Method',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Construction Method',
      isDetail: true,
    },
    saveConfig: {
      label: 'Construction Method',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_jarocha_body',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spanish_cedar_cedro', label: 'Spanish Cedar (Cedro)' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'mango', label: 'Mango' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_jarocha',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Wood',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_strings_jarana_jarocha_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '5_courses_8_strings_traditional', label: '5 Courses / 8 Strings (Traditional)' },
      { option: '4_double_courses_8_strings', label: '4 Double Courses / 8 Strings' },
      { option: '5_courses_10_strings', label: '5 Courses / 10 Strings' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_strings_jarana_jarocha',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Strings',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_strings_sitar_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'bina', label: 'Bina' },
      { option: 'mukta_das', label: 'Mukta Das' },
      { option: 'hemen_co', label: 'Hemen & Co.' },
      { option: 'hiren_roy_sons', label: 'Hiren Roy & Sons' },
      { option: 'k_s_sitarmaker', label: 'K.S. Sitarmaker' },
      { option: 'somnath_kakade', label: 'Somnath Kakade' },
      { option: 'kanai_lal_sons', label: 'Kanai Lal & Sons' },
      { option: 'naba_kanji', label: 'Naba Kanji' },
      { option: 'maharaja_musicals', label: 'Maharaja Musicals' },
      { option: 'makhanlal_sons', label: 'Makhanlal & Sons' },
      { option: 'mirajkar_musical', label: 'Mirajkar Musical' },
      { option: 'mm_musician_s_mall', label: 'MM (Musician\'s Mall)' },
      { option: 'naeem_sitarmaker', label: 'Naeem Sitarmaker' },
      { option: 'naskar_co', label: 'Naskar & Co.' },
      { option: 'old_delhi_music', label: 'Old Delhi Music' },
      { option: 'paloma', label: 'Paloma' },
      { option: 'mangla_prasad', label: 'Mangla Prasad' },
      { option: 'r_a_sitarmaker', label: 'R.A. Sitarmaker' },
      { option: 'barun_ray_hiren_roy_s_son', label: 'Barun Ray (Hiren Roy\'s Son)' },
      { option: 'rikhi_ram', label: 'Rikhi Ram' },
      { option: 'monoj_kumar_sardar_mks', label: 'Monoj Kumar Sardar (MKS)' },
      { option: 'jayanta_kumar_sengupta', label: 'Jayanta Kumar Sengupta' },
      { option: 'radha_krishna_sharma', label: 'Radha Krishna Sharma' },
      { option: 'sanjay_sharma_rikhi_ram', label: 'Sanjay Sharma (Rikhi Ram)' },
      { option: 'srishti', label: 'Srishti' },
      { option: 'haridas_vhatkar', label: 'Haridas Vhatkar' },
      { option: 'jeff_whittier', label: 'Jeff Whittier' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_strings_sitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'south_asian_strings_sitar_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'kharaj_pancham_ravi_shankar_style', label: 'Kharaj Pancham (Ravi Shankar Style)' },
      { option: 'gandhar_pancham_vilayat_khan_style', label: 'Gandhar Pancham (Vilayat Khan Style)' },
      { option: 'electric', label: 'Electric' },
      { option: 'travel_flat_toomba_studio', label: 'Travel / Flat Toomba / Studio' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_strings_sitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Style',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_strings_sitar_decoration',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '1_full_decoration_angurpatta_kalka', label: '#1 Full Decoration (Angurpatta / Kalka)' },
      { option: '2_half_decoration_golapatta', label: '#2 Half Decoration (Golapatta)' },
      { option: '3_simple_decoration_khajurpatta', label: '#3 Simple Decoration (Khajurpatta)' },
      { option: 'minimal_plain', label: 'Minimal / Plain' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_strings_sitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Decoration Level',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Decoration Level',
      isDetail: true,
    },
    saveConfig: {
      label: 'Decoration Level',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_strings_sitar_neck_body_wood',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'tun_wood_indian_cedar', label: 'Tun Wood (Indian Cedar)' },
      { option: 'teak_aged_burma', label: 'Teak (Aged / Burma)' },
      { option: 'teak_domestic_indian', label: 'Teak (Domestic / Indian)' },
      { option: 'sheesham_indian_rosewood', label: 'Sheesham (Indian Rosewood)' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_strings_sitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Neck / Body Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Neck / Body Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Neck / Body Wood',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_strings_sitar_bridge_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'deer_horn_staghorn', label: 'Deer Horn / Staghorn' },
      { option: 'camel_bone', label: 'Camel Bone' },
      { option: 'buffalo_horn', label: 'Buffalo Horn' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'synthetic_composite', label: 'Synthetic / Composite' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_strings_sitar',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bridge Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bridge Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bridge Material',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_strings_oud_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'samir_abbassi', label: 'Samir Abbassi' },
      { option: 'ahmad_abd_el_halim', label: 'Ahmad Abd El Halim' },
      { option: 'yasser_abouchakra', label: 'Yasser AbouChakra' },
      { option: 'ramy_adly', label: 'Ramy Adly' },
      { option: 'ahmed_al_abdali', label: 'Ahmed al-Abdali' },
      { option: 'ali_al_ouad', label: 'Ali Al-Ouad' },
      { option: 'amr_and_ali', label: 'Amr and Ali' },
      { option: 'ara_flat_instruments', label: 'Arab Instruments' },
      { option: 'arafati', label: 'Arafati' },
      { option: 'khalid_belhaiba', label: 'Khalid Belhaiba' },
      { option: 'mohammed_bin_yahya', label: 'Mohammed bin Yahya' },
      { option: 'georges_bitar', label: 'Georges Bitar' },
      { option: 'sylvain_bouancheau_dugast', label: 'Sylvain Bouancheau Dugast' },
      { option: 'chahal', label: 'Chahal' },
      { option: 'abdessalam_chiki', label: 'Abdessalam Chiki' },
      { option: 'emir_degirmenli', label: 'Emir Degirmenli' },
      { option: 'delaram', label: 'Delaram' },
      { option: 'sumbat_derbedrosian', label: 'Sumbat DerBedrosian' },
      { option: 'dr_oud', label: 'Dr. Oud' },
      { option: 'jo_dusepo', label: 'Jo Dusepo' },
      { option: 'mohamad_el_hifnawi', label: 'Mohamad el-Hifnawi' },
      { option: 'gamil_georges', label: 'Gamil Georges' },
      { option: 'nazih_ghadban', label: 'Nazih Ghadban' },
      { option: 'ebrahim_ghanbari_mehr', label: 'Ebrahim Ghanbari Mehr' },
      { option: 'godin', label: 'Godin' },
      { option: 'rabih_haddad', label: 'Rabih Haddad' },
      { option: 'mansur_haidar', label: 'Mansur Haidar' },
      { option: 'farhan_hassan', label: 'Farhan Hassan' },
      { option: 'georges_hayek', label: 'Georges Hayek' },
      { option: 'muhammad_fathel_hussain', label: 'Muhammad Fathel Hussain' },
      { option: 'wissam_joubran', label: 'Wissam Joubran' },
      { option: 'khatem_jubran', label: 'Khatem Jubran' },
      { option: 'manolis_kaniadakis', label: 'Manolis Kaniadakis' },
      { option: 'onnik_karibyan_onnik_usta', label: 'Onnik Karibyan (Onnik Usta)' },
      { option: 'michel_khaouam', label: 'Michel Khaouam' },
      { option: 'manol_emmanuel_venios', label: 'Manol (Emmanuel Venios)' },
      { option: 'albert_mansour', label: 'Albert Mansour' },
      { option: 'fadi_matta', label: 'Fadi Matta' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'miras_erdogan_akpnar', label: 'Miras (Erdoğan Akpınar)' },
      { option: 'mohammadi_brothers', label: 'Mohammadi Brothers' },
      { option: 'michael_moussa', label: 'Michael Moussa' },
      { option: 'muzikkon', label: 'Muzikkon' },
      { option: 'abdo_nahat', label: 'Abdo Nahat' },
      { option: 'georges_nahat', label: 'Georges Nahat' },
      { option: 'hanna_nahat', label: 'Hanna Nahat' },
      { option: 'tawfik_nahat', label: 'Tawfik Nahat' },
      { option: 'viken_najarian', label: 'Viken Najarian' },
      { option: 'oud_cafe', label: 'Oud Café' },
      { option: 'yldrm_pala_flatyk', label: 'Yıldırım Palabıyık' },
      { option: 'dimitris_rapakousios', label: 'Dimitris Rapakousios' },
      { option: 'raphael', label: 'Raphael' },
      { option: 'peter_sabagh', label: 'Peter Sabagh' },
      { option: 'sala_muzik', label: 'Sala Muzik' },
      { option: 'hafiz_salman', label: 'Hafiz Salman' },
      { option: 'saadettin_bahadr_sandi', label: 'Saadettin & Bahadır Sandi' },
      { option: 'cengiz_sarikus_dr_sarikus', label: 'Cengiz Sarikus (Dr. Sarikus)' },
      { option: 'veysel_sarikus', label: 'Veysel Sarikus' },
      { option: 'william_shaheen', label: 'William Shaheen' },
      { option: 'farouk_shehata', label: 'Farouk Shehata' },
      { option: 'maurice_shehata', label: 'Maurice Shehata' },
      { option: 'ibrahim_sukar', label: 'Ibrahim Sukar' },
      { option: 'sultan_instruments', label: 'Sultan Instruments' },
      { option: 'tapadum', label: 'Tapadum' },
      { option: 'tasos_theodorakis', label: 'Tasos Theodorakis' },
      { option: 'faruk_turunz', label: 'Faruk Türünz' },
      { option: 'john_vergara', label: 'John Vergara' },
      { option: 'richard_wagner', label: 'Richard Wagner' },
      { option: 'walid', label: 'Walid' },
      { option: 'zerya_flat_khalifeh_family', label: 'Zeryab (Khalifeh Family)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_strings_oud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'middle_eastern_north_african_strings_oud_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'arabic', label: 'Arabic' },
      { option: 'turkish', label: 'Turkish' },
      { option: 'iraqi', label: 'Iraqi' },
      { option: 'persian_barbat', label: 'Persian (Barbat)' },
      { option: 'electric_solid_body', label: 'Electric (Solid Body)' },
      { option: 'electro_acoustic', label: 'Electro-Acoustic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_strings_oud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type / Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type / Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type / Style',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_strings_oud_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '10_5_courses', label: '10 (5 courses)' },
      { option: '11_5_courses_single_bass', label: '11 (5 courses + single bass)' },
      { option: '12_6_courses', label: '12 (6 courses)' },
      { option: '13_6_courses_single_bass', label: '13 (6 courses + single bass)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_strings_oud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Strings',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_strings_oud_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'pine', label: 'Pine' },
      { option: 'laminated_wood', label: 'Laminated Wood' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_strings_oud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_strings_oud_back_sides',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'apricot', label: 'Apricot' },
      { option: 'beech', label: 'Beech' },
      { option: 'carbon_fiber', label: 'Carbon Fiber' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'cypress', label: 'Cypress' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'fiberglass', label: 'Fiberglass' },
      { option: 'laminated_wood', label: 'Laminated Wood' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'mulberry', label: 'Mulberry' },
      { option: 'ovangkol', label: 'Ovangkol' },
      { option: 'padauk', label: 'Padauk' },
      { option: 'plum', label: 'Plum' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'wenge', label: 'Wenge' },
      { option: 'yew', label: 'Yew' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_strings_oud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bowl (Back & Sides)',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bowl (Back & Sides)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bowl (Back & Sides)',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_strings_oud_bridge_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'fixed', label: 'Fixed' },
      { option: 'floating', label: 'Floating' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_strings_oud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bridge Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bridge Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bridge Type',
      isRequired: false,
    },
  },
  {
    key: 'eastern_european_strings_domra_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a_tsygankov_workshop', label: 'A. Tsygankov Workshop' },
      { option: 'apc_antonio_pinto_carvalho', label: 'APC (Antonio Pinto Carvalho)' },
      { option: 'aruchenkov', label: 'Aruchenkov' },
      { option: 'chernihiv_musical_instrument_factory', label: 'Chernihiv Musical Instrument Factory' },
      { option: 'doff', label: 'Doff' },
      { option: 'i_v_emeliyanov', label: 'I.V. Emeliyanov' },
      { option: 'harmony_workshop', label: 'Harmony Workshop' },
      { option: 'hora', label: 'HORA' },
      { option: 'khulakhov', label: 'Khulakhov' },
      { option: 'v_v_kostin', label: 'V.V. Kostin' },
      { option: 'lunacharsky_factory', label: 'Lunacharsky Factory' },
      { option: 'lutner', label: 'Lutner' },
      { option: 'a_i_makeev', label: 'A.I. Makeev' },
      { option: 'mukhin', label: 'Mukhin' },
      { option: 'pavlov', label: 'Pavlov' },
      { option: 'm_saveliyev', label: 'M. Saveliyev' },
      { option: 'evgeny_serjantov', label: 'Evgeny Serjantov' },
      { option: 'a_simakov', label: 'A. Simakov' },
      { option: 'stpgoods', label: 'STPGoods' },
      { option: 'tchaikovsky_factory', label: 'Tchaikovsky Factory' },
      { option: 'thomann', label: 'Thomann' },
      { option: 'ukrmuzprom_odessa_factory', label: 'Ukrmuzprom (Odessa Factory)' },
      { option: 'b_usenko', label: 'B. Usenko' },
      { option: 'valeylin', label: 'Valeylin' },
      { option: 'zubchenko', label: 'Zubchenko' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'eastern_european_strings_domra',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'eastern_european_strings_domra_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'piccolo', label: 'Piccolo' },
      { option: 'prima', label: 'Prima' },
      { option: 'alto', label: 'Alto' },
      { option: 'tenor', label: 'Tenor' },
      { option: 'bass', label: 'Bass' },
      { option: 'contrabass', label: 'Contrabass' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'eastern_european_strings_domra',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'eastern_european_strings_domra_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3_string', label: '3-String' },
      { option: '4_string', label: '4-String' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'eastern_european_strings_domra',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'eastern_european_strings_domra_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'spruce', label: 'Spruce' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'fir', label: 'Fir' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'eastern_european_strings_domra',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top Material',
      isRequired: false,
    },
  },
  {
    key: 'eastern_european_strings_domra_grade',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'student_tourist', label: 'Student / Tourist' },
      { option: 'first_grade', label: 'First Grade' },
      { option: 'highest_concert_grade', label: 'Highest / Concert Grade' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'eastern_european_strings_domra',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Grade Level',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Grade Level',
      isDetail: true,
    },
    saveConfig: {
      label: 'Grade Level',
      isRequired: false,
    },
  },
  {
    key: 'lute_archlute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'barber_harris', label: 'Barber & Harris' },
      { option: 'alexander_batov', label: 'Alexander Batov' },
      { option: 'paolo_busato', label: 'Paolo Busato' },
      { option: 'travis_carey', label: 'Travis Carey' },
      { option: 'early_music_shop_ems', label: 'Early Music Shop (EMS)' },
      { option: 'hendrik_hasenfuss', label: 'Hendrik Hasenfuss' },
      { option: 'martin_haycock', label: 'Martin Haycock' },
      { option: 'klaus_jacobsen', label: 'Klaus Jacobsen' },
      { option: 'le_luth_dore', label: 'Le Luth Doré' },
      { option: 'liuto_forte_andre_burguete', label: 'Liuto Forte (André Burguete)' },
      { option: 'cezar_mateus', label: 'Cezar Mateus' },
      { option: 'jose_miguel_moreno', label: 'José Miguel Moreno' },
      { option: 'stephen_murphy', label: 'Stephen Murphy' },
      { option: 'lauri_niskanen', label: 'Lauri Niskanen' },
      { option: 'tihamer_romanek', label: 'Tihamér Romanek' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'saadettin_sandi', label: 'Saadettin Sandi' },
      { option: 'scala_vilagio_thomann', label: 'Scala Vilagio (Thomann)' },
      { option: 'michael_schreiner', label: 'Michael Schreiner' },
      { option: 'grant_tomlinson', label: 'Grant Tomlinson' },
      { option: 'lars_trressen', label: 'Lars Tørressen' },
      { option: 'makoto_tsuruda', label: 'Makoto Tsuruda' },
      { option: 'david_van_edwards', label: 'David Van Edwards' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_archlute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'lute_archlute_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '13', label: '13' },
      { option: '14', label: '14' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_archlute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'lute_archlute_back',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'kingwood', label: 'Kingwood' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'maple_curly_figured', label: 'Maple (Curly/Figured)' },
      { option: 'pear', label: 'Pear' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'snakewood', label: 'Snakewood' },
      { option: 'sycamore', label: 'Sycamore' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'yew', label: 'Yew' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_archlute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back / Bowl Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back / Bowl Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back / Bowl Material',
      isRequired: false,
    },
  },
  {
    key: 'lute_archlute_string_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gut', label: 'Gut' },
      { option: 'nylgut_aquila', label: 'Nylgut (Aquila)' },
      { option: 'nylon', label: 'Nylon' },
      { option: 'fluorocarbon_carbon', label: 'Fluorocarbon / Carbon' },
      { option: 'wound_bass_strings_diapasons', label: 'Wound Bass Strings (diapasons)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_archlute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'String Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'String Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'String Type',
      isRequired: false,
    },
  },
  {
    key: 'lute_archlute_stringing',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single_strung', label: 'Single-Strung' },
      { option: 'double_courses', label: 'Double Courses' },
      { option: 'mixed_single_chanterelle_double_courses', label: 'Mixed (single chanterelle + double courses)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_archlute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Stringing',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Stringing',
      isDetail: true,
    },
    saveConfig: {
      label: 'Stringing',
      isRequired: false,
    },
  },
  {
    key: 'lute_baroque_lute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'alexander_batov', label: 'Alexander Batov' },
      { option: 'barber_harris', label: 'Barber & Harris' },
      { option: 'bruce_brook', label: 'Bruce Brook' },
      { option: 'paolo_busato', label: 'Paolo Busato' },
      { option: 'travis_carey', label: 'Travis Carey' },
      { option: 'jo_dusepo', label: 'Jo Dusepo' },
      { option: 'early_music_shop_ems', label: 'Early Music Shop (EMS)' },
      { option: 'luke_emmet_orlando_lutes', label: 'Luke Emmet (Orlando Lutes)' },
      { option: 'wolfgang_fruh_le_point_d_accroche', label: 'Wolfgang Früh (Le Point d\'Accroche)' },
      { option: 'hendrik_hasenfuss', label: 'Hendrik Hasenfuss' },
      { option: 'martin_haycock', label: 'Martin Haycock' },
      { option: 'klaus_jacobsen', label: 'Klaus Jacobsen' },
      { option: 'le_luth_dore', label: 'Le Luth Doré' },
      { option: 'liuto_forte_andre_burguete', label: 'Liuto Forte (André Burguete)' },
      { option: 'michael_lowe', label: 'Michael Lowe' },
      { option: 'james_luff', label: 'James Luff' },
      { option: 'james_marriage', label: 'James Marriage' },
      { option: 'cezar_mateus', label: 'Cezar Mateus' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'stephen_murphy', label: 'Stephen Murphy' },
      { option: 'muzikkon', label: 'Muzikkon' },
      { option: 'lauri_niskanen', label: 'Lauri Niskanen' },
      { option: 'ray_nurse', label: 'Ray Nurse' },
      { option: 'tihamer_romanek', label: 'Tihamér Romanek' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'scala_vilagio_thomann', label: 'Scala Vilagio (Thomann)' },
      { option: 'andreas_schlegel', label: 'Andreas Schlegel' },
      { option: 'michael_schreiner', label: 'Michael Schreiner' },
      { option: 'grant_tomlinson', label: 'Grant Tomlinson' },
      { option: 'lars_trressen', label: 'Lars Tørressen' },
      { option: 'makoto_tsuruda', label: 'Makoto Tsuruda' },
      { option: 'david_van_edwards', label: 'David Van Edwards' },
      { option: 'oliver_wadsworth', label: 'Oliver Wadsworth' },
      { option: 'alan_wright', label: 'Alan Wright' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_baroque_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'lute_baroque_lute_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '11', label: '11' },
      { option: '13', label: '13' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_baroque_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'lute_baroque_lute_back_bowl',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'kingwood', label: 'Kingwood' },
      { option: 'maple', label: 'Maple' },
      { option: 'maple_bird_s_eye', label: 'Maple (Bird\'s-Eye)' },
      { option: 'maple_curly_figured', label: 'Maple (Curly / Figured)' },
      { option: 'padauk', label: 'Padauk' },
      { option: 'pear', label: 'Pear' },
      { option: 'plum', label: 'Plum' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'snakewood', label: 'Snakewood' },
      { option: 'sycamore', label: 'Sycamore' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'yew', label: 'Yew' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_baroque_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back / Bowl Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back / Bowl Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back / Bowl Material',
      isRequired: false,
    },
  },
  {
    key: 'lute_baroque_lute_string_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gut', label: 'Gut' },
      { option: 'nylgut_aquila', label: 'Nylgut (Aquila)' },
      { option: 'nylon', label: 'Nylon' },
      { option: 'fluorocarbon_carbon', label: 'Fluorocarbon / Carbon' },
      { option: 'wound_bass_strings', label: 'Wound Bass Strings' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_baroque_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'String Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'String Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'String Type',
      isRequired: false,
    },
  },
  {
    key: 'lute_baroque_lute_stringing',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'double_courses_standard', label: 'Double Courses (standard)' },
      { option: 'single_strung', label: 'Single-Strung' },
      { option: 'mixed', label: 'Mixed' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_baroque_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Stringing',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Stringing',
      isDetail: true,
    },
    saveConfig: {
      label: 'Stringing',
      isRequired: false,
    },
  },
  {
    key: 'lute_renaissance_lute_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'andrea_andalo', label: 'Andrea Andalò' },
      { option: 'barber_harris', label: 'Barber & Harris' },
      { option: 'alexander_batov', label: 'Alexander Batov' },
      { option: 'jaume_bosser', label: 'Jaume Bosser' },
      { option: 'martin_bowers', label: 'Martin Bowers' },
      { option: 'bruce_brook', label: 'Bruce Brook' },
      { option: 'larry_brown', label: 'Larry Brown' },
      { option: 'paolo_busato', label: 'Paolo Busato' },
      { option: 'travis_carey', label: 'Travis Carey' },
      { option: 'antonio_dattis', label: 'Antonio Dattis' },
      { option: 'jo_dusepo', label: 'Jo Dusepo' },
      { option: 'early_music_shop_ems', label: 'Early Music Shop (EMS)' },
      { option: 'luke_emmet', label: 'Luke Emmet' },
      { option: 'paul_fischer', label: 'Paul Fischer' },
      { option: 'david_fitzpatrick', label: 'David Fitzpatrick' },
      { option: 'richard_fletcher', label: 'Richard Fletcher' },
      { option: 'wolfgang_fruh', label: 'Wolfgang Früh' },
      { option: 'marco_golinelli', label: 'Marco Golinelli' },
      { option: 'stephen_gottlie_flat', label: 'Stephen Gottlieb' },
      { option: 'hendrik_hasenfuss', label: 'Hendrik Hasenfuss' },
      { option: 'martin_haycock', label: 'Martin Haycock' },
      { option: 'klaus_jacobsen', label: 'Klaus Jacobsen' },
      { option: 'tony_johnson', label: 'Tony Johnson' },
      { option: 'daniel_larson', label: 'Daniel Larson' },
      { option: 'le_luth_dore', label: 'Le Luth Doré' },
      { option: 'michael_lowe', label: 'Michael Lowe' },
      { option: 'james_luff', label: 'James Luff' },
      { option: 'robert_lundberg', label: 'Robert Lundberg' },
      { option: 'james_marriage', label: 'James Marriage' },
      { option: 'cezar_mateus', label: 'Cezar Mateus' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'stephen_murphy', label: 'Stephen Murphy' },
      { option: 'muzikkon', label: 'Muzikkon' },
      { option: 'lauri_niskanen', label: 'Lauri Niskanen' },
      { option: 'ray_nurse', label: 'Ray Nurse' },
      { option: 'tihamer_romanek', label: 'Tihamér Romanek' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'david_rubio', label: 'David Rubio' },
      { option: 'andrew_rutherford', label: 'Andrew Rutherford' },
      { option: 'scala_vilagio_thomann', label: 'Scala Vilagio (Thomann)' },
      { option: 'michael_schreiner', label: 'Michael Schreiner' },
      { option: 'eric_shull', label: 'Eric Shull' },
      { option: 'michael_sprake', label: 'Michael Sprake' },
      { option: 'paul_thomson', label: 'Paul Thomson' },
      { option: 'grant_tomlinson', label: 'Grant Tomlinson' },
      { option: 'lars_trressen', label: 'Lars Tørressen' },
      { option: 'makoto_tsuruda', label: 'Makoto Tsuruda' },
      { option: 'wilma_van_berkel', label: 'Wilma van Berkel' },
      { option: 'david_van_edwards', label: 'David Van Edwards' },
      { option: 'nico_van_der_waals', label: 'Nico van der Waals' },
      { option: 'joel_van_lennep', label: 'Joel van Lennep' },
      { option: 'oliver_wadsworth', label: 'Oliver Wadsworth' },
      { option: 'alan_wright', label: 'Alan Wright' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_renaissance_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'lute_renaissance_lute_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: '9', label: '9' },
      { option: '10', label: '10' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_renaissance_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'lute_renaissance_lute_back_bowl',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'ash', label: 'Ash' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'cypress', label: 'Cypress' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'ivory', label: 'Ivory' },
      { option: 'kingwood', label: 'Kingwood' },
      { option: 'lacewood', label: 'Lacewood' },
      { option: 'maple', label: 'Maple' },
      { option: 'maple_bird_s_eye', label: 'Maple (Bird\'s-Eye)' },
      { option: 'maple_curly_figured', label: 'Maple (Curly / Figured)' },
      { option: 'pear', label: 'Pear' },
      { option: 'plum', label: 'Plum' },
      { option: 'poplar', label: 'Poplar' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'snakewood', label: 'Snakewood' },
      { option: 'sycamore', label: 'Sycamore' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'yew', label: 'Yew' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_renaissance_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back / Bowl Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back / Bowl Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back / Bowl Material',
      isRequired: false,
    },
  },
  {
    key: 'lute_renaissance_lute_string_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gut', label: 'Gut' },
      { option: 'nylgut_aquila', label: 'Nylgut (Aquila)' },
      { option: 'nylon', label: 'Nylon' },
      { option: 'fluorocarbon_carbon', label: 'Fluorocarbon / Carbon' },
      { option: 'wound_bass_strings', label: 'Wound Bass Strings' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_renaissance_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'String Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'String Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'String Type',
      isRequired: false,
    },
  },
  {
    key: 'lute_renaissance_lute_stringing',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'double_courses_standard', label: 'Double Courses (standard)' },
      { option: 'single_strung', label: 'Single-Strung' },
      { option: 'mixed_single_chanterelle_double_courses', label: 'Mixed (single chanterelle + double courses)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_renaissance_lute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Stringing',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Stringing',
      isDetail: true,
    },
    saveConfig: {
      label: 'Stringing',
      isRequired: false,
    },
  },
  {
    key: 'lute_theorbo_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'barber_harris', label: 'Barber & Harris' },
      { option: 'alexander_batov', label: 'Alexander Batov' },
      { option: 'paolo_busato', label: 'Paolo Busato' },
      { option: 'travis_carey', label: 'Travis Carey' },
      { option: 'early_music_shop_ems', label: 'Early Music Shop (EMS)' },
      { option: 'hendrik_hasenfuss', label: 'Hendrik Hasenfuss' },
      { option: 'martin_haycock', label: 'Martin Haycock' },
      { option: 'klaus_jacobsen', label: 'Klaus Jacobsen' },
      { option: 'le_luth_dore', label: 'Le Luth Doré' },
      { option: 'liuto_forte_andre_burguete', label: 'Liuto Forte (André Burguete)' },
      { option: 'james_marriage', label: 'James Marriage' },
      { option: 'cezar_mateus', label: 'Cezar Mateus' },
      { option: 'stephen_murphy', label: 'Stephen Murphy' },
      { option: 'muzikkon', label: 'Muzikkon' },
      { option: 'lauri_niskanen', label: 'Lauri Niskanen' },
      { option: 'tihamer_romanek', label: 'Tihamér Romanek' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'saadettin_sandi', label: 'Saadettin Sandi' },
      { option: 'scala_vilagio_thomann', label: 'Scala Vilagio (Thomann)' },
      { option: 'michael_schreiner', label: 'Michael Schreiner' },
      { option: 'grant_tomlinson', label: 'Grant Tomlinson' },
      { option: 'lars_trressen', label: 'Lars Tørressen' },
      { option: 'makoto_tsuruda', label: 'Makoto Tsuruda' },
      { option: 'david_van_edwards', label: 'David Van Edwards' },
      { option: 'alan_wright', label: 'Alan Wright' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_theorbo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'lute_theorbo_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '14', label: '14' },
      { option: '15_or_more', label: '15 or more' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_theorbo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'lute_theorbo_back_bowl',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'birch', label: 'Birch' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'maple', label: 'Maple' },
      { option: 'maple_curly_figured', label: 'Maple (Curly/Figured)' },
      { option: 'pear', label: 'Pear' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sycamore', label: 'Sycamore' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'yew', label: 'Yew' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_theorbo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back / Bowl Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back / Bowl Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back / Bowl Material',
      isRequired: false,
    },
  },
  {
    key: 'lute_theorbo_string_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gut', label: 'Gut' },
      { option: 'nylgut_aquila', label: 'Nylgut (Aquila)' },
      { option: 'nylon', label: 'Nylon' },
      { option: 'fluorocarbon_carbon', label: 'Fluorocarbon / Carbon' },
      { option: 'wound_bass_strings_diapasons', label: 'Wound Bass Strings (diapasons)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_theorbo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'String Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'String Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'String Type',
      isRequired: false,
    },
  },
  {
    key: 'lute_theorbo_stringing',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single_strung_standard', label: 'Single-Strung (standard)' },
      { option: 'double_fingerboard_courses', label: 'Double Fingerboard Courses' },
      { option: 'mixed', label: 'Mixed' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_theorbo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Stringing',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Stringing',
      isDetail: true,
    },
    saveConfig: {
      label: 'Stringing',
      isRequired: false,
    },
  },
  {
    key: 'lute_other_lutes_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'barber_harris', label: 'Barber & Harris' },
      { option: 'alexander_batov', label: 'Alexander Batov' },
      { option: 'early_music_shop_ems', label: 'Early Music Shop (EMS)' },
      { option: 'marco_golinelli', label: 'Marco Golinelli' },
      { option: 'daniel_larson', label: 'Daniel Larson' },
      { option: 'le_luth_dore', label: 'Le Luth Doré' },
      { option: 'liuto_forte_andre_burguete', label: 'Liuto Forte (André Burguete)' },
      { option: 'james_marriage', label: 'James Marriage' },
      { option: 'cesar_mateus', label: 'César Mateus' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'philippe_mottet_anselmus', label: 'Philippe Mottet (Anselmus)' },
      { option: 'stephen_murphy', label: 'Stephen Murphy' },
      { option: 'muzikkon', label: 'Muzikkon' },
      { option: 'lauri_niskanen', label: 'Lauri Niskanen' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'scala_vilagio_thomann', label: 'Scala Vilagio (Thomann)' },
      { option: 'grant_tomlinson', label: 'Grant Tomlinson' },
      { option: 'david_van_edwards', label: 'David Van Edwards' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_other_lutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'lute_other_lutes_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'medieval_lute', label: 'Medieval Lute' },
      { option: 'mandora_gallichon', label: 'Mandora / Gallichon' },
      { option: 'transitional_accords_nouveaux', label: 'Transitional (accords nouveaux)' },
      { option: 'angelique', label: 'Angelique' },
      { option: 'tiorbino', label: 'Tiorbino' },
      { option: 'student_modern', label: 'Student / Modern' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_other_lutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'lute_other_lutes_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '4', label: '4' },
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: '9', label: '9' },
      { option: '10_or_more', label: '10 or more' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_other_lutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Courses',
      isRequired: false,
    },
  },
  {
    key: 'lute_other_lutes_back_bowl',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'ash', label: 'Ash' },
      { option: 'birch', label: 'Birch' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'cypress', label: 'Cypress' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'maple', label: 'Maple' },
      { option: 'maple_curly_figured', label: 'Maple (Curly/Figured)' },
      { option: 'pear', label: 'Pear' },
      { option: 'plum', label: 'Plum' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sycamore', label: 'Sycamore' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'yew', label: 'Yew' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_other_lutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back / Bowl Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back / Bowl Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back / Bowl Material',
      isRequired: false,
    },
  },
  {
    key: 'lute_other_lutes_string_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gut', label: 'Gut' },
      { option: 'nylgut_aquila', label: 'Nylgut (Aquila)' },
      { option: 'nylon', label: 'Nylon' },
      { option: 'fluorocarbon_carbon', label: 'Fluorocarbon / Carbon' },
      { option: 'wound_bass_strings', label: 'Wound Bass Strings' },
      { option: 'metal_wire', label: 'Metal / Wire' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'lute_other_lutes',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'String Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'String Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'String Type',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_viola_d_amore_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'paulus_alletsee', label: 'Paulus Alletsee' },
      { option: 'leandro_bisiach', label: 'Leandro Bisiach' },
      { option: 'david_bittner', label: 'David Bittner' },
      { option: 'lorenzo_carcassi', label: 'Lorenzo Carcassi' },
      { option: 'alfred_coletti', label: 'Alfred Coletti' },
      { option: 'johann_udalricus_eberle', label: 'Johann Udalricus Eberle' },
      { option: 'thomas_eberle', label: 'Thomas Eberle' },
      { option: 'ferdinand_gagliano', label: 'Ferdinand Gagliano' },
      { option: 'joseph_gagliano', label: 'Joseph Gagliano' },
      { option: 'giovanni_grancino', label: 'Giovanni Grancino' },
      { option: 'johannes_florenus_guidantus', label: 'Johannes Florenus Guidantus' },
      { option: 'johann_georg_hellmer', label: 'Johann Georg Hellmer' },
      { option: 'jonathan_hill', label: 'Jonathan Hill' },
      { option: 'josef_huber', label: 'Josef Huber' },
      { option: 'thomas_hulinzky', label: 'Thomas Hulinzky' },
      { option: 'matthias_kayssler', label: 'Matthias Kayssler' },
      { option: 'pietro_mantegazza', label: 'Pietro Mantegazza' },
      { option: 'richard_meares', label: 'Richard Meares' },
      { option: 'charlie_ogle', label: 'Charlie Ogle' },
      { option: 'olivia_pelling', label: 'Olivia Pelling' },
      { option: 'anthony_posch', label: 'Anthony Posch' },
      { option: 'arnold_posch', label: 'Arnold Posch' },
      { option: 'luigi_rovatti', label: 'Luigi Rovatti' },
      { option: 'vincenzo_sannino', label: 'Vincenzo Sannino' },
      { option: 'johann_paul_schorn', label: 'Johann Paul Schorn' },
      { option: 'johann_joseph_stadlmann', label: 'Johann Joseph Stadlmann' },
      { option: 'jacob_steininger', label: 'Jacob Steininger' },
      { option: 'carlo_giuseppe_testore', label: 'Carlo Giuseppe Testore' },
      { option: 'joachim_tielke', label: 'Joachim Tielke' },
      { option: 'peter_wamsley', label: 'Peter Wamsley' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola_d_amore',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'bowed_strings_viola_d_amore_playing_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '5', label: '5' },
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola_d_amore',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Playing Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Playing Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Playing Strings',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_viola_d_amore_sympathetic_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'none', label: 'None' },
      { option: 'matched_equal_to_playing_strings', label: 'Matched (equal to playing strings)' },
      { option: 'extended_more_than_playing_strings', label: 'Extended (more than playing strings)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola_d_amore',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Sympathetic Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Sympathetic Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sympathetic Strings',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_viola_d_amore_setup',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'baroque', label: 'Baroque' },
      { option: 'modern', label: 'Modern' },
      { option: 'convertible_baroque_modern', label: 'Convertible (Baroque / Modern)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola_d_amore',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Setup',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Setup',
      isDetail: true,
    },
    saveConfig: {
      label: 'Setup',
      isRequired: false,
    },
  },
  {
    key: 'bowed_strings_viola_d_amore_body_outline',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'gamba_simple_curves', label: 'Gamba (simple curves)' },
      { option: 'festooned_wavy_flame_shaped', label: 'Festooned (wavy / flame-shaped)' },
      { option: 'violin_type', label: 'Violin-type' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'bowed_strings_viola_d_amore',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Outline',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Outline',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Outline',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bodhran_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'albert_alfonso', label: 'Albert Alfonso' },
      { option: 'ardglen_bodhrans_fred_graham', label: 'Ardglen Bodhrans (Fred Graham)' },
      { option: 'victor_barral', label: 'Victor Barral' },
      { option: 'darius_bartlett', label: 'Darius Bartlett' },
      { option: 'beagan_irish_drums', label: 'Beagan Irish Drums' },
      { option: 'belgarth_bodhrans_eoin_leonard', label: 'Belgarth Bodhrans (Eoin Leonard)' },
      { option: 'boreal_woodworks_steve_sleight', label: 'Boreal Woodworks (Steve Sleight)' },
      { option: 'bridget_drums', label: 'Bridget Drums' },
      { option: 'brouhaha_bodhrans_pete_mandy_collier', label: 'Brouhaha Bodhrans (Pete & Mandy Collier)' },
      { option: 'adam_brown_r_a_brown', label: 'Adam Brown (R&A Brown)' },
      { option: 'buck_musical_instruments', label: 'Buck Musical Instruments' },
      { option: 'charlie_byrne', label: 'Charlie Byrne' },
      { option: 'paddy_clancy', label: 'Paddy Clancy' },
      { option: 'cooperman', label: 'Cooperman' },
      { option: 'james_davey', label: 'James Davey' },
      { option: 'dd_percussion_mark_harmsworth', label: 'DD Percussion (Mark Harmsworth)' },
      { option: 'cj_dixon', label: 'CJ Dixon' },
      { option: 'dobani', label: 'Dobani' },
      { option: 'paul_doyle', label: 'Paul Doyle' },
      { option: 'dragon_drums', label: 'Dragon Drums' },
      { option: 'eastar', label: 'Eastar' },
      { option: 'del_eckels_del_s_drums', label: 'Del Eckels (Del\'s Drums)' },
      { option: 'eckermann_norbert_eckermann', label: 'Eckermann (Norbert Eckermann)' },
      { option: 'finnegan_hill_percussion_brent_cuyler', label: 'Finnegan Hill Percussion (Brent Cuyler)' },
      { option: 'steve_forman', label: 'Steve Forman' },
      { option: 'richard_fox', label: 'Richard Fox' },
      { option: 'gear4music', label: 'Gear4music' },
      { option: 'glenluce', label: 'Glenluce' },
      { option: 'robert_mance_grady_ace_bodhrans', label: 'Robert "Mance" Grady (Ace Bodhráns)' },
      { option: 'fred_halpin', label: 'Fred Halpin' },
      { option: 'hedwitschak_christian_hedwitschak', label: 'Hedwitschak (Christian Hedwitschak)' },
      { option: 'harold_hougaard', label: 'Harold Hougaard' },
      { option: 'gary_hudson', label: 'Gary Hudson' },
      { option: 'gerhard_kress', label: 'Gerhard Kress' },
      { option: 'mariano_la_duela', label: 'Mariano La Duela' },
      { option: 'doug_lanchbery', label: 'Doug Lanchbery' },
      { option: 'ben_march', label: 'Ben March' },
      { option: 'paul_mcauley', label: 'Paul McAuley' },
      { option: 'jonathan_mccloy', label: 'Jonathan McCloy' },
      { option: 'seanie_mcgrath', label: 'Seanie McGrath' },
      { option: 'john_mcknowall', label: 'John McKnowall' },
      { option: 'mcneela_paraic_mcneela', label: 'McNeela (Paraic McNeela)' },
      { option: 'john_mcprange', label: 'John McPrange' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'metloef_rob_forkner', label: 'Metloef (Rob Forkner)' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'chip_mulvaney', label: 'Chip Mulvaney' },
      { option: 'muzikkon', label: 'Muzikkon' },
      { option: 'nica_percussion_niall_carey', label: 'NiCa Percussion (Niall Carey)' },
      { option: 'kevin_o_connell', label: 'Kevin O\'Connell' },
      { option: 'neil_o_grady', label: 'Neil O\'Grady' },
      { option: 'diarmaid_o_kane', label: 'Diarmaid O\'Kane' },
      { option: 'seamus_o_kane', label: 'Séamus O\'Kane' },
      { option: 'olaika', label: 'Olaika' },
      { option: 'percussion_plus', label: 'Percussion Plus' },
      { option: 'mike_quinlan_q_drums', label: 'Mike Quinlan (Q Drums)' },
      { option: 'clive_quinn', label: 'Clive Quinn' },
      { option: 'patsy_quinn', label: 'Patsy Quinn' },
      { option: 'remo', label: 'Remo' },
      { option: 'renegade_rhythms_marc_moggy', label: 'Renegade Rhythms (Marc Moggy)' },
      { option: 'david_roman', label: 'David Roman' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'roundstone_malachy_kearns', label: 'Roundstone (Malachy Kearns)' },
      { option: 'david_settles_davey_drums', label: 'David Settles (Davey Drums)' },
      { option: 'shaw_percussion', label: 'Shaw Percussion' },
      { option: 'ralf_siepmann', label: 'Ralf Siepmann' },
      { option: 'skye_bodhrans_john_wilson', label: 'Skye Bodhrans (John Wilson)' },
      { option: 'bjarne_stensrud', label: 'Bjarne Stensrud' },
      { option: 'davy_stuart', label: 'Davy Stuart' },
      { option: 'sylvan_temple_drums_robin_shackleton', label: 'Sylvan Temple Drums (Robin Shackleton)' },
      { option: 'trinity_college', label: 'Trinity College' },
      { option: 'michael_vignoles', label: 'Michael Vignoles' },
      { option: 'waltons', label: 'Waltons' },
      { option: 'brendan_white', label: 'Brendan White' },
      { option: 'rohan_young', label: 'Rohan Young' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bodhran',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'folk_percussion_bodhran_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '30_cm_12', label: '30 cm / ~12"' },
      { option: '35_cm_14', label: '35 cm / ~14"' },
      { option: '37_cm_15', label: '37 cm / ~15"' },
      { option: '40_cm_16', label: '40 cm / ~16"' },
      { option: '45_cm_18', label: '45 cm / ~18"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bodhran',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Diameter',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bodhran_head',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'goatskin', label: 'Goatskin' },
      { option: 'lambeg_goatskin', label: 'Lambeg Goatskin' },
      { option: 'vellum_goatskin', label: 'Vellum Goatskin' },
      { option: 'calfskin', label: 'Calfskin' },
      { option: 'kangaroo', label: 'Kangaroo' },
      { option: 'synthetic', label: 'Synthetic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bodhran',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Type',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bodhran_tuning',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'non_tunable_fixed', label: 'Non-Tunable (Fixed)' },
      { option: 'allen_key_tuning', label: 'Allen Key Tuning' },
      { option: 'hand_tunable_thumbscrew_knob', label: 'Hand-Tunable (Thumbscrew / Knob)' },
      { option: 'cam_tuning', label: 'Cam Tuning' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bodhran',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning System',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bodhran_depth',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'shallow_up_to_10_cm_4', label: 'Shallow (up to 10 cm / ~4")' },
      { option: 'standard_10_13_cm_4_5', label: 'Standard (10–13 cm / ~4"–5")' },
      { option: 'deep_13_cm_5', label: 'Deep (13+ cm / ~5"+)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bodhran',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Rim Depth',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Rim Depth',
      isDetail: true,
    },
    saveConfig: {
      label: 'Rim Depth',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bones_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'joe_birl', label: 'Joe Birl' },
      { option: 'bone_dry_musical_instrument_company', label: 'Bone Dry Musical Instrument Company' },
      { option: 'brown_s_bones_steve_brown', label: 'Brown\'s Bones (Steve Brown)' },
      { option: 'tom_connolly', label: 'Tom Connolly' },
      { option: 'danforth_bones_percy_danforth_sandor_slomovits', label: 'Danforth Bones (Percy Danforth & Sandor Slomovits)' },
      { option: 'nicholas_driver', label: 'Nicholas Driver' },
      { option: 'pipp_gillette', label: 'Pipp Gillette' },
      { option: 'kessler_chlefeli_robi_kessler', label: 'Kessler Chlefeli (Robi Kessler)' },
      { option: 'adam_klein', label: 'Adam Klein' },
      { option: 'lark_in_the_morning', label: 'Lark in the Morning' },
      { option: 'mcneela_music', label: 'McNeela Music' },
      { option: 'menzies_instruments_jeff_menzies', label: 'Menzies Instruments (Jeff Menzies)' },
      { option: 'm_t_pockets_bones_brennan_kuhns', label: 'M.T. Pockets Bones (Brennan Kuhns)' },
      { option: 'plunkett_bones_aaron_plunkett', label: 'Plunkett Bones (Aaron Plunkett)' },
      { option: 'prokussion', label: 'ProKussion' },
      { option: 'roundstone_musical_instruments_malachy_kearns', label: 'Roundstone Musical Instruments (Malachy Kearns)' },
      { option: 'shooting_star_bones_mardeen_gordon', label: 'Shooting Star Bones (Mardeen Gordon)' },
      { option: 'triskele_rhythm_bones_jim_hesch', label: 'Triskele Rhythm Bones (Jim Hesch)' },
      { option: 'waltons', label: 'Waltons' },
      { option: 'hans_weehuizen', label: 'Hans Weehuizen' },
      { option: 'whamdiddle_rick_fogel', label: 'Whamdiddle (Rick Fogel)' },
      { option: 'wild_acoustic_music_co', label: 'Wild Acoustic Music Co.' },
      { option: 'wolf_hill_woodworking', label: 'Wolf Hill Woodworking' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bones',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'folk_percussion_bones_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ash', label: 'Ash' },
      { option: 'bakelite', label: 'Bakelite' },
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'beech', label: 'Beech' },
      { option: 'birch', label: 'Birch' },
      { option: 'bocote', label: 'Bocote' },
      { option: 'bubinga', label: 'Bubinga' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'cocuswood_cocus', label: 'Cocuswood (Cocus)' },
      { option: 'cow_rib_bone', label: 'Cow Rib Bone' },
      { option: 'cow_shin_bone', label: 'Cow Shin Bone' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'goat_rib_bone', label: 'Goat Rib Bone' },
      { option: 'hickory', label: 'Hickory' },
      { option: 'holly', label: 'Holly' },
      { option: 'horse_bone', label: 'Horse Bone' },
      { option: 'kingwood', label: 'Kingwood' },
      { option: 'lignum_vitae', label: 'Lignum Vitae' },
      { option: 'lilac', label: 'Lilac' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'oak', label: 'Oak' },
      { option: 'olivewood', label: 'Olivewood' },
      { option: 'padauk', label: 'Padauk' },
      { option: 'pine', label: 'Pine' },
      { option: 'plastic', label: 'Plastic' },
      { option: 'poplar', label: 'Poplar' },
      { option: 'purpleheart', label: 'Purpleheart' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'slate', label: 'Slate' },
      { option: 'tigerwood', label: 'Tigerwood' },
      { option: 'tulipwood', label: 'Tulipwood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'wenge', label: 'Wenge' },
      { option: 'zebrawood', label: 'Zebrawood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bones',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bones_shape',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'curved', label: 'Curved' },
      { option: 'straight_flat', label: 'Straight / Flat' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bones',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Shape',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Shape',
      isDetail: true,
    },
    saveConfig: {
      label: 'Shape',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_bones_set_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single_pair_2_bones', label: 'Single Pair (2 bones)' },
      { option: 'double_pair_4_bones', label: 'Double Pair (4 bones)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_bones',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Set Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Set Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Set Size',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_spoons_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aar', label: 'AAR' },
      { option: 'catpaws_steve_catania', label: 'Catpaws (Steve Catania)' },
      { option: 'jim_cruise_getspooned', label: 'Jim Cruise (GetSpooned)' },
      { option: 'grover_trophy', label: 'Grover/Trophy' },
      { option: 'walt_haines', label: 'Walt Haines' },
      { option: 'heritage_musical_spoons', label: 'Heritage Musical Spoons' },
      { option: 'kadence', label: 'Kadence' },
      { option: 'khokhloma', label: 'Khokhloma' },
      { option: 'kilt_emporium', label: 'Kilt Emporium' },
      { option: 'mano_percussion', label: 'Mano Percussion' },
      { option: 'matenza', label: 'Matenza' },
      { option: 'mcneela_paraic_mcneela', label: 'McNeela (Paraic McNeela)' },
      { option: 'mihey', label: 'Mihey' },
      { option: 'roosebeck', label: 'Roosebeck' },
      { option: 'tobar', label: 'Tobar' },
      { option: 'waltons', label: 'Waltons' },
      { option: 'woodstock', label: 'Woodstock' },
      { option: 'yeshone', label: 'Yeshone' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_spoons',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'folk_percussion_spoons_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'one_piece_solid', label: 'One-Piece (Solid)' },
      { option: 'joined_pair', label: 'Joined Pair' },
      { option: 'separate_pair', label: 'Separate Pair' },
      { option: 'handle_mounted', label: 'Handle-Mounted' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_spoons',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Style',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_spoons_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'maple', label: 'Maple' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'birch', label: 'Birch' },
      { option: 'acacia', label: 'Acacia' },
      { option: 'cocuswood', label: 'Cocuswood' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'other_hardwood', label: 'Other Hardwood' },
      { option: 'stainless_steel', label: 'Stainless Steel' },
      { option: 'chrome_plated_steel', label: 'Chrome-Plated Steel' },
      { option: 'polycarbonate', label: 'Polycarbonate' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_spoons',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_spoons_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'small_under_22_cm_under_8_5', label: 'Small (Under 22 cm / Under 8.5")' },
      { option: 'medium_22_24_cm_8_5_9_5', label: 'Medium (22–24 cm / 8.5"–9.5")' },
      { option: 'large_over_24_cm_over_9_5', label: 'Large (Over 24 cm / Over 9.5")' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_spoons',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_djembe_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'africa_heartwood_project', label: 'Africa Heartwood Project' },
      { option: 'african_rhythm_traders', label: 'African Rhythm Traders' },
      { option: 'afroton', label: 'Afroton' },
      { option: 'aklot', label: 'AKLOT' },
      { option: 'alpha_rhythm_roots', label: 'Alpha Rhythm Roots' },
      { option: 'arief_djembe', label: 'Arief Djembe' },
      { option: 'bamba_drums', label: 'Bamba Drums' },
      { option: 'carved_culture', label: 'Carved Culture' },
      { option: 'djembe_art', label: 'Djembe Art' },
      { option: 'djembe_direct', label: 'Djembe Direct' },
      { option: 'djembe_drums_skins', label: 'Djembe Drums & Skins' },
      { option: 'djembe_life', label: 'Djembe Life' },
      { option: 'djembemafia', label: 'Djembemafia' },
      { option: 'djoliba', label: 'Djoliba' },
      { option: 'drumconnection', label: 'DrumConnection' },
      { option: 'drumskull_drums', label: 'Drumskull Drums' },
      { option: 'guinea_gold', label: 'Guinea Gold' },
      { option: 'jive', label: 'JIVE' },
      { option: 'kangaba', label: 'Kangaba' },
      { option: 'koma_drum', label: 'Koma Drum' },
      { option: 'latin_percussion_lp', label: 'Latin Percussion (LP)' },
      { option: 'manito_percussion', label: 'Manito Percussion' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'motherland_music', label: 'Motherland Music' },
      { option: 'overseas_connection', label: 'Overseas Connection' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'remo', label: 'Remo' },
      { option: 'rhythm_traders', label: 'Rhythm Traders' },
      { option: 'sawtooth', label: 'Sawtooth' },
      { option: 'schalloch', label: 'Schalloch' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'super_mande_percussion', label: 'Super Mandé Percussion' },
      { option: 'terre', label: 'Terre' },
      { option: 'toca', label: 'Toca' },
      { option: 'tycoon_percussion', label: 'Tycoon Percussion' },
      { option: 'wassoulou_percussion', label: 'Wassoulou Percussion' },
      { option: 'wooden_roots', label: 'Wooden Roots' },
      { option: 'world_rhythm', label: 'World Rhythm' },
      { option: 'wula_drum', label: 'Wula Drum' },
      { option: 'x8_drums', label: 'X8 Drums' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_djembe',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'african_percussion_djembe_head_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '7_or_smaller', label: '7" or smaller' },
      { option: '8', label: '8"' },
      { option: '9', label: '9"' },
      { option: '10', label: '10"' },
      { option: '12', label: '12"' },
      { option: '13', label: '13"' },
      { option: '14', label: '14"' },
      { option: '15_or_larger', label: '15" or larger' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_djembe',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Diameter',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_djembe_shell_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acousticon', label: 'Acousticon' },
      { option: 'dimba_dugura_cordyla', label: 'Dimba / Dugura (Cordyla)' },
      { option: 'djalla_african_mahogany_khaya', label: 'Djalla / African Mahogany (Khaya)' },
      { option: 'fiberglass', label: 'Fiberglass' },
      { option: 'gele_prosopis', label: 'Gele (Prosopis)' },
      { option: 'gueni_hare_pterocarpus', label: 'Gueni / Haré (Pterocarpus)' },
      { option: 'iroko_milicia', label: 'Iroko (Milicia)' },
      { option: 'lenke_lingue_afzelia', label: 'Lenke / Lingue (Afzelia)' },
      { option: 'mahogany_plantation', label: 'Mahogany (Plantation)' },
      { option: 'mango_wood', label: 'Mango Wood' },
      { option: 'melina', label: 'Melina' },
      { option: 'pvc_plastic', label: 'PVC / Plastic' },
      { option: 'siam_oak_rubber_wood', label: 'Siam Oak / Rubber Wood' },
      { option: 'tweneboa', label: 'Tweneboa' },
      { option: 'unknown', label: 'Unknown' },
      { option: 'other_wood', label: 'Other Wood' },
      { option: 'other_synthetic', label: 'Other Synthetic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_djembe',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Shell Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Shell Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Shell Material',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_djembe_head',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'goatskin', label: 'Goatskin' },
      { option: 'cowhide_calfskin', label: 'Cowhide / Calfskin' },
      { option: 'mule_skin', label: 'Mule Skin' },
      { option: 'antelope_skin', label: 'Antelope Skin' },
      { option: 'synthetic', label: 'Synthetic' },
      { option: 'other_natural_skin', label: 'Other Natural Skin' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_djembe',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Type',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_djembe_tuning',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'rope_tuned_mali_weave', label: 'Rope-Tuned (Mali Weave)' },
      { option: 'mechanically_tuned_lug_key', label: 'Mechanically Tuned (Lug / Key)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_djembe',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning System',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_kalimba_thumb_piano_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aklot', label: 'Aklot' },
      { option: 'beautmelo', label: 'Beautmelo' },
      { option: 'carved_culture', label: 'Carved Culture' },
      { option: 'catania', label: 'Catania' },
      { option: 'chill_angels', label: 'Chill Angels' },
      { option: 'chron_crafts', label: 'Chron Crafts' },
      { option: 'coffinsea', label: 'Coffinsea' },
      { option: 'cuel', label: 'CUEL' },
      { option: 'donner', label: 'Donner' },
      { option: 'dripex', label: 'Dripex' },
      { option: 'dual_april_yang', label: 'Dual (April Yang)' },
      { option: 'eastrock', label: 'Eastrock' },
      { option: 'ekato', label: 'Ekato' },
      { option: 'gecko', label: 'Gecko' },
      { option: 'genround', label: 'Genround' },
      { option: 'himi', label: 'Himi' },
      { option: 'hluru', label: 'Hluru' },
      { option: 'hokema', label: 'Hokema' },
      { option: 'honhand', label: 'Honhand' },
      { option: 'kimi', label: 'Kimi' },
      { option: 'kookalimba', label: 'KooKalimba' },
      { option: 'lingting', label: 'LingTing' },
      { option: 'luna', label: 'Luna' },
      { option: 'magadi', label: 'Magadi' },
      { option: 'mahalo', label: 'Mahalo' },
      { option: 'martin_smith', label: 'Martin Smith' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'mifoge', label: 'Mifoge' },
      { option: 'moozica', label: 'Moozica' },
      { option: 'newlam', label: 'Newlam' },
      { option: 'orora', label: 'Orora' },
      { option: 'purm', label: 'Purm' },
      { option: 'quokka_tunes', label: 'Quokka Tunes' },
      { option: 'ranch', label: 'Ranch' },
      { option: 'rayzm', label: 'Rayzm' },
      { option: 'rd_music', label: 'RD Music' },
      { option: 'remo', label: 'Remo' },
      { option: 'schlagwerk', label: 'Schlagwerk' },
      { option: 'seeds', label: 'Seeds' },
      { option: 'sela', label: 'Sela' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'thumb_fun_kalimbas', label: 'Thumb Fun Kalimbas' },
      { option: 'toca', label: 'Toca' },
      { option: 'hugh_tracey_ami', label: 'Hugh Tracey (AMI)' },
      { option: 'unokki', label: 'Unokki' },
      { option: 'waltert', label: 'Waltert' },
      { option: 'x8_drums', label: 'X8 Drums' },
      { option: 'xinqidian', label: 'Xinqidian' },
      { option: 'april_yang_shakala', label: 'April Yang (Shakala)' },
      { option: 'yundie', label: 'Yundie' },
      { option: 'zaidenberg', label: 'Zaidenberg' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_kalimba_thumb_piano',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'african_percussion_kalimba_thumb_piano_tines',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '5', label: '5' },
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: '10', label: '10' },
      { option: '11', label: '11' },
      { option: '15', label: '15' },
      { option: '17', label: '17' },
      { option: '21', label: '21' },
      { option: '24', label: '24' },
      { option: '34', label: '34' },
      { option: '36', label: '36' },
      { option: '41', label: '41' },
      { option: '42', label: '42' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_kalimba_thumb_piano',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Tines',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Tines',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Tines',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_kalimba_thumb_piano_body_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'hollow_body_box', label: 'Hollow Body (Box)' },
      { option: 'flat_board_solid', label: 'Flat Board (Solid)' },
      { option: 'sansula_membrane', label: 'Sansula (Membrane)' },
      { option: 'electric_pickup_equipped', label: 'Electric / Pickup-Equipped' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_kalimba_thumb_piano',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Type',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_kalimba_thumb_piano_body_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acacia', label: 'Acacia' },
      { option: 'acrylic_resin', label: 'Acrylic / Resin' },
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'camphor', label: 'Camphor' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'coconut_shell', label: 'Coconut Shell' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'gourd_calabash', label: 'Gourd / Calabash' },
      { option: 'kiaat_wild_teak', label: 'Kiaat (Wild Teak)' },
      { option: 'koa', label: 'Koa' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'okoume', label: 'Okoume' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sandalwood', label: 'Sandalwood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'spruce', label: 'Spruce' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_kalimba_thumb_piano',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Material',
      isRequired: false,
    },
  },
  {
    key: 'african_percussion_kalimba_thumb_piano_scale_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'diatonic', label: 'Diatonic' },
      { option: 'pentatonic', label: 'Pentatonic' },
      { option: 'chromatic', label: 'Chromatic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'african_percussion_kalimba_thumb_piano',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Type',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_bongos_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '3a', label: '3A' },
      { option: '5d2_percussion', label: '5d2 Percussion' },
      { option: 'a_tempo', label: 'A Tempo' },
      { option: 'cadeson', label: 'Cadeson' },
      { option: 'club_salsa_gewa', label: 'Club Salsa (Gewa)' },
      { option: 'colombia_percussion', label: 'Colombia Percussion' },
      { option: 'cosmic_percussion_cp', label: 'Cosmic Percussion (CP)' },
      { option: 'd_flat_percussion', label: 'DB Percussion' },
      { option: 'eastar', label: 'Eastar' },
      { option: 'eastrock', label: 'EastRock' },
      { option: 'el_piernas_hector_rocha', label: 'El Piernas (Hector Rocha)' },
      { option: 'fat_congas_jim_long', label: 'Fat Congas (Jim Long)' },
      { option: 'gewa', label: 'Gewa' },
      { option: 'gon_bops', label: 'Gon Bops' },
      { option: 'gp_percussion', label: 'GP Percussion' },
      { option: 'headliner_meinl', label: 'Headliner (Meinl)' },
      { option: 'hr_percussion_hector_rocha', label: 'HR Percussion (Hector Rocha)' },
      { option: 'jcr_calixto_caly_rivera', label: 'JCR (Calixto "Caly" Rivera)' },
      { option: 'latin_percussion_lp', label: 'Latin Percussion (LP)' },
      { option: 'mano_percussion', label: 'Mano Percussion' },
      { option: 'manito_percussion_ryan_manito_wendel', label: 'Manito Percussion (Ryan "Manito" Wendel)' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'millenium', label: 'Millenium' },
      { option: 'moperc', label: 'Moperc' },
      { option: 'natal', label: 'Natal' },
      { option: 'nino_meinl', label: 'Nino (Meinl)' },
      { option: 'ossi_percussion', label: 'Ossi Percussion' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'pm_percussion_peter_musser', label: 'PM Percussion (Peter Musser)' },
      { option: 'remo', label: 'Remo' },
      { option: 'requena', label: 'Requena' },
      { option: 'resolution_drums_ralph_flores', label: 'Resolution Drums (Ralph Flores)' },
      { option: 'rhythm_tech', label: 'Rhythm Tech' },
      { option: 'rockjam', label: 'RockJam' },
      { option: 'schalloch', label: 'Schalloch' },
      { option: 'skin_on_skin_jay_berek', label: 'Skin On Skin (Jay Berek)' },
      { option: 'matthew_smith_ritmo', label: 'Matthew Smith (Ritmo)' },
      { option: 'sol_percussion', label: 'Sol Percussion' },
      { option: 'songo_percussion_elvis_crazy_el', label: 'Songo Percussion (Elvis "Crazy El")' },
      { option: 'sonor', label: 'Sonor' },
      { option: 'spirit_in_the_wood_conrad_kubiak', label: 'Spirit In The Wood (Conrad Kubiak)' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'timbas_del_valle', label: 'Timbas del Valle' },
      { option: 'junior_tirado', label: 'Junior Tirado' },
      { option: 'toca', label: 'Toca' },
      { option: 'tycoon_percussion', label: 'Tycoon Percussion' },
      { option: 'valje_tom_flores', label: 'Valje (Tom Flores)' },
      { option: 'charlie_vega_santiago_cvs_percussion', label: 'Charlie Vega Santiago (CVS Percussion)' },
      { option: 'vergara', label: 'Vergara' },
      { option: 'volcano_percussion_tom_alexander', label: 'Volcano Percussion (Tom Alexander)' },
      { option: 'x8_drums', label: 'X8 Drums' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_bongos',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_percussion_bongos_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'mini_4_5', label: 'Mini (4"–5")' },
      { option: '6_7', label: '6" / 7"' },
      { option: '61_2_71_2', label: '6½" / 7½"' },
      { option: '63_4_8', label: '6¾" / 8"' },
      { option: '7_81_2', label: '7" / 8½"' },
      { option: '71_4_85_8', label: '7¼" / 8⅝"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_bongos',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_bongos_shell_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'abs_synthetic', label: 'ABS / Synthetic' },
      { option: 'acrylic', label: 'Acrylic' },
      { option: 'ash', label: 'Ash' },
      { option: 'birch', label: 'Birch' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'fiberglass', label: 'Fiberglass' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'oak_other', label: 'Oak (Other)' },
      { option: 'poplar', label: 'Poplar' },
      { option: 'rubberwood', label: 'Rubberwood' },
      { option: 'siam_oak', label: 'Siam Oak' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_bongos',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Shell Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Shell Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Shell Material',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_bongos_head_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'rawhide_natural_skin_unspecified', label: 'Rawhide (Natural Skin, Unspecified)' },
      { option: 'buffalo_water_buffalo_skin', label: 'Buffalo / Water Buffalo Skin' },
      { option: 'goat_skin', label: 'Goat Skin' },
      { option: 'calfskin_cowhide', label: 'Calfskin / Cowhide' },
      { option: 'horsehide', label: 'Horsehide' },
      { option: 'synthetic', label: 'Synthetic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_bongos',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Material',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_cajon_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a_tempo', label: 'A Tempo' },
      { option: 'a_p_cajon', label: 'A&P Cajón' },
      { option: 'abueno_percusion', label: 'ABueno Percusión' },
      { option: 'afrika_percussion', label: 'Afrika Percussion' },
      { option: 'aklot', label: 'Aklot' },
      { option: 'al_andalus_cajones', label: 'Al Andalus Cajones' },
      { option: 'alexander_percussion', label: 'Alexander Percussion' },
      { option: 'arlequin_street_art_percussion', label: 'Arlequín Street Art Percussion' },
      { option: 'bahamut', label: 'Bahamut' },
      { option: 'cajon_flamenco_soler', label: 'Cajón Flamenco Soler' },
      { option: 'cajones_flamencos_tormo', label: 'Cajones Flamencos Tormo' },
      { option: 'camarada_percusion', label: 'Camarada Percusión' },
      { option: 'carbono_instruments', label: 'Carbono Instruments' },
      { option: 'chm_percusion', label: 'CHM Percusión' },
      { option: 'clapbox', label: 'Clapbox' },
      { option: 'cperu', label: 'CPeru' },
      { option: 'cruz_cajon', label: 'Cruz Cajón' },
      { option: 'custom_cajon_drums', label: 'Custom Cajon Drums' },
      { option: 'd_manue_percusion', label: 'D\'Manué Percusión' },
      { option: 'daydrums_cajon_co', label: 'DayDrums Cajon Co.' },
      { option: 'dg_de_gregorio', label: 'DG De Gregorio' },
      { option: 'dog_s_percussion', label: 'Dog\'s Percussion' },
      { option: 'donner', label: 'Donner' },
      { option: 'duende_percussion', label: 'Duende Percussion' },
      { option: 'eastrock', label: 'EastRock' },
      { option: 'fabox_cajones', label: 'FaBox Cajones' },
      { option: 'fat_congas', label: 'Fat Congas' },
      { option: 'gecko', label: 'Gecko' },
      { option: 'gon_bops', label: 'Gon Bops' },
      { option: 'juan_heredia_percusion', label: 'Juan Heredia Percusión' },
      { option: 'ime', label: 'IME' },
      { option: 'jc_percusion', label: 'JC Percusión' },
      { option: 'jp_cajon', label: 'JP Cajón' },
      { option: 'kandu', label: 'Kandu' },
      { option: 'katho_percusion', label: 'Katho Percusión' },
      { option: 'keo_percussion', label: 'KEO Percussion' },
      { option: 'kimto_percusion', label: 'Kimto Percusión' },
      { option: 'kopf_percussion', label: 'Kopf Percussion' },
      { option: 'kotz', label: 'Kotz' },
      { option: 'la_rosa_percusion', label: 'La Rosa Percusión' },
      { option: 'latin_percussion_lp', label: 'Latin Percussion (LP)' },
      { option: 'lieke', label: 'Lieke' },
      { option: 'lk_percussion', label: 'LK Percussion' },
      { option: 'malby_cajones_flamencos', label: 'Malby Cajones Flamencos' },
      { option: 'manuel_moya_artesania_percusion', label: 'Manuel Moya Artesanía Percusión' },
      { option: 'mediterranea_musical', label: 'Mediterránea Musical' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'moravian_percussion', label: 'Moravian Percussion' },
      { option: 'nativo_percusion', label: 'Nativo Percusión' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'pepote_percusion', label: 'Pepote Percusión' },
      { option: 'percusion_real_pr', label: 'Percusión Real (PR)' },
      { option: 'peru_percusion', label: 'Peru Percusión' },
      { option: 'pjp_percussion', label: 'PJP Percussion' },
      { option: 'pogola_flat', label: 'Pogolab' },
      { option: 'popercussion', label: 'PoPercussion' },
      { option: 'ramon_porrina_percusion', label: 'Ramón Porrina Percusión' },
      { option: 'prk', label: 'PRK' },
      { option: 'pyle', label: 'Pyle' },
      { option: 'remo', label: 'Remo' },
      { option: 'requena_percusion', label: 'Requena Percusión' },
      { option: 'rhythm_tech', label: 'Rhythm Tech' },
      { option: 'rj_cajon', label: 'RJ Cajón' },
      { option: 'rockhouse', label: 'Rockhouse' },
      { option: 'roland', label: 'Roland' },
      { option: 'schalloch', label: 'Schalloch' },
      { option: 'schlagwerk', label: 'Schlagwerk' },
      { option: 'sela', label: 'Sela' },
      { option: 'skull_percussion', label: 'Skull Percussion' },
      { option: 'sol_percussion', label: 'Sol Percussion' },
      { option: 'sonor', label: 'Sonor' },
      { option: 'sr_cajon', label: 'SR Cajón' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'swan_percussion', label: 'Swan Percussion' },
      { option: 'tierrapiano', label: 'Tierrapiano' },
      { option: 'toca', label: 'Toca' },
      { option: 'tovar_percussion', label: 'Tovar Percussion' },
      { option: 'tres_lados', label: 'Tres Lados' },
      { option: 'tycoon_percussion', label: 'Tycoon Percussion' },
      { option: 'ubeta', label: 'Ubeta' },
      { option: 'valter_percussion', label: 'Valter Percussion' },
      { option: 'vevor', label: 'Vevor' },
      { option: 'vp_cajones', label: 'VP Cajones' },
      { option: 'x8_drums', label: 'X8 Drums' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_cajon',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'latin_american_percussion_cajon_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'full_size', label: 'Full Size' },
      { option: 'compact_3_4_size', label: 'Compact / ¾ Size' },
      { option: 'travel_lap', label: 'Travel / Lap' },
      { option: 'mini', label: 'Mini' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_cajon',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_cajon_snare',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'none_peruvian_traditional', label: 'None (Peruvian / Traditional)' },
      { option: 'fixed_guitar_strings', label: 'Fixed Guitar Strings' },
      { option: 'adjustable_guitar_strings', label: 'Adjustable Guitar Strings' },
      { option: 'fixed_snare_wires', label: 'Fixed Snare Wires' },
      { option: 'adjustable_snare_wires', label: 'Adjustable Snare Wires' },
      { option: 'on_off_switchable_snare', label: 'On/Off Switchable Snare' },
      { option: 'dual_system_strings_wires', label: 'Dual System (Strings + Wires)' },
      { option: 'electronic_hybrid', label: 'Electronic / Hybrid' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_cajon',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Snare System',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Snare System',
      isDetail: true,
    },
    saveConfig: {
      label: 'Snare System',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_cajon_tapa',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ash', label: 'Ash' },
      { option: 'beech', label: 'Beech' },
      { option: 'birch', label: 'Birch' },
      { option: 'cedar', label: 'Cedar' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'ironwood', label: 'Ironwood' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'mohena', label: 'Mohena' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'zebrawood', label: 'Zebrawood' },
      { option: 'ziricote', label: 'Ziricote' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_cajon',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tapa (Playing Surface)',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tapa (Playing Surface)',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tapa (Playing Surface)',
      isRequired: false,
    },
  },
  {
    key: 'latin_american_percussion_cajon_body_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acrylic', label: 'Acrylic' },
      { option: 'alder', label: 'Alder' },
      { option: 'beech', label: 'Beech' },
      { option: 'birch_plywood', label: 'Birch Plywood' },
      { option: 'fiberglass', label: 'Fiberglass' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'mdf', label: 'MDF' },
      { option: 'solid_hardwood_various', label: 'Solid Hardwood (Various)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'latin_american_percussion_cajon',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Material',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_castanets_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'black_swamp_percussion', label: 'Black Swamp Percussion' },
      { option: 'castanuelas_del_sur', label: 'Castañuelas del Sur' },
      { option: 'danmar', label: 'Danmar' },
      { option: 'elton', label: 'Elton' },
      { option: 'frank_epstein', label: 'Frank Epstein' },
      { option: 'filigrana', label: 'Filigrana' },
      { option: 'galiano_victor_galiano', label: 'Galiano (Víctor Galiano)' },
      { option: 'gonalca', label: 'Gonalca' },
      { option: 'honsuy', label: 'Honsuy' },
      { option: 'jale', label: 'Jale' },
      { option: 'lp_latin_percussion', label: 'LP (Latin Percussion)' },
      { option: 'ludwig', label: 'Ludwig' },
      { option: 'marca', label: 'Marca' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'nino', label: 'Nino' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'lucero_tena', label: 'Lucero Tena' },
      { option: 'toca', label: 'Toca' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_castanets',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'folk_percussion_castanets_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'hand_castanets', label: 'Hand Castanets' },
      { option: 'handle_castanets', label: 'Handle Castanets' },
      { option: 'machine_mounted_castanets', label: 'Machine / Mounted Castanets' },
      { option: 'finger_castanets', label: 'Finger Castanets' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_castanets',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_castanets_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'boxwood', label: 'Boxwood' },
      { option: 'bubinga', label: 'Bubinga' },
      { option: 'chestnut', label: 'Chestnut' },
      { option: 'cocobolo', label: 'Cocobolo' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'granadillo_african_blackwood', label: 'Granadillo (African Blackwood)' },
      { option: 'plastic', label: 'Plastic' },
      { option: 'pressed_fiber_fiberglass', label: 'Pressed Fiber / Fiberglass' },
      { option: 'pressed_canvas_cloth', label: 'Pressed Canvas / Cloth' },
      { option: 'purpleheart', label: 'Purpleheart' },
      { option: 'rosewood_palo_santo', label: 'Rosewood / Palo Santo' },
      { option: 'wenge', label: 'Wenge' },
      { option: 'other_hardwood', label: 'Other Hardwood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_castanets',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_castanets_sound_box',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single', label: 'Single' },
      { option: 'double_doble_caja', label: 'Double (Doble Caja)' },
      { option: 'n_a', label: 'N/A' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_castanets',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Sound Box',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Sound Box',
      isDetail: true,
    },
    saveConfig: {
      label: 'Sound Box',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_castanets_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'extra_small_child', label: 'Extra Small / Child' },
      { option: 'small', label: 'Small' },
      { option: 'medium', label: 'Medium' },
      { option: 'large', label: 'Large' },
      { option: 'other_see_description', label: 'Other / See Description' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_castanets',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_percussion_doumbek_darbuka_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'agean', label: 'Agean' },
      { option: 'alexandria', label: 'Alexandria' },
      { option: 'ara_flat_instruments', label: 'Arab Instruments' },
      { option: 'artdrum', label: 'Artdrum' },
      { option: 'clapbox', label: 'Clapbox' },
      { option: 'cumbus', label: 'Cümbüş' },
      { option: 'donizetti', label: 'Donizetti' },
      { option: 'earth_percussion', label: 'Earth Percussion' },
      { option: 'el_masry', label: 'El Masry' },
      { option: 'emin_percussion', label: 'Emin Percussion' },
      { option: 'gawharet_el_fan', label: 'Gawharet El Fan' },
      { option: 'hawkdancing_studio', label: 'Hawkdancing Studio' },
      { option: 'liberty_one', label: 'Liberty One' },
      { option: 'lp_latin_percussion', label: 'LP (Latin Percussion)' },
      { option: 'masterwork', label: 'Masterwork' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'nakhle', label: 'Nakhle' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'powerbeat', label: 'PowerBeat' },
      { option: 'q_drums_quentin_shaw', label: 'Q Drums (Quentin Shaw)' },
      { option: 'remo', label: 'Remo' },
      { option: 'sala_muzik', label: 'Sala Muzik' },
      { option: 'savvas', label: 'Savvas' },
      { option: 'schalloch', label: 'Schalloch' },
      { option: 'sultan_instrument', label: 'Sultan Instrument' },
      { option: 'tapadum', label: 'Tapadum' },
      { option: 'toca', label: 'Toca' },
      { option: 'trirmusic', label: 'Trirmusic' },
      { option: 'tycoon', label: 'Tycoon' },
      { option: 'unosell', label: 'Unosell' },
      { option: 'wambooka', label: 'Wambooka' },
      { option: 'x8_drums', label: 'X8 Drums' },
      { option: 'zaza_percussion', label: 'ZAZA Percussion' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_percussion_doumbek_darbuka',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'middle_eastern_north_african_percussion_doumbek_darbuka_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'egyptian_rounded_rim', label: 'Egyptian (Rounded Rim)' },
      { option: 'turkish_exposed_edge', label: 'Turkish (Exposed Edge)' },
      { option: 'clay_ceramic', label: 'Clay / Ceramic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_percussion_doumbek_darbuka',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Style',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_percussion_doumbek_darbuka_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solo_classic_8_9_20_23_cm_head', label: 'Solo / Classic (8–9" / 20–23 cm head)' },
      { option: 'sombaty_sumbati_9_9_5_23_25_cm_head', label: 'Sombaty / Sumbati (9–9.5" / 23–25 cm head)' },
      { option: 'dohola_bass_10_12_27_30_cm_head', label: 'Dohola / Bass (10–12" / 27–30 cm head)' },
      { option: 'mini_travel_under_8_under_20_cm_head', label: 'Mini / Travel (under 8" / under 20 cm head)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_percussion_doumbek_darbuka',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_percussion_doumbek_darbuka_shell_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'brass', label: 'Brass' },
      { option: 'cast_aluminum', label: 'Cast Aluminum' },
      { option: 'clay_ceramic', label: 'Clay / Ceramic' },
      { option: 'copper', label: 'Copper' },
      { option: 'fiberglass', label: 'Fiberglass' },
      { option: 'steel', label: 'Steel' },
      { option: 'synthetic_composite', label: 'Synthetic / Composite' },
      { option: 'wood', label: 'Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_percussion_doumbek_darbuka',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Shell Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Shell Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Shell Material',
      isRequired: false,
    },
  },
  {
    key: 'middle_eastern_north_african_percussion_doumbek_darbuka_head',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'calfskin', label: 'Calfskin' },
      { option: 'fish_skin', label: 'Fish Skin' },
      { option: 'goatskin', label: 'Goatskin' },
      { option: 'synthetic', label: 'Synthetic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'middle_eastern_north_african_percussion_doumbek_darbuka',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Type',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_percussion_tabla_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'akbar_mian_bros', label: 'Akbar Mian & Bros' },
      { option: 'anwar_tabla_maker', label: 'Anwar Tabla Maker' },
      { option: 'badya_niketan', label: 'Badya Niketan' },
      { option: 'banjira', label: 'banjira' },
      { option: 'bina', label: 'Bina' },
      { option: 'chopra', label: 'Chopra' },
      { option: 'mukta_das', label: 'Mukta Das' },
      { option: 'gullu_tabla_maker', label: 'Gullu Tabla Maker' },
      { option: 'bharat_kakade', label: 'Bharat Kakade' },
      { option: 'somnath_kakade', label: 'Somnath Kakade' },
      { option: 'maharaja_musicals', label: 'Maharaja Musicals' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'mm_musician_s_mall', label: 'MM (Musician\'s Mall)' },
      { option: 'narayan_badya_bhandar_shyamal_das', label: 'Narayan Badya Bhandar (Shyamal Das)' },
      { option: 'nasir_ali', label: 'Nasir Ali' },
      { option: 'old_delhi_music_taal', label: 'Old Delhi Music (TAAL)' },
      { option: 'paloma_haribhau_vishwanath', label: 'Paloma (Haribhau Vishwanath)' },
      { option: 'qasim_khan_niyazi_sons', label: 'Qasim Khan Niyazi & Sons' },
      { option: 'queen_brass', label: 'Queen Brass' },
      { option: 'remo', label: 'Remo' },
      { option: 'rikhi_ram', label: 'Rikhi Ram' },
      { option: 'sai_musicals', label: 'Sai Musicals' },
      { option: 'monoj_kumar_sardar_mks', label: 'Monoj Kumar Sardar (MKS)' },
      { option: 'satnam', label: 'Satnam' },
      { option: 'sg_musical', label: 'SG Musical' },
      { option: 'shreyas', label: 'Shreyas' },
      { option: 'sound_labs', label: 'Sound Labs' },
      { option: 'store_one_india', label: 'Store One India' },
      { option: 'vishnu_sutar', label: 'Vishnu Sutar' },
      { option: 'haridas_vhatkar', label: 'Haridas Vhatkar' },
      { option: 'kishor_manoj_vhatkar', label: 'Kishor & Manoj Vhatkar' },
      { option: 'zaza_percussion', label: 'Zaza Percussion' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_percussion_tabla',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'south_asian_percussion_tabla_config',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'set_dayan_bayan', label: 'Set (Dayan + Bayan)' },
      { option: 'dayan_only', label: 'Dayan Only' },
      { option: 'bayan_only', label: 'Bayan Only' },
      { option: 'tabla_tarang_melodic_set', label: 'Tabla Tarang (Melodic Set)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_percussion_tabla',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Configuration',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Configuration',
      isDetail: true,
    },
    saveConfig: {
      label: 'Configuration',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_percussion_tabla_dayan_head_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '5', label: '5"' },
      { option: '5_25', label: '5.25"' },
      { option: '5_5', label: '5.5"' },
      { option: '5_75', label: '5.75"' },
      { option: '6', label: '6"' },
      { option: '6_5', label: '6.5"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_percussion_tabla',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Dayan Head Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Dayan Head Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Dayan Head Diameter',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_percussion_tabla_bayan_shell_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'brass', label: 'Brass' },
      { option: 'chrome_plated_copper', label: 'Chrome-Plated Copper' },
      { option: 'chrome_plated_brass', label: 'Chrome-Plated Brass' },
      { option: 'clay', label: 'Clay' },
      { option: 'copper', label: 'Copper' },
      { option: 'iron', label: 'Iron' },
      { option: 'nickel_plated_copper', label: 'Nickel-Plated Copper' },
      { option: 'nickel_plated_brass', label: 'Nickel-Plated Brass' },
      { option: 'steel', label: 'Steel' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_percussion_tabla',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bayan Shell Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bayan Shell Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bayan Shell Material',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_percussion_tabla_bayan_weight',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'under_3_kg', label: 'Under 3 kg' },
      { option: '3_3_5_kg', label: '3–3.5 kg' },
      { option: '3_5_4_kg', label: '3.5–4 kg' },
      { option: '4_4_5_kg', label: '4–4.5 kg' },
      { option: '4_5_5_kg', label: '4.5–5 kg' },
      { option: 'over_5_kg', label: 'Over 5 kg' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_percussion_tabla',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bayan Weight',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bayan Weight',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bayan Weight',
      isRequired: false,
    },
  },
  {
    key: 'south_asian_percussion_tabla_dayan_shell_wood',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'babul_acacia', label: 'Babul (Acacia)' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'mango', label: 'Mango' },
      { option: 'neem', label: 'Neem' },
      { option: 'sheesham_indian_rosewood', label: 'Sheesham (Indian Rosewood)' },
      { option: 'teak', label: 'Teak' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'south_asian_percussion_tabla',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Dayan Shell Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Dayan Shell Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Dayan Shell Wood',
      isRequired: false,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'antique_singing_bowls_uk', label: 'Antique Singing Bowls (UK)' },
      { option: 'bells_of_bliss', label: 'Bells of Bliss' },
      { option: 'best_himalaya', label: 'Best Himalaya' },
      { option: 'best_singing_bowls', label: 'Best Singing Bowls' },
      { option: 'bodhisattva_trading_company', label: 'Bodhisattva Trading Company' },
      { option: 'bowls_of_sound', label: 'Bowls of Sound' },
      { option: 'cottage_craft', label: 'Cottage Craft' },
      { option: 'crystal_tones', label: 'Crystal Tones' },
      { option: 'crystal_vibes', label: 'Crystal Vibes' },
      { option: 'cvnc', label: 'CVNC' },
      { option: 'dharma_store', label: 'Dharma Store' },
      { option: 'dharmaobjects', label: 'DharmaObjects' },
      { option: 'energysound', label: 'EnergySound' },
      { option: 'exotic_india', label: 'Exotic India' },
      { option: 'hands_of_tibet', label: 'Hands of Tibet' },
      { option: 'healing_lama', label: 'Healing Lama' },
      { option: 'healing_singing_bowls', label: 'Healing Singing Bowls' },
      { option: 'healingbowl', label: 'Healingbowl' },
      { option: 'heaven_of_sound', label: 'Heaven of Sound' },
      { option: 'peter_hess', label: 'Peter Hess' },
      { option: 'himalayan_bazaar', label: 'Himalayan Bazaar' },
      { option: 'himalayan_bowls', label: 'Himalayan Bowls' },
      { option: 'himalayas_shop', label: 'Himalayas Shop' },
      { option: 'juccini', label: 'Juccini' },
      { option: 'kvka', label: 'KVKA' },
      { option: 'maha_bodhi', label: 'Maha Bodhi' },
      { option: 'meinl_sonic_energy', label: 'Meinl Sonic Energy' },
      { option: 'nepal_singing_bowls', label: 'Nepal Singing Bowls' },
      { option: 'the_ohm_store', label: 'The Ohm Store' },
      { option: 'ohm_therapeutics', label: 'Ohm Therapeutics' },
      { option: 'om_sajan_sajan_thapaliya', label: 'OM Sajan (Sajan Thapaliya)' },
      { option: 'prana_bowl', label: 'Prana Bowl' },
      { option: 'rainbow_sounds', label: 'Rainbow Sounds' },
      { option: 'samyak_international', label: 'Samyak International' },
      { option: 'sanskar_nepal', label: 'Sanskar Nepal' },
      { option: 'shanti_bowl', label: 'Shanti Bowl' },
      { option: 'silent_mind', label: 'Silent Mind' },
      { option: 'singing_bowl_nepal', label: 'Singing Bowl Nepal' },
      { option: 'solshine', label: 'SolShine' },
      { option: 'soundtopia_silver_sky_imports', label: 'Soundtopia (Silver Sky Imports)' },
      { option: 'sunreed_instruments', label: 'Sunreed Instruments' },
      { option: 'thamelmart', label: 'Thamelmart' },
      { option: 'tibet_tree_of_life', label: 'Tibet Tree of Life' },
      { option: 'topfund', label: 'Topfund' },
      { option: 'unlimited_singing_bowls', label: 'Unlimited Singing Bowls' },
      { option: 'vangoa', label: 'Vangoa' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'alchemy_gemstone_fusion_crystal', label: 'Alchemy / Gemstone Fusion Crystal' },
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'bell_metal_bronze_copper_tin_alloy', label: 'Bell Metal Bronze (Copper-Tin Alloy)' },
      { option: 'brass_copper_zinc_alloy', label: 'Brass (Copper-Zinc Alloy)' },
      { option: 'clear_quartz_crystal', label: 'Clear Quartz Crystal' },
      { option: 'frosted_quartz_crystal', label: 'Frosted Quartz Crystal' },
      { option: 'mixed_metal_alloy', label: 'Mixed Metal Alloy' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'under_5_under_13_cm_small', label: 'Under 5" / Under 13 cm (Small)' },
      { option: '5_6_13_15_cm_small_medium', label: '5–6" / 13–15 cm (Small-Medium)' },
      { option: '7_8_18_20_cm_medium', label: '7–8" / 18–20 cm (Medium)' },
      { option: '9_10_23_25_cm_medium_large', label: '9–10" / 23–25 cm (Medium-Large)' },
      { option: '11_12_28_30_cm_large', label: '11–12" / 28–30 cm (Large)' },
      { option: '13_15_33_38_cm_extra_large', label: '13–15" / 33–38 cm (Extra Large)' },
      { option: '16_18_41_46_cm_giant', label: '16–18" / 41–46 cm (Giant)' },
      { option: 'over_18_over_46_cm_foot_bowl', label: 'Over 18" / Over 46 cm (Foot Bowl)' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Diameter',
      isRequired: false,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_bowl_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'contemporary_modern', label: 'Contemporary/Modern' },
      { option: 'coprabati', label: 'Coprabati' },
      { option: 'crystal_chalice', label: 'Crystal Chalice' },
      { option: 'empyrean_crystal_bowl', label: 'Empyrean Crystal Bowl' },
      { option: 'featherlight_crystal_bowl', label: 'Featherlight Crystal Bowl' },
      { option: 'handle_crystal_bowl', label: 'Handle Crystal Bowl' },
      { option: 'jambati', label: 'Jambati' },
      { option: 'lingam', label: 'Lingam' },
      { option: 'mani', label: 'Mani' },
      { option: 'manipuri', label: 'Manipuri' },
      { option: 'naga_pedestal', label: 'Naga/Pedestal' },
      { option: 'remuna', label: 'Remuna' },
      { option: 'rin_gong_japanese', label: 'Rin Gong (Japanese)' },
      { option: 'standard_crystal_bowl', label: 'Standard Crystal Bowl' },
      { option: 'thadobati', label: 'Thadobati' },
      { option: 'therapy_bowl', label: 'Therapy Bowl' },
      { option: 'ultabati', label: 'Ultabati' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Bowl Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Bowl Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Bowl Style',
      isRequired: false,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_construction',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'hand_hammered', label: 'Hand-Hammered' },
      { option: 'kiln_formed_crystal', label: 'Kiln-Formed (Crystal)' },
      { option: 'lathe_turned_spun', label: 'Lathe-Turned / Spun' },
      { option: 'machine_made_pressed', label: 'Machine-Made / Pressed' },
      { option: 'sand_cast', label: 'Sand Cast' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Construction Method',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Construction Method',
      isDetail: true,
    },
    saveConfig: {
      label: 'Construction Method',
      isRequired: false,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_note',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a', label: 'A' },
      { option: 'b_flat', label: 'Bb' },
      { option: 'b', label: 'B' },
      { option: 'c', label: 'C' },
      { option: 'c_sharp_d_flat', label: 'C#/Db' },
      { option: 'd', label: 'D' },
      { option: 'e_flat', label: 'Eb' },
      { option: 'e', label: 'E' },
      { option: 'f', label: 'F' },
      { option: 'f_sharp', label: 'F#' },
      { option: 'g', label: 'G' },
      { option: 'a_flat', label: 'Ab' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Note',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Note',
      isDetail: true,
    },
    saveConfig: {
      label: 'Note',
      isRequired: false,
    },
  },
  {
    key: 'indigenous_ceremonial_percussion_singing_bowl_tuning',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '440_hz_concert_pitch', label: '440 Hz (Concert Pitch)' },
      { option: '432_hz', label: '432 Hz' },
      { option: 'other_unknown', label: 'Other/Unknown' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'indigenous_ceremonial_percussion_singing_bowl',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tuning Standard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tuning Standard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tuning Standard',
      isRequired: false,
    },
  },
  {
    key: 'percussion_handpan_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acero_harmonic', label: 'Acero Harmonic' },
      { option: 'adn_pan_instruments', label: 'ADN Pan Instruments' },
      { option: 'agharta_pantam', label: 'Agharta Pantam' },
      { option: 'ahurani_handpans', label: 'Ahurani Handpans' },
      { option: 'alamot_instruments', label: 'Alamot Instruments' },
      { option: 'alchemy_sound_sculpture', label: 'Alchemy Sound Sculpture' },
      { option: 'amarel_handpan', label: 'Amarel Handpan' },
      { option: 'amatis_handpans', label: 'Amatis Handpans' },
      { option: 'amaya_instruments', label: 'Amaya Instruments' },
      { option: 'anapana_handpan', label: 'AnaPanA Handpan' },
      { option: 'angel_handpans', label: 'Angel Handpans' },
      { option: 'aos_handpans', label: 'AOS Handpans' },
      { option: 'arcana_musical_instruments', label: 'Arcana Musical Instruments' },
      { option: 'arjuna_handpans', label: 'Arjuna Handpans' },
      { option: 'arran_handpan', label: 'Arran Handpan' },
      { option: 'arsha_pantam', label: 'Arsha Pantam' },
      { option: 'arsoun_soundsculptures', label: 'Arsoun Soundsculptures' },
      { option: 'art_melodious', label: 'Art Melodious' },
      { option: 'asteman', label: 'Asteman' },
      { option: 'astro_handpan', label: 'Astro Handpan' },
      { option: 'atlas_handpans', label: 'Atlas Handpans' },
      { option: 'atom_handpan', label: 'Atom Handpan' },
      { option: 'aura_handpan_instruments', label: 'Aura Handpan Instruments' },
      { option: 'aurora_handpan', label: 'Aurora Handpan' },
      { option: 'avalon_instruments', label: 'Avalon Instruments' },
      { option: 'axiom_handpans', label: 'Axiom Handpans' },
      { option: 'aya_sounds', label: 'Aya Sounds' },
      { option: 'ayasa_instruments', label: 'Ayasa Instruments' },
      { option: 'azamera_handpans', label: 'Azamera Handpans' },
      { option: 'bamboometal_handpans', label: 'Bamboometal Handpans' },
      { option: 'baopan_instruments', label: 'Baopan Instruments' },
      { option: 'battiloro_handpan', label: 'Battiloro Handpan' },
      { option: 'baur_brown_handpans', label: 'Baur & Brown Handpans' },
      { option: 'beatrise', label: 'BeatRise' },
      { option: 'bellart', label: 'BellArt' },
      { option: 'blackholepan', label: 'BlackHolePan' },
      { option: 'blacksteel_handpan', label: 'Blacksteel Handpan' },
      { option: 'blesspan', label: 'Blesspan' },
      { option: 'boras_handpan_instruments', label: 'Boras Handpan Instruments' },
      { option: 'jan_borren', label: 'Jan Borren' },
      { option: 'celestial_sound', label: 'Celestial Sound' },
      { option: 'cenit_handpans', label: 'Cenit Handpans' },
      { option: 'chaya_handpans', label: 'Chaya Handpans' },
      { option: 'chopipan', label: 'Chopipan' },
      { option: 'chroma_drum', label: 'Chroma Drum' },
      { option: 'chronos_handpan', label: 'Chronos Handpan' },
      { option: 'cosmos_handpan', label: 'Cosmos Handpan' },
      { option: 'daniels_made_handpans', label: 'Daniels Made Handpans' },
      { option: 'dave_s_island_instruments', label: 'Dave\'s Island Instruments' },
      { option: 'd_flat_handpan', label: 'db Handpan' },
      { option: 'deepan', label: 'Deepan' },
      { option: 'marco_della_ratta_mdr', label: 'Marco Della Ratta (MDR)' },
      { option: 'djangar_pans', label: 'Djangar Pans' },
      { option: 'djillhi_instruments', label: 'Djillhi Instruments' },
      { option: 'dugimago', label: 'Dugimago' },
      { option: 'easka_handpan', label: 'Easka Handpan' },
      { option: 'echo_sound_sculptures', label: 'Echo Sound Sculptures' },
      { option: 'edelweiss_handpan', label: 'Edelweiss Handpan' },
      { option: 'ederod_handpan', label: 'Ederod Handpan' },
      { option: 'eforia_instruments', label: 'Eforia Instruments' },
      { option: 'elaia_handpans', label: 'Elaia Handpans' },
      { option: 'elitepan', label: 'Elitepan' },
      { option: 'elixia_instruments', label: 'Elixia Instruments' },
      { option: 'elysian_instruments', label: 'Elysian Instruments' },
      { option: 'esapan_sound_sculpture', label: 'Esapan Sound Sculpture' },
      { option: 'essence_handpans', label: 'Essence Handpans' },
      { option: 'flowra_sound', label: 'Flowra Sound' },
      { option: 'fropan', label: 'Fropan' },
      { option: 'gadung_musical_instruments', label: 'Gadung Musical Instruments' },
      { option: 'gio_s_instruments', label: 'Gio\'s Instruments' },
      { option: 'godan_handpan_instruments', label: 'Godan Handpan Instruments' },
      { option: 'guda_drum', label: 'Guda Drum' },
      { option: 'hafez_handpan', label: 'Hafez Handpan' },
      { option: 'haganenote', label: 'HaganeNote' },
      { option: 'haig_instruments', label: 'Haig Instruments' },
      { option: 'hamsa_handpans', label: 'Hamsa Handpans' },
      { option: 'handpan_of_the_cedars', label: 'Handpan of the Cedars' },
      { option: 'handpan_overtone', label: 'Handpan Overtone' },
      { option: 'handromeda_handpan', label: 'Handromeda Handpan' },
      { option: 'hapi_drum', label: 'Hapi Drum' },
      { option: 'harmonic_sculpture_instruments', label: 'Harmonic Sculpture Instruments' },
      { option: 'harmony_handpan', label: 'Harmony Handpan' },
      { option: 'harper_s_handpans', label: 'Harper\'s Handpans' },
      { option: 'heka_handpans', label: 'Heka Handpans' },
      { option: 'heliton_handcrafts', label: 'Heliton Handcrafts' },
      { option: 'hende_sound_sculptures', label: 'Hende Sound Sculptures' },
      { option: 'hephapantam', label: 'Hephapantam' },
      { option: 'hluru', label: 'Hluru' },
      { option: 'hoia_pantam', label: 'Hoia Pantam' },
      { option: 'instruments_de_l_elf', label: 'Instruments de l\'Elf' },
      { option: 'isthmus_instruments', label: 'Isthmus Instruments' },
      { option: 'jz_soundart', label: 'JZ Soundart' },
      { option: 'kalimaa_handpan', label: 'Kalimaa Handpan' },
      { option: 'karen_handpan', label: 'Karen Handpan' },
      { option: 'karumi_steel', label: 'Karumi Steel' },
      { option: 'kauaine_sound_sculptures', label: 'Kauaine Sound Sculptures' },
      { option: 'kitapantam', label: 'KitaPantam' },
      { option: 'koan_handpan', label: 'Koan Handpan' },
      { option: 'ksl_handpan', label: 'KSL Handpan' },
      { option: 'kurpan', label: 'KurPan' },
      { option: 'kyougakusya', label: 'Kyougakusya' },
      { option: 'l_atelier_de_o', label: 'L\'Atelier de O' },
      { option: 'l_y_handpan', label: 'L.Y Handpan' },
      { option: 'leaf_sound_sculptures', label: 'Leaf Sound Sculptures' },
      { option: 'lidah_drums', label: 'LIDAH Drums' },
      { option: 'live_metal_art', label: 'Live Metal Art' },
      { option: 'lombardo_handpan', label: 'Lombardo Handpan' },
      { option: 'luna_handpan', label: 'Luna Handpan' },
      { option: 'lupan_handpans', label: 'Lupan Handpans' },
      { option: 'mag_instruments', label: 'MAG Instruments' },
      { option: 'makai_instruments', label: 'Makai Instruments' },
      { option: 'manik_sound_sculptures', label: 'Manik Sound Sculptures' },
      { option: 'mantra_handpans', label: 'Mantra Handpans' },
      { option: 'mash_handpan', label: 'MASH Handpan' },
      { option: 'maya_handpan', label: 'Maya Handpan' },
      { option: 'mayura_instruments', label: 'Mayura Instruments' },
      { option: 'mayza_handpan', label: 'Mayza Handpan' },
      { option: 'mccarthy_instruments', label: 'McCarthy Instruments' },
      { option: 'mehrdrum', label: 'Mehrdrum' },
      { option: 'mejzlik_handpan', label: 'Mejzlik Handpan' },
      { option: 'valerio_menon_instruments', label: 'Valerio Menon Instruments' },
      { option: 'meraki_instruments', label: 'Meraki Instruments' },
      { option: 'mercury_handpans', label: 'Mercury Handpans' },
      { option: 'meridian_handpans', label: 'Meridian Handpans' },
      { option: 'metal_sounds_spacedrum', label: 'Metal Sounds (Spacedrum)' },
      { option: 'mishte_instruments', label: 'Mishte Instruments' },
      { option: 'mokum_handpan', label: 'Mokum Handpan' },
      { option: 'mountain_man_handpan', label: 'Mountain Man Handpan' },
      { option: 'mrpans_instruments', label: 'MrPans Instruments' },
      { option: 'mudra_handpan', label: 'Mudra Handpan' },
      { option: 'muse_handpan', label: 'Muse Handpan' },
      { option: 'muta_sound_sculptures', label: 'MUTA Sound Sculptures' },
      { option: 'mypan_handpan', label: 'Mypan Handpan' },
      { option: 'mystical_steel', label: 'Mystical Steel' },
      { option: 'nada_brahma_handpan', label: 'Nada Brahma Handpan' },
      { option: 'nala_sound_sculptures', label: 'Nala Sound Sculptures' },
      { option: 'nami_handpan', label: 'Nami Handpan' },
      { option: 'naos_instruments', label: 'Naos Instruments' },
      { option: 'nebula_handpan', label: 'Nebula Handpan' },
      { option: 'nimo_sounds_handpans', label: 'Nimo Sounds Handpans' },
      { option: 'nirvana_handpan_acolyte_instruments', label: 'Nirvana Handpan (Acolyte Instruments)' },
      { option: 'noblet_pantam', label: 'Noblet Pantam' },
      { option: 'nomad_instruments', label: 'Nomad Instruments' },
      { option: 'noosound', label: 'NooSound' },
      { option: 'nororai_instruments', label: 'Nororai Instruments' },
      { option: 'novapans', label: 'NovaPans' },
      { option: 'numen_instruments', label: 'Numen Instruments' },
      { option: 'oasis_sound_sculpture', label: 'Oasis Sound Sculpture' },
      { option: 'omana_handpan', label: 'Omana Handpan' },
      { option: 'onda_handpan', label: 'Onda Handpan' },
      { option: 'opsilon_handpan', label: 'Opsilon Handpan' },
      { option: 'ostara_instruments', label: 'Ostara Instruments' },
      { option: 'ovo_handpan_instruments', label: 'OVO Handpan Instruments' },
      { option: 'owl_pan', label: 'Owl-Pan' },
      { option: 'own_sound_sculpture', label: 'Own Sound Sculpture' },
      { option: 'oxymora_instruments', label: 'Oxymora Instruments' },
      { option: 'pampan_instruments', label: 'Pampan Instruments' },
      { option: 'pampeano_handpan', label: 'Pampeano Handpan' },
      { option: 'panamor_handpans', label: 'PanAmor Handpans' },
      { option: 'panart_hang', label: 'PanArt (Hang)' },
      { option: 'panatolian', label: 'Panatolian' },
      { option: 'pandakini', label: 'Pandakini' },
      { option: 'pandora_pantam', label: 'Pandora Pantam' },
      { option: 'panersia', label: 'Panersia' },
      { option: 'panla_flat_handpan', label: 'Panlab Handpan' },
      { option: 'panlima_handpan', label: 'PanLima Handpan' },
      { option: 'pansmith_handpans', label: 'PanSmith Handpans' },
      { option: 'pansnap_solaris', label: 'Pansnap (Solaris)' },
      { option: 'panstream_handpan', label: 'PanStream Handpan' },
      { option: 'pantheon_steel_halo', label: 'Pantheon Steel (Halo)' },
      { option: 'pasha_aeon_handpan', label: 'Pasha Aeon Handpan' },
      { option: 'patera_handpan', label: 'Patera Handpan' },
      { option: 'peacepans', label: 'PeacePans' },
      { option: 'peransa_pan', label: 'Peransa Pan' },
      { option: 'peterpan_handpans', label: 'Peterpan Handpans' },
      { option: 'piris_instruments', label: 'Piris Instruments' },
      { option: 'prana_handpan', label: 'Prana Handpan' },
      { option: 'prima_handpan', label: 'Prima Handpan' },
      { option: 'prisma_drums', label: 'Prisma Drums' },
      { option: 'puresmusic', label: 'Puresmusic' },
      { option: 'qi_pan', label: 'Qi-Pan' },
      { option: 'rammer_drum', label: 'Rammer Drum' },
      { option: 'rasa_pantam', label: 'Rasa Pantam' },
      { option: 'raysen_music', label: 'Raysen Music' },
      { option: 'rinpantam', label: 'Rinpantam' },
      { option: 'root_harmonics', label: 'Root Harmonics' },
      { option: 'salvatore_handpans', label: 'Salvatore Handpans' },
      { option: 'samadhi_soundscapes', label: 'Samadhi Soundscapes' },
      { option: 'samasta_handpan', label: 'Samasta Handpan' },
      { option: 'saraz_handpans', label: 'Saraz Handpans' },
      { option: 'satya_sound_sculptures', label: 'Satya Sound Sculptures' },
      { option: 'schlagwerk_harmonic_art', label: 'Schlagwerk Harmonic Art' },
      { option: 'seed_handpan', label: 'Seed Handpan' },
      { option: 'sela_percussion', label: 'Sela Percussion' },
      { option: 'sew_handpan', label: 'Sew Handpan' },
      { option: 'shaktipan', label: 'Shaktipan' },
      { option: 'sheez_instruments', label: 'Sheez Instruments' },
      { option: 'shellopan', label: 'Shellopan' },
      { option: 'sibiria_sound_sculptures', label: 'Sibiria Sound Sculptures' },
      { option: 'sinesama_handpan', label: 'Sinesama Handpan' },
      { option: 'snd_handpan', label: 'SND Handpan' },
      { option: 'solopan', label: 'Solopan' },
      { option: 'solos_handpan', label: 'Solos Handpan' },
      { option: 'soma_sound_sculptures', label: 'Soma Sound Sculptures' },
      { option: 'sonobe_handpan', label: 'Sonobe Handpan' },
      { option: 'sons_da_terra', label: 'Sons da Terra' },
      { option: 'sonus_art', label: 'Sonus Art' },
      { option: 'soul_drum_handpans', label: 'Soul Drum Handpans' },
      { option: 'soulpan_instruments', label: 'SoulPan Instruments' },
      { option: 'soulshine_sounds', label: 'Soulshine Sounds' },
      { option: 'sound_sculpture', label: 'Sound Sculpture' },
      { option: 'soyuz_handpans', label: 'Soyuz Handpans' },
      { option: 'sp_handpan', label: 'SP Handpan' },
      { option: 'spb_pantam', label: 'SPB Pantam' },
      { option: 'spirit_handpans', label: 'Spirit Handpans' },
      { option: 'star_handpan', label: 'Star Handpan' },
      { option: 'steel_monkey_sound_sculptures', label: 'Steel Monkey Sound Sculptures' },
      { option: 'studio_waita', label: 'Studio Waita' },
      { option: 'subtlesounds_handpans', label: 'SubtleSounds Handpans' },
      { option: 'sunpan', label: 'SunPan' },
      { option: 'sunyata_sound_sculptures', label: 'Sunyata Sound Sculptures' },
      { option: 'svaraa_sound_sculptures', label: 'Svaraa Sound Sculptures' },
      { option: 'sympan_instruments', label: 'Sympan Instruments' },
      { option: 'tacta_handpans', label: 'Tacta Handpans' },
      { option: 'tala_handpans', label: 'Tala Handpans' },
      { option: 'taopan_handpan', label: 'Taopan Handpan' },
      { option: 'teana_handpan', label: 'Teana Handpan' },
      { option: 'telos_instruments', label: 'Telos Instruments' },
      { option: 'tiflis_handpan', label: 'Tiflis Handpan' },
      { option: 'tiznajo_instruments', label: 'Tiznajo Instruments' },
      { option: 'torsepan', label: 'Torsepan' },
      { option: 'turtle_pan_sound_sculptures', label: 'Turtle Pan Sound Sculptures' },
      { option: 'tympan_handpan', label: 'Tympan Handpan' },
      { option: 'tzevaot_handpan', label: 'Tzevaot Handpan' },
      { option: 'tzun_s_handpan', label: 'Tzun\'s Handpan' },
      { option: 'ugur_handpan', label: 'Ugur Handpan' },
      { option: 'ushipan', label: 'Ushipan' },
      { option: 'vanda_pantam', label: 'Vanda Pantam' },
      { option: 'veles_sound_sculpture', label: 'Veles Sound Sculpture' },
      { option: 'veritas_sound_sculpture', label: 'Veritas Sound Sculpture' },
      { option: 'vibe_handpan', label: 'VIBE Handpan' },
      { option: 'vibratus_handpan', label: 'Vibratus Handpan' },
      { option: 'vision_instruments', label: 'Vision Instruments' },
      { option: 'xenith_handpans', label: 'Xenith Handpans' },
      { option: 'xuansound', label: 'XuanSound' },
      { option: 'yatao', label: 'Yatao' },
      { option: 'yishama', label: 'Yishama' },
      { option: 'yonapan', label: 'Yonapan' },
      { option: 'your_pan', label: 'Your Pan' },
      { option: 'zambelli_handpan', label: 'Zambelli Handpan' },
      { option: 'zaora_instruments', label: 'Zaora Instruments' },
      { option: 'zenapan', label: 'ZenaPan' },
      { option: 'zephyr_handpans', label: 'Zephyr Handpans' },
      { option: 'ziba_drums', label: 'Ziba Drums' },
      { option: 'ziran_handpan', label: 'Ziran Handpan' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_handpan',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'percussion_handpan_scale',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aeolian', label: 'Aeolian' },
      { option: 'aegean', label: 'Aegean' },
      { option: 'akebono', label: 'Akebono' },
      { option: 'amara_celtic_minor', label: 'Amara / Celtic Minor' },
      { option: 'ashakiran', label: 'Ashakiran' },
      { option: 'blues', label: 'Blues' },
      { option: 'dorian', label: 'Dorian' },
      { option: 'equinox', label: 'Equinox' },
      { option: 'harmonic_minor', label: 'Harmonic Minor' },
      { option: 'hijaz', label: 'Hijaz' },
      { option: 'integral', label: 'Integral' },
      { option: 'jibuk', label: 'Jibuk' },
      { option: 'kurd_annaziska', label: 'Kurd / Annaziska' },
      { option: 'la_sirena', label: 'La Sirena' },
      { option: 'low_pygmy', label: 'Low Pygmy' },
      { option: 'magic_voyage', label: 'Magic Voyage' },
      { option: 'major', label: 'Major' },
      { option: 'mixolydian', label: 'Mixolydian' },
      { option: 'mystic', label: 'Mystic' },
      { option: 'nordlys', label: 'Nordlys' },
      { option: 'oxalis', label: 'Oxalis' },
      { option: 'phrygian', label: 'Phrygian' },
      { option: 'pygmy', label: 'Pygmy' },
      { option: 'ragadesh', label: 'Ragadesh' },
      { option: 'romanian_hijaz', label: 'Romanian Hijaz' },
      { option: 'sabye', label: 'Sabye' },
      { option: 'saladin', label: 'Saladin' },
      { option: 'shang_diao', label: 'Shang Diao' },
      { option: 'tarznauyn', label: 'Tarznauyn' },
      { option: 'ysha_savita', label: 'Ysha Savita' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_handpan',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale',
      isRequired: false,
    },
  },
  {
    key: 'percussion_handpan_notes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: '9', label: '9' },
      { option: '10', label: '10' },
      { option: '11', label: '11' },
      { option: '12', label: '12' },
      { option: '13', label: '13' },
      { option: '14', label: '14' },
      { option: '15', label: '15' },
      { option: '16', label: '16' },
      { option: '17', label: '17' },
      { option: '18', label: '18+' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_handpan',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Notes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Notes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Notes',
      isRequired: false,
    },
  },
  {
    key: 'percussion_handpan_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ember_steel', label: 'Ember Steel' },
      { option: 'nitrided_steel', label: 'Nitrided Steel' },
      { option: 'raw_steel', label: 'Raw Steel' },
      { option: 'stainless_steel', label: 'Stainless Steel' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_handpan',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'percussion_handpan_frequency',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '440_hz', label: '440 Hz' },
      { option: '432_hz', label: '432 Hz' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_handpan',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Frequency',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Frequency',
      isDetail: true,
    },
    saveConfig: {
      label: 'Frequency',
      isRequired: false,
    },
  },
  {
    key: 'percussion_tongue_drum_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'ajna', label: 'Ajna' },
      { option: 'amahi', label: 'Amahi' },
      { option: 'aquadrum', label: 'Aquadrum' },
      { option: 'asmuse', label: 'Asmuse' },
      { option: 'axiom', label: 'Axiom' },
      { option: 'bali_treasures', label: 'Bali Treasures' },
      { option: 'beat_root', label: 'Beat Root' },
      { option: 'brandnewnoise', label: 'BrandNewNoise' },
      { option: 'buddha_stones', label: 'Buddha Stones' },
      { option: 'burning_lin', label: 'Burning & Lin' },
      { option: 'calm_panda', label: 'Calm Panda' },
      { option: 'chakra', label: 'Chakra' },
      { option: 'cosmos', label: 'Cosmos' },
      { option: 'dobani', label: 'Dobani' },
      { option: 'jim_doble', label: 'Jim Doble' },
      { option: 'etherealdrum', label: 'Etherealdrum' },
      { option: 'flatsons', label: 'Flatsons' },
      { option: 'four_uncles', label: 'Four Uncles' },
      { option: 'gdrum_garrahand', label: 'GDrum (Garrahand)' },
      { option: 'guda_zen_percussion', label: 'Guda (Zen Percussion)' },
      { option: 'hapi_drum', label: 'Hapi Drum' },
      { option: 'hardwood_music_company', label: 'Hardwood Music Company' },
      { option: 'hluru', label: 'Hluru' },
      { option: 'hopwell', label: 'Hopwell' },
      { option: 'idiopan', label: 'Idiopan' },
      { option: 'iphungo', label: 'Iphungo' },
      { option: 'kosmosky', label: 'Kosmosky' },
      { option: 'lomuty', label: 'Lomuty' },
      { option: 'lronbird', label: 'Lronbird' },
      { option: 'luvay', label: 'Luvay' },
      { option: 'mag_instruments', label: 'MAG Instruments' },
      { option: 'manastone', label: 'ManaStone' },
      { option: 'meinl_sonic_energy', label: 'Meinl (Sonic Energy)' },
      { option: 'melodrum', label: 'Melodrum' },
      { option: 'metal_sounds_zenko', label: 'Metal Sounds (Zenko)' },
      { option: 'moukey', label: 'Moukey' },
      { option: 'novadrum', label: 'NovaDrum' },
      { option: 'ocarinawind', label: 'OcarinaWind' },
      { option: 'ohuhu', label: 'Ohuhu' },
      { option: 'panda_drum', label: 'Panda Drum' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'pulsar_yudin_workshop', label: 'Pulsar (Yudin Workshop)' },
      { option: 'qingshi', label: 'QingShi' },
      { option: 'rakumi', label: 'Rakumi' },
      { option: 'rammerdrum', label: 'Rammerdrum' },
      { option: 'rav_vast', label: 'RAV Vast' },
      { option: 'regis', label: 'Regis' },
      { option: 'sela', label: 'Sela' },
      { option: 'sine_studio', label: 'Sine Studio' },
      { option: 'sonodrum', label: 'Sonodrum' },
      { option: 'treeworks', label: 'TreeWorks' },
      { option: 'ulalov', label: 'Ulalov' },
      { option: 'unlax', label: 'Unlax' },
      { option: 'vibedrum', label: 'VibeDrum' },
      { option: 'vivohome', label: 'Vivohome' },
      { option: 'woodpack', label: 'WoodPack' },
      { option: 'yinama', label: 'Yinama' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_tongue_drum',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'percussion_tongue_drum_notes',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6', label: '6' },
      { option: '7', label: '7' },
      { option: '8', label: '8' },
      { option: '9', label: '9' },
      { option: '10', label: '10' },
      { option: '11', label: '11' },
      { option: '12', label: '12' },
      { option: '13', label: '13' },
      { option: '14', label: '14' },
      { option: '15', label: '15' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_tongue_drum',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Notes',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Notes',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Notes',
      isRequired: false,
    },
  },
  {
    key: 'percussion_tongue_drum_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6_or_smaller', label: '6" or smaller' },
      { option: '8', label: '8"' },
      { option: '10', label: '10"' },
      { option: '12', label: '12"' },
      { option: '13', label: '13"' },
      { option: '14', label: '14"' },
      { option: '15', label: '15"' },
      { option: '16', label: '16"' },
      { option: '20_or_larger', label: '20" or larger' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_tongue_drum',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Diameter',
      isRequired: false,
    },
  },
  {
    key: 'percussion_tongue_drum_scale',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'major_diatonic', label: 'Major (Diatonic)' },
      { option: 'minor_natural', label: 'Minor (Natural)' },
      { option: 'pentatonic_major', label: 'Pentatonic Major' },
      { option: 'pentatonic_minor', label: 'Pentatonic Minor' },
      { option: 'akebono', label: 'Akebono' },
      { option: 'celtic_minor', label: 'Celtic Minor' },
      { option: 'chromatic', label: 'Chromatic' },
      { option: 'dorian', label: 'Dorian' },
      { option: 'equinox', label: 'Equinox' },
      { option: 'harmonic_minor', label: 'Harmonic Minor' },
      { option: 'hijaz', label: 'Hijaz' },
      { option: 'integral', label: 'Integral' },
      { option: 'kurd', label: 'Kurd' },
      { option: 'lydian', label: 'Lydian' },
      { option: 'mixolydian', label: 'Mixolydian' },
      { option: 'pygmy', label: 'Pygmy' },
      { option: 'multi_scale_tunable', label: 'Multi-Scale / Tunable' },
      { option: 'custom', label: 'Custom' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_tongue_drum',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Type',
      isRequired: false,
    },
  },
  {
    key: 'global_key',
    scope: 'public',
    schemaType: 'multi-enum',
    enumOptions: [
      { option: 'a', label: 'A' },
      { option: 'bb', label: 'Bb' },
      { option: 'b', label: 'B' },
      { option: 'c', label: 'C' },
      { option: 'c_db', label: 'C# / Db' },
      { option: 'd', label: 'D' },
      { option: 'eb', label: 'Eb' },
      { option: 'e', label: 'E' },
      { option: 'f', label: 'F' },
      { option: 'f_2', label: 'F#' },
      { option: 'g', label: 'G' },
      { option: 'ab', label: 'Ab' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_tongue_drum',
        'bagpipes_other_bagpipes',
        'east_asian_flutes_dizi',
        'east_asian_flutes_shakuhachi',
        'east_asian_flutes_xiao',
        'flute_other_flutes',
        'harmonica_chromatic_harmonica',
        'harmonica_diatonic_harmonica',
        'harmonica_other_harmonicas',
        'andean_flutes_quena',
        'middle_eastern_north_african_flutes_ney',
        'flute_native_american_flute',
        'flute_pan_flute',
        'flute_simple_system_irish_flute',
        'south_asian_flutes_bansuri',
        'wind_reed_tin_whistle',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Key',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Key',
      isDetail: true,
    },
    saveConfig: {
      label: 'Key',
      isRequired: false,
    },
  },
  {
    key: 'percussion_tongue_drum_material',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'carbon_steel', label: 'Carbon Steel' },
      { option: 'nitrided_steel', label: 'Nitrided Steel' },
      { option: 'stainless_steel', label: 'Stainless Steel' },
      { option: 'titanium_steel_alloy', label: 'Titanium-Steel Alloy' },
      { option: 'wood', label: 'Wood' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'percussion_tongue_drum',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Material',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_tambourine_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'adams', label: 'Adams' },
      { option: 'bergerault', label: 'Bergerault' },
      { option: 'black_swamp_percussion', label: 'Black Swamp Percussion' },
      { option: 'cannon', label: 'Cannon' },
      { option: 'cooperman', label: 'Cooperman' },
      { option: 'cp_cosmic_percussion', label: 'CP (Cosmic Percussion)' },
      { option: 'dixon', label: 'Dixon' },
      { option: 'dobani', label: 'Dobani' },
      { option: 'dw_drum_workshop', label: 'DW (Drum Workshop)' },
      { option: 'first_act', label: 'First Act' },
      { option: 'gon_bops', label: 'Gon Bops' },
      { option: 'gp_percussion', label: 'GP Percussion' },
      { option: 'grover_pro_percussion', label: 'Grover Pro Percussion' },
      { option: 'hohner', label: 'Hohner' },
      { option: 'lp_latin_percussion', label: 'LP (Latin Percussion)' },
      { option: 'ludwig', label: 'Ludwig' },
      { option: 'mano_percussion', label: 'Mano Percussion' },
      { option: 'meinl', label: 'Meinl' },
      { option: 'mid_east_manufacturing', label: 'Mid-East Manufacturing' },
      { option: 'nino', label: 'Nino' },
      { option: 'noble_cooley', label: 'Noble & Cooley' },
      { option: 'pearl', label: 'Pearl' },
      { option: 'percussion_plus', label: 'Percussion Plus' },
      { option: 'remo', label: 'Remo' },
      { option: 'rhythm_band', label: 'Rhythm Band' },
      { option: 'rhythm_tech', label: 'Rhythm Tech' },
      { option: 'rogers', label: 'Rogers' },
      { option: 'schlagwerk', label: 'Schlagwerk' },
      { option: 'sound_percussion_labs', label: 'Sound Percussion Labs' },
      { option: 'stagg', label: 'Stagg' },
      { option: 'suzuki', label: 'Suzuki' },
      { option: 'tama', label: 'Tama' },
      { option: 'tambourinela_flat_guy_du_blet', label: 'TambourineLAB (Guy du Blêt)' },
      { option: 'toca', label: 'Toca' },
      { option: 'treeworks', label: 'Treeworks' },
      { option: 'tycoon_percussion', label: 'Tycoon Percussion' },
      { option: 'vic_firth', label: 'Vic Firth' },
      { option: 'yamaha', label: 'Yamaha' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_tambourine',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'folk_percussion_tambourine_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'headed_with_drumhead', label: 'Headed (with drumhead)' },
      { option: 'headless_open_frame', label: 'Headless (open frame)' },
      { option: 'half_moon_crescent', label: 'Half-Moon / Crescent' },
      { option: 'mountable_hi_hat_kit', label: 'Mountable (hi-hat / kit)' },
      { option: 'foot_tambourine', label: 'Foot Tambourine' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_tambourine',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Type',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_tambourine_diameter',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6', label: '6"' },
      { option: '8', label: '8"' },
      { option: '10', label: '10"' },
      { option: '12', label: '12"' },
      { option: '14', label: '14"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_tambourine',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Diameter',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Diameter',
      isDetail: true,
    },
    saveConfig: {
      label: 'Diameter',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_tambourine_jingle',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aluminum', label: 'Aluminum' },
      { option: 'beryllium_copper', label: 'Beryllium Copper' },
      { option: 'brass', label: 'Brass' },
      { option: 'bronze', label: 'Bronze' },
      { option: 'chromium_25', label: 'Chromium 25' },
      { option: 'copper', label: 'Copper' },
      { option: 'german_silver_nickel_silver', label: 'German Silver (Nickel Silver)' },
      { option: 'phosphor_bronze', label: 'Phosphor Bronze' },
      { option: 'silver_heat_treated', label: 'Silver (Heat-Treated)' },
      { option: 'steel', label: 'Steel' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_tambourine',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Jingle Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Jingle Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Jingle Material',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_tambourine_rows',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'single_row', label: 'Single Row' },
      { option: 'double_row', label: 'Double Row' },
      { option: 'triple_row', label: 'Triple Row' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_tambourine',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Jingle Rows',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Jingle Rows',
      isDetail: true,
    },
    saveConfig: {
      label: 'Jingle Rows',
      isRequired: false,
    },
  },
  {
    key: 'folk_percussion_tambourine_head',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'fiberskyn_renaissance_synthetic', label: 'Fiberskyn / Renaissance (Synthetic)' },
      { option: 'natural_calfskin', label: 'Natural Calfskin' },
      { option: 'natural_goatskin', label: 'Natural Goatskin' },
      { option: 'none_headless', label: 'None (Headless)' },
      { option: 'synthetic_plastic_mylar', label: 'Synthetic (Plastic/Mylar)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'folk_percussion_tambourine',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Head Material',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Head Material',
      isDetail: true,
    },
    saveConfig: {
      label: 'Head Material',
      isRequired: false,
    },
  },
  {
    key: 'ukulele_ukulele_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'anuenue', label: 'aNueNue' },
      { option: 'aria', label: 'Aria' },
      { option: 'big_island_ukuleles', label: 'Big Island Ukuleles' },
      { option: 'blackbird', label: 'Blackbird' },
      { option: 'blueberry_handmade_guitars', label: 'Blueberry Handmade Guitars' },
      { option: 'bruko', label: 'Brüko' },
      { option: 'caramel', label: 'Caramel' },
      { option: 'collings', label: 'Collings' },
      { option: 'cordoba', label: 'Córdoba' },
      { option: 'donner', label: 'Donner' },
      { option: 'eastman', label: 'Eastman' },
      { option: 'eddy_finn', label: 'Eddy Finn' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'favilla', label: 'Favilla' },
      { option: 'fender', label: 'Fender' },
      { option: 'flight', label: 'Flight' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'guild', label: 'Guild' },
      { option: 'harmony', label: 'Harmony' },
      { option: 'hilo', label: 'Hilo' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'islander', label: 'Islander' },
      { option: 'kala', label: 'Kala' },
      { option: 'kamaka_hawaii', label: 'Kamaka Hawaii' },
      { option: 'kamoa', label: 'Kamoa' },
      { option: 'kanile_a', label: 'Kanile\'a' },
      { option: 'kiwaya', label: 'Kiwaya' },
      { option: 'kmise', label: 'Kmise' },
      { option: 'koaloha', label: 'KoAloha' },
      { option: 'ko_olau', label: 'Ko\'olau' },
      { option: 'lag', label: 'LAG' },
      { option: 'lanikai', label: 'Lanikai' },
      { option: 'lava_music', label: 'LAVA Music' },
      { option: 'leho', label: 'Leho' },
      { option: 'lichty_guitars', label: 'Lichty Guitars' },
      { option: 'loprinzi_ukuleles', label: 'LoPrinzi Ukuleles' },
      { option: 'luna', label: 'Luna' },
      { option: 'mahalo', label: 'Mahalo' },
      { option: 'makala', label: 'Makala' },
      { option: 'c_f_martin_co', label: 'C.F. Martin & Co.' },
      { option: 'mele_ukulele', label: 'Mele Ukulele' },
      { option: 'mya_moe', label: 'Mya-Moe' },
      { option: 'oceana_ukuleles', label: 'Oceana Ukuleles' },
      { option: 'ohana', label: 'Ohana' },
      { option: 'ortega', label: 'Ortega' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'outdoor_ukulele', label: 'Outdoor Ukulele' },
      { option: 'pepe_romero', label: 'Pepe Romero' },
      { option: 'pono', label: 'Pono' },
      { option: 'puka', label: 'Puka' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'regal', label: 'Regal' },
      { option: 'risa', label: 'RISA' },
      { option: 'road_toad_music', label: 'Road Toad Music' },
      { option: 'romero_creations', label: 'Romero Creations' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'vega', label: 'Vega' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'ukulele_ukulele_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'soprano', label: 'Soprano' },
      { option: 'concert', label: 'Concert' },
      { option: 'tenor', label: 'Tenor' },
      { option: 'baritone', label: 'Baritone' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Size',
      isRequired: false,
    },
  },
  {
    key: 'ukulele_ukulele_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_acacia', label: 'Solid Acacia' },
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'solid_koa', label: 'Solid Koa' },
      { option: 'solid_mahogany', label: 'Solid Mahogany' },
      { option: 'solid_mango', label: 'Solid Mango' },
      { option: 'solid_maple', label: 'Solid Maple' },
      { option: 'solid_ovangkol', label: 'Solid Ovangkol' },
      { option: 'solid_redwood', label: 'Solid Redwood' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'solid_walnut', label: 'Solid Walnut' },
      { option: 'solid_ziricote', label: 'Solid Ziricote' },
      { option: 'laminate_cedar', label: 'Laminate Cedar' },
      { option: 'laminate_koa', label: 'Laminate Koa' },
      { option: 'laminate_mahogany', label: 'Laminate Mahogany' },
      { option: 'laminate_spruce', label: 'Laminate Spruce' },
      { option: 'carbon_fiber_composite', label: 'Carbon Fiber / Composite' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'ukulele_ukulele_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acacia', label: 'Acacia' },
      { option: 'carbon_fiber_composite', label: 'Carbon Fiber / Composite' },
      { option: 'cherry', label: 'Cherry' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'koa', label: 'Koa' },
      { option: 'laminate', label: 'Laminate' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'mango', label: 'Mango' },
      { option: 'maple', label: 'Maple' },
      { option: 'ovangkol', label: 'Ovangkol' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'zebrawood', label: 'Zebrawood' },
      { option: 'ziricote', label: 'Ziricote' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'ukulele_u_bass_bass_ukulele_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'aklot', label: 'AKLOT' },
      { option: 'aquila', label: 'Aquila' },
      { option: 'donner', label: 'Donner' },
      { option: 'fender', label: 'Fender' },
      { option: 'flight', label: 'Flight' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'hadean', label: 'Hadean' },
      { option: 'harley_benton', label: 'Harley Benton' },
      { option: 'ibanez', label: 'Ibanez' },
      { option: 'kala', label: 'Kala' },
      { option: 'kanile_a', label: 'Kanile\'a' },
      { option: 'koaloha', label: 'KoAloha' },
      { option: 'lanikai', label: 'Lanikai' },
      { option: 'lichty_guitars', label: 'Lichty Guitars' },
      { option: 'luna', label: 'Luna' },
      { option: 'magic_fluke_timber_bass', label: 'Magic Fluke (Timber Bass)' },
      { option: 'ohana', label: 'Ohana' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'road_toad_music', label: 'Road Toad Music' },
      { option: 'romero_creations', label: 'Romero Creations' },
      { option: 'taylor', label: 'Taylor' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_u_bass_bass_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'ukulele_u_bass_bass_ukulele_body_style',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acoustic', label: 'Acoustic' },
      { option: 'acoustic_electric', label: 'Acoustic-Electric' },
      { option: 'semi_hollow_electric', label: 'Semi-Hollow Electric' },
      { option: 'solid_body_electric', label: 'Solid Body Electric' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_u_bass_bass_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Body Style',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Body Style',
      isDetail: true,
    },
    saveConfig: {
      label: 'Body Style',
      isRequired: false,
    },
  },
  {
    key: 'ukulele_u_bass_bass_ukulele_frets',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'fretted', label: 'Fretted' },
      { option: 'fretless', label: 'Fretless' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_u_bass_bass_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Frets',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Frets',
      isDetail: true,
    },
    saveConfig: {
      label: 'Frets',
      isRequired: false,
    },
  },
  {
    key: 'ukulele_u_bass_bass_ukulele_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_ash_swamp_ash', label: 'Solid Ash / Swamp Ash' },
      { option: 'solid_bamboo', label: 'Solid Bamboo' },
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'solid_striped_ebony', label: 'Solid Striped Ebony' },
      { option: 'solid_koa', label: 'Solid Koa' },
      { option: 'solid_mahogany', label: 'Solid Mahogany' },
      { option: 'solid_maple_spalted_figured', label: 'Solid Maple (Spalted / Figured)' },
      { option: 'solid_okoume', label: 'Solid Okoume' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'solid_zebrawood', label: 'Solid Zebrawood' },
      { option: 'laminate_mahogany', label: 'Laminate Mahogany' },
      { option: 'laminate_spruce', label: 'Laminate Spruce' },
      { option: 'polycarbonate_synthetic', label: 'Polycarbonate / Synthetic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_u_bass_bass_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_bandurria_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'admira', label: 'Admira' },
      { option: 'alhambra', label: 'Alhambra' },
      { option: 'vicente_arias', label: 'Vicente Arias' },
      { option: 'azahar', label: 'Azahar' },
      { option: 'marcelo_barbero', label: 'Marcelo Barbero' },
      { option: 'manuel_bellido', label: 'Manuel Bellido' },
      { option: 'hermanos_camps', label: 'Hermanos Camps' },
      { option: 'vicente_carrillo', label: 'Vicente Carrillo' },
      { option: 'paco_castillo', label: 'Paco Castillo' },
      { option: 'esteve', label: 'Esteve' },
      { option: 'hora_instruments', label: 'Hora Instruments' },
      { option: 'lyon_healy', label: 'Lyon & Healy' },
      { option: 'c_f_martin_co', label: 'C.F. Martin & Co.' },
      { option: 'raimundo', label: 'Raimundo' },
      { option: 'guitarras_ramirez', label: 'Guitarras Ramírez' },
      { option: 'jaime_ribot', label: 'Jaime Ribot' },
      { option: 'manuel_rodriguez', label: 'Manuel Rodríguez' },
      { option: 'prudencio_saez', label: 'Prudencio Sáez' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_bandurria',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'ukulele_u_bass_bass_ukulele_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acacia', label: 'Acacia' },
      { option: 'ash_swamp_ash', label: 'Ash / Swamp Ash' },
      { option: 'bamboo', label: 'Bamboo' },
      { option: 'black_limba', label: 'Black Limba' },
      { option: 'striped_ebony', label: 'Striped Ebony' },
      { option: 'koa', label: 'Koa' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple_spalted_figured', label: 'Maple (Spalted / Figured)' },
      { option: 'myrtle', label: 'Myrtle' },
      { option: 'okoume', label: 'Okoume' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'zebrawood', label: 'Zebrawood' },
      { option: 'polycarbonate_synthetic', label: 'Polycarbonate / Synthetic' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'ukulele_u_bass_bass_ukulele',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_bandurria_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'solid_engelmann_spruce', label: 'Solid Engelmann Spruce' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'laminate_spruce', label: 'Laminate Spruce' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_bandurria',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_bandurria_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cypress', label: 'Cypress' },
      { option: 'ebony', label: 'Ebony' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_bandurria',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_laud_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'alhambra', label: 'Alhambra' },
      { option: 'a_pinto_carvalho_apc', label: 'A. Pinto Carvalho (APC)' },
      { option: 'azahar', label: 'Azahar' },
      { option: 'modesto_borreguero', label: 'Modesto Borreguero' },
      { option: 'hermanos_camps', label: 'Hermanos Camps' },
      { option: 'paco_castillo', label: 'Paco Castillo' },
      { option: 'conde_hermanos', label: 'Conde Hermanos' },
      { option: 'esteve', label: 'Esteve' },
      { option: 'raimundo', label: 'Raimundo' },
      { option: 'guitarras_ramirez', label: 'Guitarras Ramírez' },
      { option: 'manuel_rodriguez', label: 'Manuel Rodríguez' },
      { option: 'prudencio_saez', label: 'Prudencio Sáez' },
      { option: 'vicente_sanchis', label: 'Vicente Sanchis' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_laud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'iberian_strings_laud_courses',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '6_courses_12_strings_standard', label: '6 Courses / 12 Strings (Standard)' },
      { option: '10_courses_20_strings_extended_archlaud', label: '10 Courses / 20 Strings (Extended / Archlaúd)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_laud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Courses',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Courses',
      isDetail: true,
    },
    saveConfig: {
      label: 'Courses',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_laud_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_cedar', label: 'Solid Cedar' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'laminate_spruce', label: 'Laminate Spruce' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_laud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_laud_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cypress', label: 'Cypress' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'laminate_rosewood', label: 'Laminate Rosewood' },
      { option: 'rosewood', label: 'Rosewood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_laud',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_timple_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'admira', label: 'Admira' },
      { option: 'a_pinto_carvalho_apc', label: 'A. Pinto Carvalho (APC)' },
      { option: 'vicente_corujo', label: 'Vicente Corujo' },
      { option: 'francisco_farina', label: 'Francisco Fariña' },
      { option: 'antonio_lemez_hernandez', label: 'Antonio Lemez Hernández' },
      { option: 'david_sanchez', label: 'David Sánchez' },
      { option: 'antonio_santana', label: 'Antonio Santana' },
      { option: 'simon_morales_tavio', label: 'Simón Morales Tavío' },
      { option: 'folk_instruments', label: 'Folk Instruments' },
      { option: 'francisco_de_rosa', label: 'Francisco de Rosa' },
      { option: 'beselch_rodriguez', label: 'Beselch Rodríguez' },
      { option: 'thomann', label: 'Thomann' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_timple',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'iberian_strings_timple_number_strings',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '5_strings_standard', label: '5 Strings (Standard)' },
      { option: '4_strings_traditional_variant', label: '4 Strings (Traditional Variant)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_timple',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Number of Strings',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Number of Strings',
      isDetail: true,
    },
    saveConfig: {
      label: 'Number of Strings',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_timple_top',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'solid_pine', label: 'Solid Pine' },
      { option: 'solid_spruce', label: 'Solid Spruce' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_timple',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Top',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Top',
      isDetail: true,
    },
    saveConfig: {
      label: 'Top',
      isRequired: false,
    },
  },
  {
    key: 'iberian_strings_timple_back_sides',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acacia', label: 'Acacia' },
      { option: 'black_mulberry_traditional', label: 'Black Mulberry (Traditional)' },
      { option: 'indian_rosewood', label: 'Indian Rosewood' },
      { option: 'sandalwood', label: 'Sandalwood' },
      { option: 'sapele', label: 'Sapele' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'iberian_strings_timple',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back & Sides',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back & Sides',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back & Sides',
      isRequired: false,
    },
  },
  {
    key: 'banjo_6_string_banjo_brand',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'adam_black', label: 'Adam Black' },
      { option: 'alabama', label: 'Alabama' },
      { option: 'aria', label: 'Aria' },
      { option: 'bacon_day', label: 'Bacon & Day' },
      { option: 'barnes_mullins', label: 'Barnes & Mullins' },
      { option: 'bishline', label: 'Bishline' },
      { option: 'buckeye', label: 'Buckeye' },
      { option: 'cedar_creek_luthiers', label: 'Cedar Creek Luthiers' },
      { option: 'danville', label: 'Danville' },
      { option: 'dean', label: 'Dean' },
      { option: 'deering', label: 'Deering' },
      { option: 'epiphone', label: 'Epiphone' },
      { option: 'fairbanks', label: 'Fairbanks' },
      { option: 'fender', label: 'Fender' },
      { option: 'gibson', label: 'Gibson' },
      { option: 'gold_tone', label: 'Gold Tone' },
      { option: 'gretsch', label: 'Gretsch' },
      { option: 'heartland', label: 'Heartland' },
      { option: 'huber_banjos', label: 'Huber Banjos' },
      { option: 'iida', label: 'Iida' },
      { option: 'chuck_lee', label: 'Chuck Lee' },
      { option: 'luna', label: 'Luna' },
      { option: 'morgan_monroe', label: 'Morgan Monroe' },
      { option: 'nechville', label: 'Nechville' },
      { option: 'ode', label: 'ODE' },
      { option: 'ome', label: 'Ome' },
      { option: 'orpheum', label: 'Orpheum' },
      { option: 'ortega', label: 'Ortega' },
      { option: 'oscar_schmidt', label: 'Oscar Schmidt' },
      { option: 'ozark', label: 'Ozark' },
      { option: 'paramount', label: 'Paramount' },
      { option: 'pisgah_banjo_co', label: 'Pisgah Banjo Co.' },
      { option: 'recording_king', label: 'Recording King' },
      { option: 'rickard_banjos', label: 'Rickard Banjos' },
      { option: 'j_romero_banjos', label: 'J. Romero Banjos' },
      { option: 'seeders_instruments', label: 'Seeders Instruments' },
      { option: 'stagg', label: 'Stagg' },
      { option: 's_s_stewart', label: 'S.S. Stewart' },
      { option: 'stelling', label: 'Stelling' },
      { option: 'tanglewood', label: 'Tanglewood' },
      { option: 'vega', label: 'Vega' },
      { option: 'washburn', label: 'Washburn' },
      { option: 'weymann', label: 'Weymann' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Brand / Maker',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Brand / Maker',
      isDetail: true,
    },
    saveConfig: {
      label: 'Brand / Maker',
      isRequired: true,
    },
  },
  {
    key: 'banjo_6_string_banjo_back_type',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'resonator_closed_back', label: 'Resonator (Closed Back)' },
      { option: 'open_back', label: 'Open Back' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Back Type',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Back Type',
      isDetail: true,
    },
    saveConfig: {
      label: 'Back Type',
      isRequired: false,
    },
  },
  {
    key: 'banjo_6_string_banjo_tone_ring',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'none_wood_rim_only', label: 'None (Wood Rim Only)' },
      { option: 'archtop', label: 'Archtop' },
      { option: 'bell_bronze', label: 'Bell Bronze' },
      { option: 'flathead', label: 'Flathead' },
      { option: 'rolled_steel_rim', label: 'Rolled Steel Rim' },
      { option: 'tubaphone', label: 'Tubaphone' },
      { option: 'twenty_hole_bell_brass', label: 'Twenty-Hole Bell Brass' },
      { option: 'twenty_ten_brass_deering', label: 'Twenty-Ten Brass (Deering)' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Tone Ring',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Tone Ring',
      isDetail: true,
    },
    saveConfig: {
      label: 'Tone Ring',
      isRequired: false,
    },
  },
  {
    key: 'banjo_6_string_banjo_scale_length',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '26_25_banjo_scale', label: '26.25" (Banjo Scale)' },
      { option: '25_5_guitar_scale', label: '25.5" (Guitar Scale)' },
      { option: '25_4', label: '25.4"' },
      { option: '24_75_gibson_scale', label: '24.75" (Gibson Scale)' },
      { option: 'short_scale_under_24', label: 'Short Scale (under 24")' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Scale Length',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Scale Length',
      isDetail: true,
    },
    saveConfig: {
      label: 'Scale Length',
      isRequired: false,
    },
  },
  {
    key: 'banjo_6_string_banjo_rim_size',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: '10', label: '10"' },
      { option: '11', label: '11"' },
      { option: '12', label: '12"' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Rim Size',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Rim Size',
      isDetail: true,
    },
    saveConfig: {
      label: 'Rim Size',
      isRequired: false,
    },
  },
  {
    key: 'banjo_6_string_banjo_neck_wood',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'cherry', label: 'Cherry' },
      { option: 'mahogany', label: 'Mahogany' },
      { option: 'maple', label: 'Maple' },
      { option: 'walnut', label: 'Walnut' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Neck Wood',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Neck Wood',
      isDetail: true,
    },
    saveConfig: {
      label: 'Neck Wood',
      isRequired: false,
    },
  },
  {
    key: 'global_electronics',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'acoustic', label: 'Acoustic' },
      { option: 'acousticelectric', label: 'Acoustic-Electric' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'banjo_6_string_banjo',
        'guitar_acoustic_steel_string_guitar',
        'guitar_archtop_guitar',
        'guitar_classical_nylon_string_guitar',
        'guitar_resonator_guitar',
        'harp_pedal_harp',
        'latin_american_strings_bajo_quinto_sexto',
        'latin_american_strings_cuatro_llanero',
        'latin_american_strings_cuatro_puertorriqueno',
        'latin_american_strings_guitarron',
        'mandolin_family_mandocello',
        'mandolin_family_mandolin',
        'strings_bouzouki',
        'ukulele_u_bass_bass_ukulele',
        'ukulele_ukulele',
        'zither_autoharp',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Electronics',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Electronics',
      isDetail: true,
    },
    saveConfig: {
      label: 'Electronics',
      isRequired: false,
      placeholderMessage: '',
    },
  },
  {
    key: 'flute_native_american_flute_pitch_standard',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'a440', label: 'A=440' },
      { option: 'a432', label: 'A=432' },
      { option: 'other', label: 'Other' },
    ],
    categoryConfig: {
      limitToCategoryIds: true,
      categoryIds: [
        'flute_native_american_flute',
      ],
    },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Pitch Standard',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Pitch Standard',
      isDetail: true,
    },
    saveConfig: {
      label: 'Pitch Standard',
      isRequired: false,
    },
  },
  {
    key: 'global_made_in',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'afghanistan', label: 'Afghanistan' },
      { option: 'albania', label: 'Albania' },
      { option: 'algeria', label: 'Algeria' },
      { option: 'andorra', label: 'Andorra' },
      { option: 'angola', label: 'Angola' },
      { option: 'antigua_and_barbuda', label: 'Antigua and Barbuda' },
      { option: 'argentina', label: 'Argentina' },
      { option: 'armenia', label: 'Armenia' },
      { option: 'australia', label: 'Australia' },
      { option: 'austria', label: 'Austria' },
      { option: 'azerbaijan', label: 'Azerbaijan' },
      { option: 'bahamas', label: 'Bahamas' },
      { option: 'bahrain', label: 'Bahrain' },
      { option: 'bangladesh', label: 'Bangladesh' },
      { option: 'barbados', label: 'Barbados' },
      { option: 'belarus', label: 'Belarus' },
      { option: 'belgium', label: 'Belgium' },
      { option: 'belize', label: 'Belize' },
      { option: 'benin', label: 'Benin' },
      { option: 'bhutan', label: 'Bhutan' },
      { option: 'bolivia', label: 'Bolivia' },
      { option: 'bosnia_and_herzegovina', label: 'Bosnia and Herzegovina' },
      { option: 'botswana', label: 'Botswana' },
      { option: 'brazil', label: 'Brazil' },
      { option: 'brunei', label: 'Brunei' },
      { option: 'bulgaria', label: 'Bulgaria' },
      { option: 'burkina_faso', label: 'Burkina Faso' },
      { option: 'burundi', label: 'Burundi' },
      { option: 'cabo_verde', label: 'Cabo Verde' },
      { option: 'cambodia', label: 'Cambodia' },
      { option: 'cameroon', label: 'Cameroon' },
      { option: 'canada', label: 'Canada' },
      { option: 'central_african_republic', label: 'Central African Republic' },
      { option: 'chad', label: 'Chad' },
      { option: 'chile', label: 'Chile' },
      { option: 'china', label: 'China' },
      { option: 'colombia', label: 'Colombia' },
      { option: 'comoros', label: 'Comoros' },
      { option: 'congo_democratic_republic', label: 'Congo (Democratic Republic)' },
      { option: 'congo_republic', label: 'Congo (Republic)' },
      { option: 'costa_rica', label: 'Costa Rica' },
      { option: 'cote_divoire', label: 'Côte d\'Ivoire' },
      { option: 'croatia', label: 'Croatia' },
      { option: 'cuba', label: 'Cuba' },
      { option: 'cyprus', label: 'Cyprus' },
      { option: 'czech_republic', label: 'Czech Republic' },
      { option: 'denmark', label: 'Denmark' },
      { option: 'djibouti', label: 'Djibouti' },
      { option: 'dominica', label: 'Dominica' },
      { option: 'dominican_republic', label: 'Dominican Republic' },
      { option: 'ecuador', label: 'Ecuador' },
      { option: 'egypt', label: 'Egypt' },
      { option: 'el_salvador', label: 'El Salvador' },
      { option: 'equatorial_guinea', label: 'Equatorial Guinea' },
      { option: 'eritrea', label: 'Eritrea' },
      { option: 'estonia', label: 'Estonia' },
      { option: 'eswatini', label: 'Eswatini' },
      { option: 'ethiopia', label: 'Ethiopia' },
      { option: 'fiji', label: 'Fiji' },
      { option: 'finland', label: 'Finland' },
      { option: 'france', label: 'France' },
      { option: 'gabon', label: 'Gabon' },
      { option: 'gambia', label: 'Gambia' },
      { option: 'georgia', label: 'Georgia' },
      { option: 'germany', label: 'Germany' },
      { option: 'ghana', label: 'Ghana' },
      { option: 'greece', label: 'Greece' },
      { option: 'grenada', label: 'Grenada' },
      { option: 'guatemala', label: 'Guatemala' },
      { option: 'guinea', label: 'Guinea' },
      { option: 'guineabissau', label: 'Guinea-Bissau' },
      { option: 'guyana', label: 'Guyana' },
      { option: 'haiti', label: 'Haiti' },
      { option: 'honduras', label: 'Honduras' },
      { option: 'hungary', label: 'Hungary' },
      { option: 'iceland', label: 'Iceland' },
      { option: 'india', label: 'India' },
      { option: 'indonesia', label: 'Indonesia' },
      { option: 'iran', label: 'Iran' },
      { option: 'iraq', label: 'Iraq' },
      { option: 'ireland', label: 'Ireland' },
      { option: 'israel', label: 'Israel' },
      { option: 'italy', label: 'Italy' },
      { option: 'jamaica', label: 'Jamaica' },
      { option: 'japan', label: 'Japan' },
      { option: 'jordan', label: 'Jordan' },
      { option: 'kazakhstan', label: 'Kazakhstan' },
      { option: 'kenya', label: 'Kenya' },
      { option: 'kiribati', label: 'Kiribati' },
      { option: 'korea_north', label: 'Korea (North)' },
      { option: 'korea_south', label: 'Korea (South)' },
      { option: 'kosovo', label: 'Kosovo' },
      { option: 'kuwait', label: 'Kuwait' },
      { option: 'kyrgyzstan', label: 'Kyrgyzstan' },
      { option: 'laos', label: 'Laos' },
      { option: 'latvia', label: 'Latvia' },
      { option: 'lebanon', label: 'Lebanon' },
      { option: 'lesotho', label: 'Lesotho' },
      { option: 'liberia', label: 'Liberia' },
      { option: 'libya', label: 'Libya' },
      { option: 'liechtenstein', label: 'Liechtenstein' },
      { option: 'lithuania', label: 'Lithuania' },
      { option: 'luxembourg', label: 'Luxembourg' },
      { option: 'madagascar', label: 'Madagascar' },
      { option: 'malawi', label: 'Malawi' },
      { option: 'malaysia', label: 'Malaysia' },
      { option: 'maldives', label: 'Maldives' },
      { option: 'mali', label: 'Mali' },
      { option: 'malta', label: 'Malta' },
      { option: 'marshall_islands', label: 'Marshall Islands' },
      { option: 'mauritania', label: 'Mauritania' },
      { option: 'mauritius', label: 'Mauritius' },
      { option: 'mexico', label: 'Mexico' },
      { option: 'micronesia', label: 'Micronesia' },
      { option: 'moldova', label: 'Moldova' },
      { option: 'monaco', label: 'Monaco' },
      { option: 'mongolia', label: 'Mongolia' },
      { option: 'montenegro', label: 'Montenegro' },
      { option: 'morocco', label: 'Morocco' },
      { option: 'mozambique', label: 'Mozambique' },
      { option: 'myanmar', label: 'Myanmar' },
      { option: 'namibia', label: 'Namibia' },
      { option: 'nauru', label: 'Nauru' },
      { option: 'nepal', label: 'Nepal' },
      { option: 'netherlands', label: 'Netherlands' },
      { option: 'new_zealand', label: 'New Zealand' },
      { option: 'nicaragua', label: 'Nicaragua' },
      { option: 'niger', label: 'Niger' },
      { option: 'nigeria', label: 'Nigeria' },
      { option: 'north_macedonia', label: 'North Macedonia' },
      { option: 'norway', label: 'Norway' },
      { option: 'oman', label: 'Oman' },
      { option: 'pakistan', label: 'Pakistan' },
      { option: 'palau', label: 'Palau' },
      { option: 'palestine', label: 'Palestine' },
      { option: 'panama', label: 'Panama' },
      { option: 'papua_new_guinea', label: 'Papua New Guinea' },
      { option: 'paraguay', label: 'Paraguay' },
      { option: 'peru', label: 'Peru' },
      { option: 'philippines', label: 'Philippines' },
      { option: 'poland', label: 'Poland' },
      { option: 'portugal', label: 'Portugal' },
      { option: 'qatar', label: 'Qatar' },
      { option: 'romania', label: 'Romania' },
      { option: 'russia', label: 'Russia' },
      { option: 'rwanda', label: 'Rwanda' },
      { option: 'saint_kitts_and_nevis', label: 'Saint Kitts and Nevis' },
      { option: 'saint_lucia', label: 'Saint Lucia' },
      { option: 'saint_vincent_and_the_grenadines', label: 'Saint Vincent and the Grenadines' },
      { option: 'samoa', label: 'Samoa' },
      { option: 'san_marino', label: 'San Marino' },
      { option: 'sao_tome_and_principe', label: 'São Tomé and Príncipe' },
      { option: 'saudi_arabia', label: 'Saudi Arabia' },
      { option: 'senegal', label: 'Senegal' },
      { option: 'serbia', label: 'Serbia' },
      { option: 'seychelles', label: 'Seychelles' },
      { option: 'sierra_leone', label: 'Sierra Leone' },
      { option: 'singapore', label: 'Singapore' },
      { option: 'slovakia', label: 'Slovakia' },
      { option: 'slovenia', label: 'Slovenia' },
      { option: 'solomon_islands', label: 'Solomon Islands' },
      { option: 'somalia', label: 'Somalia' },
      { option: 'south_africa', label: 'South Africa' },
      { option: 'south_sudan', label: 'South Sudan' },
      { option: 'spain', label: 'Spain' },
      { option: 'sri_lanka', label: 'Sri Lanka' },
      { option: 'sudan', label: 'Sudan' },
      { option: 'suriname', label: 'Suriname' },
      { option: 'sweden', label: 'Sweden' },
      { option: 'switzerland', label: 'Switzerland' },
      { option: 'syria', label: 'Syria' },
      { option: 'taiwan', label: 'Taiwan' },
      { option: 'tajikistan', label: 'Tajikistan' },
      { option: 'tanzania', label: 'Tanzania' },
      { option: 'thailand', label: 'Thailand' },
      { option: 'timorleste', label: 'Timor-Leste' },
      { option: 'togo', label: 'Togo' },
      { option: 'tonga', label: 'Tonga' },
      { option: 'trinidad_and_tobago', label: 'Trinidad and Tobago' },
      { option: 'tunisia', label: 'Tunisia' },
      { option: 'turkey', label: 'Turkey' },
      { option: 'turkmenistan', label: 'Turkmenistan' },
      { option: 'tuvalu', label: 'Tuvalu' },
      { option: 'uganda', label: 'Uganda' },
      { option: 'ukraine', label: 'Ukraine' },
      { option: 'united_arab_emirates', label: 'United Arab Emirates' },
      { option: 'united_kingdom', label: 'United Kingdom' },
      { option: 'united_states', label: 'United States' },
      { option: 'uruguay', label: 'Uruguay' },
      { option: 'uzbekistan', label: 'Uzbekistan' },
      { option: 'vanuatu', label: 'Vanuatu' },
      { option: 'vatican_city', label: 'Vatican City' },
      { option: 'venezuela', label: 'Venezuela' },
      { option: 'vietnam', label: 'Vietnam' },
      { option: 'yemen', label: 'Yemen' },
      { option: 'zambia', label: 'Zambia' },
      { option: 'zimbabwe', label: 'Zimbabwe' },
      { option: 'unknown', label: 'Unknown' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Made In',
      group: 'primary',
      searchMode: 'has_all',
    },
    showConfig: {
      label: 'Made In',
      isDetail: true,
    },
    saveConfig: {
      label: 'Made In',
      isRequired: false,
    },
  },
  {
    // Global Condition filter (decided Aug 19, 2026 — plans/category-nav-design-context.md in git history).
    // The value is entered via the hardcoded five-card picker on EditListingDetailsForm
    // (publicData.condition), NOT through this config: this entry exists only to power
    // the stock SearchPage filter. It is suppressed on the edit form (REMOVE_LISTING_FIELD_IDS)
    // and on the listing page (isDetail: false — the order panel already shows condition).
    key: 'condition',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'new', label: 'New' },
      { option: 'excellent', label: 'Excellent' },
      { option: 'good', label: 'Good' },
      { option: 'fair', label: 'Fair' },
      { option: 'poor', label: 'Poor' },
    ],
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectMultipleFilter',
      label: 'Condition',
      group: 'primary',
    },
    showConfig: {
      label: 'Condition',
      isDetail: false,
    },
    saveConfig: {
      label: 'Condition',
      isRequired: false,
    },
  },
];

///////////////////////////////////////////////////////////////////////
// Configurations related to listing types and transaction processes //
///////////////////////////////////////////////////////////////////////

// A presets of supported listing configurations
//
// Note 1: With first iteration of hosted configs, we are unlikely to support
//         multiple listing types, even though this template has some
//         rudimentary support for it.
// Note 2: transaction type is part of listing type. It defines what transaction process and units
//         are used when transaction is created against a specific listing.

/**
 * Configuration options for listing experience:
 * - listingType:         Unique string. This will be saved to listing's public data on
 *                        EditListingWizard.
 * - label                Label for the listing type. Used as microcopy for options to select
 *                        listing type in EditListingWizard.
 * - transactionType      Set of configurations how this listing type will behave when transaction is
 *                        created.
 *   - process              Transaction process.
 *                          The process must match one of the processes that this client app can handle
 *                          (check src/util/transactions/transaction.js) and the process must also exists in correct
 *                          marketplace environment.
 *   - alias                Valid alias for the aforementioned process. This will be saved to listing's
 *                          public data as transctionProcessAlias and transaction is initiated with this.
 *   - unitType             Unit type is mainly used as pricing unit. This will be saved to
 *                          transaction's protected data.
 *                          Recommendation: don't use same unit types in completely different processes
 *                          ('item' sold should not be priced the same as 'item' booked).
 * - stockType            This is relevant only to listings using default-purchase process.
 *                        If set to 'oneItem', stock management is not showed and the listing is
 *                        considered unique (stock = 1).
 *                        Possible values: 'oneItem' and 'multipleItems'.
 *                        Default: 'multipleItems'.
 * - defaultListingFields This is relevant only to listings using default-inquiry process atm.
 *                        It contains price: true/false value to indicate, whether price should be shown.
 *                        If defaultListingFields.price is not explicitly set to _false_, price will be shown.
 */

export const listingTypes = [
  {
    listingType: 'daily-booking',
    label: 'Daily booking',
    transactionType: {
      process: 'default-booking',
      alias: 'default-booking/release-1',
      unitType: 'day',
    },
  },
  {
    listingType: 'product-selling',
    label: 'Sell musical instruments',
    transactionType: {
      process: 'default-purchase',
      alias: 'default-purchase/release-1',
      unitType: 'item',
    },
    stockType: 'multipleItems',
  },

  // // Here are some examples for other listingTypes
  // // TODO: SearchPage does not work well if both booking and product selling are used at the same time
  // {
  //   listingType: 'nightly-booking',
  //   label: 'Nightly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'night',
  //   },
  // },
  // {
  //   listingType: 'hourly-booking',
  //   label: 'Hourly booking',
  //   transactionType: {
  //     process: 'default-booking',
  //     alias: 'default-booking/release-1',
  //     unitType: 'hour',
  //   },
  // },
  // {
  //   listingType: 'inquiry',
  //   label: 'Inquiry',
  //   transactionType: {
  //     process: 'default-inquiry',
  //     alias: 'default-inquiry/release-1',
  //     unitType: 'inquiry',
  //   },
  //   defaultListingFields: {
  //     price: false,
  //   },
  // },
];

// SearchPage can enforce listing query to only those listings with valid listingType
// However, it only works if you have set 'enum' type search schema for the public data fields
//   - listingType
//
//  Similar setup could be expanded to 2 other extended data fields:
//   - transactionProcessAlias
//   - unitType
//
// Read More:
// https://www.sharetribe.com/docs/how-to/manage-search-schemas-with-flex-cli/#adding-listing-search-schemas
export const enforceValidListingType = false;
